import type {
  ActivitySubjectType,
  AdditionalCostType,
  AncillaryCostsType,
  ApiCallCredentialClass,
  ApiCallRejectionReason,
  ApiKeyRequestOutcomeDto,
  DashboardDataGap,
  DashboardFlaggedDeal,
  EmailLogKind,
  EmailLogStatus,
  ArchiveReason,
  CommercialAmenityCode,
  AreaCode,
  Condition,
  ConstructionPhase,
  ContactSource,
  ContactType,
  DataFieldGroup,
  DataFieldName,
  DocumentCategoryGroup,
  DocumentSection,
  DocumentTemplateCategory,
  SigningProofEmail,
  DOCUMENT_SOURCE_INPUTS,
  DealPhase,
  InquiryOutcome,
  ProspectLossReason,
  ProspectPhase,
  ProspectSource,
  ProspectSourceGroup,
  ProspectStatus,
  DealStatus,
  DealSource,
  DealProbability,
  DealCompletionHorizon,
  DealLossReason,
  DealType,
  MandateType,
  LeadSource,
  PropertyContactRole,
  EnergyCertificateBasis,
  EnergyCertificateYear,
  EnergyClass,
  FiringType,
  Furnishing,
  HeatingType,
  InteriorQuality,
  LeaseTerm,
  MandatePropertyState,
  MarketingType,
  PropertyStatus,
  RentType,
  Subletting,
  SiteConstructibleType,
  SiteDevelopmentType,
  ResidentialAmenityCode,
  ValuationRequestStatus,
  ValuationObjectType,
  ValuationUsage,
  ValuationReason,
  ValuationTimeHorizon,
  ValuationPriceExpectationKind,
  ValuationPriceExpectationType,
  ValuationHeatingType,
  ValuationEnergyEfficiencyClass,
  ValuationHouseType,
  ValuationBasement,
  ValuationDevelopmentPlan,
  ValuationServicingStatus,
  ValuationContaminationSuspicion,
  ValuationGraduatedIndexRent,
  ValuationUnitBreakdown,
  ValuationQuality,
  ValuationCondition,
  ValuationBuildingElement,
  ValuationParkingSpaceType,
  ValuationUnitLettingStatus,
  ValuationFieldKey,
  CompletenessProduct,
  ValuationAgentRequestKind,
  ValuationSource,
  ValuationViewingKind,
  CompletenessMarker,
  VatTreatment,
  PropertyChangeSection,
} from '@immo/api-contract';
import type { EnumTranslation } from '../types';
import { DOCUMENT_CATEGORY_LABELS } from './document-category-labels';

// Domain-enum label tables, keyed by the en-US tokens from
// `@immo/api-contract`. Declared with `satisfies EnumTranslation<E>` so
// adding a value to the contract enum without a matching translation
// breaks `tsc` (ADR 0009, 2026-05-21 amendment).
const VALUATION_PRODUCT_LABELS = {
  ACQUISITION_BRIEFING: 'Akquisebriefing',
  VALUATION_1A: 'Bewertung 1a',
  VALUATION_1B: 'Bewertung 1b',
} as const satisfies EnumTranslation<ValuationAgentRequestKind>;

const COMPLETENESS_PRODUCT_LABELS = {
  ...VALUATION_PRODUCT_LABELS,
  EXPOSE: 'Exposé',
} as const satisfies EnumTranslation<CompletenessProduct>;

const VALUATION_SOURCE_KIND_LABELS = {
  OPERATOR: 'Operator-Bewertung',
  VALUATION_1A: 'Bewertung 1a',
  VALUATION_1B: 'Bewertung 1b · {viewingKind}',
} as const satisfies EnumTranslation<ValuationSource['kind']>;

const VALUATION_VIEWING_KIND_LABELS = {
  INTERIOR_AND_EXTERIOR: 'Innen- und Außenbesichtigung',
  EXTERIOR_ONLY: 'nur Außenbesichtigung',
  PARTIAL: 'Teilbesichtigung',
  VIDEO: 'Videobesichtigung',
  NONE: 'keine Besichtigung',
} as const satisfies EnumTranslation<ValuationViewingKind>;

const COMPLETENESS_MARKER_LABELS = {
  CONTRADICTED: 'widersprüchlich',
  ASSUMED: 'angenommen',
  UNKNOWN: 'unbekannt',
} as const satisfies EnumTranslation<CompletenessMarker>;

const MARKETING_TYPE_LABELS = {
  SALE: 'Verkauf',
  RENTAL: 'Vermietung',
} as const satisfies EnumTranslation<MarketingType>;

const PROPERTY_STATUS_LABELS = {
  DRAFT: 'Entwurf',
  IN_ACQUISITION: 'In Akquise',
  ACTIVE: 'In Vermarktung',
  INACTIVE: 'Pausiert',
  ARCHIVED: 'Archiviert',
} as const satisfies EnumTranslation<PropertyStatus>;

const ARCHIVE_REASON_LABELS = {
  SOLD: 'Verkauft',
  RENTED: 'Vermietet',
  TRANSACTED_EXTERNALLY: 'Anderweitig verkauft/vermietet',
  WITHDRAWN: 'Zurückgezogen',
  MANDATE_EXPIRED: 'Auftrag ausgelaufen',
  MANDATE_TERMINATED: 'Auftrag gekündigt',
  DUPLICATE: 'Dublette / Fehleintrag',
  OTHER: 'Sonstiges',
} as const satisfies EnumTranslation<ArchiveReason>;

const CONDITION_LABELS = {
  FIRST_OCCUPATION: 'Erstbezug',
  LIKE_NEW: 'Neuwertig',
  WELL_MAINTAINED: 'Gepflegt',
  MODERNISED: 'Modernisiert',
  PARTIALLY_RENOVATED: 'Teilsaniert',
  FULLY_RENOVATED: 'Vollsaniert',
  PARTIALLY_FULLY_RENOVATED: 'Teil-/Vollsaniert',
  NEEDS_PARTIAL_FULL_RENOVATION: 'Teil-/Vollsanierungsbedürftig',
  NEEDS_RENOVATION: 'Renovierungsbedürftig',
  DILAPIDATED: 'Abbruchreif',
  BY_ARRANGEMENT: 'Nach Vereinbarung',
} as const satisfies EnumTranslation<Condition>;

const INTERIOR_QUALITY_LABELS = {
  STANDARD: 'Standard',
  UPSCALE: 'Gehoben',
  LUXURY: 'Luxus',
} as const satisfies EnumTranslation<InteriorQuality>;

const CONSTRUCTION_PHASE_LABELS = {
  PROJECTED: 'Projektiert',
  UNDER_CONSTRUCTION: 'Im Bau',
  COMPLETED: 'Fertiggestellt',
} as const satisfies EnumTranslation<ConstructionPhase>;

const ENERGY_CERTIFICATE_BASIS_LABELS = {
  DEMAND: 'Bedarfsausweis',
  CONSUMPTION: 'Verbrauchsausweis',
} as const satisfies EnumTranslation<EnergyCertificateBasis>;

const ENERGY_CERTIFICATE_YEAR_LABELS = {
  EN_2008: 'EnEV 2008',
  EN_2014: 'EnEV 2014',
  GMODG_2027: 'GModG (ab 2027)',
  WITHOUT: 'Ohne Ausweis',
  NOT_REQUIRED: 'Nicht erforderlich',
  AT_VIEWING: 'Bei Besichtigung',
} as const satisfies EnumTranslation<EnergyCertificateYear>;

// IMM-614.2: the atomic address field labels, declared ONCE and consumed by the
// read card, the edit dialog and the edit form, so the three surfaces cannot
// disagree. They used to: `district` read „Stadtteil"/„Bezirk"/„Bezirk" and
// `city` read „Stadt"/„Ort"/„Ort" across the three. Composite read-card labels
// („Straße & Nr.", „PLZ & Ort") are deliberately NOT here — each covers two
// inputs and has no 1:1 edit counterpart. Same convention as the enum-label
// constants above (e.g. ENERGY_CERTIFICATE_YEAR_LABELS).
const ADDRESS_FIELD_LABELS = {
  district: 'Stadtteil',
  city: 'Ort',
  country: 'Land',
  state: 'Bundesland',
} as const;

const ENERGY_CLASS_LABELS = {
  A_PLUS: 'A+',
  A: 'A',
  B: 'B',
  C: 'C',
  D: 'D',
  E: 'E',
  F: 'F',
  G: 'G',
  H: 'H',
} as const satisfies EnumTranslation<EnergyClass>;
/** The 20 catalog group names, shared by the fact review and the capture. */
const FACT_KEY_GROUP_LABELS = {
  AUF: 'Auftrag, Stichtage und Besichtigung',
  KAT: 'Kataster und Grundstück',
  LAG: 'Lage und Umfeld',
  PLR: 'Planungsrecht und öffentlich-rechtliche Lage',
  ERS: 'Erschließung und Beiträge',
  GB: 'Grundbuch, Rechte und Baulasten',
  GEB: 'Gebäude allgemein',
  FLA: 'Flächen',
  BAU: 'Bauteile und Ausstattung',
  ENE: 'Energie und Haustechnik',
  MOD: 'Modernisierung',
  ZUS: 'Zustand und Schäden',
  NEB: 'Nebengebäude, Stellplätze und Außenanlagen',
  NUT: 'Nutzung und Vermietung',
  WEG: 'Wohnungseigentum',
  ZVG: 'Zwangsversteigerung',
  UNT: 'Unterlagen und Auskünfte',
  SON: 'Sonstiges',
  VMK: 'Vermarktung',
  GEW: 'Gewerbe',
} as const;

/**
 * The enum↔label binding the operator Dossier renders through (IMM-686), keyed by
 * the fact key the taxonomy seeds — NOT by the contract type name, because a
 * `DossierEntry` only ever carries its `factKey`.
 *
 * Composed from the five maps above rather than re-typed, so the Dossier and the
 * customer-app surfaces that already read them can never disagree: this adds ZERO
 * new copy. `dossier-value-labels.test.ts` asserts totality against
 * `DOSSIER_FACT_ENUM_VALUES` — every enum fact key present, every declared value
 * labelled — which is BL-069's remedy in the executable form a runtime field set
 * allows.
 */
const DOSSIER_FACT_ENUM_LABELS = {
  'ZUS.OBJEKTZUSTAND': CONDITION_LABELS,
  'BAU.AUSSTATTUNGSQUALITAET': INTERIOR_QUALITY_LABELS,
  'GEB.BAUPHASE': CONSTRUCTION_PHASE_LABELS,
  'ENE.ENERGIEAUSWEIS.ausweisart': ENERGY_CERTIFICATE_BASIS_LABELS,
  'ENE.ENERGIEAUSWEIS.effizienzklasse': ENERGY_CLASS_LABELS,
} as const;

const HEATING_TYPE_LABELS = {
  STOVE: 'Ofen',
  FLOOR_LEVEL: 'Etagenheizung',
  CENTRAL: 'Zentralheizung',
  DISTRICT: 'Fernwärme',
  UNDERFLOOR: 'Fußbodenheizung',
} as const satisfies EnumTranslation<HeatingType>;

const ADDITIONAL_COST_TYPE_LABELS = {
  SERVICE_CHARGE: 'Nebenkosten',
  HEATING_COSTS: 'Heizkosten',
  MAINTENANCE_RESERVE: 'Instandhaltungsrücklage',
  RENT_SUBSIDY: 'Mietzuschuss',
  PARKING_SPACE_PRICE: 'Stellplatzpreis',
  FURNITURE_SURCHARGE: 'Möblierungszuschlag',
} as const satisfies EnumTranslation<AdditionalCostType>;

// IMM-788.1 — one dictionary block for the VAT treatment, imported by every
// price section/dialog that renders it (BL-082, BL-024's totality guard).
const VAT_TREATMENT_LABELS = {
  NET_PLUS_VAT: 'netto zzgl. USt',
  VAT_INCLUSIVE: 'inkl. USt',
} as const satisfies EnumTranslation<VatTreatment>;

const ANCILLARY_COSTS_TYPE_LABELS = {
  ADVANCE: 'Vorauszahlung',
  FLAT_RATE: 'Pauschale',
} as const satisfies EnumTranslation<AncillaryCostsType>;

const FURNISHING_LABELS = {
  NONE: 'nicht',
  PARTIALLY_FURNISHED: 'teilmöbliert',
  FURNISHED: 'möbliert',
} as const satisfies EnumTranslation<Furnishing>;

const LEASE_TERM_LABELS = {
  INDEFINITE: 'unbefristet',
  FIXED_TERM: 'befristet',
} as const satisfies EnumTranslation<LeaseTerm>;

const RENT_TYPE_LABELS = {
  FIXED: 'Festmiete',
  GRADUATED: 'Staffelmiete',
  INDEXED: 'Indexmiete',
} as const satisfies EnumTranslation<RentType>;

const SUBLETTING_LABELS = {
  ALLOWED: 'erlaubt',
  NOT_ALLOWED: 'nicht erlaubt',
  ON_REQUEST: 'auf Anfrage',
} as const satisfies EnumTranslation<Subletting>;

export const FIRING_TYPE_LABELS = {
  OIL: 'Öl',
  GAS: 'Gas',
  LIQUID_GAS: 'Flüssiggas',
  NATURAL_GAS_LIGHT: 'Erdgas leicht',
  NATURAL_GAS_HEAVY: 'Erdgas schwer',
  ELECTRIC: 'Elektro',
  ALTERNATIVE: 'Alternativ',
  SOLAR: 'Solar',
  GEOTHERMAL: 'Erdwärme',
  AIR_HEAT_PUMP: 'Luft-Wärmepumpe',
  DISTRICT: 'Fernwärme',
  DISTRICT_STEAM: 'Fernwärme-Dampf',
  LOCAL_HEAT: 'Nahwärme',
  HEAT_DELIVERY: 'Wärmelieferung',
  BLOCK_CHP: 'Blockheizkraftwerk',
  WATER_ELECTRIC: 'Wasser-Elektro',
  PELLET: 'Pellet',
  WOOD: 'Holz',
  COAL: 'Kohle',
  BIOENERGY: 'Bioenergie',
  WIND_ENERGY: 'Windenergie',
  WATER_ENERGY: 'Wasserenergie',
  ENVIRONMENTAL_HEAT: 'Umweltwärme',
} as const satisfies EnumTranslation<FiringType>;

const SITE_DEVELOPMENT_TYPE_LABELS = {
  UNDEVELOPED: 'Unerschlossen',
  PARTIAL: 'Teilerschlossen',
  FULL: 'Voll erschlossen',
  LOCAL_STANDARD: 'Ortsüblich erschlossen',
} as const satisfies EnumTranslation<SiteDevelopmentType>;

const SITE_CONSTRUCTIBLE_TYPE_LABELS = {
  PARAGRAPH_34_NEIGHBOURHOOD: 'Innenbereich (§ 34 BauGB)',
  PARAGRAPH_35_OUTSIDE_AREA: 'Außenbereich (§ 35 BauGB)',
  B_PLAN: 'Bebauungsplan',
  NO_BUILDING_LAND: 'Kein Bauland',
} as const satisfies EnumTranslation<SiteConstructibleType>;

const RESIDENTIAL_AMENITY_LABELS = {
  BALCONY: 'Balkon',
  GARDEN: 'Garten',
  LIFT: 'Aufzug',
  BARRIER_FREE: 'Barrierefrei',
  GUEST_TOILET: 'Gäste-WC',
  BUILT_IN_KITCHEN: 'Einbauküche',
  LISTED_MONUMENT: 'Denkmalschutz',
  LODGER_FLAT: 'Einliegerwohnung',
  FLAT_SHARE_SUITABLE: 'WG-geeignet',
  HOLIDAY_HOME: 'Ferienhaus / -wohnung',
  REQUIRES_CERTIFICATE_OF_ELIGIBILITY: 'Wohnberechtigungsschein erforderlich',
  LAN_CABLING: 'LAN-Verkabelung',
  AIR_CONDITIONING: 'Klimaanlage',
  PARKING_SPACES: 'Stellplätze',
  NUMBER_OF_BALCONIES: 'Anzahl Balkone',
  NUMBER_OF_TERRACES: 'Anzahl Terrassen',
  PARKING_SPACE_PRICE: 'Stellplatz-Preis',
  CELLAR_STATE: 'Keller',
  PETS_POLICY: 'Haustiere',
  PARKING_SPACE_TYPE: 'Stellplatz-Art',
  FLOORING_TYPE: 'Bodenbelag',
} as const satisfies EnumTranslation<ResidentialAmenityCode>;

const COMMERCIAL_AMENITY_LABELS = {
  GOODS_LIFT: 'Lastenaufzug',
  LOADING_RAMP: 'Laderampe',
  HALL_HEIGHT: 'Hallenhöhe',
  GOODS_LIFT_LOAD: 'Lastenaufzug-Tragkraft',
  FLOOR_LOAD: 'Bodenbelastbarkeit',
  CONNECTED_LOAD: 'Anschlussleistung',
  CRANE_RUNWAY_LOAD: 'Kranbahn-Tragkraft',
  SHOP_WINDOW_LENGTH: 'Schaufensterlänge',
  NUMBER_OF_SEATS: 'Sitzplätze',
  NUMBER_OF_BEDS: 'Betten',
} as const satisfies EnumTranslation<CommercialAmenityCode>;

const CONTACT_TYPE_LABELS = {
  OWNER: 'Eigentümer',
  BUYER: 'Käufer',
  TENANT: 'Mieter',
  LANDLORD: 'Vermieter',
  PARTNER: 'Partner',
} as const satisfies EnumTranslation<ContactType>;

const PROPERTY_CONTACT_ROLE_LABELS = {
  OWNER: 'Eigentümer',
  PURCHASE_PROSPECT: 'Kaufinteressent',
  RENTAL_PROSPECT: 'Mietinteressent',
  TENANT: 'Mieter',
  PROPERTY_MANAGER: 'Hausverwalter',
  EXTERNAL_BROKER: 'Externer Makler',
  NOTARY: 'Notar',
  APPRAISER: 'Gutachter',
  CRAFTSMAN: 'Handwerker',
  FINANCIER: 'Finanzierer',
  OTHER: 'Sonstige',
} as const satisfies EnumTranslation<PropertyContactRole>;

// IMM-218 — document taxonomy labels. Group labels drive the filter pills,
// the grouped list headers and the category-picker headers; the category
// labels are the ~40 document types shown in the tags + picker options.
const DOCUMENT_CATEGORY_GROUP_LABELS = {
  MARKETING: 'Vermarktung',
  ENERGY_TECHNICAL: 'Energie & Technik',
  OWNERSHIP_LAND_REGISTRY: 'Eigentum & Grundbuch',
  BUILDING_PERMITS: 'Bau & Genehmigung',
  ADMINISTRATION: 'Verwaltung',
  CONTRACTS_TRANSACTION: 'Verträge & Transaktion',
  FINANCE_TAX: 'Finanzen & Steuern',
  PERSON_LEGITIMATION: 'Person & Legitimation',
  INSURANCE: 'Versicherung',
  OTHER: 'Sonstiges',
} as const satisfies EnumTranslation<DocumentCategoryGroup>;

const DOCUMENT_SECTION_LABELS = {
  PROPERTY: 'Objektunterlagen',
  INTERNAL: 'Interne Unterlagen',
} as const satisfies EnumTranslation<DocumentSection>;

// IMM-451.1 — die beiden Auto-Einsortierungs-Marker. Eigene Konstante seit
// IMM-501.3, weil die Bewertungs-Detailseite des Operators dieselben Zustände
// zeigt: derselbe Zustand muss auf jeder Oberfläche denselben Satz sagen.
const DOCUMENT_CLASSIFICATION_MARKERS = {
  sorting: 'Wird einsortiert …',
  'auto-sorted': 'Automatisch einsortiert',
} as const;

const CONTACT_SOURCE_LABELS = {
  MANUAL: 'Manuell',
  PARTNER: 'Partner',
  PORTAL: 'Portal',
  WEBSITE: 'Website',
  REFERRAL: 'Empfehlung',
  COLD_OUTREACH: 'Kaltakquise',
  OTHER: 'Sonstiges',
} as const satisfies EnumTranslation<ContactSource>;

const AREA_CODE_LABELS = {
  LIVING_AREA: 'Wohnfläche',
  PLOT_AREA: 'Grundstücksfläche',
  USABLE_AREA: 'Nutzfläche',
  TOTAL_AREA: 'Gesamtfläche',
  NET_FLOOR_SPACE: 'Hauptnutzfläche',
  OFFICE_AREA: 'Bürofläche',
  RETAIL_AREA: 'Verkaufsfläche',
  SHOP_AREA: 'Ladenfläche',
  STORAGE_AREA: 'Lagerfläche',
  HALL_AREA: 'Hallenfläche',
  GASTRONOMY_AREA: 'Gastrofläche',
  LETTABLE_AREA: 'Vermietbare Fläche',
  PARKING_SPACES: 'Stellplätze',
  ADDITIONAL_AREA: 'Zusatzfläche',
  BALCONY_AREA: 'Balkonfläche',
  CUBATURE: 'Kubatur',
  MIN_DIVISIBLE_AREA: 'Min. teilbar',
  ROOMS: 'Zimmer',
  BEDROOMS: 'Schlafzimmer',
  BATHROOMS: 'Bäder',
  FLOORS_TOTAL: 'Etagen',
  FLOOR: 'Etage',
} as const satisfies EnumTranslation<AreaCode>;

const PROSPECT_PHASE_LABELS = {
  INTEREST: 'Interesse',
  WITHDRAWAL_NOTICE_CONFIRMED: 'Widerruf bestätigt',
  CONTACTED: 'Kontaktiert',
  VIEWING_PROPOSED: 'Terminvorschlag verschickt',
  VIEWING_SCHEDULED: 'Zu Termin eingeladen',
  VIEWING_COMPLETED: 'Besichtigung erfolgt',
  OFFER_RECEIVED: 'Kaufangebot',
  FINANCING_CONFIRMED: 'Finanzierung nachgewiesen',
  RESERVED: 'Reserviert',
  NOTARY_APPOINTMENT: 'Notartermin',
  PURCHASED: 'Gekauft',
} as const satisfies EnumTranslation<ProspectPhase>;

const PROSPECT_STATUS_LABELS = {
  ACTIVE: 'Aktiv',
  WON: 'Gewonnen',
  LOST: 'Verloren',
} as const satisfies EnumTranslation<ProspectStatus>;

const PROSPECT_LOSS_REASON_LABELS = {
  NO_LONGER_INTERESTED: 'Kein Interesse mehr',
  WITHDRAWAL_EXERCISED: 'Widerruf ausgeübt',
  BOUGHT_ELSEWHERE: 'Anderes Objekt gekauft',
  FINANCING_FAILED: 'Finanzierung nicht möglich',
  PRICE_TOO_HIGH: 'Preis zu hoch',
  PROPERTY_NOT_SUITABLE: 'Objekt passt nicht',
  SOLD_TO_OTHER: 'Objekt anderweitig verkauft',
  UNREACHABLE: 'Nicht erreichbar',
  OTHER: 'Sonstiges',
} as const satisfies EnumTranslation<ProspectLossReason>;

const PROSPECT_SOURCE_LABELS = {
  IMMOSCOUT24: 'ImmoScout24',
  IMMOWELT: 'Immowelt',
  KLEINANZEIGEN: 'Kleinanzeigen',
  OTHER_PORTAL: 'Anderes Portal',
  WEBSITE: 'Website',
  WEB_EXPOSE: 'Web-Exposé',
  SOCIAL_MEDIA: 'Soziale Medien',
  PROPERTY_SIGN: 'Schild am Objekt',
  SHOP_WINDOW_OR_PRINT: 'Schaufenster / Print',
  BUYER_DATABASE_OUTREACH: 'Aktive Ansprache aus Kartei',
  REFERRAL: 'Empfehlung',
  TIPSTER_OR_PARTNER: 'Tippgeber / Partner',
  PHONE: 'Telefon',
  EMAIL: 'E-Mail',
  OPEN_HOUSE: 'Offene Besichtigung',
  OTHER: 'Sonstiges',
} as const satisfies EnumTranslation<ProspectSource>;

const INQUIRY_OUTCOME_LABELS = {
  PROSPECT_CREATED: 'Interessent angelegt',
  ATTACHED_TO_ACTIVE_PROSPECT: 'An bestehenden Interessenten angehängt',
  NO_PROSPECT_PROPERTY_ARCHIVED: 'Kein Interessent — Objekt archiviert',
  NO_PROSPECT_RENTAL: 'Kein Interessent — Vermietung',
} as const satisfies EnumTranslation<InquiryOutcome>;

const PROSPECT_SOURCE_GROUP_LABELS = {
  PORTALS: 'Portale',
  OWN_CHANNELS: 'Eigene Kanäle',
  AGENT_OUTREACH: 'Makler aktiv',
  NETWORK: 'Netzwerk',
  DIRECT_CONTACT: 'Direktkontakt',
} as const satisfies EnumTranslation<ProspectSourceGroup>;

const DEAL_PHASE_LABELS = {
  IN_EXCHANGE: 'Im Austausch',
  APPOINTMENT_SCHEDULED: 'Termin vereinbart',
  APPOINTMENT_COMPLETED: 'Termin erfolgt',
  VALUATION_COMPLETED: 'Bewertung erfolgt',
  MANDATE_RECEIVED: 'Auftrag erhalten',
  CONTRACT_SENT: 'Vertrag verschickt',
  CONTRACT_RECEIVED: 'Vertrag erhalten',
  EXPOSE_REVIEW: 'Exposé-Prüfung',
  ONLINE: 'Online',
} as const satisfies EnumTranslation<DealPhase>;

const DEAL_STATUS_LABELS = {
  DRAFT: 'Entwurf',
  ACTIVE: 'Aktiv',
  DEFERRED: 'Zurückgestellt',
  WON: 'Gewonnen',
  LOST: 'Verloren',
} as const satisfies EnumTranslation<DealStatus>;

const DEAL_SOURCE_LABELS = {
  MANUAL: 'Manuell',
  LEAD: 'Lead',
} as const satisfies EnumTranslation<DealSource>;

// One table, two mount points (IMM-456.6): the Leads-Liste and the Deal-Rail's
// Herkunft-Karte name the same acquisition channel, so a second copy is exactly
// how the two surfaces would start wording it differently.
const LEAD_SOURCE_LABELS = {
  MANUAL: 'Manuell',
  PARTNER: 'Partner',
  PORTAL: 'Portal',
  WEBSITE: 'Website',
  REFERRAL: 'Empfehlung',
  COLD_OUTREACH: 'Kaltakquise',
  OTHER: 'Sonstiges',
} as const satisfies EnumTranslation<LeadSource>;

const DEAL_PROBABILITY_LABELS = {
  LOW: 'Niedrig',
  MEDIUM: 'Mittel',
  HIGH: 'Hoch',
} as const satisfies EnumTranslation<DealProbability>;

const DEAL_COMPLETION_HORIZON_LABELS = {
  UNDER_ONE_MONTH: 'Unter 1 Monat',
  ONE_TO_THREE_MONTHS: '1–3 Monate',
  THREE_TO_SIX_MONTHS: '3–6 Monate',
  OVER_SIX_MONTHS: 'Über 6 Monate',
} as const satisfies EnumTranslation<DealCompletionHorizon>;

const DEAL_TYPE_LABELS = {
  SALE: 'Verkauf',
  RENTAL: 'Vermietung',
} as const satisfies EnumTranslation<DealType>;

const DEAL_MANDATE_TYPE_LABELS = {
  SIMPLE_BROKERAGE: 'Einfacher Maklerauftrag',
  SIMPLE_EXCLUSIVE: 'Einfacher Alleinauftrag',
  QUALIFIED_EXCLUSIVE: 'Qualifizierter Alleinauftrag',
} as const satisfies EnumTranslation<MandateType>;

const DEAL_LOSS_REASON_LABELS = {
  OTHER_BROKER: 'Anderer Makler',
  OWNER_NOT_SELLING: 'Eigentümer verkauft nicht mehr',
  OWNER_UNREACHABLE: 'Eigentümer nicht erreichbar',
  UNREALISTIC_PRICE: 'Unrealistische Preisvorstellung',
  OWNER_SELLS_PRIVATE: 'Eigentümer verkauft privat',
  WRONG_CONTACT_TYPE: 'Falscher Ansprechpartner',
  OWNER_UNDECIDED: 'Eigentümer unentschlossen',
  PROPERTY_NOT_SELLABLE: 'Objekt nicht vermarktbar',
  SALE_UNECONOMICAL: 'Verkauf unwirtschaftlich',
  OWNER_NO_RESPONSE: 'Keine Rückmeldung',
  OWNER_ALREADY_KNOWN: 'Eigentümer bereits anderweitig bekannt',
  NO_AGREEMENT: 'Keine Einigung erzielt',
  OTHER: 'Sonstiges',
} as const satisfies EnumTranslation<DealLossReason>;

// Bewertungsanfrage enum labels (IMM-407.2.1), keyed by the German lexeme tokens
// the valuation contract persists. `satisfies EnumTranslation<E>` forces a label
// for every token so a new contract enum value breaks `tsc` until translated.
const VALUATION_STATUS_LABELS = {
  DRAFT: 'Entwurf',
  REQUESTED: 'Angefragt',
  IN_PROGRESS: 'In Bearbeitung',
  AWAITING_RELEASE: 'Warte auf Freigabe',
  RELEASED: 'Freigegeben',
  WITHDRAWN: 'Zurückgezogen',
} as const satisfies EnumTranslation<ValuationRequestStatus>;

const VALUATION_OBJEKTART_LABELS = {
  CONDOMINIUM: 'Eigentumswohnung',
  SINGLE_FAMILY_TERRACED_HOUSE: 'Einfamilien-/Reihenhaus',
  MULTI_FAMILY_HOUSE: 'Mehrfamilienhaus',
  PLOT: 'Grundstück',
  COMMERCIAL_PROPERTY: 'Gewerbeobjekt',
  GARAGE_PARKING_SPACE: 'Garage/Stellplatz',
} as const satisfies EnumTranslation<ValuationObjectType>;

const VALUATION_NUTZUNG_LABELS = {
  OWNER_OCCUPIED: 'Selbstgenutzt',
  RENTED: 'Vermietet',
  PARTIALLY_RENTED: 'Teilvermietet',
  VACANT: 'Leerstehend',
} as const satisfies EnumTranslation<ValuationUsage>;

const VALUATION_ANLASS_LABELS = {
  SALE_PREPARATION: 'Verkaufsvorbereitung',
  ACQUISITION: 'Ankauf',
  INHERITANCE_DIVISION: 'Erbschaft/Teilung',
  DIVORCE: 'Scheidung',
  RELOCATION: 'Umzug',
  FINANCING: 'Finanzierung',
  INVESTMENT_DECISION: 'Kapitalanlage-Entscheidung',
  OTHER: 'Sonstiges',
} as const satisfies EnumTranslation<ValuationReason>;

const VALUATION_ZEITHORIZONT_LABELS = {
  QUICK_SALE: 'Schneller Verkauf gewünscht',
  WITHIN_3_MONTHS: 'Innerhalb 3 Monate',
  WITHIN_12_MONTHS: 'Innerhalb 12 Monate',
  NO_TIME_PRESSURE: 'Kein Zeitdruck',
  MARKET_EXPLORATION_ONLY: 'Nur Marktsondierung',
} as const satisfies EnumTranslation<ValuationTimeHorizon>;

const VALUATION_PREISVORSTELLUNG_ART_LABELS = {
  NONE: 'Keine',
  FIXED_PRICE: 'Festpreis',
  PRICE_RANGE: 'Preisspanne',
} as const satisfies EnumTranslation<ValuationPriceExpectationType>;

// Wessen Preisvorstellung — Formular, Vorschau und Operator-Detail
// beschriften die drei Erfassungen aus derselben Tabelle.
const VALUATION_PRICE_EXPECTATION_KIND_LABELS = {
  OWNER: 'Preisvorstellung des Eigentümers',
  BROKER: 'Preisvorstellung des Maklers',
  TARGET: 'Realistischer Zielpreis',
} as const satisfies EnumTranslation<ValuationPriceExpectationKind>;

// Type-specific enum labels (IMM-407.2.2), keyed by the German lexeme tokens.
const VALUATION_HEIZUNGSART_LABELS = {
  GAS: 'Gas',
  OIL: 'Öl',
  DISTRICT_HEATING: 'Fernwärme',
  HEAT_PUMP: 'Wärmepumpe',
  PELLET_WOOD: 'Pellet/Holz',
  NIGHT_STORAGE: 'Nachtspeicher',
  OTHER: 'Sonstige',
} as const satisfies EnumTranslation<ValuationHeatingType>;

const VALUATION_ENERGIEEFFIZIENZKLASSE_LABELS = {
  A_PLUS: 'A+',
  A: 'A',
  B: 'B',
  C: 'C',
  D: 'D',
  E: 'E',
  F: 'F',
  G: 'G',
  H: 'H',
  UNKNOWN: 'Unbekannt',
} as const satisfies EnumTranslation<ValuationEnergyEfficiencyClass>;

const VALUATION_HAUSTYP_LABELS = {
  SINGLE_FAMILY_HOUSE: 'Einfamilienhaus',
  SEMI_DETACHED_HOUSE: 'Doppelhaushälfte',
  MID_TERRACE_HOUSE: 'Reihenmittelhaus',
  END_TERRACE_HOUSE: 'Reihenendhaus',
  VILLA: 'Villa',
} as const satisfies EnumTranslation<ValuationHouseType>;

const VALUATION_UNTERKELLERT_LABELS = {
  NO_BASEMENT: 'Nicht basement',
  PARTIAL_BASEMENT: 'Teilunterkellert',
  FULL_BASEMENT: 'Vollunterkellert',
} as const satisfies EnumTranslation<ValuationBasement>;

const VALUATION_BEBAUUNGSPLAN_LABELS = {
  DEVELOPMENT_PLAN_IN_PLACE: 'B-Plan vorhanden',
  PARAGRAPH_34_BAUGB: '§ 34 BauGB (Innenbereich)',
  PARAGRAPH_35_BAUGB: '§ 35 BauGB (Außenbereich)',
  UNKNOWN: 'Unbekannt',
} as const satisfies EnumTranslation<ValuationDevelopmentPlan>;

const VALUATION_ERSCHLIESSUNGSZUSTAND_LABELS = {
  FULLY_SERVICED: 'Voll erschlossen',
  PARTIALLY_SERVICED: 'Teilerschlossen',
  NOT_SERVICED: 'Nicht erschlossen',
  UNKNOWN: 'Unbekannt',
} as const satisfies EnumTranslation<ValuationServicingStatus>;

const VALUATION_ALTLASTENVERDACHT_LABELS = {
  YES: 'Ja',
  NO: 'Nein',
  UNKNOWN: 'Unbekannt',
} as const satisfies EnumTranslation<ValuationContaminationSuspicion>;

const VALUATION_STAFFEL_INDEX_MIETE_LABELS = {
  NONE: 'Keine',
  GRADUATED_RENT: 'Staffelmiete',
  INDEX_RENT: 'Indexmiete',
} as const satisfies EnumTranslation<ValuationGraduatedIndexRent>;

const VALUATION_ANGABE_DER_EINHEITEN_LABELS = {
  AGGREGATE: 'Pauschal',
  INDIVIDUAL: 'Einzeln',
} as const satisfies EnumTranslation<ValuationUnitBreakdown>;

// Gesamtzustand + collection enums (IMM-407.2.3), keyed by the German lexeme tokens.
const VALUATION_QUALITAET_LABELS = {
  BASIC: 'Einfach',
  STANDARD: 'Normal',
  UPSCALE: 'Gehoben',
  HIGH_END: 'Stark gehoben',
} as const satisfies EnumTranslation<ValuationQuality>;

const VALUATION_ZUSTAND_LABELS = {
  NEEDS_MODERNISATION: 'Modernisierungsbedürftig',
  WELL_KEPT: 'Gepflegt',
  AS_NEW: 'Neuwertig',
} as const satisfies EnumTranslation<ValuationCondition>;

const VALUATION_GEBAEUDEELEMENT_LABELS = {
  KITCHEN: 'Küche',
  BATHROOM: 'Badezimmer',
  FLOORING: 'Fußboden',
  WINDOWS: 'Fenster',
} as const satisfies EnumTranslation<ValuationBuildingElement>;

const VALUATION_STELLPLATZART_LABELS = {
  GARAGE: 'Garage',
  UNDERGROUND_PARKING_SPACE: 'Tiefgaragenstellplatz',
  CARPORT: 'Carport',
  OUTDOOR_PARKING_SPACE: 'Außenstellplatz',
  DUPLEX_STACKER: 'Duplex/Doppelparker',
} as const satisfies EnumTranslation<ValuationParkingSpaceType>;

const VALUATION_EINHEIT_VERMIETUNGSSTATUS_LABELS = {
  LET: 'Vermietet',
  VACANT: 'Leerstehend',
} as const satisfies EnumTranslation<ValuationUnitLettingStatus>;

// Short field names, keyed by the type-field key. Used both as the block field
// labels and to name the fields the Objektart/Nutzung switch would discard
// (IMM-407.2.2, AC 22). Vermietungsquote is the LET share, never Leerstand (RD 10).
const VALUATION_FIELD_NAME_LABELS = {
  yearBuilt: 'Baujahr',
  plotArea: 'Grundstücksfläche',
  rooms: 'Zimmer',
  heatingType: 'Heizungsart',
  energyEfficiencyClass: 'Energieeffizienzklasse',
  lastModernisation: 'Letzte Modernisierung',
  modernisationsDone: 'Durchgeführte Modernisierungen',
  livingArea: 'Wohnfläche',
  floor: 'Etage',
  hasLift: 'Aufzug vorhanden',
  houseType: 'Haustyp',
  basement: 'Unterkellert',
  residentialUnits: 'Anzahl Wohneinheiten',
  commercialUnits: 'Anzahl Gewerbeeinheiten',
  totalLivingArea: 'Gesamtwohnfläche',
  commercialArea: 'Gewerbefläche',
  deferredMaintenance: 'Instandhaltungsstau',
  unitBreakdown: 'Angabe der Einheiten',
  developmentPlan: 'Bebauungsplan',
  groundAreaRatio: 'GRZ (Grundflächenzahl)',
  floorAreaRatio: 'GFZ (Geschossflächenzahl)',
  permittedDevelopment: 'Zulässige Bebauung',
  servicingStatus: 'Erschließungszustand',
  positivePreliminaryEnquiry: 'Positive Bauvoranfrage',
  hasBuildingPermit: 'Baugenehmigung vorhanden',
  hasExistingBuilding: 'Bestandsgebäude vorhanden',
  estimatedDemolitionCost: 'Abrisskosten geschätzt',
  contaminationSuspicion: 'Altlastenverdacht',
  actualRentMonthly: 'Ist-Miete (netto, monatlich)',
  serviceCharges: 'Umlagen / Nebenkosten',
  occupancyRate: 'Vermietungsquote (vermieteter Anteil)',
  tenancyStart: 'Mietvertrag Beginn',
  graduatedIndexRent: 'Staffel-/Indexmiete',
  monthlyServiceCharge: 'Hausgeld monatlich',
  nonRecoverableShare: 'davon nicht umlagefähig',
  maintenanceReserve: 'Instandhaltungsrücklage',
  coOwnershipShare: 'Miteigentumsanteil',
  resolvedSpecialLevies: 'Beschlossene Sonderumlagen',
  // Gesamtzustand (IMM-407.2.3) — per element × axis, named in the switch-discard
  // warning like every other field key.
  kitchenQuality: 'Küche – Qualität',
  kitchenCondition: 'Küche – Zustand',
  bathroomQuality: 'Badezimmer – Qualität',
  bathroomCondition: 'Badezimmer – Zustand',
  flooringQuality: 'Fußboden – Qualität',
  flooringCondition: 'Fußboden – Zustand',
  windowsQuality: 'Fenster – Qualität',
  windowsCondition: 'Fenster – Zustand',
} as const satisfies EnumTranslation<ValuationFieldKey>;

/**
 * Strings rendered by individual web-app pages and route segments.
 * Grouped under `pages.{route}` to mirror the route tree, so future
 * surface growth slots in without reshuffling existing keys.
 */
export const pages = {
  shell: {
    nav: {
      dashboard: 'Übersicht',
      workspace: 'Workspace',
      organisations: 'Standorte',
      members: 'Mitglieder',
      activities: 'Aktivitäten',
      chat: 'Ask AI',
      'chat-experimental': 'Experimentell',
      emails: 'E-Mails',
      'emails-inbox': 'Posteingang',
      'emails-outbox': 'Postausgang',
      'emails-expand': 'E-Mails aufklappen',
      'emails-collapse': 'E-Mails zuklappen',
      calendar: 'Kalender',
      assets: 'Assets',
      companies: 'Firmen',
      property: 'Immobilien',
      properties: 'Objekte',
      relationship: 'CRM',
      contacts: 'Kontakte',
      leads: 'Leads',
      owners: 'Eigentümer',
      acquisition: 'Akquise',
      acquisitions: 'Akquisitionen',
      deals: 'Deals',
      settings: 'Einstellungen',
      start: 'Start',
      akquise: 'Akquise',
      'akquise-flow': 'Lead → Auftrag',
      vermarktung: 'Vermarktung',
      'vermarktung-flow': 'Auftrag → Abschluss',
      stammdaten: 'Stammdaten',
      'owner-leads': 'Eigentümer-Leads',
      'acquisition-pipeline': 'Akquise-Pipeline',
      'deal-overview': 'Deal-Überblick',
      valuations: 'Bewertungen',
      mandates: 'Aufträge',
      prospects: 'Interessenten',
      'sales-pipeline': 'Verkaufs-Pipeline',
      persons: 'Kontakte',
      'real-estate': 'Immobilien',
      partner: 'Partner',
    },
    search: {
      placeholder: 'Suchen … Kontakte · Objekte · Deals · Leads · Dokumente',
      'topbar-hint': 'Klick oder ⌘K öffnet die Suche',
      'quick-nav-title': 'Schnell springen',
      'results-label': 'Suchergebnisse',
      'no-hits': 'Keine Treffer für „{query}".',
      'group-empty': 'Keine Treffer',
      truncated: '{shown} von {total}',
      // Die Zeile einer gekürzten Gruppe (IMM-448.4): sie öffnet die Liste des
      // Bereichs mit derselben Suche. Dokumente hat keine — es gibt keine
      // arbeitsbereichsweite Dokumentenliste, auf die sie zeigen könnte.
      'all-results': 'Alle Treffer in {area}',
      failed: 'Die Suche ist fehlgeschlagen. Bitte versuche es erneut.',
      footer: '↑ ↓ navigieren · ↵ öffnen · esc schließen',
      groups: {
        kontakt: 'Kontakte',
        objekt: 'Objekte',
        deal: 'Deals',
        lead: 'Leads',
        dokument: 'Dokumente',
      },
      // Every enum token a result row can carry on `secondaryToken`, in one flat
      // namespace — the backend never sends a translated label (ADR 0009 confines
      // `t()` to the communications and document generators), so the client
      // resolves them all here. Objekt-Status (IMM-448.2) plus, from IMM-448.3,
      // Deal-Phase, Lead-Status and Dokument-Kategorie. The token sets do not
      // overlap except on `ARCHIVED`, which reads the same for an Objekt and a
      // Lead.
      status: {
        objekt: { ...PROPERTY_STATUS_LABELS },
        deal: { ...DEAL_PHASE_LABELS },
        dokument: { ...DOCUMENT_CATEGORY_LABELS },
        lead: {
          NEW: 'Neu',
          CONTACTED: 'Kontaktiert',
          CONVERTED: 'Konvertiert',
          LOST: 'Verloren',
          ARCHIVED: 'Archiviert',
        },
      },
    },
    'user-menu': {
      settings: 'Einstellungen',
      'sign-out': 'Abmelden',
    },
    'dashboard-link': {
      back: 'Zurück zur Übersicht',
      chip: 'Aus der Übersicht gefiltert',
      clear: 'Filter entfernen',
      'chip-status': 'Status: {statuses}',
      'chip-waiting': 'ohne Reaktion seit über {hours} Std.',
      'chip-received': 'Eingang im gewählten Zeitraum',
    },
    'location-selector': {
      'workspace-label': 'Arbeitsbereich',
      'standort-label': 'Standort',
      'all-standorte': 'Alle Standorte',
      'all-my-standorte': 'Alle meine Standorte',
      'all-workspace-standorte': 'Alle Standorte in {workspace}',
      'modal-title': 'Standort wechseln',
      standort: 'Standort: {name}',
      // Tooltip on a workspace that can't be entered: its company onboarding is
      // still open and the member lacks the permission to complete it (IMM-322).
      'not-onboarded': 'Diese Organisation ist noch nicht vollständig eingerichtet',
    },
    'test-banner': {
      message: 'Testumgebung für Workspace <name>{workspaceName}</name>',
    },
    // Gehostete Sandbox (Kopie der Produktionsdaten); gebaut mit VITE_SANDBOX=true.
    'sandbox-banner': {
      message:
        '<name>Sandbox</name> — Kopie der Produktionsdaten. Alle E-Mails und Nachrichten gehen an das Engineering-Team, nicht an Kunden.',
    },
    'impersonation-banner': {
      message: 'Du agierst im Auftrag von <name>{memberName}</name>',
    },
  },
  chat: {
    'coming-soon': 'Bald verfügbar',
    headline: 'Frag mich alles',
    subline: 'Dein nächster Deal ist vielleicht nur eine Nachricht entfernt.',
    'disabled-note': 'Der KI-Chat ist noch in Entwicklung und steht dir bald zur Verfügung.',
    'input-label': 'Nachricht',
    'input-placeholder': 'Wie kann ich dir heute helfen?',
    'model-label': 'Modell wählen',
    'new-chat': 'Neuer Chat',
    'conversations-label': 'Unterhaltung wählen',
    untitled: 'Neue Unterhaltung',
    'page-read': 'Seite gelesen: {title}',
    attach: 'Anhang hinzufügen',
    voice: 'Spracheingabe',
    'voice-mode': 'Sprachmodus',
    working: 'Denkt nach...',
    // Live loader label, updated per run step (IMM-348): the engine emits the
    // current activity (`model` → thinking, `tool:<name>` → that tool) and the
    // client narrates it here. Unknown/other steps fall back to `thinking`.
    activity: {
      thinking: 'Denkt nach…',
      composing: 'Antwort wird formuliert…',
      deal_list: 'Deals werden durchsucht…',
      deal_detail: 'Deal-Details werden geladen…',
      listing_list: 'Objekte werden durchsucht…',
      listing_detail: 'Objektdetails werden geladen…',
    },
    // Live token counter (IMM-348), shown only when the token-count debug flag is
    // on (?feature_flag=token-count:enable). Formats a running total.
    tokens: '{count} Tokens',
    send: 'Senden',
    'empty-title': 'Frag mich alles',
    'empty-subline': 'Stell mir eine Frage zu deinen Objekten, Deals oder Kontakten.',
    'error-generic': 'Da ist etwas schiefgelaufen. Bitte versuche es noch einmal.',
    // Display labels for the assistant models, keyed by the backend's wire id
    // (IMM-348). The backend defines the model identity; these translate it.
    models: {
      'nvidia.nemotron-nano-3-30b': 'Nemotron Nano — schnell & günstig',
      'nvidia.nemotron-super-3-120b': 'Nemotron Super — leistungsstärker',
      'openai.gpt-oss-120b': 'GPT-OSS 120B — leistungsstark & offen',
      'amazon.nova-pro-v1:0': 'Amazon Nova Pro — vielseitig',
      'anthropic.claude-sonnet-5': 'Claude Sonnet — ausgewogen',
      'anthropic.claude-opus-4-8': 'Claude Opus — am gründlichsten',
      'anthropic.claude-haiku-4-5': 'Claude Haiku — am schnellsten',
    },
  },
  placeholder: {
    description: 'Dieser Bereich ist noch nicht verfügbar.',
  },
  home: {
    title: 'Übersicht',
    'signed-in-as': 'Angemeldet als {name}',
    'choose-standort': 'Wähle einen Standort, um seine Übersicht zu sehen.',
    'all-standorte': 'Alle Standorte',
    'as-of': 'Stand {date}, {time}',
    funnel: {
      title: 'Lead → Auftrag → Online',
      'excluded-valuation':
        '{count, plural, one {# Deal ohne Bewertung nicht enthalten} other {# Deals ohne Bewertung nicht enthalten}}',
      leads: {
        title: 'Leads',
        open: 'offen',
        received: '{count} eingegangen',
        converted: '{count} zum Deal',
        new: '{count} neu',
        contacted: '{count} kontaktiert',
        rate: '{rate} % → Deal',
        'rate-none': 'Keine neuen Leads im Zeitraum',
      },
      acquisition: { title: 'Deals in Akquise', courtage: 'Pipeline-Courtage', intake: '+{count} neu angelegt' },
      mandates: { title: 'Aufträge', courtage: 'Courtage', intake: '+{count} Auftrag erhalten' },
      online: {
        title: 'Online',
        unit: '{count, plural, one {Objekt} other {Objekte}}',
        courtage: 'Courtage',
        intake: '+{count} online gegangen',
        empty: 'Aktuell ist kein Objekt online.',
        since: 'seit {date}',
        prospects: '{count, plural, one {# Interessent} other {# Interessenten}}',
        more: '+{count} weitere',
      },
      'deals-unit': '{count, plural, one {Deal} other {Deals}}',
      'phase-count': '{phase} {count}',
    },
    period: {
      label: 'Zeitraum',
      LAST_7_DAYS: 'Letzte 7 Tage',
      LAST_30_DAYS: 'Letzte 30 Tage',
      LAST_90_DAYS: 'Letzte 90 Tage',
      ALL_TIME: 'Gesamt',
    },
    'new-leads': { title: 'Neue Leads', empty: 'Keine neuen Leads.' },
    'new-inquiries': { title: 'Neue Kundenanfragen', empty: 'Keine neuen Kundenanfragen.' },
    attention: {
      title: 'Braucht Aufmerksamkeit',
      view: 'Ansehen →',
      'calm-line': '✓ {text}',
      'view-named': 'Ansehen → {title}',
      preview: {
        more: '+ {count} weitere',
        'more-named': '+ {count} weitere · {title}',
        'overdue-days': '{days, plural, =0 {unter 1 Tag} one {# Tag} other {# Tage}} überfällig',
        'waiting-days': 'wartet {days, plural, =0 {unter 1 Tag} one {# Tag} other {# Tage}}',
        flag: {
          OVERDUE: 'Überfällig',
          NO_NEXT_STEP: 'Ohne nächsten Schritt',
          TOO_LONG_IN_PHASE: 'Zu lange in Phase',
        } satisfies Record<DashboardFlaggedDeal['flag'], string>,
        gap: {
          WITHOUT_VALUATION: 'Ohne Bewertung',
          UNASSIGNED: 'Nicht zugewiesen',
        } satisfies Record<DashboardDataGap, string>,
        'property-unavailable': 'Objekt nicht verfügbar',
      },
      'overdue-tasks': {
        title: 'Überfällige Aufgaben',
        context: 'Älteste seit {days, plural, =0 {weniger als einem Tag} one {# Tag} other {# Tagen}} überfällig',
        calm: 'Nichts überfällig',
      },
      'leads-without-response': {
        title: 'Leads ohne Reaktion',
        context: 'Längster wartet seit {days, plural, =0 {weniger als einem Tag} one {# Tag} other {# Tagen}}',
        calm: 'Alle Leads kontaktiert',
      },
      'deals-needing-action': {
        title: 'Deals mit Handlungsbedarf',
        part: {
          OVERDUE: '{count} überfällig',
          NO_NEXT_STEP: '{count} ohne nächsten Schritt',
          TOO_LONG_IN_PHASE: '{count} zu lange in Phase',
        },
        calm: 'Alle Deals auf Kurs',
      },
      'data-gaps': {
        title: 'Datenlücken',
        part: { 'without-valuation': '{count} ohne Bewertung', unassigned: '{count} ohne Zuständigkeit' },
        calm: 'Keine Lücken',
      },
    },
    upcoming: { title: 'Anstehende Aufgaben', all: 'Alle anzeigen', empty: 'Keine anstehenden Aufgaben.' },
    tables: {
      'show-all': 'Alle anzeigen',
      'show-all-count': 'Alle anzeigen ({count})',
      waiting: 'seit {count, plural, one {# Tag} other {# Tagen}}',
      'current-state': '{kind} · {date} · {text}',
      'no-contact': 'Noch kein Kontakt',
      column: {
        owner: 'Eigentümer',
        prospect: 'Interessent',
        property: 'Objekt',
        source: 'Quelle',
        received: 'Eingang',
        status: 'Status',
        'current-state': 'Aktueller Stand',
      },
    },
    setup: {
      title: 'Was noch zu tun ist',
      progress: '{done} von {total} erledigt',
      more: '+{count} weitere',
      // One chip per open step; a leader's chips name the member, a Makler's own do not.
      chip: {
        MEMBER_ONBOARDING: 'Profil vervollständigen',
        MAILBOX: { OPEN: 'Postfach verbinden', RECONNECT: 'Postfach neu verbinden' },
      },
      'chip-named': '{name}: {action}',
    },
    team: {
      title: 'Team',
      column: {
        member: 'Name',
        'active-deals': 'Aktive Deals',
        'pipeline-courtage': 'Pipeline-Courtage',
        'mandate-online-courtage': 'Courtage aus Aufträgen & Online',
        'overdue-tasks': 'Überfällige Aufgaben',
        'leads-without-response': 'Leads ohne Reaktion',
      },
      unassigned: 'Ohne Zuständigkeit',
      'figure-label': '{member}, {column}',
    },
    'block-failed': 'Dieser Bereich konnte nicht geladen werden.',
    // Names the block that failed: several can fail at once and the message alone does not say which.
    'block-name': {
      funnel: 'Lead → Auftrag → Online',
      'new-records': 'Neue Leads und Kundenanfragen',
      attention: 'Braucht Aufmerksamkeit',
      setup: 'Was noch zu tun ist',
      team: 'Team',
    },
    retry: 'Erneut laden',
    'count-link': '{label}: {count}',
  },
  error: {
    title: 'Seite konnte nicht geladen werden',
    body: 'Ein unerwarteter Fehler ist aufgetreten.',
    'try-again': 'Erneut versuchen',
    home: 'Zur Startseite',
  },
  loading: 'Wird geladen…',
  'not-found': {
    title: 'Nicht gefunden',
    body: 'Die angeforderte Seite existiert nicht.',
    'back-to-home': 'Zurück zur Startseite',
  },
  // Sandbox sign-in is engineer-facing tooling: English in both locales (PRD Copy, slice D5). Do not translate.
  sandbox: {
    'sign-in': {
      title: 'Sandbox sign-in',
      google: 'Sign in with Google',
      'signed-in-as': 'Signed in with Google as {email}',
      'signed-in-as-agent': 'Signed in with the agent token as {email}',
      login: 'Log in',
      'search-label': 'Search members',
      'search-placeholder': 'Search by name, e-mail or workspace…',
      'loading-members': 'Loading members…',
      empty: 'No members found',
      loading: 'Signing in…',
    },
    callback: {
      loading: 'Signing in…',
      'try-another': 'Try another account',
    },
  },
  auth: {
    'sign-in': {
      title: 'Bei immoOS anmelden',
      'email-password': 'Mit E-Mail und Passwort anmelden',
      google: 'Mit Google anmelden',
      microsoft: 'Mit Microsoft anmelden',
      loading: 'Anmeldung läuft…',
      error: 'Anmeldung fehlgeschlagen — bitte erneut versuchen.',
    },
    callback: {
      loading: 'Anmeldung wird abgeschlossen…',
      'missing-code': 'Der Anmeldevorgang ist unvollständig. Bitte starte ihn erneut.',
      failed: 'Anmeldung fehlgeschlagen — bitte erneut versuchen.',
      'back-to-sign-in': 'Zurück zur Anmeldung',
    },
    impersonate: {
      loading: 'Sitzung im Auftrag wird gestartet…',
      'missing-code': 'Dieser Link ist unvollständig. Bitte starte erneut in der Admin-Konsole.',
      failed: 'Dieser Link ist nicht mehr gültig. Bitte starte eine neue Sitzung in der Admin-Konsole.',
      'back-to-sign-in': 'Zurück zur Anmeldung',
    },
    'sign-out': {
      loading: 'Abmeldung läuft…',
      failed: 'Abmeldung fehlgeschlagen — bitte erneut versuchen.',
      'back-to-sign-in': 'Zur Anmeldung',
    },
    /*
     * Die Zustimmungsseite des OAuth-Ablaufs (IMM-601.8.2): ein MCP-Client
     * schickt das Mitglied hierher, es meldet sich normal an und erteilt den
     * Zugriff — ohne irgendwo ein Geheimnis einzufügen.
     *
     * Die Seite nennt bewusst BEIDE Gründe, warum eine angefragte Berechtigung
     * nicht erteilt wird, denn sie führen zu verschiedenen nächsten Schritten:
     * „hast du selbst nicht" löst ein Owner, „gibt es nicht" die Entwickler des
     * Clients. Ein Client, der still weniger bekommt als er angefragt hat,
     * erfährt sonst nirgends, was fehlt.
     */
    'oauth-consent': {
      title: 'Client verbinden',
      'subtitle-named': '„{clientName}“ möchte in deinem Namen auf immoOS zugreifen.',
      subtitle: 'Ein Client möchte in deinem Namen auf immoOS zugreifen.',
      'workspace-label': 'Workspace',
      'workspace-choose-label': 'Workspace auswählen',
      'workspace-choose-hint': 'Der Client wird für diesen Workspace verbunden.',
      'account-label': 'Angemeldet als',
      'granted-empty': 'Es bleibt keine Berechtigung übrig, die du erteilen kannst.',
      'withheld-heading': 'Das erlaubst du nicht',
      'withheld-hint':
        'Diese Berechtigungen hast du selbst nicht. immoOS gibt nie mehr weiter, als du hast — ein Owner deines Workspace kann sie dir erteilen.',
      'unsupported-hint':
        'Diese Berechtigungen gibt es in immoOS nicht. Sag den Entwicklerinnen und Entwicklern des Clients Bescheid.',
      'revoke-hint': 'Du kannst diesen Zugriff jederzeit unter Einstellungen → MCP ändern oder entziehen.',
      'not-allowed':
        'Du darfst in diesem Workspace keine Clients verbinden. Bitte einen Owner, das für dich zu übernehmen.',
      'approve-cta': 'Verbinden',
      'approve-loading': 'Wird verbunden…',
      'cancel-cta': 'Abbrechen',
      'cancel-loading': 'Wird abgelehnt…',
      'generic-error': 'Die Verbindung konnte nicht abgeschlossen werden — bitte erneut versuchen.',
      'back-cta': 'Zurück zu den Einstellungen',
    },
  },
  settings: {
    title: 'Einstellungen',
    'title-scoped': '{workspace} Einstellungen',
    back: 'Zurück',
    version: 'Version {version}',
    nav: {
      'load-error': 'Zusatzanwendungen konnten nicht geladen werden.',
      retry: 'Erneut versuchen',
      skills: 'Skills',
      'api-keys': 'API-Schlüssel',
      'company-mailboxes': 'Firmenpostfächer',
      mcp: 'MCP',
      'lead-partners': 'Lead-Partner',
      'print-material': 'Druckmaterial',
      locations: 'Standorte',
      'my-profile': 'Mein Profil',
      mailbox: 'Postfach',
      company: 'Unternehmen',
      branding: 'Branding & CI',
      'document-templates': 'Vorlagen',
      portals: 'Portale',
      members: 'Mitglieder',
      admins: 'Admins',
      personal: 'Persönlich',
      workspace: 'Arbeitsbereich',
    },
    admins: {
      title: 'Arbeitsbereichs-Admins',
      subtitle: 'Verwalte, wer diesen Arbeitsbereich verwaltet – Eigentümer, Admins und Abrechnung.',
      'empty-title': 'Noch keine Admins',
      'empty-description': 'Noch hat kein Mitglied eine Arbeitsbereichsrolle.',
      'load-error': 'Mitglieder konnten nicht geladen werden.',
      'add-cta': 'Admin hinzufügen',
      'add-title': 'Admin hinzufügen',
      'add-submit': 'Rolle zuweisen',
      'add-loading': 'Wird zugewiesen…',
      'cancel-cta': 'Abbrechen',
      'remove-cta': 'Entfernen',
      you: 'Du',
      'save-success': 'Arbeitsbereichsrolle aktualisiert.',
      'save-error': 'Arbeitsbereichsrolle konnte nicht aktualisiert werden – bitte erneut versuchen.',
      'remove-success': 'Arbeitsbereichsrolle entfernt.',
      columns: {
        member: 'Mitglied',
        email: 'E-Mail',
        role: 'Rolle',
      },
      roles: {
        'workspace-owner': 'Eigentümer',
        'workspace-admin': 'Admin',
        billing: 'Abrechnung',
      },
    },
    skills: {
      title: 'Skills',
      subtitle:
        'Skills erledigen wiederkehrende Arbeit mit deinem Zugriff. Du entscheidest, welche du aktivierst — und kannst sie jederzeit widerrufen.',
      'list-load-error': 'Die Skills konnten nicht geladen werden. Bitte versuche es erneut.',
      'mine-heading': 'Deine Skills',
      'mine-empty': 'Du hast noch keinen Skill aktiviert.',
      'available-heading': 'Verfügbare Skills',
      'available-empty': 'Derzeit sind keine weiteren Skills verfügbar.',
      'activate-cta': 'Aktivieren',
      'revoke-cta': 'Widerrufen',
      'next-run': 'Nächster Lauf: {date}',
      'no-next-run': 'Kein Lauf geplant',
      status: {
        ACTIVE: 'Aktiv',
        INACTIVE: 'Nicht aktiviert',
        PAUSED_OPERATOR: 'Vom Betreiber pausiert',
        PAUSED_FAILURES: 'Pausiert nach Fehlern',
        REVOKED: 'Widerrufen',
      },
      'reactivate-cta': 'Wieder aktivieren',
      approvals: {
        heading: 'Wartet auf deine Freigabe',
        waiting: '„{skill}" wartet auf deine Freigabe: {summary}',
        expires: 'Ohne Antwort verfällt diese Freigabe am {date}.',
        'review-cta': 'Ansehen',
        'detail-title': 'Freigabe erforderlich',
        'detail-body': '„{skill}" möchte folgende Aktion ausführen:',
        'expires-note': 'Ohne Antwort verfällt diese Freigabe am {date}.',
        'approve-cta': 'Freigeben',
        'reject-cta': 'Ablehnen',
        close: 'Schließen',
        'generic-error': 'Das hat nicht geklappt. Bitte versuche es erneut.',
        terminal: {
          APPROVED: 'Freigegeben — die Aktion wurde ausgeführt.',
          REJECTED: 'Abgelehnt — die Aktion wurde nicht ausgeführt.',
          EXPIRED: 'Verfallen — die Aktion wurde nicht ausgeführt.',
          VOID: 'Hinfällig — der Zugriff wurde widerrufen, die Aktion wurde nicht ausgeführt.',
          PENDING: '',
        },
      },
      runs: {
        heading: 'Läufe',
        empty: 'Noch keine Läufe.',
        'run-cta': 'Jetzt ausführen',
        'run-error': 'Der Lauf konnte nicht gestartet werden. Bitte versuche es erneut.',
        'already-running': 'Läuft bereits.',
        'load-error': 'Die Läufe konnten nicht geladen werden.',
        columns: {
          trigger: 'Auslöser',
          status: 'Status',
          started: 'Gestartet',
          tokens: 'Tokens',
          cost: 'Kosten',
        },
        status: {
          QUEUED: 'In Warteschlange',
          RUNNING: 'Läuft',
          COMPLETED: 'Ausgeführt',
          FAILED: 'Fehlgeschlagen',
          STOPPED: 'Abgebrochen',
        },
        'status-revoked': 'Abgebrochen — Zugriff widerrufen',
        trigger: {
          MANUAL: 'Manuell gestartet',
          SCHEDULE: 'Geplant',
          EVENT: 'Ereignis',
        },
        'tool-calls': 'Werkzeugaufrufe',
        // Was der Skill genau aufgerufen hat und was zurückkam — bei einem
        // Fehler die einzige Stelle, an der der Grund steht.
        'call-parameters': 'Aufruf',
        'call-result': 'Ergebnis',
        outcome: {
          OK: 'OK',
          ERROR: 'Fehler',
          REFUSED: 'Abgelehnt',
          PROPOSED: 'Wartet auf Freigabe',
        },
        fingerprint: 'Signatur',
        'not-started': '—',
      },
      state: {
        heading: 'Zustand',
        empty: 'Dieser Skill hat sich noch nichts gemerkt.',
        'reset-cta': 'Zustand zurücksetzen',
        'reset-error': 'Der Zustand konnte nicht zurückgesetzt werden. Bitte versuche es erneut.',
        'load-error': 'Der Zustand konnte nicht geladen werden.',
        columns: {
          key: 'Schlüssel',
          value: 'Wert',
          updated: 'Aktualisiert',
        },
      },
      // Trigger-Konfiguration (IMM-436.6/.7) — dieselben Felder im Zustimmungs-
      // und im Konfigurationsdialog. Ereignisnamen selbst bleiben unübersetzt:
      // der Katalog wird aus der Ereignis-Registry abgeleitet, eine Handliste
      // von Labels würde beim nächsten neuen Ereignis unbemerkt veralten.
      trigger: {
        kind: 'Wann soll dieser Skill laufen?',
        kinds: {
          MANUAL: 'Nur auf Knopfdruck',
          SCHEDULE: 'Nach Zeitplan',
          EVENT: 'Bei einem Ereignis',
        },
        'events-heading': 'Ereignisse',
        'events-hint': 'Der Skill läuft, sobald eines dieser Ereignisse in deinem Arbeitsbereich passiert.',
        'events-filter': 'Ereignisse filtern…',
        'events-empty': 'Kein Ereignis passt zu deiner Suche.',
        'events-selected': '{count} ausgewählt',
        'coalescing-label': 'Ereignisse zusammenfassen (Minuten)',
        'coalescing-hint': 'Mehrere Ereignisse innerhalb dieses Fensters lösen nur einen Lauf aus.',
        'weekdays-label': 'Wochentage',
        weekdays: {
          '1': 'Mo',
          '2': 'Di',
          '3': 'Mi',
          '4': 'Do',
          '5': 'Fr',
          '6': 'Sa',
          '7': 'So',
        },
        'times-label': 'Uhrzeiten',
        'time-at': '{index}. Uhrzeit',
        'time-add': 'Uhrzeit hinzufügen',
        'time-remove': 'Entfernen',
        'timezone-label': 'Zeitzone',
        'missed-label': 'Verpasste Läufe',
        missed: {
          SKIP: 'Überspringen',
          CATCH_UP_ONCE: 'Einmal nachholen',
        },
        problems: {
          'events-empty': 'Wähle mindestens ein Ereignis aus.',
          'schedule-empty': 'Wähle mindestens einen Wochentag und eine Uhrzeit.',
          'schedule-too-tight': 'Zwischen zwei Läufen müssen mindestens {minutes} Minuten liegen.',
        },
      },
      configure: {
        cta: 'Konfigurieren',
        title: '{name} konfigurieren',
        'tools-heading': 'Werkzeuge',
        'tools-hint': 'Du kannst nur einschränken, was dieser Skill nutzen darf — nie erweitern.',
        save: 'Speichern',
        cancel: 'Abbrechen',
        'load-error': 'Die Konfiguration konnte nicht geladen werden.',
        'generic-error': 'Die Konfiguration konnte nicht gespeichert werden. Bitte versuche es erneut.',
      },
      consent: {
        title: '{name} aktivieren',
        'acting-identity': 'Dieser Skill handelt mit deinem Zugriff — er sieht und tut nur, was auch du darfst.',
        'tools-heading': 'Diese Werkzeuge darf er nutzen',
        'tools-empty': 'Noch keine Werkzeuge freigegeben.',
        // Was ein Werkzeug ohne Rückfrage darf — die Klassifizierung aus dem
        // Katalog, nicht aus der Instanz-Konfiguration. UNKNOWN: das Werkzeug
        // ist nicht mehr katalogisiert, also ist jede Aussage darüber geraten.
        'tool-effect': {
          QUERY: 'liest nur',
          AUTO_WITH_AUDIT: 'ohne Rückfrage',
          APPROVAL_REQUIRED: 'nur mit deiner Freigabe',
          UNKNOWN: 'unbekannt',
        },
        'approval-window': 'Eine vorgeschlagene Aktion wartet {days} Tage auf deine Freigabe, danach verfällt sie.',
        revocable: 'Du kannst diesen Zugriff jederzeit widerrufen.',
        confirm: 'Zugriff gewähren und aktivieren',
        cancel: 'Abbrechen',
        'generic-error': 'Der Skill konnte nicht aktiviert werden. Bitte versuche es erneut.',
      },
      revoke: {
        title: 'Zugriff widerrufen?',
        body: '{name} startet ab sofort keine weiteren Läufe mehr.',
        'history-note':
          'Ein bereits laufender Lauf wird noch zu Ende geführt. Der Verlauf bleibt lesbar — ein Widerruf löscht nichts.',
        confirm: 'Widerrufen',
        cancel: 'Abbrechen',
        'generic-error': 'Der Zugriff konnte nicht widerrufen werden. Bitte versuche es erneut.',
      },
    },
    'company-mailboxes': {
      title: 'Firmenpostfächer',
      subtitle: 'Firmenadressen wie bewerbung@ oder info@ — nur Owner und Admins sehen sie.',
      'add-cta': 'Firmenpostfach hinzufügen',
      empty: {
        title: 'Noch kein Firmenpostfach verbunden',
        body: 'Verbinde ein Firmenpostfach wie bewerbung@ oder info@, um seine E-Mails in immoOS zu lesen. Nur Owner und Admins sehen es.',
      },
      status: {
        ACTIVE: 'Aktiv',
        SYNCING: 'Synchronisiert …',
        EXPIRED: 'Abgelaufen',
      },
      'connected-by': 'Verbunden von {name} am {date}',
      'imap-connected-by': 'IMAP · {provider} · Verbunden von {name} am {date}',
      forbidden: 'Du bist für diese Aktion nicht berechtigt.',
      'load-error': 'Die Firmenpostfächer konnten nicht geladen werden.',
      'load-retry': 'Erneut versuchen',
      'connect-dialog': {
        title: 'Firmenpostfach hinzufügen',
        intro: 'Wähle den Anbieter des Firmenpostfachs.',
        hint: 'Die Adresse muss ein eigenes Google-Workspace-Konto sein. Google-Gruppen und Verteilerlisten können nicht verbunden werden. Melde dich im nächsten Schritt mit dem Firmenpostfach an — nicht mit deinem eigenen Konto.',
        'hint-with-microsoft':
          'Die Adresse muss ein eigenes Postfach bei Google Workspace oder Microsoft 365 sein. Gruppen, Verteilerlisten und freigegebene Postfächer können nicht verbunden werden. Melde dich im nächsten Schritt mit dem Firmenpostfach an — nicht mit deinem eigenen Konto.',
        'hint-with-imap':
          'Die Adresse muss ein eigenes Postfach sein — bei Google Workspace, Microsoft 365 oder einem Anbieter mit IMAP. Gruppen, Verteilerlisten und freigegebene Postfächer können nicht verbunden werden. Melde dich im nächsten Schritt mit dem Firmenpostfach an — nicht mit deinem eigenen Konto.',
        'continue-imap': 'Weiter',
        'cancel-cta': 'Abbrechen',
        'confirm-cta': 'Weiter zur Anmeldung',
      },
      'reconnect-cta': 'Erneut verbinden',
      'disconnect-cta': 'Trennen',
      'disconnect-dialog': {
        title: 'Firmenpostfach trennen?',
        body: 'Die Verbindung zu {email} wird beim Anbieter widerrufen und aus immoOS entfernt. Du kannst das Firmenpostfach später jederzeit erneut verbinden.',
        'body-imap':
          'Die Zugangsdaten zu {email} werden aus immoOS gelöscht. Das Postfach beim Anbieter bleibt unverändert. Du kannst das Firmenpostfach später jederzeit erneut verbinden.',
        'cancel-cta': 'Abbrechen',
        'confirm-cta': 'Trennen',
      },
    },
    'email-templates': {
      columns: {
        name: 'Name',
        area: 'Bereich',
        subject: 'Betreff',
        'updated-at': 'Aktualisiert am',
        'updated-by': 'Aktualisiert von',
        actions: 'Aktionen',
      },
      immoos: 'immoOS',
      empty: 'Noch keine E-Mail-Vorlagen.',
      'new-cta': 'Neue E-Mail-Vorlage',
      'load-error': 'Die E-Mail-Vorlagen konnten nicht geladen werden.',
      'delete-dialog': {
        body: 'E-Mail-Vorlage ‚{name}‘ löschen? Bereits gesendete E-Mails bleiben unverändert.',
        success: 'E-Mail-Vorlage gelöscht',
      },
      view: {
        breadcrumb: 'Einstellungen / <link>Vorlagen</link> / {name}',
        'subject-label': 'Betreff',
        'load-error': 'Die E-Mail-Vorlage konnte nicht geladen werden.',
        gone: 'Diese E-Mail-Vorlage gibt es nicht mehr.',
        back: 'Zurück zu den Vorlagen',
      },
      editor: {
        name: 'Name',
        area: 'Bereich',
        subject: 'Betreff',
        text: 'Text',
        'insert-placeholder': 'Platzhalter einfügen',
        'insert-fill-field': 'Ausfüllfeld einfügen',
        'fill-dialog': {
          title: 'Ausfüllfeld einfügen',
          label: 'Bezeichnung',
          placeholder: 'z. B. Termin',
          insert: 'Einfügen',
        },
        saved: 'E-Mail-Vorlage gespeichert',
        'name-required': 'Bitte gib einen Namen an.',
        'name-too-long': 'Der Name darf höchstens {max} Zeichen lang sein.',
      },
    },
    'document-templates': {
      title: 'Vorlagen',
      tabs: {
        documents: 'Dokumente',
        email: 'E-Mail-Vorlagen',
      },
      subtitle:
        'Pflege hier die Vorlagen deiner Agentur. Datenfelder wie Eigentümer, Objekt und Provision werden beim Erstellen eines Dokuments automatisch gefüllt.',
      columns: {
        name: 'Name',
        category: 'Kategorie',
        'updated-at': 'Aktualisiert am',
        'created-by': 'Erstellt von',
        actions: 'Aktionen',
      },
      'sort-label': 'Nach {column} sortieren',
      'updated-at-value': '{date} {time}',
      'no-creator': '—',
      empty: 'Noch keine Vorlage angelegt.',
      'read-only-banner': 'Nur Owner und Admins können Vorlagen bearbeiten.',
      actions: {
        edit: 'Bearbeiten',
        view: 'Ansehen',
        delete: 'Löschen',
      },
      'load-error': 'Die Vorlagen konnten nicht geladen werden.',
      'load-retry': 'Erneut versuchen',
      'delete-dialog': {
        title: 'Vorlage löschen',
        body: 'Vorlage ‚{name}‘ löschen? Bereits erzeugte Dokumente bleiben erhalten.',
        confirm: 'Löschen',
        cancel: 'Abbrechen',
        error: 'Die Vorlage konnte nicht gelöscht werden. Bitte versuche es erneut.',
      },
      'new-cta': 'Neue Vorlage',
      'new-dialog': {
        title: 'Neue Vorlage',
        category: 'Kategorie',
        name: 'Name',
        start: 'Wie möchtest du beginnen?',
        'start-blank': 'Leer beginnen',
        'start-import': 'Aus Word-Datei importieren',
        'import-hint':
          'Lade eine Word-Datei (.docx) hoch. Google Docs lädst du über Datei → Herunterladen → Microsoft Word (.docx) herunter.',
        drop: 'Datei hierher ziehen oder auswählen',
        'drop-sub': '.docx · bis 20 MB',
        'other-file': 'Andere Datei',
        reading: 'Datei wird gelesen …',
        suggesting: 'Datenfelder werden gesucht …',
        next: 'Weiter',
        cancel: 'Abbrechen',
        errors: {
          'name-invalid': 'Gib einen Namen mit höchstens 80 Zeichen ein.',
          'not-docx':
            'Nur Word-Dateien (.docx) können importiert werden. Google Docs lädst du über Datei → Herunterladen → Microsoft Word (.docx) herunter.',
          'too-large': 'Die Datei ist größer als 20 MB.',
          unreadable: 'Die Datei konnte nicht gelesen werden. Prüfe, ob sie beschädigt oder passwortgeschützt ist.',
        },
      },
      editor: {
        'toolbar-label': 'Formatierung',
        paragraph: 'Absatz',
        'paragraph-short': '¶',
        'heading-1': 'Überschrift 1',
        'heading-1-short': 'H1',
        'heading-2': 'Überschrift 2',
        'heading-2-short': 'H2',
        'heading-3': 'Überschrift 3',
        'heading-3-short': 'H3',
        bold: 'Fett',
        'bold-short': 'B',
        italic: 'Kursiv',
        'italic-short': 'I',
        underline: 'Unterstrichen',
        'underline-short': 'U',
        'bullet-list': 'Aufzählung',
        'bullet-list-short': '•',
        'ordered-list': 'Nummerierte Liste',
        'ordered-list-short': '1.',
        'insert-table': 'Tabelle einfügen',
        'add-row': 'Zeile hinzufügen',
        'add-column': 'Spalte hinzufügen',
        'delete-row': 'Zeile löschen',
        'delete-column': 'Spalte löschen',
        'page-break': 'Seitenumbruch',
        'table-rows': 'Zeilen',
        'table-columns': 'Spalten',
        'table-insert': 'Einfügen',
        'footer-label': 'Fußzeile',
        'footer-hint': 'Erscheint auf jeder Seite, daneben ‚Seite x / y‘.',
        'footer-counter': '{count} / 200',
        'footer-too-long': 'Die Fußzeile darf höchstens 200 Zeichen lang sein.',
        'palette-title': 'Datenfelder einfügen',
        'chip-required': 'Pflichtfeld',
        'chip-optional': 'Darf leer bleiben',
        'chip-optional-hint': 'Fehlt der Wert, wird eine Linie zum Ausfüllen gedruckt.',
        save: 'Speichern',
        cancel: 'Abbrechen',
        saved: 'Vorlage gespeichert',
        'save-error': 'Die Vorlage konnte nicht gespeichert werden. Bitte versuche es erneut.',
        'category-label': 'Kategorie',
        'footer-truncated': 'Die Fußzeile wurde auf 200 Zeichen gekürzt.',
        'not-carried-over':
          'Einige Inhalte wurden nicht übernommen: {list}. Logo, Farben und Schriften kommen aus deinem Branding.',
        dropped: {
          IMAGES: 'Bilder',
          HEADER: 'Kopfzeile',
          FOOTNOTES: 'Fußnoten',
          TEXT_COLOURS: 'Schriftfarben',
          FONTS: 'Schriftarten',
          MERGED_CELLS: 'verbundene Zellen',
        },
        suggestions: {
          count: '{count, plural, one {# Vorschlag für Datenfelder} other {# Vorschläge für Datenfelder}}',
          accept: 'Übernehmen',
          reject: 'Verwerfen',
          'accept-all': 'Alle übernehmen',
          'reject-all': 'Alle verwerfen',
          proposal: '→ {chip}',
          none: 'Keine Vorschläge gefunden — setze die Datenfelder selbst.',
          'save-blocked': 'Prüfe zuerst alle Vorschläge für Datenfelder.',
        },
        discard: {
          title: 'Ungespeicherte Änderungen verwerfen?',
          confirm: 'Verwerfen',
          keep: 'Weiter bearbeiten',
        },
      },
      gone: 'Diese Vorlage gibt es nicht mehr.',
      back: 'Zurück zu den Vorlagen',
      'buyer-contract': {
        adapt: 'Anpassen',
        edit: 'Bearbeiten',
        'adapt-banner': 'Du passt den Standard an. Nach dem Speichern ist deine Vorlage aktiv.',
        'standard-read-only':
          'Den Standard kannst du nicht bearbeiten. Mit „Anpassen“ in der Vorlagenliste erstellst du daraus deine eigene Vorlage.',
        'default-name': 'Maklervertrag Käufer mit Widerrufsbelehrung – {companyName}',
        'blocks-required': 'Fülle Maklervertrag, Widerrufsbelehrung und Muster-Widerrufsformular aus.',
        'name-label': 'Name',
        activate: 'Aktivieren',
        activated: '{name} ist jetzt aktiv.',
        'activate-error': 'Die Vorlage konnte nicht aktiviert werden. Bitte versuche es erneut.',
        delete: {
          title: 'Vorlage löschen',
          body: 'Vorlage ‚{name}‘ löschen? Danach ist der Standard aktiv. Bereits geschlossene Verträge behalten ihren Wortlaut.',
          confirm: 'Löschen',
          cancel: 'Abbrechen',
        },
      },
      view: {
        breadcrumb: 'Einstellungen / <link>Vorlagen</link> / {name}',
        'footer-label': 'Fußzeile',
        'no-footer': '—',
        'page-break': 'Seitenumbruch',
        'load-error': 'Die Vorlage konnte nicht geladen werden.',
      },
      categories: {
        BROKER_CONTRACT: DOCUMENT_CATEGORY_LABELS.BROKER_CONTRACT,
        POWER_OF_ATTORNEY: DOCUMENT_CATEGORY_LABELS.POWER_OF_ATTORNEY,
      } satisfies Record<DocumentTemplateCategory, string>,
      'data-fields': {
        groups: {
          OWNERS: 'Eigentümer (alle)',
          OWNER_1: 'Eigentümer 1',
          OWNER_2: 'Eigentümer 2',
          OWNER_3: 'Eigentümer 3',
          OWNER_4: 'Eigentümer 4',
          PROPERTY: 'Objekt',
          DEAL: 'Deal',
          MANDATE: 'Auftrag',
          LOCATION: 'Standort',
          COMPANY: 'Unternehmen',
          DATE: 'Datum',
        } satisfies Record<DataFieldGroup, string>,
        'chip-prefix': {
          OWNERS: 'Eigentümer',
          OWNER_1: 'Eigentümer 1',
          OWNER_2: 'Eigentümer 2',
          OWNER_3: 'Eigentümer 3',
          OWNER_4: 'Eigentümer 4',
          PROPERTY: 'Objekt',
          DEAL: 'Deal',
          MANDATE: 'Auftrag',
          LOCATION: 'Standort',
          COMPANY: 'Unternehmen',
          DATE: 'Datum',
        } satisfies Record<DataFieldGroup, string>,
        'field-labels': {
          NAMES: 'Namen',
          ADDRESS_BLOCK: 'Anschriftenblock',
          NAME: 'Name',
          BIRTH_DATE: 'Geburtsdatum',
          NATIONALITY: 'Nationalität',
          HOME_ADDRESS: 'Wohnanschrift',
          EMAIL: 'E-Mail',
          PHONE: 'Telefon',
          BIRTH_NAME: 'Geburtsname',
          BIRTH_PLACE: 'Geburtsort',
          ADDRESS: 'Adresse',
          PROPERTY_TYPE: 'Objektart',
          EXPECTED_SALE_PRICE: 'Erwarteter Verkaufspreis',
          MANDATE_TYPE: 'Auftragsart',
          START_OF_WORK: 'Tätigkeitsaufnahme',
          DURATION: 'Laufzeit',
          ENDS_ON: 'Läuft bis',
          SELLER_COMMISSION: 'Provision Verkäufer',
          BUYER_COMMISSION: 'Provision Käufer',
          BROKERS_NAMES: 'Makler (Namen)',
          BROKERS_EMAILS: 'Makler (E-Mail)',
          BROKERS_PHONES: 'Makler (Telefon)',
          BROKERS_REPRESENTATION: 'Makler (Vertretung)',
          COMPANY_NAME: 'Firmenname',
          LEGAL_FORM: 'Rechtsform',
          MANAGING_DIRECTORS: 'Geschäftsführer',
          COMMERCIAL_REGISTER_COURT: 'Handelsregister',
          COMMERCIAL_REGISTER_NUMBER: 'Registernummer',
          VAT_ID: 'USt-IdNr.',
          SUPERVISORY_AUTHORITY: 'Aufsichtsbehörde §34c',
          PROFESSIONAL_INDEMNITY_INSURANCE: 'Berufshaftpflicht',
          TODAY: 'Heute',
        } satisfies Record<DataFieldName, string>,
        chip: '{prefix}: {label}',
      },
    },
    /*
     * IMM-895 — Einstellungen → MCP trägt jeden MCP-Zugang: die über die
     * Anmeldung verbundenen Clients und die MCP-Schlüssel. API-Schlüssel
     * behält nur HTTP-API-Schlüssel (Design-Run imm-895-mcp-credentials-split).
     */
    mcp: {
      title: 'MCP-Verbindungen',
      description: 'Clients wie Claude, die in deinem Namen mit immoOS arbeiten. Jeder kann nur, was du selbst kannst.',
      'connected-clients-description':
        'Über deine Anmeldung verbunden — ohne Schlüssel. Widerrufen trennt den Client sofort.',
      keys: {
        heading: 'MCP-Schlüssel',
        description:
          'Für MCP-Clients, die sich nicht über die Anmeldung verbinden können. Ein MCP-Schlüssel läuft immer ab.',
        'issue-cta': 'MCP-Schlüssel erstellen',
        empty: 'Du hast noch keinen MCP-Schlüssel erstellt.',
        'list-load-error': 'MCP-Schlüssel konnten nicht geladen werden.',
        issue: {
          title: 'MCP-Schlüssel erstellen',
          'secret-heading': 'Dein neuer MCP-Schlüssel',
        },
      },
    },
    'api-keys': {
      title: 'API-Schlüssel',
      description:
        'Für Integrationen, die die immoOS-API aufrufen. MCP-Clients verbindest du unter <mcpLink>Einstellungen → MCP</mcpLink>.',
      'issue-cta': 'Schlüssel erstellen',
      'list-load-error': 'API-Schlüssel konnten nicht geladen werden.',
      'list-load-retry': 'Erneut versuchen',
      'empty-title': 'Noch keine API-Schlüssel',
      'empty-description': 'Erstelle deinen ersten Schlüssel für den programmatischen API-Zugriff.',
      'never-used': 'Nie',
      'revoke-action': 'Widerrufen',
      // Non-MCP keys never expire, so their „Gültig bis" cell shows a dash.
      'no-expiry': '—',
      'expired-hint': 'Dieser Schlüssel ist abgelaufen. Erstelle einen neuen, um deinen Client wieder zu verbinden.',
      // IMM-829.1 — a SECOND mark beside „Aktiv", never a fourth Status. The
      // credential really is active; the integration behind it is not, so the
      // copy has to keep the two apart.
      'partner-inactive-badge': 'Partner deaktiviert',
      'partner-inactive-hint':
        'Der Lead-Partner ist deaktiviert. Übermittelte Leads werden abgelehnt, bis er wieder aktiviert wird — <leadPartnersLink>Einstellungen → Lead-Partner</leadPartnersLink>.',
      columns: {
        name: 'Name',
        status: 'Status',
        permissions: 'Berechtigungen',
        'rate-limit': 'Ratenlimit',
        created: 'Erstellt',
        'valid-until': 'Gültig bis',
        'last-used': 'Zuletzt verwendet',
        actions: 'Aktionen',
      },
      status: {
        active: 'Aktiv',
        revoked: 'Widerrufen',
        expired: 'Abgelaufen',
      },
      'rate-limit-value': '{count}/s',
      revoke: {
        title: 'Schlüssel widerrufen?',
        'confirm-body':
          'Der Schlüssel „{name}" wird sofort dauerhaft deaktiviert. Anfragen mit diesem Schlüssel werden danach abgelehnt. Dies kann nicht rückgängig gemacht werden.',
        'cancel-cta': 'Abbrechen',
        'confirm-cta': 'Widerrufen',
        'confirm-loading': 'Wird widerrufen…',
        'success-toast': 'Schlüssel „{name}" wurde widerrufen.',
        'generic-error': 'Schlüssel konnte nicht widerrufen werden — bitte erneut versuchen.',
      },
      'edit-action': 'Bearbeiten',
      edit: {
        title: 'Schlüssel „{name}" bearbeiten',
        intro:
          'Änderungen gelten ab der nächsten Anfrage. Der Schlüssel selbst bleibt gleich — verbundene Integrationen und MCP-Clients musst du nicht neu einrichten.',
        'expiry-fixed': 'Gültig bis {date}. Die Gültigkeit eines MCP-Schlüssels lässt sich nicht ändern.',
        'rate-limit-label': 'Ratenlimit (Anfragen pro Sekunde)',
        'permissions-hint':
          'Neue Berechtigungen kannst du nur vergeben, wenn du sie selbst hast und auch das Mitglied, an das der Schlüssel gebunden ist.',
        'retained-group': 'Weitere Berechtigungen',
        'retained-hint':
          'Diese Berechtigungen hat das immoOS-Team vergeben. Entfernst du eine davon, kannst du sie nicht selbst wieder hinzufügen.',
        'submit-cta': 'Speichern',
        'submit-loading': 'Wird gespeichert…',
        'cancel-cta': 'Abbrechen',
        'success-toast': 'Schlüssel „{name}" wurde aktualisiert.',
        'generic-error': 'Schlüssel konnte nicht gespeichert werden — bitte erneut versuchen.',
        errors: {
          'rate-limit': { invalid: 'Gib eine ganze Zahl zwischen 1 und 60 ein.' },
          'permission-not-held':
            'Mindestens eine neu hinzugefügte Berechtigung hast du selbst oder das Mitglied, an das dieser Schlüssel gebunden ist, nicht. Entferne sie aus der Auswahl.',
          revoked: 'Dieser Schlüssel wurde inzwischen widerrufen und kann nicht mehr bearbeitet werden.',
          'not-found': 'Diesen Schlüssel gibt es nicht mehr. Die Liste wurde aktualisiert.',
        },
      },
      issue: {
        title: 'API-Schlüssel erstellen',
        'name-label': 'Name',
        'name-placeholder': 'Produktiv-Integration',
        'description-label': 'Beschreibung',
        'description-placeholder': 'Wofür dieser Schlüssel verwendet wird (optional)',
        'permissions-label': 'Berechtigungen',
        'permissions-hint':
          'Wähle die Berechtigungen für diesen Schlüssel. Ein Schlüssel kann nur, was du selbst kannst — verlierst du eine Berechtigung, verliert der Schlüssel sie mit.',
        groups: {
          lead: 'Leads',
          deal: 'Deals',
          property: 'Objekte',
          activity: 'Aktivitäten',
          relationship: 'Kontakte & Firmen',
          valuation: 'Bewertungen',
          identity: 'Team & Standorte',
        },
        'permissions-not-held-hint':
          'Ausgegraute Berechtigungen hast du selbst nicht, deshalb kannst du sie keinem Schlüssel geben. Brauchst du sie, lass den Schlüssel von einem Owner erstellen.',
        'mcp-hint':
          'Mit diesem Schlüssel kann ein MCP-Client wie Claude auf deinen Workspace zugreifen — mit genau den Berechtigungen, die du unten auswählst.',
        'expiry-label': 'Gültig bis',
        'expiry-hint': 'MCP-Schlüssel laufen immer ab. Wähle, wie lange dieser gültig sein soll.',
        'expiry-option': '{days} Tage',
        // `{limit, number}` renders with the active locale's grouping (de-DE: 2.000).
        'volume-cap-hint': 'Dieser Schlüssel darf höchstens {limit, number} Anfragen pro Tag stellen.',
        'submit-cta': 'Schlüssel erstellen',
        'submit-loading': 'Wird erstellt…',
        'cancel-cta': 'Abbrechen',
        'done-cta': 'Fertig',
        'generic-error': 'Schlüssel konnte nicht erstellt werden — bitte erneut versuchen.',
        errors: {
          name: {
            required: 'Name ist erforderlich.',
            'too-long': 'Name ist zu lang.',
            invalid: 'Name ist ungültig.',
          },
          description: {
            'too-long': 'Beschreibung ist zu lang.',
          },
          permissions: {
            required: 'Wähle mindestens eine Berechtigung.',
          },
          expiry: {
            required: 'Wähle, wie lange dieser Schlüssel gültig sein soll.',
          },
          // Keyed on the refusal's error code, not on a field. Carries NO
          // placeholder: the response deliberately withholds which grant was
          // refused (IMM-569.3), so the copy states the rule and sends the Owner
          // back to their own selection.
          'permission-not-held':
            'Du kannst nur Berechtigungen vergeben, die du selbst hast. Prüfe deine Auswahl oder lass den Schlüssel von einem Owner erstellen.',
        },
        'secret-heading': 'Dein neuer API-Schlüssel',
        'secret-warning':
          'Kopiere diesen Schlüssel jetzt — er wird nur einmal angezeigt und kann später nicht erneut abgerufen werden.',
        'copy-cta': 'Kopieren',
        'copied-toast': 'API-Schlüssel in die Zwischenablage kopiert.',
        'config-heading': 'So verbindest du deinen Client',
        'config-hint':
          'Kopiere diese Konfiguration in deinen MCP-Client. Sie enthält deinen Schlüssel — behandle sie wie ein Passwort.',
        'config-copy-cta': 'Konfiguration kopieren',
        'config-copied-toast': 'Konfiguration in die Zwischenablage kopiert.',
        'config-endpoint-label': 'Endpoint-URL',
        'config-key-label': 'Schlüssel',
      },
      /*
       * IMM-601.8.2 — die OAuth-Verbindungen der MCP-Clients, auf derselben
       * Seite wie die Schlüssel, aber als eigene Liste: eine Verbindung hat kein
       * Geheimnis, keine Gültigkeit und kein Ratenlimit, also auch keine Spalten
       * dafür.
       */
      'connected-clients': {
        heading: 'Verbundene Clients',
        empty: 'Du hast noch keinen Client verbunden.',
        'list-load-error': 'Verbundene Clients konnten nicht geladen werden.',
        // Dieselbe Schreibweise wie in der Schlüsselliste: eine nie benutzte
        // Verbindung zeigt „Nie", kein leeres Feld und kein Datum.
        'never-used': 'Nie',
        'all-tools': 'Alle Werkzeuge',
        'revoke-action': 'Widerrufen',
        'edit-action': 'Berechtigungen bearbeiten',
        columns: {
          client: 'Client',
          permissions: 'Berechtigungen',
          'connected-at': 'Verbunden am',
          'last-used': 'Zuletzt verwendet',
          actions: 'Aktionen',
        },
        revoke: {
          title: 'Zugriff widerrufen?',
          'confirm-body':
            'Der Zugriff von „{name}" wird sofort entzogen. Anfragen dieses Clients werden danach abgelehnt.',
          'cancel-cta': 'Abbrechen',
          'confirm-cta': 'Widerrufen',
          'confirm-loading': 'Wird widerrufen…',
          'success-toast': 'Der Zugriff von „{name}" wurde widerrufen.',
          'generic-error': 'Zugriff konnte nicht widerrufen werden — bitte erneut versuchen.',
        },
        edit: {
          title: 'Berechtigungen bearbeiten',
          subtitle:
            'Änderungen gelten ab dem nächsten Aufruf von „{clientName}". Du musst den Client nicht neu verbinden.',
          'save-cta': 'Speichern',
          'save-loading': 'Wird gespeichert…',
          'cancel-cta': 'Abbrechen',
          'success-toast': 'Berechtigungen gespeichert.',
          'generic-error': 'Die Berechtigungen konnten nicht gespeichert werden — bitte erneut versuchen.',
        },
      },
    },
    portals: {
      title: 'Portale',
      subtitle:
        'Verbinde das Konto deines Workspace bei einem Immobilienportal, um Objekte direkt dort zu veröffentlichen.',
      'card-title': 'ImmoScout24',
      status: {
        'not-connected': 'Nicht verbunden',
        connected: 'Verbunden',
        expired: 'Verbindung abgelaufen',
      },
      state: {
        'not-connected':
          'Noch nicht verbunden. Verbinde das ImmoScout24-Konto deines Workspace, um Objekte direkt zu veröffentlichen.',
        connected: 'Verbunden als {accountName} · verbunden von {displayName} am {date, date, medium}',
        'connected-no-member': 'Verbunden als {accountName} · verbunden am {date, date, medium}',
        expired:
          'Die Verbindung zu ImmoScout24 ist abgelaufen oder wurde widerrufen. Veröffentlichungen sind pausiert, bis die Verbindung erneuert wird.',
        unavailable: 'Die ImmoScout24-Anbindung ist in dieser Umgebung nicht eingerichtet.',
        'read-only': 'Nur Eigentümer und Admins können die ImmoScout24-Verbindung verwalten.',
      },
      'connect-cta': 'Konto verbinden',
      'disconnect-cta': 'Verbindung trennen',
      'renew-cta': 'Verbindung erneuern',
      'connecting-loading': 'Weiterleitung …',
      'load-error': 'Die Portalzugänge konnten nicht geladen werden.',
      'load-retry': 'Erneut versuchen',
      disconnect: {
        title: 'ImmoScout24-Verbindung trennen?',
        body: 'Bestehende Inserate bleiben online, lassen sich aber nicht mehr aus immoOS steuern.',
        'confirm-cta': 'Trennen',
        'cancel-cta': 'Zurück',
        'confirm-loading': 'Wird getrennt …',
      },
      'disconnect-success': 'Die ImmoScout24-Verbindung wurde getrennt.',
      openimmo: {
        status: { expired: 'Zugang ungültig' },
        state: {
          'not-connected':
            'Noch nicht verbunden. Trage den Upload-Zugang ein, den {portal} deiner Agentur geschickt hat.',
          connected: 'Verbunden · eingetragen von {displayName} am {date, date, medium}',
          'connected-no-member': 'Verbunden · eingetragen am {date, date, medium}',
          expired: 'Der Zugang zu {portal} ist ungültig. Bitte prüfe die Zugangsdaten.',
          unavailable: 'Die {portal}-Anbindung ist in dieser Umgebung nicht eingerichtet.',
          'read-only': 'Nur Eigentümer und Admins können Portalzugänge verwalten.',
        },
        'enter-cta': 'Zugang eintragen',
        'change-cta': 'Zugang ändern',
        'disconnect-cta': 'Verbindung trennen',
        dialog: {
          title: 'Zugang zu {portal} eintragen',
          hint: {
            kleinanzeigen:
              'Den Upload-Zugang (FTP) erhältst du von Kleinanzeigen, sobald deine Agentur eine Immobilien-Partnerschaft gebucht hat. Gib die Daten am besten von Hand ein – beim Kopieren entstehen leicht Leerzeichen.',
            immowelt:
              'Den Upload-Zugang (FTP) erhältst du vom Immowelt-Kundenservice zu deiner Partnerschaft. Er ist nicht dein Login für immowelt.de.',
          },
          fields: {
            host: 'Server',
            username: 'Benutzername',
            password: 'Passwort',
            'provider-id': 'Anbieter-ID',
            'listing-email': 'E-Mail für Inserate',
          },
          'listing-email-hint':
            'Diese Adresse steht als Kontakt in jedem Inserat auf {portal}. Bei Kleinanzeigen siehst du deine Inserate unter dem Konto mit genau dieser Adresse.',
          required: 'Bitte gib {field} an.',
          'invalid-email': 'Bitte gib eine gültige E-Mail-Adresse an.',
          'submit-cta': 'Zugang prüfen und speichern',
          'submit-loading': 'Zugang wird geprüft …',
          'cancel-cta': 'Abbrechen',
        },
        disconnect: {
          title: 'Verbindung zu {portal} trennen?',
          body: 'Der gespeicherte Zugang wird gelöscht. Inserate, die schon auf {portal} sind, bleiben dort, lassen sich aber nicht mehr aus immoOS steuern.',
          'confirm-cta': 'Trennen',
          'cancel-cta': 'Zurück',
          'confirm-loading': 'Wird getrennt …',
        },
        'disconnect-success': 'Die Verbindung zu {portal} wurde getrennt.',
      },
      callback: {
        loading: 'Verbindung zu ImmoScout24 wird hergestellt …',
        'back-cta': 'Zurück zu Portale',
      },
      banner: {
        title:
          'Die ImmoScout24-Verbindung ist abgelaufen. Inserate können nicht veröffentlicht, aktualisiert oder zurückgezogen werden.',
        'renew-cta': 'Verbindung erneuern',
        'openimmo-title':
          'Der Zugang zu {portal} ist ungültig. Inserate können dort nicht veröffentlicht, aktualisiert oder zurückgezogen werden.',
        'openimmo-cta': 'Zugang ändern',
      },
      extension: {
        title: 'Browser-Erweiterung für ImmoScout24',
        body: 'Mit der immoOS-Erweiterung für Chrome legst du Objekte in deinem eigenen ImmoScout24-Konto an und hältst sie aktuell. Dein ImmoScout24-Login bleibt in deinem Browser — immoOS bekommt ihn nie zu sehen.',
        'connect-cta': 'Erweiterung verbinden',
        'connect-loading': 'Wird verbunden …',
        connected:
          'Die Erweiterung ist mit immoOS verbunden. Öffne ImmoScout24 und klicke auf das immoOS-Symbol in der Symbolleiste.',
        'not-installed':
          'In diesem Browser ist die immoOS-Erweiterung nicht installiert. Installiere sie und klicke dann noch einmal auf „Erweiterung verbinden“.',
        'install-cta': 'Erweiterung installieren',
        failed: 'Die Erweiterung konnte nicht verbunden werden. Versuch es noch einmal.',
        'read-only': 'Du darfst keine Erweiterung verbinden. Frag eine Person mit Adminrechten.',
        'key-name': 'Browser-Erweiterung',
        'import-cta': 'ImmoScout24-Inserate importieren',
      },
    },
    mailbox: {
      title: 'Postfach',
      subtitle: 'Verbinde dein E-Mail-Postfach, damit immoOS in deinem Namen lesen und senden kann.',
      'load-error': 'Der Verbindungsstatus deines Postfachs konnte nicht geladen werden.',
      'load-retry': 'Erneut versuchen',
      status: {
        'connected-label': 'Verbunden',
        'not-connected-label': 'Nicht verbunden',
        'expired-label': 'Verbindung abgelaufen',
      },
      provider: {
        GOOGLE: 'Google',
        MICROSOFT: 'Microsoft 365',
        IMAP: 'IMAP',
      },
      // Short brand names shown on the account row's provider badge (design 08b).
      'provider-brand': {
        GOOGLE: 'Gmail',
        MICROSOFT: 'Outlook',
        IMAP: 'IMAP',
      },
      'section-title': 'Meine Postfächer',
      'secure-note':
        'Sichere Anmeldung über den Anbieter — immoOS speichert keine Passwörter. Verbinden und Trennen laufen über die gesicherte Anbieter-Anmeldung.',
      'imap-provider-line': 'IMAP · {provider}',
      'providers-note-with-imap':
        'Bei Google und Microsoft meldest du dich direkt beim Anbieter an — immoOS speichert dort kein Passwort. Bei IMAP speichert immoOS dein Passwort verschlüsselt.',
      imap: {
        title: 'Postfach per IMAP verbinden',
        'title-company': 'Firmenpostfach per IMAP verbinden',
        email: 'E-Mail-Adresse',
        'email-placeholder': 'name@anbieter.de',
        password: 'Passwort',
        'password-hint':
          'Bei manchen Anbietern brauchst du ein eigenes Passwort für E-Mail-Programme oder ein App-Passwort.',
        recognised: 'Anbieter erkannt: {provider}',
        'edit-servers': 'Server-Daten selbst eingeben',
        'unknown-intro':
          'Für {domain} kennen wir die Server-Daten nicht. Du findest sie in der Hilfe deines Anbieters.',
        'imap-host': 'Posteingangsserver (IMAP)',
        'smtp-host': 'Postausgangsserver (SMTP)',
        port: 'Port',
        username: 'Benutzername',
        'imap-host-placeholder': 'imap.beispiel.de',
        'smtp-host-placeholder': 'smtp.beispiel.de',
        'security-note':
          'immoOS speichert dein Passwort verschlüsselt und nutzt es nur, um dein Postfach abzurufen und in deinem Namen zu senden.',
        'security-note-company':
          'immoOS speichert das Passwort verschlüsselt und nutzt es nur, um das Firmenpostfach abzurufen.',
        back: 'Zurück',
        connect: 'Verbinden',
        checking: 'Verbindung wird geprüft …',
        hint: {
          'gmx-web-de':
            "Bei {provider} musst du IMAP zuerst einschalten: E-Mail-Einstellungen → POP3/IMAP → ‚POP3- und IMAP-Zugriff erlauben'. Mit Zwei-Faktor-Anmeldung brauchst du ein App-Passwort.",
          't-online':
            'Bei T-Online brauchst du das Passwort für E-Mail-Programme, nicht dein Telekom-Login-Passwort. Du legst es im E-Mail Center unter Einstellungen → Passwörter an.',
          freenet:
            "Bei freenet musst du den Zugriff zuerst in deinem Postfach einschalten: ‚POP3, IMAP und SMTP aktivieren'.",
        },
        'required-email': 'Bitte gib deine E-Mail-Adresse ein.',
        'invalid-email': 'Bitte gib eine gültige E-Mail-Adresse ein.',
        'required-password': 'Bitte gib dein Passwort ein.',
        'required-imap-host': 'Bitte gib den Posteingangsserver ein.',
        'required-smtp-host': 'Bitte gib den Postausgangsserver ein.',
        'invalid-port': 'Bitte gib einen Port zwischen 1 und 65535 ein.',
        providers: {
          GMX: 'GMX',
          WEB_DE: 'WEB.DE',
          T_ONLINE: 'T-Online',
          IONOS: 'IONOS',
          STRATO: 'STRATO',
          MAILBOX_ORG: 'mailbox.org',
          POSTEO: 'Posteo',
          FREENET: 'freenet',
        },
      },
      'connected-as': 'Verbunden als {email}',
      'provider-label': 'Anbieter: {provider}',
      'not-connected-hint': 'Wähle deinen Anbieter, um dein Postfach zu verbinden.',
      // Sagt geradeheraus, welche Anbieter fehlen, statt sie kommentarlos wegzulassen (IMM-530.2).
      'unsupported-providers':
        'Aktuell kann nur ein Gmail-Postfach verbunden werden. Microsoft 365, Exchange und IMAP werden noch nicht unterstützt.',
      'unsupported-providers-with-microsoft':
        'Gmail- und Microsoft-Postfächer können verbunden werden. Exchange auf eigenen Servern und IMAP werden noch nicht unterstützt.',
      'connect-cta': 'Postfach verbinden',
      'connect-with': 'Mit {provider} verbinden',
      'connecting-loading': 'Wird verbunden…',
      'disconnect-cta': 'Trennen',
      'reconnect-cta': 'Postfach erneut verbinden',
      'connect-error': 'Verbindung konnte nicht gestartet werden — bitte erneut versuchen.',
      'disconnect-success': 'Postfach getrennt.',
      'disconnect-error': 'Postfach konnte nicht getrennt werden — bitte erneut versuchen.',
      'connect-success': 'Postfach verbunden: {email}.',
      expired: {
        title: 'Deine Postfach-Verbindung ist abgelaufen.',
        body: 'immoOS kann nicht mehr in deinem Namen auf E-Mails zugreifen. Das passiert meist, wenn du dein Anbieter-Passwort geändert hast. Verbinde dein Postfach erneut, um die Synchronisierung fortzusetzen.',
      },
      switchover: {
        title: 'Wir haben die Postfach-Anbindung verbessert.',
        body: 'immoOS verbindet sich jetzt direkt mit Google. Bitte verbinde dein Postfach einmalig neu — bis dahin funktioniert alles wie gewohnt.',
      },
      disconnect: {
        title: 'Postfach trennen?',
        'confirm-body':
          'Die Verbindung zu {email} wird beim Anbieter widerrufen und aus immoOS entfernt. Du kannst dein Postfach später jederzeit erneut verbinden.',
        'confirm-body-imap':
          'Die Zugangsdaten zu {email} werden aus immoOS gelöscht. Dein Postfach beim Anbieter bleibt unverändert. Du kannst es später jederzeit erneut verbinden.',
        'cancel-cta': 'Abbrechen',
        'confirm-cta': 'Trennen',
        'confirm-loading': 'Wird getrennt…',
      },
      callback: {
        loading: 'Postfach-Verbindung wird abgeschlossen…',
        'missing-params': 'Der Anbieter hat die Verbindung unvollständig zurückgegeben. Bitte versuche es erneut.',
        error: 'Die Postfach-Verbindung konnte nicht abgeschlossen werden. Bitte versuche es erneut.',
        'back-cta': 'Zurück zu den Postfach-Einstellungen',
        release: {
          title: 'Deine IT muss immoOS zuerst freigeben',
          body: 'Dein Microsoft-Konto gehört zu einer Organisation, in der nur Administratoren neue Apps zulassen. Bitte deine IT, immoOS einmalig freizugeben — die Anleitung dafür kannst du direkt weiterleiten. Danach verbindest du dein Postfach hier erneut.',
          'guide-cta': 'Anleitung für deine IT öffnen',
        },
      },
    },
    'print-material': {
      title: 'Druckmaterial',
      subtitle:
        'Visitenkarten und Präsentationsmappen für deinen Standort bestellen — mit den Daten, die schon in immoOS stehen.',
      action: 'Zur Bestellung',
      // Replaces the action rather than rendering it disabled: a disabled button
      // invites clicking and answers nothing.
      pending: 'Wird gerade eingerichtet — in Kürze verfügbar.',
      'not-available': 'Für deinen Workspace ist keine Anwendung freigegeben.',
    },
    'lead-partners': {
      title: 'Lead-Partner',
      subtitle:
        'Die Partner, von denen dein Workspace Eigentümer-Leads erhält — wiederverwendbar über Leads hinweg und später auswertbar.',
      'add-cta': 'Lead-Partner hinzufügen',
      'list-load-error': 'Lead-Partner konnten nicht geladen werden.',
      'empty-title': 'Noch keine Lead-Partner',
      'empty-description':
        'Füge deinen ersten Lead-Partner hinzu, damit er bei der Lead-Erfassung mit Quelle »Partner« auswählbar ist.',
      'success-toast': 'Lead-Partner „{name}“ hinzugefügt.',
      banner: {
        title: 'Lead-Partner gelten workspace-weit.',
        body: 'Deaktivierte Partner bleiben an bestehenden Leads, sind aber für neue Leads nicht mehr wählbar. Ein Hard-Delete gibt es nicht — Deaktivieren ist der einzige Weg zur Stilllegung.',
      },
      columns: {
        name: 'Partner',
        slug: 'Slug',
        status: 'Status',
        'api-key': 'API-Schlüssel',
        actions: 'Aktionen',
      },
      status: {
        active: 'Aktiv',
        inactive: 'Deaktiviert',
      },
      'key-status': {
        issued: 'Verknüpft',
        none: '—',
      },
      'deactivate-action': 'Deaktivieren',
      'reactivate-action': 'Reaktivieren',
      'issue-key-action': 'Schlüssel erstellen',
      add: {
        title: 'Lead-Partner hinzufügen',
        'name-label': 'Name',
        'name-placeholder': 'z. B. Aroundhome',
        'submit-cta': 'Partner hinzufügen',
        'submit-loading': 'Wird hinzugefügt…',
        'cancel-cta': 'Abbrechen',
        errors: {
          'name-required': 'Name ist erforderlich.',
          'name-taken': 'Ein Lead-Partner mit diesem Namen existiert bereits.',
        },
        'generic-error': 'Der Lead-Partner konnte nicht hinzugefügt werden – bitte versuche es erneut.',
        'forbidden-error': 'Du darfst keine Lead-Partner verwalten.',
      },
      'issue-key': {
        title: 'Push-API-Schlüssel erstellen',
        'confirm-body':
          'Erstelle einen Lead-Push-API-Schlüssel für „{name}“. Mit diesem Schlüssel übermittelte Leads werden automatisch diesem Partner zugeordnet. Der Schlüssel wird aus Sicherheitsgründen genau einmal angezeigt.',
        'cancel-cta': 'Abbrechen',
        'confirm-cta': 'Schlüssel erstellen',
        'confirm-loading': 'Wird erstellt…',
        'secret-heading': 'Dein API-Schlüssel',
        'secret-warning': 'Kopiere den Schlüssel jetzt — er wird danach nicht erneut angezeigt.',
        'workspace-id-label': 'Workspace-ID',
        'secret-label': 'API-Schlüssel',
        'copy-cta': 'Kopieren',
        'copied-toast': 'In die Zwischenablage kopiert.',
        'done-cta': 'Fertig',
        'success-toast': 'API-Schlüssel für „{name}“ erstellt.',
        'generic-error': 'Der Schlüssel konnte nicht erstellt werden – bitte versuche es erneut.',
      },
    },
    'my-profile': {
      title: 'Mein Profil',
      subtitle: 'Diese Angaben erscheinen im Exposé und auf deinem Makler-Profil.',
      'empty-hint': 'Vervollständige dein Profil, damit es im Exposé und auf deinem Makler-Profil erscheint.',
      'load-error': 'Dein Profil konnte nicht geladen werden.',
      retry: 'Erneut versuchen',
      'edit-cta': 'Profil bearbeiten',
      'cancel-cta': 'Abbrechen',
      'save-cta': 'Speichern',
      'save-loading': 'Wird gespeichert…',
      'save-success': 'Profil gespeichert',
      'generic-error': 'Profil konnte nicht gespeichert werden — bitte erneut versuchen.',
      'forbidden-error': 'Du darfst dieses Profil nicht bearbeiten.',
      'location-source': 'aus Standorten',
      'picklist-no-results': 'Keine Treffer',
      'remove-entry': '{label} entfernen',
      'add-certification': 'Zertifizierung hinzufügen',
      'add-social-link': 'Social-Link hinzufügen',
      badges: {
        'source-account': 'aus deinem Konto',
        'source-readonly': 'read-only',
      },
      'password-stub': {
        title: 'Passwort ändern',
        badge: 'Bald verfügbar',
        hint: 'Diese Funktion wird in Kürze verfügbar sein.',
        'change-cta': 'Ändern',
      },
      whatsapp: {
        heading: 'WhatsApp',
        intro: 'Verbinde deine WhatsApp-Nummer, um immoOS von unterwegs zu fragen.',
        'connect-cta': 'WhatsApp verbinden',
        'step-1': 'Öffne WhatsApp über den Button unten.',
        'step-2': 'Die Nachricht mit deinem Code ist schon eingetragen.',
        'step-3': 'Schick sie ab — fertig.',
        'open-cta': 'In WhatsApp öffnen',
        fallback: 'Klappt der Button nicht? Schick diesen Code per WhatsApp an {immoosNumber}: {code}',
        waiting: 'Warte auf deine Nachricht …',
        connected: 'Verbunden mit {phone}. Schreib an {immoosNumber}, um immoOS per WhatsApp zu fragen.',
        'connected-badge': 'Verbunden',
        'disconnect-cta': 'WhatsApp trennen',
        prefilled: 'Hallo immoOS, ich möchte meine WhatsApp-Nummer verbinden. Mein Code: {code}',
        error: 'Das hat nicht geklappt. Bitte versuch es noch einmal.',
      },
      telegram: {
        heading: 'Telegram',
        intro:
          'Frag immoOS per Telegram nach deinen Leads, Kontakten und Terminen. Verbinde dazu einmal dein Telegram-Konto.',
        'connect-cta': 'Telegram verbinden',
        'step-1': 'Tippe auf „In Telegram öffnen". Telegram öffnet den immoOS-Bot.',
        'step-2': 'Tippe im Bot auf „Starten". Du musst nichts schreiben.',
        'step-3': 'Fertig — wir melden uns per Telegram, sobald dein Konto verbunden ist.',
        'open-cta': 'In Telegram öffnen',
        fallback: 'Klappt der Button nicht? Schick diesen Code per Telegram an {botName}: {code}',
        waiting: 'Warte auf deine Nachricht …',
        connected: 'Verbunden mit {account}. Schreib an {botName}, um immoOS per Telegram zu fragen.',
        'connected-badge': 'Verbunden',
        'disconnect-cta': 'Telegram trennen',
        error: 'Das hat nicht geklappt. Bitte versuch es noch einmal.',
        'load-error': 'Telegram konnte nicht geladen werden. Bitte versuch es noch einmal.',
        'retry-cta': 'Erneut versuchen',
        'disconnect-title': 'Telegram-Konto {account} trennen?',
        'disconnect-body':
          'Danach antwortet dir der Assistent nicht mehr in {botName}. Du kannst dein Konto jederzeit wieder verbinden.',
        'cancel-cta': 'Abbrechen',
        'disconnect-confirm-cta': 'Trennen',
        'disconnect-loading': 'Wird getrennt…',
      },
      photo: {
        label: 'Profilfoto',
        change: 'Foto ersetzen',
        remove: 'Foto entfernen',
        uploading: 'Wird hochgeladen…',
        alt: 'Profilfoto',
        formats: 'JPG, PNG oder WebP · max. 5 MB',
        invalid: 'Bitte lade ein JPG-, PNG- oder WebP-Bild bis 5 MB hoch.',
        'upload-error': 'Das Foto konnte nicht hochgeladen werden — bitte erneut versuchen.',
        saving: 'Wird gespeichert…',
        'save-error': 'Das Foto wurde hochgeladen, konnte aber nicht gespeichert werden — bitte erneut versuchen.',
      },
      sections: {
        identity: 'Stammdaten',
        'agent-profile': 'Maklerprofil',
        profile: 'Profil',
        'contact-qualification': 'Kontakt & Qualifikation',
      },
      'location-empty': 'Nicht festgelegt',
      fields: {
        'display-name': 'Anzeigename',
        'first-name': 'Vorname',
        'last-name': 'Nachname',
        email: 'E-Mail',
        location: 'Standort',
        salutation: 'Anrede',
        'academic-title': 'Titel',
        position: 'Position',
        phone: 'Telefon',
        mobile: 'Mobil',
        bio: 'Kurzbio',
        specialisations: 'Spezialisierungen',
        languages: 'Sprachen',
        certifications: 'Zertifizierungen',
        'social-links': 'Social-Links',
      },
      placeholders: {
        'first-name': 'z. B. Anna',
        'last-name': 'z. B. Becker',
        salutation: 'Bitte wählen',
        'academic-title': 'z. B. Dr.',
        position: 'z. B. Immobilienberaterin',
        phone: '+49 40 123456',
        mobile: '+49 151 23456789',
        bio: 'Kurze Vorstellung für dein Maklerprofil',
        certifications: 'Eine Angabe pro Zeile',
        'social-links': 'Ein Link pro Zeile',
        specialisations: 'Spezialisierung wählen…',
        languages: 'Sprache wählen…',
        'certification-entry': 'z. B. IHK-Immobilienmakler',
        'social-link-entry': 'https://…',
      },
      helpers: {
        location: 'Dein primärer Standort — hier nicht bearbeitbar.',
        'location-derived': 'Abgeleitet aus deinem primären Standort — hier nicht editierbar.',
        picklist: 'Mehrfachauswahl aus fester Liste · kein Freitext',
        bio: 'Höchstens 600 Zeichen.',
        certifications: 'Eine Angabe pro Zeile.',
        'social-links': 'Ein Link pro Zeile, beginnend mit https://.',
      },
      errors: {
        'name-required': 'Bitte gib deinen Vor- und Nachnamen ein.',
        bio: 'Die Kurzbio darf höchstens 600 Zeichen lang sein.',
        phone: 'Bitte gib eine gültige Telefonnummer ein.',
        'social-links': 'Bitte gib eine gültige URL ein (beginnend mit https://).',
        list: 'Bitte wähle einen Wert aus der Liste.',
        'too-long': 'Der Wert ist zu lang.',
      },
      salutations: {
        MR: 'Herr',
        MRS: 'Frau',
        DIVERSE: 'Divers',
      },
      specialisations: {
        RESIDENTIAL: 'Wohnimmobilien',
        COMMERCIAL: 'Gewerbeimmobilien',
        INVESTMENT: 'Anlageimmobilien',
        NEW_BUILD: 'Neubau',
        LAND: 'Grundstücke',
        RENTAL: 'Vermietung',
      },
      languages: {
        DE: 'Deutsch',
        EN: 'Englisch',
        FR: 'Französisch',
        ES: 'Spanisch',
        IT: 'Italienisch',
        TR: 'Türkisch',
        RU: 'Russisch',
        PL: 'Polnisch',
      },
    },
    company: {
      title: 'Unternehmensdaten',
      subtitle:
        'Hinterlege die rechtlichen Stammdaten deines Unternehmens. Sie speisen das Impressum und die Pflichtangaben in deinen Exposés und Dokumenten.',
      'empty-hint':
        'Hinterlege die Daten deiner Firma — sie erscheinen im Impressum und im Exposé-Footer nach außen gehender Dokumente.',
      'load-error': 'Die Unternehmensdaten konnten nicht geladen werden.',
      'load-retry': 'Erneut versuchen',
      'readonly-hint': 'Nur Owner und Admins können die Unternehmensdaten bearbeiten.',
      'save-cta': 'Speichern',
      'save-loading': 'Wird gespeichert…',
      'save-success': 'Unternehmensdaten gespeichert',
      'generic-error': 'Unternehmensdaten konnten nicht gespeichert werden — bitte erneut versuchen.',
      'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
      'web-address': {
        label: 'Web-Adresse',
        help: 'Unter dieser Adresse erscheinen eure Web-Exposés: immoos.ai/{webAddress}/expose/…',
        'change-confirm': {
          title: 'Web-Adresse ändern?',
          body: 'Bestehende Web-Exposé-Links funktionieren danach nicht mehr. Bereits verschickte Links musst du neu versenden.',
          confirm: 'Ändern',
          cancel: 'Abbrechen',
        },
      },
      'address-search-label': 'Adresse suchen',
      'social-links-add': 'Link hinzufügen',
      'social-links-remove': 'Link entfernen',
      countries: {
        DE: 'Deutschland',
        AT: 'Österreich',
        CH: 'Schweiz',
      },
      sections: {
        company: 'Firmendaten',
        address: 'Adresse',
        contact: 'Kontakt',
        legal: 'Impressum & Recht',
      },
      fields: {
        'company-name': 'Firmenname',
        'legal-form': 'Rechtsform',
        street: 'Straße',
        'house-number': 'Hausnummer',
        'postal-code': 'PLZ',
        city: 'Ort',
        country: 'Land',
        phone: 'Telefon',
        fax: 'Fax',
        email: 'E-Mail',
        website: 'Website',
        'social-links': 'Social-Links',
        'managing-directors': 'Geschäftsführer',
        'commercial-register-court': 'Handelsregister',
        'commercial-register-number': 'Registernummer',
        'vat-id': 'USt-IdNr.',
        'supervisory-authority': 'Aufsichtsbehörde (§34c GewO)',
        'professional-indemnity-insurance': 'Berufshaftpflicht',
        'privacy-policy-url': 'Datenschutzerklärung (URL)',
      },
      placeholders: {
        'company-name': 'Mustermakler GmbH',
        'legal-form': 'Bitte wählen',
        'address-search': 'Adresse eingeben…',
        street: 'Musterstraße',
        'house-number': '1',
        'postal-code': '20251',
        city: 'Hamburg',
        country: 'Deutschland',
        phone: '+49 40 123456',
        fax: '+49 40 123457',
        email: 'info@mustermakler.de',
        website: 'https://www.mustermakler.de',
        'social-links': 'https://www.linkedin.com/company/…',
        'managing-directors': 'Max Mustermann, Erika Mustermann',
        'commercial-register-court': 'Amtsgericht Hamburg',
        'commercial-register-number': 'HRB 123456',
        'vat-id': 'DE123456789',
        'supervisory-authority': 'Behörde für Wirtschaft Hamburg, Erlaubnis §34c GewO',
        'professional-indemnity-insurance': 'Allianz Versicherungs-AG, Police 4711',
        'privacy-policy-url': 'https://www.deine-firma.de/datenschutz',
      },
      helpers: {
        'social-links': 'Jeweils eine vollständige URL, beginnend mit https://.',
        'managing-directors': 'Mehrere Namen mit Komma trennen.',
      },
      errors: {
        'privacy-policy-url': 'Bitte gib eine gültige Adresse an, die mit https:// beginnt.',
        'company-name': 'Der Firmenname darf höchstens 120 Zeichen lang sein.',
        email: 'Bitte gib eine gültige E-Mail-Adresse ein.',
        website: 'Bitte gib eine gültige URL ein (beginnend mit https://).',
        'social-links': 'Bitte gib eine gültige URL ein (beginnend mit https://).',
        'social-links-duplicate': 'Dieser Link ist bereits vorhanden — bitte entferne das Duplikat.',
        'legal-form': 'Bitte wähle eine Rechtsform aus der Liste.',
        phone: 'Bitte gib eine gültige Telefonnummer ein.',
        'vat-id': 'Bitte gib eine gültige USt-IdNr. ein (z. B. DE123456789).',
        'too-long': 'Der Wert ist zu lang.',
        address: {
          street: 'Bitte gib eine Straße ein.',
          'house-number': 'Bitte gib eine Hausnummer ein.',
          'postal-code': 'Die PLZ muss aus 5 Ziffern bestehen.',
          city: 'Bitte gib eine Stadt ein.',
          country: 'Bitte gib ein Land ein.',
        },
      },
      'legal-forms': {
        GMBH: 'GmbH',
        UG: 'UG (haftungsbeschränkt)',
        GMBH_CO_KG: 'GmbH & Co. KG',
        EINZELUNTERNEHMEN: 'Einzelunternehmen',
        EK: 'e.K.',
        GBR: 'GbR',
        PARTG_MBB: 'PartG mbB',
        OHG: 'OHG',
        KG: 'KG',
        UG_CO_KG: 'UG (haftungsbeschränkt) & Co. KG',
        AG: 'AG',
      },
    },
    branding: {
      title: 'Branding & CI',
      subtitle: 'Lege Farben und Schrift für das Erscheinungsbild deiner Firma fest — sie fließen in jedes Exposé ein.',
      'empty-hint':
        'Hinterlege das visuelle Erscheinungsbild deiner Firma — Logo, Farben und Schrift erscheinen in jedem Exposé.',
      'load-error': 'Das Branding konnte nicht geladen werden.',
      'readonly-hint': 'Nur Owner und Admins können das Firmen-Branding bearbeiten.',
      'save-cta': 'Speichern',
      'save-loading': 'Wird gespeichert…',
      'save-success': 'Branding gespeichert',
      'glyph-warning':
        '{fonts}: Diese Schrift enthält nicht alle Zeichen (ä, ö, ü, ß, €, ²). Im Exposé können einzelne Zeichen fehlen — du kannst sie trotzdem verwenden.',
      'generic-error': 'Branding konnte nicht gespeichert werden — bitte erneut versuchen.',
      'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
      sections: {
        logo: 'Logo',
        colours: 'Farben',
        typography: 'Typografie',
      },
      groups: {
        'house-colours': 'Hausfarben',
        'document-colours': 'Dokumentfarben',
      },
      fields: {
        logoPrimary: 'Logo (primär)',
        logoLight: 'Logo hell',
        logoDark: 'Logo dunkel',
        favicon: 'Favicon',
        brandColour: 'Markenfarbe (primär)',
        secondaryColor: 'Markenfarbe (sekundär)',
        accentColor: 'Akzentfarbe',
        paperColor: 'Papierfarbe',
        inkColor: 'Textfarbe',
        mutedColor: 'Gedämpfte Farbe',
        headingFont: 'Schriftart Überschrift',
        bodyFont: 'Schriftart Fließtext',
        labelFont: 'Schriftart Label',
        labelCasing: 'Label-Versalien',
        labelTracking: 'Label-Tracking',
      },
      placeholders: {
        font: 'Bitte wählen',
        'label-casing': 'Bitte wählen',
      },
      'font-picker': {
        title: 'Schrift wählen',
        choose: 'Schrift wählen',
        'preview-label': 'Vorschau',
        sample: 'Häuser & Höfe · Größe 85 m² · 1.250 € · Zürich',
        recommended: 'Empfohlene Schriften',
        'default-option': 'Standard (Vorlage)',
        'browse-google': 'Alle Google Fonts durchsuchen',
        'search-placeholder': 'Google Fonts durchsuchen …',
        'no-results': 'Keine Schriften gefunden.',
        'source-google': 'Google Fonts',
        'source-curated': 'Empfohlen',
      },
      'label-casings': {
        NORMAL: 'Normal',
        UPPERCASE: 'GROSSBUCHSTABEN',
        SMALL_CAPS: 'Kapitälchen',
      },
      errors: {
        colour: 'Bitte gib eine gültige Farbe als Hex-Wert ein (z. B. #0F5132).',
        font: 'Bitte wähle eine Schrift aus der Liste.',
        tracking: 'Bitte gib einen gültigen Wert in em oder px ein (z. B. 0.08em).',
      },
      logo: {
        change: 'Ändern',
        remove: 'Entfernen',
        uploading: 'Wird hochgeladen…',
        invalid: 'Bitte lade ein PNG- oder JPG-Bild bis 5 MB hoch.',
        'invalid-favicon': 'Bitte lade ein PNG- oder ICO-Bild bis 5 MB hoch.',
        'upload-error': 'Das Bild konnte nicht hochgeladen werden — bitte erneut versuchen.',
      },
    },
    signature: {
      title: 'E-Mail-Signatur',
      subtitle:
        'Eine Firmen-Signatur-Vorlage für den gesamten Arbeitsbereich. Profil-Variablen werden je Absender automatisch aus dem Profil aufgelöst.',
      'load-error': 'Die Signatur konnte nicht geladen werden.',
      'readonly-hint': 'Nur Owner und Admins können die Firmen-Signatur bearbeiten.',
      'editor-label': 'Signatur-Editor',
      'footer-hint': 'Diese Signatur wird standardmäßig unter ausgehende E-Mails gesetzt.',
      counter: '{count} / {max}',
      preview: {
        title: 'Vorschau',
        'profile-label': 'Profil',
        'empty-hint': 'Für dieses Profil ist noch keine Signatur hinterlegt.',
        'load-error': 'Die Vorschau konnte nicht geladen werden.',
        'degradation-note': 'Fehlende Werte (z. B. Mobil oder Profilfoto) werden je Profil automatisch ausgelassen.',
      },
      'save-cta': 'Speichern',
      'save-loading': 'Wird gespeichert…',
      'save-success': 'Signatur gespeichert',
      'generic-error': 'Die Signatur konnte nicht gespeichert werden — bitte erneut versuchen.',
      'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
      toolbar: {
        label: 'Formatierung',
        'font-family': 'Schriftart',
        'font-size': 'Schriftgröße',
        bold: 'Fett',
        italic: 'Kursiv',
        underline: 'Unterstrichen',
        colour: 'Textfarbe',
        'insert-logo': 'Logo einfügen',
      },
      fonts: {
        standard: 'Standard',
        'empty-hint': 'Noch keine CI-Schrift hinterlegt — nur „Standard“ verfügbar.',
      },
      palette: {
        title: 'Profil-Variablen',
        subtitle: 'Werte werden je Nutzer automatisch aus dem Profil gezogen.',
        groups: {
          person: 'Person',
          contact: 'Kontakt',
          company: 'Firma',
        },
      },
      logo: {
        'block-title': 'Logo in der Signatur',
        'size-label': 'Logo-Größe',
        'placeholder-alt': 'Firmenlogo',
        sizes: {
          SMALL: 'Klein · 120 px',
          MEDIUM: 'Mittel · 180 px',
          LARGE: 'Groß · 240 px',
        },
      },
      errors: {
        'too-long': 'Die Signatur darf höchstens 5.000 Zeichen lang sein.',
        'unsupported-variable': 'Diese Variable wird nicht unterstützt.',
      },
    },
  },
  workspace: {
    'buyer-commission': {
      rate: '{rate, number, ::.00} % inkl. MwSt.',
      amount: '{amount, number, ::currency/EUR} inkl. MwSt.',
      'buyer-free': 'käuferprovisionsfrei',
      missing: 'Käuferprovision fehlt',
      label: 'Käuferprovision',
      edit: 'Bearbeiten',
    },
    'objekt-commission': {
      'load-error': 'Das Objekt konnte nicht geladen werden. Bitte versuche es erneut.',
    },
    completeness: {
      products: COMPLETENESS_PRODUCT_LABELS,
      markers: { ...COMPLETENESS_MARKER_LABELS, separator: ' · ' },
      title: 'Datenstand',
      complete: 'Vollständig',
      'missing-required': '{count, plural, one {# Pflichtangabe fehlt} other {# Pflichtangaben fehlen}}',
      'missing-recommended': '{count, plural, one {# empfohlene Angabe fehlt} other {# empfohlene Angaben fehlen}}',
      'load-error': 'Der Datenstand konnte nicht geladen werden.',
      'load-retry': 'Erneut versuchen',
      'no-needs': 'Für diese Objektart ist noch kein Bedarf hinterlegt.',
      'agent-outcome': {
        'no-comparison-data': {
          'acquisition-briefing':
            'Für diese Region fehlen Vergleichsdaten. Ein Akquisebriefing ist hier noch nicht möglich.',
          'valuation-1a': 'Für diese Region fehlen Vergleichsdaten. Eine Bewertung 1a ist hier noch nicht möglich.',
          'valuation-1b': 'Für diese Region fehlen Vergleichsdaten. Eine Bewertung 1b ist hier noch nicht möglich.',
        },
        'address-not-found': 'Die Adresse wurde nicht gefunden. Bitte prüfe die Adresse des Objekts.',
        'object-type-not-supported': 'Diese Objektart unterstützt der Bewertungs-Agent noch nicht.',
        error: '{product} konnte nicht erstellt werden: {hint}',
        'timed-out': {
          'acquisition-briefing': 'Das Akquisebriefing konnte nicht erstellt werden.',
          'valuation-1a': 'Die Bewertung 1a konnte nicht erstellt werden.',
          'valuation-1b': 'Die Bewertung 1b konnte nicht erstellt werden.',
        },
      },
      'desk-valuation': {
        start: 'Bewertung 1a erstellen',
        'missing-hint':
          '{count, plural, one {Es fehlt # Pflichtangabe.} other {Es fehlen # Pflichtangaben.}} Der Bewertungs-Agent rechnet dann mit Annahmen und nennt sie in der Bewertung.',
        preparing: 'Bewertung 1a wird erstellt … (gestartet {time})',
        delivered: 'Bewertung 1a vom {date}: {from}–{to} €',
        view: 'Ansehen',
        renew: 'Neu erstellen',
        retry: 'Erneut versuchen',
        'object-type-unsupported': 'Für diese Objektart gibt es noch keine Bewertung 1a.',
        'address-incomplete': 'Für eine Bewertung braucht das Objekt eine vollständige Adresse.',
        'renew-dialog': {
          title: 'Neue Bewertung 1a erstellen?',
          description: 'Die bisherige wird ersetzt.',
          confirm: 'Neu erstellen',
          cancel: 'Abbrechen',
        },
      },
    },
    'valuation-source': {
      kind: VALUATION_SOURCE_KIND_LABELS,
      'viewing-kind': VALUATION_VIEWING_KIND_LABELS,
      'with-assumptions': 'mit Annahmen',
    },
    // Der aus der ⌘K-Suche mitgebrachte Filter (IMM-448.4). Kein Suchfeld — der
    // Chip zeigt nur, was gerade gefiltert wird, und lässt sich entfernen.
    'search-filter': {
      chip: 'Suche: {query}',
      remove: 'Suche „{query}" entfernen',
    },
    // Ein Kontakt oder Objekt, das nur über einen verknüpften Deal, Lead oder ein
    // Objekt eines anderen Standorts geöffnet wurde (IMM-1105): nur lesen.
    'linked-record': {
      'read-only-title': 'Nur Lesezugriff',
      'read-only-hint':
        'Du siehst diesen Datensatz über eine Verknüpfung. Bearbeiten kann ihn nur, wer am Standort {organization} berechtigt ist.',
      'read-only-hint-unknown': 'Du siehst diesen Datensatz über eine Verknüpfung. Bearbeiten ist hier nicht möglich.',
      'read-only-reason': 'Über eine Verknüpfung geöffnet — nur Lesezugriff.',
    },
    'compose-entry': {
      label: 'E-Mail schreiben',
      'no-permission': 'Dir fehlt die Berechtigung, E-Mails zu senden.',
      'no-mailbox': 'Dein Postfach ist nicht verbunden. Verbinde es, um E-Mails zu schreiben.',
      'no-address': 'Für diesen Kontakt ist keine E-Mail-Adresse hinterlegt.',
    },
    'connection-gap': {
      'mailbox-and-calendar':
        'Postfach und Kalender sind nicht verbunden. Verbinde ein Postfach mit Kalender, um E-Mails zu schreiben und zu Besichtigungen einzuladen – ein IMAP-Postfach hat keinen Kalender.',
      'connect-calendar': 'Kalender verbinden',
      'connect-mailbox': 'Postfach verbinden',
    },
    'reassign-standort': {
      title: 'Standort ändern',
      label: 'Standort',
      cancel: 'Abbrechen',
      submit: 'Standort ändern',
      submitting: 'Wird geändert…',
      success: 'Standort geändert',
      'forbidden-error': 'Du bist nicht berechtigt, diesen Datensatz auf den gewählten Standort zu verschieben.',
      'not-found-error': 'Datensatz nicht gefunden. Bitte lade die Seite neu.',
      'generic-error': 'Der Standort konnte nicht geändert werden. Bitte versuche es erneut.',
    },
    activityTimeline: {
      'prospect-phase-reason-code': { VIEWING_CANCELLED: 'Besichtigung abgesagt' },
      'email-delete-title': 'Diese E-Mail aus der Aktivität entfernen?',
      'email-delete-hint': 'Die E-Mail bleibt in deinem Postfach. Nur der Eintrag im Verlauf wird entfernt.',
      'direction-inbound': 'Eingehend',
      'direction-outbound': 'Ausgehend',
      'email-delete-confirm': 'Entfernen',
      'email-delete-cancel': 'Abbrechen',
      'email-delete-error': 'Die E-Mail konnte nicht entfernt werden — bitte erneut versuchen.',
      'group-today': 'Heute',
      'group-yesterday': 'Gestern',
      'relative-just-now': 'Gerade eben',
      'relative-minutes': 'Vor {count} Minuten',
      'relative-hours': 'Vor {count} Stunden',
      'relative-yesterday': 'Gestern',
      'entry-note': 'Notiz',
      'entry-appointment': 'Termin',
      'entry-appointment-valuation': 'Bewertungstermin',
      'entry-appointment-on-site': 'Vor-Ort-Termin',
      'entry-appointment-property-survey': 'Objektaufnahme',
      'entry-appointment-contract-signing': 'Vertragstermin',
      'entry-appointment-other': 'Sonstiger Termin',
      'entry-appointment-viewing-on-site': 'Besichtigung vor Ort',
      'entry-appointment-viewing-virtual': 'Besichtigung virtuell',
      'entry-appointment-consultation': 'Beratungsgespräch',
      'entry-appointment-financing-consultation': 'Finanzierungsgespräch',
      'entry-appointment-negotiation': 'Verhandlungsgespräch',
      'entry-appointment-notary': 'Notartermin',
      'entry-appointment-handover': 'Übergabe',
      'entry-viewing': 'Besichtigung',
      'entry-viewing-assessed': 'Besichtigung vom {date} ausgewertet: {outcome}',
      'entry-viewing-response-ACCEPTED': '{name} hat die Besichtigung zugesagt',
      'entry-viewing-response-TENTATIVE': '{name} hat die Besichtigung vorläufig zugesagt',
      'entry-viewing-response-DECLINED': '{name} hat die Besichtigung abgelehnt',
      'entry-viewing-response-ACCEPTED-unnamed': 'Der Interessent hat die Besichtigung zugesagt',
      'entry-viewing-response-TENTATIVE-unnamed': 'Der Interessent hat die Besichtigung vorläufig zugesagt',
      'entry-viewing-response-DECLINED-unnamed': 'Der Interessent hat die Besichtigung abgelehnt',
      'filter-appointment': 'Termin',
      'entry-call-inbound': 'Eingehender Anruf',
      'entry-call-outbound': 'Ausgehender Anruf',
      'entry-sms-inbound': 'Eingehende SMS',
      'entry-sms-outbound': 'Ausgehende SMS',
      'entry-whatsapp-inbound': 'Eingehende WhatsApp',
      'entry-whatsapp-outbound': 'Ausgehende WhatsApp',
      'entry-fax-inbound': 'Eingehendes Fax',
      'entry-fax-outbound': 'Ausgehendes Fax',
      'entry-letter-inbound': 'Eingehender Brief',
      'entry-letter-outbound': 'Ausgehender Brief',
      'entry-email-inbound': 'Eingehende E-Mail',
      'entry-email-outbound': 'Ausgehende E-Mail',
      'entry-email-empty-subject': '(Kein Betreff)',
      'entry-email-open-thread': 'Konversation öffnen',
      'entry-company-badge': 'Firma',
      'entry-email-reply': 'Antworten',
      'entry-email-recipient': 'An: {recipient}',
      'entry-email-unavailable': 'Konversation beim Anbieter nicht mehr verfügbar',
      'entry-email-auto-placed': 'Automatisch zugeordnet',
      'entry-email-mailbox-of': 'Aus dem Postfach von {name}',
      'entry-email-attachment': 'Anhang',
      'entry-calendar-cancelled': 'Abgesagt: {title}',
      'entry-calendar-untitled': '(Ohne Titel)',
      'email-activity-footer':
        'E-Mail ist dieselbe Aktivität wie alles andere — Anruf, Notiz, Aufgabe. Nutze den Filter „{filter}“, um nur Konversationen zu sehen.',
      'entry-prospect-created': 'Interessent angelegt',
      'entry-prospect-created-via-inquiry': 'Angelegt über {source}-Anfrage',
      'entry-inquiry-received': 'Anfrage über {source}',
      'prospect-source': 'Quelle {source}',
      'prospect-offer': 'Angebot {price}',
      'prospect-purchase-price': 'Kaufpreis {price}',
      'prospect-notary-date': 'Notartermin {date}',
      'prospect-won': 'Kaufprozess gewonnen',
      'entry-prospect-offer-changed': 'Angebot geändert',
      'entry-prospect-offer-captured': 'Angebot erfasst',
      'entry-prospect-counter-offer-captured': 'Gegenangebot erfasst',
      'entry-prospect-offer-accepted': 'Angebot angenommen',
      'entry-prospect-counter-offer-accepted': 'Gegenangebot angenommen',
      'entry-prospect-offer-rejected': 'Angebot abgelehnt',
      'entry-prospect-counter-offer-rejected': 'Gegenangebot abgelehnt',
      'entry-prospect-offer-withdrawn': 'Angebot zurückgezogen',
      'entry-prospect-counter-offer-withdrawn': 'Gegenangebot zurückgezogen',
      'entry-prospect-offer-reopened': 'Angebot wieder geöffnet',
      'entry-prospect-counter-offer-reopened': 'Gegenangebot wieder geöffnet',
      'entry-prospect-notary-moved': 'Notartermin verschoben',
      'entry-prospect-notary-captured': 'Notartermin eingetragen',
      'entry-prospect-lost': 'Als verloren markiert',
      'entry-prospect-reopened': 'Wieder aufgenommen',
      'entry-brokerage-notice-sent': 'Widerrufsbelehrung gesendet an {recipient}',
      'entry-brokerage-reminder-sent': 'Erinnerung gesendet an {recipient}',
      'entry-brokerage-concluded-early-start': 'Online abgeschlossen · vorzeitiger Beginn verlangt',
      'entry-brokerage-concluded-no-early-start': 'Online abgeschlossen · vorzeitiger Beginn nicht verlangt',
      'entry-brokerage-acceptance-reply-received': 'Annahme per E-Mail vermerkt',
      'entry-brokerage-withdrawal-receipt-sent': 'Widerruf eingegangen · Eingangsbestätigung gesendet',
      'entry-brokerage-withdrawal-receipt-failed': 'Widerruf eingegangen · Eingangsbestätigung fehlgeschlagen',
      'actor-prospect-online': 'Interessent (online)',
      'entry-deal-created': 'Deal angelegt',
      'entry-deal-advanced': 'Phase vorwärts',
      'entry-deal-reverted': 'Phase zurück',
      'entry-deal-contact-added': 'Eigentümer hinzugefügt',
      'entry-deal-contact-removed': 'Eigentümer entfernt',
      'entry-deal-resumed': 'Wieder aufgenommen',
      'entry-deal-restored': 'Deal wiederhergestellt',
      'entry-deal-deferred': 'Zurückgestellt',
      'entry-deal-deferred-until': 'bis {date}',
      'entry-deal-lost': 'Verloren',
      'entry-deal-reopened': 'Wieder geöffnet',
      'entry-deal-document-generated': 'Dokument erstellt',
      'entry-deal-document-open': 'PDF öffnen',
      'entry-deal-document-line': '{documentType} · {templateName}',
      'entry-deal-document-manually-adjusted': 'Manuell angepasst',
      'entry-deal-signing-sent': 'Zur Unterschrift gesendet',
      'entry-deal-signing-sent-line': '{documentType} an {names}',
      'entry-deal-signing-signed': 'Unterschrieben von {name}',
      'entry-deal-signing-signed-line': '{documentType}',
      'entry-deal-signing-completed': 'Vollständig unterschrieben',
      'entry-deal-signing-withdrawn': 'Unterschriftsanfrage zurückgezogen',
      'entry-deal-signing-acceptance-marked': 'Annahme per E-Mail von {name} vermerkt',
      'entry-deal-signing-revoked': 'Widerruf von {name}',
      'entry-deal-signing-completed-open': 'PDF öffnen',
      'origin-online-signature': 'Unterschrift (online)',
      'entry-deal-document-type': {
        BROKER_CONTRACT: DOCUMENT_CATEGORY_LABELS.BROKER_CONTRACT,
        POWER_OF_ATTORNEY: DOCUMENT_CATEGORY_LABELS.POWER_OF_ATTORNEY,
      } satisfies Record<DocumentTemplateCategory, string>,
      'entry-property-status-changed': 'Status geändert',
      'entry-property-archive-reason-changed': 'Archivierungsgrund geändert',
      'entry-property-pricing-sale-changed': 'Preis (Kauf) geändert',
      'entry-property-areas-changed': 'Flächen geändert',
      'entry-property-hand-reservation-marked': 'Als reserviert markiert',
      'entry-property-hand-reservation-unmarked': 'Reservierung aufgehoben',
      'entry-property-hand-reservation-lifted': 'Reservierung aufgehoben – Objekt nicht mehr aktiv',
      'entry-deal-resumed-auto': 'Automatisch wieder aufgenommen',
      'entry-deal-responsibility-manual': '{actor} hat {names} als verantwortlich gesetzt.',
      'entry-deal-responsibility-created': 'Deal angelegt — verantwortlich: {names}.',
      'entry-deal-responsibility-qualified': 'Deal aus Lead übernommen — verantwortlich: {names}.',
      'entry-deal-responsibility-deactivated': '{name} wurde deaktiviert und ist nicht mehr verantwortlich.',
      'entry-deal-responsibility-none': 'Dieser Deal ist derzeit niemandem zugewiesen.',
      'entry-valuation-submitted': '{actor} hat eine Bewertung angefragt.',
      'entry-valuation-taken-on': '{actor} hat die Bewertung in Bearbeitung genommen.',
      'entry-valuation-submitted-for-approval': '{actor} hat die Bewertung zur Freigabe eingereicht.',
      'entry-valuation-returned': '{actor} hat die Bewertung zur Überarbeitung zurückgegeben: {reason}',
      'entry-valuation-adjusted': '{actor} hat die Preisspanne angepasst: {previous} → {next}',
      'entry-valuation-released': '{actor} hat die Bewertung freigegeben.',
      'entry-valuation-withdrawn': '{actor} hat die Bewertung zurückgezogen.',
      'entry-valuation-price-range': '{from} – {to}',
      'entry-lead-lost': 'Lead verloren',
      'entry-lead-archived': 'Lead archiviert',
      'entry-lead-reopened': 'Lead wieder aufgenommen',
      'entry-lead-contacted': "Status auf ‚Kontaktiert' gesetzt",
      'entry-lead-contacted-meta': 'automatisch · {activity}',
      'entry-contacted-after-call-outbound': 'nach ausgehendem Anruf',
      'entry-contacted-after-call-inbound': 'nach eingehendem Anruf',
      'entry-contacted-after-sms-outbound': 'nach ausgehender SMS',
      'entry-contacted-after-sms-inbound': 'nach eingehender SMS',
      'entry-contacted-after-whatsapp-outbound': 'nach ausgehender WhatsApp',
      'entry-contacted-after-whatsapp-inbound': 'nach eingehender WhatsApp',
      'entry-contacted-after-fax-outbound': 'nach ausgehendem Fax',
      'entry-contacted-after-fax-inbound': 'nach eingehendem Fax',
      'entry-contacted-after-letter-outbound': 'nach ausgehendem Brief',
      'entry-contacted-after-letter-inbound': 'nach eingehendem Brief',
      'entry-contacted-after-email-outbound': 'nach ausgehender E-Mail',
      'entry-contacted-after-email-inbound': 'nach eingehender E-Mail',
      'deal-transition': '{from} → {to}',
      'actor-system': 'System',
      'actor-admin': 'immoOS',
      'actor-platform-support': 'immoOS-Support',
      'actor-change-system': 'Automatisch',
      'property-change-archive-reason': 'Archivierungsgrund: {reason}',
      'property-change-archive-note': 'Notiz: {note}',
      'property-change-line': '{field}: {from} → {to}',
      'property-section-change': {
        'section-title': {
          IDENTITY: 'Identität geändert',
          ADDRESS: 'Adresse geändert',
          BUILDING: 'Gebäude geändert',
          ENERGY: 'Energie geändert',
          INVESTMENT_YIELD: 'Ertrag geändert',
          PLOT_DETAILS: 'Grundstück geändert',
          AMENITIES: 'Ausstattung geändert',
          PRICING_RENTAL: 'Preis (Miete) geändert',
          RENTAL_TERMS: 'Mietkonditionen geändert',
          ADDITIONAL_COSTS: 'Nebenkosten geändert',
          DESCRIPTIONS: 'Texte geändert',
          REFERENCE_SETTINGS: 'Referenz geändert',
        } as const satisfies EnumTranslation<PropertyChangeSection>,
        'contact-link-title': 'Kontakt-Rolle geändert',
        'item-field-value-line': '{item} – {field}: {from} → {to}',
        'text-changed-line': '{field} geändert',
        'note-changed-line': '{item}: Notiz geändert',
        'deleted-contact': 'Gelöschter Kontakt',
      },
      'property-status': PROPERTY_STATUS_LABELS,
      'archive-reason': ARCHIVE_REASON_LABELS,
      'connected-client-marker': 'Über einen verbundenen Client',
      'connected-client-tooltip':
        'Diese Aktivität wurde über einen verbundenen MCP-Client geschrieben — im Namen von {name}.',
      'recorder-unknown': 'Unbekanntes Mitglied',
      'contact-unknown': 'Unbekannter Kontakt',
      edited: 'bearbeitet',
      'outcome-reached': 'Erreicht',
      'outcome-not-reached': 'Nicht erreicht',
      'outcome-voicemail': 'Mailbox',
      'outcome-callback-agreed': 'Rückruf vereinbart',
      // Das E-Mail-eigene Ergebnisvokabular (IMM-913.3): „Antwort erhalten" /
      // „Keine Antwort" sind REACHED / NOT_REACHED, für eine E-Mail relabelt.
      'outcome-email-reached': 'Antwort erhalten',
      'outcome-email-not-reached': 'Keine Antwort',
      // Termin-Ergebnisse (IMM-430.3) — ein erledigter Termin zeigt sein
      // Ergebnis im Verlauf genauso wie eine Kommunikation.
      'outcome-attended': 'Wahrgenommen',
      'outcome-not-attended': 'Nicht wahrgenommen',
      'outcome-rescheduled': 'Verschoben',
      'edit-cta': 'Bearbeiten',
      'delete-cta': 'Löschen',
      'empty-heading': 'Noch keine Aktivitäten',
      'empty-body': 'Hier erscheint die gesamte Aktivität — Anrufe, Notizen, Nachrichten und mehr.',
      'empty-cta': 'Aktivität hinzufügen',
      'load-more': 'Mehr laden',
      'loading-more': 'Wird geladen …',
      loading: 'Wird geladen …',
      'load-error': 'Aktivität konnte nicht geladen werden.',
      'load-retry': 'Erneut versuchen',
      'chip-kind-contact': 'Kontakt',
      'chip-kind-deal': 'Deal',
      'chip-kind-property': 'Objekt',
      'filter-label': 'Filtern nach Typ',
      'filter-note': 'Notiz',
      'filter-call': 'Anruf',
      'filter-sms': 'SMS',
      'filter-whatsapp': 'WhatsApp',
      'filter-fax': 'Fax',
      'filter-letter': 'Brief',
      'filter-email': 'E-Mail',
      'filter-inquiry': 'Anfrage',
      'filter-valuation': 'Bewertung',
      'filter-phase': 'Phase',
      'filter-brokerage': 'Maklervertrag',
      'filter-deal': 'Deal',
      'filter-change': 'Änderung',
      'filter-type-label': 'Typ',
      'filter-reset': 'Filter zurücksetzen',
      'filter-empty': 'Keine Einträge für den gewählten Filter.',
      'email-open-cta': 'Konversation öffnen',
    },
    emailThread: {
      forbidden: 'Du bist für diese Aktion nicht berechtigt.',
      title: 'E-Mail-Konversation',
      loading: 'Konversation wird geladen …',
      'load-error': 'Die Konversation konnte nicht geladen werden.',
      empty: 'Diese Konversation enthält keine Nachrichten.',
      close: 'Schließen',
      'back-to-activity': 'zurück zur Aktivität',
      'back-to-inbox': 'zurück zum Posteingang',
      'message-count': '{count} Nachrichten',
      'with-counterpart': 'mit {counterpart}',
      'direction-inbound': 'Eingehend',
      'direction-outbound': 'Ausgehend',
      'unread-badge': 'Ungelesen',
      from: 'Von',
      to: 'An',
      'no-subject': '(Kein Betreff)',
      'body-loading': 'Nachricht wird geladen …',
      'body-error': 'Der Nachrichteninhalt konnte vorübergehend nicht geladen werden.',
      'body-error-retry': 'Erneut versuchen',
      'body-error-disconnected':
        'Dein Postfach ist nicht mehr verbunden. Bitte verbinde es erneut, um diese Nachricht zu lesen.',
      'company-mailbox-expired':
        'Das Firmenpostfach {email} ist nicht mehr verbunden. Verbinde es in den Einstellungen unter Firmenpostfächer erneut.',
      'company-mailbox-expired-unknown':
        'Das Firmenpostfach ist nicht mehr verbunden. Verbinde es in den Einstellungen unter Firmenpostfächer erneut.',
      'body-error-gone': 'Diese Nachricht ist beim Anbieter nicht mehr verfügbar.',
      'show-images': 'Bilder anzeigen',
      'images-blocked-hint': 'Externe Bilder wurden zum Schutz vor Tracking blockiert.',
      attachments: 'Anhänge',
      'attachments-present': 'Diese Nachricht enthält Anhänge.',
      'attachment-fallback-name': 'anhang',
      'show-quoted': 'Zitierten Verlauf anzeigen',
      'hide-quoted': 'Zitierten Verlauf ausblenden',
      // Short provider brand badged on the expanded message (design 02).
      'provider-brand': { GOOGLE: 'Gmail', MICROSOFT: 'Outlook', IMAP: 'IMAP' },
      collapse: 'Einklappen',
      expand: 'Ausklappen',
    },
    mailboxSurface: {
      'connect-heading': 'Postfach verbinden',
      'connect-body':
        'Verbinde dein E-Mail-Postfach, um deine Konversationen mit Kontakten direkt in immoOS zu sehen und Antworten von deiner eigenen Adresse zu senden.',
      'connect-cta': 'Postfach verbinden',
      'reconnect-heading': 'Postfach erneut verbinden',
      'reconnect-body':
        'Dein Postfach ist nicht mehr verbunden. Bitte verbinde es erneut, um neue E-Mails zu empfangen und zu senden.',
      'reconnect-cta': 'Erneut verbinden',
      'switchover-heading': 'Postfach einmalig neu verbinden',
      'switchover-body':
        'Wir haben die Postfach-Anbindung verbessert: immoOS verbindet sich jetzt direkt mit Google. Bis du dein Postfach neu verbindest, funktioniert alles wie gewohnt.',
      syncing: 'Synchronisierung läuft',
      'no-email-address': 'Für diesen Kontakt ist keine E-Mail-Adresse hinterlegt.',
      'load-error': 'Der Verbindungsstatus deines Postfachs konnte nicht geladen werden.',
      'load-retry': 'Erneut versuchen',
    },
    emailAddress: {
      'compose-tooltip': 'E-Mail schreiben',
      'connect-body':
        'Verbinde dein E-Mail-Postfach, um E-Mails direkt aus immoOS von deiner eigenen Adresse zu senden.',
      cancel: 'Abbrechen',
    },
    mailboxComposer: {
      'insert-template': 'Vorlage einfügen',
      'templates-empty': 'Noch keine E-Mail-Vorlagen. Owner und Admins legen sie unter Einstellungen → Vorlagen an.',
      'templates-empty-area':
        'Noch keine E-Mail-Vorlagen im Bereich {area}. Owner und Admins legen sie unter Einstellungen → Vorlagen an.',
      'templates-failed': 'Die E-Mail-Vorlagen konnten nicht geladen werden. Versuche es noch einmal.',
      'template-insert-failed': 'Die E-Mail-Vorlage konnte nicht eingefügt werden. Versuche es noch einmal.',
      'personal-link-text': 'Zu Ihrer persönlichen Seite',
      'error-open-fields': 'Ersetze zuerst die markierten Felder: {list}.',
      'template-values': {
        'greeting-mr': 'Guten Tag Herr {lastName}',
        'greeting-mrs': 'Guten Tag Frau {lastName}',
        'greeting-name': 'Guten Tag {firstName} {lastName}',
        'salutation-mr': 'Herr',
        'salutation-mrs': 'Frau',
      },
      'new-email': 'Neue E-Mail',
      reply: 'Antworten',
      'reply-all': 'Allen antworten',
      forward: 'Weiterleiten',
      'title-new': 'Neue E-Mail',
      'title-reply': 'Antworten',
      'title-reply-all': 'Allen antworten',
      'title-forward': 'Weiterleiten',
      from: 'Von',
      to: 'An',
      cc: 'Cc',
      bcc: 'Bcc',
      subject: 'Betreff',
      'cc-bcc-toggle': 'Cc/Bcc',
      'recipient-placeholder': 'Name oder E-Mail-Adresse',
      'link-title': 'Link einfügen',
      'link-text-label': 'Text',
      'link-url-label': 'URL',
      'link-insert': 'Einfügen',
      'link-cancel': 'Abbrechen',
      preview: 'Vorschau',
      'preview-subtitle': 'So sieht der Empfänger die E-Mail',
      'preview-back': 'Zurück zum Bearbeiten',
      send: 'Senden',
      sending: 'Senden …',
      discard: 'Verwerfen',
      minimise: 'Minimieren',
      restore: 'Wiederherstellen',
      close: 'Schließen',
      'attachment-add': 'Anhang hinzufügen',
      'attachment-remove': 'Anhang entfernen',
      'attachments-total': 'Gesamtgröße: {size}',
      'attachments-count': '{count, plural, one {# Anhang} other {# Anhänge}}',
      'reply-placeholder': 'Antwort schreiben …',
      'signature-label': 'Signatur',
      're-prefix': 'Re: ',
      'fwd-prefix': 'Fwd: ',
      'forward-header': '---------- Weitergeleitete Nachricht ----------',
      'forward-from': 'Von: {sender}',
      'forward-subject': 'Betreff: {subject}',
      'forward-date': 'Datum: {date}',
      'forward-to': 'An: {recipients}',
      'sent-success': 'E-Mail gesendet',
      'send-failure': 'Fehler beim Senden der E-Mail',
      'error-no-recipient': 'Bitte gib mindestens einen Empfänger an.',
      'error-invalid-recipient': 'Bitte gib eine gültige E-Mail-Adresse an: {value}',
      'error-empty-subject': 'Bitte gib einen Betreff an.',
      'error-subject-too-long': 'Der Betreff darf höchstens {max} Zeichen lang sein.',
      'error-attachments-too-large': 'Die Anhänge überschreiten das Limit von {limit}.',
      'grant-expired-block': 'Dein Postfach ist nicht mehr verbunden. Bitte verbinde es erneut, um E-Mails zu senden.',
      'no-subject': '(Kein Betreff)',
    },
    calendar: {
      title: 'Kalender',
      'viewing-type': { ON_SITE: 'Besichtigung vor Ort', VIRTUAL: 'Besichtigung virtuell' },
      'view-switcher': 'Ansicht',
      'view-week': 'Woche',
      'view-day': 'Tag',
      'view-month': 'Monat',
      'view-agenda': 'Agenda',
      today: 'Heute',
      create: 'Neuer Termin',
      'previous-period': 'Vorheriger Zeitraum',
      'next-period': 'Nächster Zeitraum',
      'range-from': 'ab {date}',
      month: { label: 'Monat-Ansicht', more: '+{count} weitere' },
      agenda: {
        label: 'Agenda-Ansicht',
        today: 'Heute · {day}',
        empty: 'Keine Termine in diesem Zeitraum.',
        'all-day': 'Ganztägig',
      },
      sidebar: {
        label: 'Kalender-Navigation',
        shortcuts: 'T — Tag · W — Woche · M — Monat · A — Agenda · H — Heute · N — Neuer Termin',
      },
      'quick-create': {
        label: 'Neuer Termin',
        'title-placeholder': 'Titel hinzufügen',
        title: 'Titel',
        save: 'Speichern',
        saving: 'Wird gespeichert …',
        more: 'Weitere Optionen',
      },
      form: {
        label: 'Termin-Formular',
        title: 'Neuer Termin',
        'edit-title': 'Termin bearbeiten',
        type: 'Typ',
        'title-field': 'Titel',
        when: 'Datum & Uhrzeit',
        date: 'Datum',
        start: 'Beginn',
        end: 'Ende',
        duration: 'Dauer',
        'duration-option': '{minutes} Min.',
        'all-day': 'Ganztägig',
        location: 'Ort',
        'join-url': 'Einwahllink',
        notes: 'Notizen',
        links: 'Verknüpfungen',
        link: {
          label: 'Datensatz verknüpfen',
          placeholder: 'Objekt, Kontakt, Deal oder Lead suchen …',
          hint: '↵ verknüpfen',
          empty: 'Nichts gefunden.',
          loading: 'Suche läuft …',
          failed: 'Die Suche ist fehlgeschlagen.',
          results: 'Treffer',
          chips: 'Verknüpft',
          remove: '{name} entfernen',
          adopt: {
            title: 'Aus dem Deal übernehmen',
            property: 'Objekt',
            contacts: 'Kontakte',
            ambiguous: 'Dieser Deal hat mehrere Kontakte — wähle aus, wer gemeint ist.',
            confirm: 'Übernehmen',
            later: 'Später',
          },
        },
        attendees: 'Teilnehmer',
        attendee: {
          label: 'Teilnehmer hinzufügen',
          placeholder: 'Name oder E-Mail-Adresse',
          'no-email': 'Dieser Kontakt hat keine E-Mail-Adresse.',
          invalid: 'Bitte gib eine gültige E-Mail-Adresse ein.',
          colleagues: 'Team',
          contacts: 'Kontakte',
          loading: 'Suche läuft …',
          failed: 'Die Suche ist fehlgeschlagen.',
          chips: 'Teilnehmer',
          remove: '{name} entfernen',
        },
        conference: {
          label: 'Videokonferenz',
          add: 'Google Meet hinzufügen',
          name: 'Google Meet',
          'add-teams': 'Teams-Besprechung hinzufügen',
          'name-teams': 'Microsoft Teams',
          'personal-account-hint':
            'Teams-Besprechungen gibt es nur mit einem Microsoft-365-Geschäftskonto. Einen Link aus einem anderen Dienst kannst du unter „Einwahllink“ einfügen.',
          'removed-locally-hint':
            'Der Link wird nur in immoOS entfernt. In Outlook bleibt die Teams-Besprechung bestehen — wer eingeladen ist, kann weiter teilnehmen.',
          pending: 'Wird beim Speichern erstellt.',
          copy: 'Link kopieren',
          copied: 'Link kopiert.',
          remove: 'Entfernen',
        },
        save: 'Speichern',
        saving: 'Wird gespeichert …',
        cancel: 'Abbrechen',
      },
      'mini-month': { label: 'Mini-Monat' },
      'account-connected': 'Verbunden · {email}',
      loading: 'Kalender wird geladen …',
      'load-error': 'Dein Kalender konnte nicht geladen werden.',
      'load-retry': 'Erneut versuchen',
      empty: {
        title: 'Kein Kalender verbunden',
        description:
          'Verbinde deinen Kalender, um Termine zu sehen und automatisch mit deinem Team zu synchronisieren.',
        'description-imap':
          'Dein Postfach ist über IMAP verbunden. IMAP überträgt nur E-Mails, keine Termine — deshalb zeigt immoOS hier keinen Kalender.',
        connect: 'Kalender verbinden',
        'connect-error': 'Die Verbindung konnte nicht gestartet werden. Bitte versuche es erneut.',
      },
      reconnect: {
        title: 'Kalender-Synchronisation unterbrochen — bitte verbinde deinen Kalender erneut.',
        action: 'Erneut verbinden',
      },
      grid: {
        'week-label': 'Woche-Ansicht',
        'day-label': 'Tag-Ansicht',
        'all-day': 'Ganztägig',
        'all-day-more': '+{count} weitere',
        'all-day-less': 'Weniger anzeigen',
        move: 'Termin verschieben',
        resize: 'Dauer ändern',
      },
      appointment: {
        private: 'Privater Termin',
        untitled: '(Ohne Titel)',
        cancelled: 'Abgesagt',
        conflict: 'Konflikt',
        conference: 'Videokonferenz',
      },
      'conference-warning':
        'Der Meet-Link konnte nicht erstellt werden. Der Termin ist gespeichert — du kannst es erneut versuchen.',
      'conference-warning-teams':
        'Der Teams-Link konnte nicht erstellt werden. Der Termin ist gespeichert — du kannst es erneut versuchen.',
      types: {
        VIEWING: 'Besichtigung',
        ON_SITE: 'Vor-Ort-Termin',
        OWNER_MEETING: 'Eigentümergespräch',
        VALUATION: 'Bewertungstermin',
        NOTARY: 'Notartermin',
        HANDOVER: 'Übergabe',
        PHONE: 'Telefonat',
        ONLINE_MEETING: 'Online-Meeting',
        OTHER: 'Sonstiger Termin',
      },
      legend: { label: 'Terminarten' },
      'link-kinds': { PROPERTY: 'Objekt', CONTACT: 'Kontakt', DEAL: 'Deal', LEAD: 'Lead', PROSPECT: 'Interessent' },
      'link-unnamed': '(Ohne Name)',
      'create-from-record': { action: 'Termin anlegen', 'viewing-title': 'Besichtigung {address}' },
      upcoming: {
        title: 'Anstehende Termine',
        'empty-title': 'Keine anstehenden Termine.',
        'empty-body': {
          PROPERTY: 'Sobald ein Termin mit diesem Objekt verknüpft ist, erscheint er hier.',
          CONTACT: 'Sobald ein Termin mit diesem Kontakt verknüpft ist, erscheint er hier.',
          DEAL: 'Sobald ein Termin mit diesem Deal verknüpft ist, erscheint er hier.',
          LEAD: 'Sobald ein Termin mit diesem Lead verknüpft ist, erscheint er hier.',
          PROSPECT: 'Sobald ein Termin mit diesem Interessenten verknüpft ist, erscheint er hier.',
        },
        'unknown-owner': 'Unbekanntes Mitglied',
        'load-error': 'Die anstehenden Termine konnten nicht geladen werden.',
      },
      'cancel-dialog': {
        title: 'Termin absagen?',
        body: 'Die Teilnehmer werden über deinen Kalender benachrichtigt. Diese Aktion kann nicht rückgängig gemacht werden.',
        confirm: 'Termin absagen',
        dismiss: 'Zurück',
        pending: 'Wird abgesagt …',
      },
      'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
      'change-error': { label: 'Änderung am Termin' },
      panel: {
        label: 'Termindetails',
        close: 'Schließen',
        location: 'Ort',
        notes: 'Notizen',
        links: 'Verknüpfungen',
        attendees: 'Teilnehmer',
        conference: 'Videokonferenz',
        join: 'Teilnehmen',
        copy: 'Link kopieren',
        copied: 'Link kopiert.',
        'join-url': 'Einwahllink',
        'viewing-assessment-heading': 'Teilnahme',
        'viewing-assessment-heading-named': 'Teilnahme · {label}',
        'conflict-notice': 'In beiden Kalendern geändert — zuletzt gespeicherte Version gilt.',
        edit: 'Bearbeiten',
        'cancel-appointment': 'Termin absagen',
        'all-day': 'Ganztägig',
        'duration-hours': '{hours} Std.',
        'duration-minutes': '{minutes} Min.',
        'duration-hours-minutes': '{hours} Std. {minutes} Min.',
        response: {
          NEEDS_ACTION: 'Ausstehend',
          ACCEPTED: 'Zugesagt',
          DECLINED: 'Abgelehnt',
          TENTATIVE: 'Vorläufig',
        },
      },
    },
    mailboxHome: {
      title: 'E-Mails',
      posteingang: 'Posteingang',
      postausgang: 'Postausgang',
      'subtitle-inbox': 'Alle empfangenen E-Mails aus deinen verbundenen Postfächern — neueste zuerst.',
      'subtitle-outbox': 'Alle gesendeten E-Mails aus deinen verbundenen Postfächern — neueste zuerst.',
      'search-inbox': 'Im Posteingang suchen …',
      'search-outbox': 'Im Postausgang suchen …',
      'account-all': 'Alle Konten',
      'account-filter-label': 'Konto',
      'company-badge': 'Firma',
      'unread-only': 'Nur ungelesen',
      'filter-unplaced': 'Nur nicht zugeordnete',
      'unplaced-count': '{count} nicht zugeordnet',
      'placement-needs-review': 'Zuordnung prüfen',
      // Row order for the Posteingang (a sort, not the unread filter).
      'view-default': 'Standard',
      'view-unread-first': 'Ungelesene zuerst',
      'load-more': 'Mehr laden',
      loading: 'E-Mails werden geladen …',
      'load-error': 'Deine E-Mails konnten nicht geladen werden.',
      'load-retry': 'Erneut versuchen',
      'empty-inbox': 'Noch keine E-Mails im Posteingang',
      'empty-outbox': 'Noch keine gesendeten E-Mails im Postausgang',
      'no-match-title': 'Keine Treffer',
      'no-match-description': 'Passe Suche oder Filter an, um E-Mails zu finden.',
      filters: {
        reset: 'Filter zurücksetzen',
      },
      'no-subject': '(Kein Betreff)',
      'no-counterpart': '(Unbekannt)',
      'unread-badge': 'Ungelesen',
      'outgoing-badge': 'Ausgehend',
      'has-attachments': 'Anhang',
      // Contact chip: the matched contact's name, or the placeholder when the
      // thread has no contact.
      'contact-unassigned': 'Nicht zugeordnet',
      // Relative date formatting (lists), per the PRD copy.
      'date-this-year': '{day}. {month}',
      'date-older': '{day}. {month} {year}',
    },
    mailboxConnectDialog: {
      title: 'Konto hinzufügen',
      intro: 'Wähle den Anbieter deines Postfachs.',
      'unsupported-providers':
        'Aktuell kann nur ein Gmail-Postfach verbunden werden. Microsoft 365, Exchange und IMAP werden noch nicht unterstützt.',
      'unsupported-providers-with-microsoft':
        'Gmail- und Microsoft-Postfächer können verbunden werden. Exchange auf eigenen Servern und IMAP werden noch nicht unterstützt.',
      'provider-google-label': 'Google',
      'provider-google-desc': 'Gmail oder Google Workspace',
      'provider-microsoft-label': 'Microsoft',
      'provider-microsoft-desc': 'Outlook oder Microsoft 365',
      'provider-imap-label': 'Anderer Anbieter (IMAP)',
      'provider-imap-desc': 'GMX, WEB.DE, T-Online, IONOS u. a.',
      'hint-oauth':
        'Sichere Anmeldung über den Anbieter — immoOS speichert keine Passwörter. Verbinden und Trennen laufen über die gesicherte Anbieter-Anmeldung.',
      'hint-imap':
        'Anderer Anbieter (IMAP): Anmeldung mit E-Mail-Adresse und Passwort (bzw. App-Passwort) deines Anbieters.',
      cancel: 'Abbrechen',
      continue: 'Weiter zur Anmeldung',
      'continue-imap': 'Weiter',
      'calendar-no-imap':
        'IMAP-Postfächer haben keinen Kalender. Du kannst sie unter Einstellungen → Postfach verbinden.',
      error: 'Verbindung konnte nicht gestartet werden — bitte erneut versuchen.',
    },
    // Kontakt-Zuordnung für E-Mails (IMM-233.4).
    mailboxAssign: {
      action: 'Kontakt zuordnen',
      title: 'Zuordnen',
      'title-change': 'Zuordnung ändern',
      intro:
        'Wähle aus, wozu diese Konversation gehört. Alle Nachrichten der Konversation werden dort als Aktivität erfasst.',
      'change-hint': 'Alle Nachrichten dieser Konversation ziehen um — auch spätere Antworten.',
      'type-contact': 'Kontakt',
      'type-lead': 'Lead',
      'type-deal': 'Deal',
      'type-property': 'Objekt',
      'search-placeholder': 'Datensatz suchen …',
      'suggestion-label': 'Vorschlag',
      'suggestion-hint': '{name} hat mehrere Deals oder Objekte. Wähle, wozu diese Konversation gehört.',
      'current-tag': 'Aktuell',
      'nothing-selected': 'Bitte wähle einen Datensatz.',
      'success-change': 'Zuordnung geändert.',
      'needs-review': 'Zuordnung prüfen',
      'action-change': 'Zuordnung ändern',
      'search-loading': 'Kontakte werden geladen …',
      'search-no-results': 'Keine Kontakte gefunden',
      'apply-all-label': 'Alle E-Mails von diesem Absender zuordnen',
      confirm: 'Zuordnen',
      'confirm-loading': 'Wird zugeordnet …',
      cancel: 'Abbrechen',
      'no-contact-selected': 'Bitte wähle einen Kontakt.',
      error: 'Zuordnung fehlgeschlagen — bitte erneut versuchen.',
      'success-toast': 'E-Mail zugeordnet.',
      'remove-action': 'Zuordnung entfernen',
      'remove-loading': 'Wird entfernt …',
      'remove-error': 'Zuordnung konnte nicht entfernt werden — bitte erneut versuchen.',
      'remove-success-toast': 'Zuordnung entfernt.',
      // Shown on an assigned email so the member sees the current state.
      'assigned-label': 'Zugeordnet',
    },
    'assign-membership': {
      'role-label': 'Rolle',
      'cancel-cta': 'Abbrechen',
      'save-cta': 'Speichern',
      'save-loading': 'Wird gespeichert…',
      'save-error': 'Speichern fehlgeschlagen – bitte erneut versuchen.',
    },
    members: {
      title: 'Mitglieder',
      subtitle:
        'Verwalte, wer Zugriff auf diesen Workspace hat und welche Rollen die Mitglieder in den Standorten innehaben.',
      'invite-cta': 'Mitglied einladen',
      'invite-success-toast': 'Mitglied „{email}" eingeladen.',
      'invite-success-dismiss': 'Schließen',
      'list-load-error': 'Mitglieder konnten nicht geladen werden.',
      'list-load-retry': 'Erneut versuchen',
      'empty-title': 'Noch keine Mitglieder',
      'empty-description': 'Lade dein erstes Teammitglied ein, um loszulegen.',
      'no-match-title': 'Keine Treffer',
      'no-match-description': 'Passe den Filter an, um Mitglieder zu finden.',
      columns: {
        name: 'Name',
        email: 'E-Mail',
        status: 'Status',
        'last-active': 'Zuletzt aktiv',
        'joined-at': 'Beigetreten am',
      },
      status: {
        active: 'Aktiv',
        deactivated: 'Deaktiviert',
      },
      'status-filter': {
        active: 'Aktive',
        deactivated: 'Deaktivierte',
        all: 'Alle',
      },
      'role-change-cta': 'Rolle ändern',
      'last-active-never': '—',
      'over-limit-warning':
        'Dieser Workspace hat mehr als 50 aktive Mitglieder. Größere Teams können die Übersicht erschweren.',
      filter: {
        'status-label': 'Status',
        'status-active': 'Aktiv',
        'status-deactivated': 'Deaktiviert',
        'status-all': 'Alle Mitglieder',
        reset: 'Zurücksetzen',
      },
      sort: {
        label: 'Sortieren',
        name: 'Name',
        joined: 'Beitrittsdatum',
      },
      'role-edit-label': 'Rollen bearbeiten',
      'role-modal-heading': 'Organisationsrollen',
      'role-modal-org-label': 'Standort',
      'role-save-cta': 'Speichern',
      'role-save-loading': 'Wird gespeichert…',
      'role-save-error': 'Rollen konnten nicht gespeichert werden — bitte erneut versuchen.',
      'role-cancel-cta': 'Abbrechen',
      'role-self-edit-notice': 'Du kannst deine eigene Organisationsrolle nicht ändern.',
      affiliation: {
        label: 'Standortzugehörigkeit',
        helper: 'Mitglied gehört zu einem oder mehreren Standorten. Bestimmt die Sichtbarkeit von Datensätzen.',
        'primary-label': 'Primärer Standort',
        'primary-helper':
          'Standardansicht im Dashboard und Vorbelegung beim Anlegen neuer Datensätze. Muss Teil der Standortzugehörigkeit sein.',
        'locked-badge': 'Rolle erforderlich',
        'locked-hint':
          'Standorte ohne Rolle lassen sich hier nicht auswählen. Vergib die Rolle zuerst über „Standorte zuweisen“.',
        errors: {
          required: 'Mindestens ein Standort muss ausgewählt sein.',
          'primary-not-in-selection': 'Der primäre Standort muss Teil der Standortzugehörigkeit sein.',
          'requires-role':
            'Für {locations} ist noch keine Rolle vergeben. Ein Mitglied kann nur Standorten zugeordnet werden, in denen es eine Rolle hat — vergib die Rolle zuerst über „Standorte zuweisen“.',
          'requires-role-unknown':
            'Für mindestens einen ausgewählten Standort ist noch keine Rolle vergeben. Ein Mitglied kann nur Standorten zugeordnet werden, in denen es eine Rolle hat — vergib die Rolle zuerst über „Standorte zuweisen“.',
        },
      },
      invite: {
        title: 'Mitglied einladen',
        'email-label': 'E-Mail',
        'email-placeholder': 'ada@beispiel.de',
        'first-name-label': 'Vorname',
        'first-name-placeholder': 'Ada',
        'last-name-label': 'Nachname',
        'last-name-placeholder': 'Lovelace',
        'organisation-label': 'Organisation',
        'no-organisations':
          'Es stehen keine Organisationen für eine Einladung zur Verfügung. Lade die Seite neu oder versuche es später erneut.',
        'organisation-role-label': 'Organisationsrolle',
        'organisation-role': {
          'organisation-owner': 'Inhaber',
          agent: 'Makler',
          assistant: 'Assistenz',
          viewer: 'Betrachter',
        },
        'submit-cta': 'Einladen',
        'submit-loading': 'Wird gespeichert…',
        'cancel-cta': 'Abbrechen',
        'generic-error': 'Einladung fehlgeschlagen — bitte erneut versuchen.',
        'forbidden-error': 'Du hast keine Berechtigung, an diesem Standort Mitglieder einzuladen.',
        'unauthorized-error': 'Deine Sitzung ist abgelaufen. Bitte melde dich erneut an und versuche es erneut.',
        errors: {
          email: {
            required: 'E-Mail ist erforderlich.',
            invalid: 'Bitte gib eine gültige E-Mail-Adresse ein.',
            duplicate: 'Dieses Mitglied gehört bereits zum Workspace.',
            'duplicate-pending':
              'Für diese E-Mail-Adresse gibt es bereits eine ausstehende Einladung. Widerrufe sie unten im Bereich „Ausstehende Einladungen" oder sende sie erneut.',
          },
          'first-name': {
            required: 'Vorname ist erforderlich.',
            'too-long': 'Vorname ist zu lang.',
            invalid: 'Vorname ist ungültig.',
          },
          'last-name': {
            required: 'Nachname ist erforderlich.',
            'too-long': 'Nachname ist zu lang.',
            invalid: 'Nachname ist ungültig.',
          },
        },
      },
      invitations: {
        title: 'Ausstehende Einladungen',
        'list-load-error': 'Einladungen konnten nicht geladen werden.',
        'action-error': 'Aktion fehlgeschlagen — bitte erneut versuchen.',
        'role-pending': 'Rolle folgt',
        'resend-cta': 'Erneut senden',
        'revoke-cta': 'Widerrufen',
        'resend-success-toast': 'Einladung an „{email}" erneut gesendet.',
        'revoke-success-toast': 'Einladung an „{email}" widerrufen.',
        columns: {
          email: 'E-Mail',
          role: 'Rolle',
          'issued-by': 'Eingeladen von',
          'expires-at': 'Läuft ab am',
          status: 'Status',
        },
        status: {
          pending: 'Ausstehend',
        },
        confirm: {
          cancel: 'Abbrechen',
          submitting: 'Wird gespeichert…',
          resend: {
            title: 'Einladung erneut senden?',
            intro:
              'Die bisherige Einladung an „{email}" wird widerrufen und eine neue mit frischer Gültigkeit gesendet.',
            submit: 'Erneut senden',
          },
          revoke: {
            title: 'Einladung widerrufen?',
            intro: 'Der Link für „{email}" funktioniert danach nicht mehr.',
            submit: 'Widerrufen',
          },
        },
      },
      'profile-edit': {
        heading: 'Profil von {name}',
        subtitle: 'Du bearbeitest das Maklerprofil dieses Mitglieds für Exposé und Makler-Profil.',
        intro: 'Du bearbeitest ein fremdes Profil als Owner/Admin. Anzeigename und E-Mail bleiben unverändert.',
        'save-cta': 'Profil speichern',
      },
      detail: {
        tabs: {
          overview: 'Übersicht',
          organisations: 'Organisationen',
          activity: 'Aktivität',
        },
        'profile-heading': 'Profil',
        'status-heading': 'Status',
        'status-label': 'Status',
        'joined-label': 'Beigetreten am',
        'last-active-label': 'Zuletzt aktiv',
        'organisations-empty': 'Dieses Mitglied ist keiner Organisation zugeordnet.',
        'edit-profile-intro':
          'Du bearbeitest das Profil eines anderen Mitglieds als Owner/Admin. Anzeigename und E-Mail bleiben unverändert.',
        'email-label': 'E-Mail',
        'first-name-label': 'Vorname',
        'last-name-label': 'Nachname',
        'workspace-roles-label': 'Workspace-Rollen',
        'organisation-roles-label': 'Organisationsrollen',
        'edit-cta': 'Bearbeiten',
        'edit-profile-cta': 'Profil bearbeiten',
        'actions-trigger-label': 'Aktionen',
        'assign-locations-cta': 'Standorte zuweisen',
        'assign-locations-title': 'Standorte zuweisen',
        'assign-locations-heading': 'Wählen Sie die Standorte, denen dieses Mitglied angehört.',
        'assign-locations-empty': 'Keine Standorte verfügbar.',
        'edit-title': 'Mitglied bearbeiten',
        'back-cta': 'Abbrechen',
        'save-cta': 'Speichern',
        'save-loading': 'Wird gespeichert…',
        'save-success': 'Profil aktualisiert.',
        'save-error': 'Speichern fehlgeschlagen — bitte erneut versuchen.',
        'deactivate-cta': 'Deaktivieren',
        'reactivate-cta': 'Reaktivieren',
        'deactivate-description': 'Deaktivierte Mitglieder verlieren den Zugriff auf den Workspace.',
        'deactivate-confirm-title': '{name} deaktivieren?',
        'deactivate-cancel-cta': 'Abbrechen',
        'reactivate-description': 'Reaktivierte Mitglieder können sich erneut anmelden.',
        'deactivate-self-error': 'Du kannst dich nicht selbst deaktivieren.',
        'status-error': 'Aktion fehlgeschlagen — bitte erneut versuchen.',
        'status-loading': 'Wird gespeichert…',
        errors: {
          email: {
            required: 'E-Mail ist erforderlich.',
            invalid: 'Bitte gib eine gültige E-Mail-Adresse ein.',
          },
          'first-name': {
            required: 'Vorname ist erforderlich.',
            'too-long': 'Vorname ist zu lang.',
            invalid: 'Vorname ist ungültig.',
          },
          'last-name': {
            required: 'Nachname ist erforderlich.',
            'too-long': 'Nachname ist zu lang.',
            invalid: 'Nachname ist ungültig.',
          },
        },
      },
    },
    organisations: {
      title: 'Standorte',
      'page-description':
        'Standorte teilen deinen Workspace nach Büros oder Filialen auf. Lege hier weitere Standorte an.',
      'create-cta': 'Neuen Standort einrichten',
      'success-toast': 'Standort „{name}" angelegt.',
      'success-dismiss': 'Schließen',
      'list-load-error': 'Standorte konnten nicht geladen werden.',
      'empty-title': 'Noch keine Standorte',
      'empty-description': 'Lege deinen ersten Standort an, um loszulegen.',
      'no-match-title': 'Keine Treffer',
      'no-match-description': 'Passe den Filter an, um Standorte zu finden.',
      columns: {
        name: 'Name',
        address: 'Anschrift',
        type: 'Typ',
        status: 'Status',
        members: 'Mitglieder',
        records: 'Datensätze',
        'created-at': 'Erstellt am',
      },
      type: {
        main: 'Hauptstandort',
        subsidiary: 'Sub-Standort',
      },
      status: {
        ACTIVE: 'aktiv',
        INACTIVE: 'inaktiv',
      },
      'single-intro': {
        heading: 'Dein Workspace hat aktuell nur einen Hauptstandort',
        body: 'Standorte teilen einen Workspace nach Büros oder Filialen auf — z.B. ein Hauptstandort in Frankfurt und Sub-Standorte in Hamburg und München. Solange nur der Hauptstandort existiert, arbeitet jedes Mitglied workspace-weit.',
        cta: 'Sub-Standort anlegen',
      },
      'setup-badge': 'In Einrichtung',
      filter: {
        all: 'Alle',
        'in-setup': 'In Einrichtung',
        completed: 'Eingerichtet',
        reset: 'Zurücksetzen',
      },
      stage: {
        invited: 'Eingeladen',
        in_progress: 'In Bearbeitung',
        submitted: 'Eingereicht',
        in_review: 'In Prüfung',
        changes_requested: 'Änderungen angefordert',
        approved: 'Freigegeben',
        rejected: 'Abgelehnt',
      },
      reparent: {
        heading: 'Standort verschieben',
        body: 'Du verschiebst Standort „{name}“ unter „{newParent}“. Diese Änderung wirkt sich auf {memberCount} Mitglieder aus, deren Sichtbarkeit sich entsprechend anpasst. Untergeordnete Standorte ({descendantCount}) und alle ihnen zugeordneten Datensätze ({recordCount}) ziehen mit.',
        confirm: 'Verschiebung bestätigen',
        cancel: 'Abbrechen',
      },
      review: {
        title: 'Einrichtung prüfen · {name}',
        'operator-model-pill': 'Partnerunternehmen',
        meta: {
          'location-manager': 'Standortleitung',
          'created-at': 'Erstellt am',
          'link-valid-until': 'Link gültig bis',
        },
        legal: {
          heading: 'Firmierung & rechtliche Angaben',
          'company-name': 'Firmenname',
          'legal-form': 'Rechtsform',
          'managing-directors': 'Geschäftsführer',
          'commercial-register': 'Handelsregister',
          'vat-id': 'USt-IdNr.',
          'trade-permit-authority': 'Erlaubnisbehörde (§ 34c GewO)',
          'supervisory-authority': 'Zuständige Aufsichtsbehörde',
          'professional-indemnity-insurance': 'Berufshaftpflichtversicherung',
          'content-responsible-person': 'Inhaltlich Verantwortliche (§ 18 Abs. 2 MStV)',
          'external-website': 'Externe Website',
        },
        contact: {
          heading: 'Kontakt',
          address: 'Anschrift',
          email: 'E-Mail',
          phone: 'Telefon',
        },
        consents: {
          heading: 'Einwilligungen',
          'granted-by': 'Erteilt von {by} · {at}',
          declined: 'Nicht erteilt',
          empty: 'Keine Einwilligungen erfasst.',
        },
        'empty-value': '—',
        'no-profile': 'Es wurden noch keine Daten eingereicht.',
        panel: {
          heading: 'Prüfung',
          'approve-cta': 'Freigeben',
          'request-changes-cta': 'Änderungen anfordern',
          'reject-cta': 'Ablehnen',
          'info-heading': 'Was bei Freigabe passiert',
          'info-data': 'Daten → Standort',
          'info-manager': 'GF → Standortleitung',
          'info-badge': '„In Einrichtung"-Badge entfällt',
        },
        approve: {
          title: 'Einrichtung freigeben?',
          'info-heading': 'Was bei Freigabe passiert',
          'info-data': 'Die geprüften Daten werden auf den Standort angewendet.',
          'info-manager': 'Die Standortleitung erhält die Einladung zum Standort separat.',
          'info-badge': 'Der „In Einrichtung"-Status entfällt.',
          'cancel-cta': 'Abbrechen',
          'confirm-cta': 'Freigeben & anwenden',
          'loading-cta': 'Wird angewendet…',
        },
        'request-changes': {
          title: 'Änderungen anfordern',
          'notes-label': 'Review-Notizen',
          'notes-placeholder': 'Was muss der Partner anpassen?',
          'notes-required': 'Bitte gib an, was geändert werden soll.',
          info: 'Der Magic-Link des Partners öffnet das Onboarding erneut — mit den Notizen sichtbar. Kein neuer Link nötig.',
          'cancel-cta': 'Abbrechen',
          'confirm-cta': 'Änderungen anfordern',
          'loading-cta': 'Wird gesendet…',
        },
        reject: {
          title: 'Ablehnen',
          description: 'Die Einreichung wird endgültig abgelehnt. Es werden keine Daten angewendet.',
          'cancel-cta': 'Abbrechen',
          'confirm-cta': 'Ablehnen',
          'loading-cta': 'Wird abgelehnt…',
        },
        'load-error': 'Die Prüfungsdaten konnten nicht geladen werden.',
        'decision-error': 'Die Aktion konnte nicht ausgeführt werden. Bitte versuche es erneut.',
        'approve-success': 'Einrichtung freigegeben.',
        'request-changes-success': 'Änderungen angefordert.',
        'reject-success': 'Einreichung abgelehnt.',
      },
      einzugsgebiet: {
        heading: 'Einzugsgebiet (PLZ)',
        count: '{count} PLZs zugeordnet',
        empty: 'Kein Einzugsgebiet definiert. Füge PLZs hinzu, um das Gebiet dieses Standorts festzulegen.',
        'add-cta': 'PLZs hinzufügen',
        'remove-label': 'PLZ {plz} entfernen',
        'removed-toast': 'PLZ {plz} entfernt.',
        'load-error': 'Das Einzugsgebiet konnte nicht geladen werden.',
        dialog: {
          title: 'PLZs hinzufügen',
          'input-label': 'PLZs',
          'input-placeholder': '20249, 20251, 20253 …',
          hint: 'Mehrere PLZs mit Komma, Semikolon oder Zeilenumbruch trennen.',
          'upload-cta': 'CSV/TXT-Datei hochladen',
          'parse-summary': '{recognised} PLZs erkannt, {skipped} ungültige Einträge übersprungen.',
          'file-empty': 'Die Datei enthält keine gültigen PLZs.',
          'wrong-file-type': 'Nur CSV- oder TXT-Dateien werden unterstützt.',
          'staged-count': '{count} PLZs ausgewählt',
          'remove-staged-label': 'PLZ {plz} aus der Auswahl entfernen',
          'submit-cta': 'PLZs zuordnen',
          'cancel-cta': 'Abbrechen',
          'close-cta': 'Schließen',
          'invalid-token': 'PLZ muss aus 5 Ziffern bestehen.',
          'empty-error': 'Mindestens eine PLZ angeben.',
          result: {
            assigned: '{count} PLZs zugeordnet.',
            'already-present': '{count} PLZs waren diesem Standort bereits zugeordnet.',
            'conflicts-heading': 'Bereits anderen Standorten zugeordnet',
            'conflict-line': '„{plz}" gehört bereits zu „{standort}".',
          },
        },
      },
      detail: {
        tabs: {
          overview: 'Übersicht',
          members: 'Mitglieder',
          einzugsgebiet: 'Einzugsgebiet',
        },
        'status-label': 'Status',
        'setup-label': 'Einrichtung',
        'onboarding-label': 'Onboarding',
        'members-empty': 'Diesem Standort sind keine Mitglieder zugeordnet.',
        'members-loading': 'Mitglieder werden geladen…',
        'members-load-error': 'Mitglieder konnten nicht geladen werden.',
        'members-load-retry': 'Erneut versuchen',
        'assign-members-cta': 'Mitglieder zuweisen',
        'assign-members-loading': 'Wird geladen…',
        'assign-members-title': 'Mitglieder zuweisen',
        'assign-members-heading': 'Wählen Sie die Mitglieder, die diesem Standort angehören.',
        'assign-members-empty': 'Keine Mitglieder verfügbar.',
        'role-edit-label': 'Rollen bearbeiten',
        'role-modal-heading': 'Standortrolle',
        'role-save-cta': 'Speichern',
        'role-save-loading': 'Wird gespeichert…',
        'role-save-error': 'Rolle konnte nicht gespeichert werden — bitte erneut versuchen.',
        'lead-recipient-label': 'Erhält neue Leads',
        'lead-recipient-description':
          'Eingehende Leads dieses Standorts — etwa über die Partner-Schnittstelle — werden diesem Mitglied automatisch zugewiesen.',
        'lead-recipient-owner-hint':
          'Inhaber:innen dieses Standorts sind immer als Backup auf jedem Lead. Diese Einstellung lässt sich für sie nicht ändern.',
        'lead-recipient-save-error': 'Änderung konnte nicht gespeichert werden.',
        'lead-recipients-heading': 'Empfänger neuer Leads',
        'lead-recipients-empty':
          'Noch niemand ausgewählt — eingehende Leads gehen an die Inhaber:innen dieses Standorts.',
        'role-cancel-cta': 'Abbrechen',
        'role-self-edit-notice': 'Du kannst deine eigene Standortrolle nicht ändern.',
        'details-heading': 'Details',
        'address-heading': 'Anschrift',
        'profile-heading': 'Profil',
        'edit-cta': 'Bearbeiten',
        'edit-title': 'Standort bearbeiten',
        'back-cta': 'Abbrechen',
        'id-label': 'ID',
        'type-label': 'Typ',
        'object-number-prefix-label': 'Standort-Präfix',
        'object-number-prefix-hint': 'Objekte dieses Standorts erhalten Nummern ab {firstNumber}.',
        'created-at-label': 'Angelegt am',
        'name-label': 'Name',
        'parent-label': 'Übergeordneter Standort',
        'parent-helper': 'Zum Suchen tippen. Standardwert ist der Hauptstandort.',
        'parent-placeholder': 'Standort suchen…',
        'parent-clear': 'Auswahl entfernen',
        'parent-no-results': 'Keine passenden Standorte.',
        'email-label': 'E-Mail',
        'phone-label': 'Telefon',
        'description-label': 'Beschreibung',
        'street-label': 'Straße',
        'house-number-label': 'Hausnummer',
        'postal-code-label': 'PLZ',
        'city-label': 'Stadt',
        'country-label': 'Land',
        'coordinates-label': 'Koordinaten',
        'legal-heading': 'Firmierung / Impressum',
        'contact-heading': 'Kontakt',
        'internal-website-label': 'Interne Website',
        'own-branch-note':
          'Dieser Standort firmiert unter dem Hauptunternehmen des Workspace; eine eigene Impressums-/Rechtsidentität besteht nicht.',
        'address-search-label': 'Adresse suchen',
        'address-search-placeholder': 'Straße, Hausnummer, Stadt …',
        'save-cta': 'Speichern',
        'save-loading': 'Wird gespeichert…',
        'save-success': 'Standort aktualisiert.',
        'save-error': 'Speichern fehlgeschlagen — bitte erneut versuchen.',
        errors: {
          name: {
            required: 'Standortname ist erforderlich.',
            'too-long': 'Standortname darf höchstens 80 Zeichen lang sein.',
            invalid: 'Standortname ist ungültig.',
            duplicate: 'In diesem Workspace existiert bereits ein Standort mit diesem Namen.',
          },
          email: {
            invalid: 'Bitte gib eine gültige E-Mail-Adresse ein.',
          },
          'postal-code': {
            invalid: 'PLZ muss aus 5 Ziffern bestehen.',
          },
          parent: {
            required: 'Bitte wähle einen übergeordneten Standort.',
            cycle: 'Ein Standort kann nicht sich selbst oder einem seiner untergeordneten Standorte zugeordnet werden.',
            main: 'Der Hauptstandort kann keinem anderen Standort untergeordnet werden.',
          },
        },
      },
      create: {
        title: 'Neuen Standort einrichten',
        'operating-model-heading': 'Betreibermodell',
        'operating-model-helper':
          'Legt fest, wer den Standort betreibt – nicht seine Position in der Standorthierarchie.',
        'operating-model': {
          OWN_BRANCH: 'Eigener Standort',
          PARTNER_COMPANY: 'Partnerunternehmen',
        },
        'name-label': 'Standortname',
        'name-placeholder': 'Hamburg-Eppendorf',
        'parent-label': 'Übergeordneter Standort',
        'parent-helper': 'Jeder neue Standort wird untergeordnet angelegt. Zum Suchen tippen.',
        'parent-context': 'Wird unter {parent} angelegt.',
        'parent-context-default': 'Wird unter dem Hauptstandort angelegt.',
        'parent-placeholder': 'Standort suchen…',
        'parent-clear': 'Auswahl entfernen',
        'parent-no-results': 'Keine passenden Standorte.',
        'email-label': 'E-Mail',
        'phone-label': 'Telefon',
        'description-label': 'Beschreibung',
        'location-manager-heading': 'Standortleitung',
        'location-manager-first-name-label': 'Vorname',
        'location-manager-first-name-placeholder': 'z. B. Erika',
        'location-manager-last-name-label': 'Nachname',
        'location-manager-last-name-placeholder': 'z. B. Mustermann',
        'location-manager-email-label': 'E-Mail',
        'location-manager-email-placeholder': 'name@partner.de',
        'internal-website-label': 'Interne Website',
        'internal-website-placeholder': 'https://…',
        'submit-partner-cta': 'Onboarding an Partner senden',
        'submit-partner-loading': 'Wird gesendet…',
        'address-heading': 'Anschrift',
        'address-search-label': 'Adresse suchen',
        'address-search-placeholder': 'Straße, Hausnummer, Stadt …',
        'street-label': 'Straße',
        'street-placeholder': 'Eppendorfer Landstraße',
        'house-number-label': 'Hausnummer',
        'house-number-placeholder': '47a',
        'postal-code-label': 'PLZ',
        'postal-code-placeholder': '20251',
        'city-label': 'Stadt',
        'city-placeholder': 'Hamburg',
        'country-label': 'Land',
        'country-placeholder': 'Deutschland',
        'submit-cta': 'Standort anlegen',
        'submit-loading': 'Wird angelegt…',
        'cancel-cta': 'Abbrechen',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'not-a-member-error': 'Du gehörst diesem Workspace nicht an.',
        'generic-error': 'Standort konnte nicht angelegt werden. Bitte versuche es erneut.',
        errors: {
          name: {
            required: 'Standortname ist erforderlich.',
            'too-long': 'Standortname darf höchstens 80 Zeichen lang sein.',
            duplicate: 'In diesem Workspace existiert bereits ein Standort mit diesem Namen.',
          },
          email: {
            invalid: 'Bitte gib eine gültige E-Mail-Adresse ein.',
          },
          parent: {
            required: 'Bitte wähle einen übergeordneten Standort.',
            cycle: 'Ein Standort kann nicht sich selbst oder einem seiner untergeordneten Standorte zugeordnet werden.',
            main: 'Der Hauptstandort kann keinem anderen Standort untergeordnet werden.',
          },
          street: {
            required: 'Bitte gib eine Straße ein.',
            'too-long': 'Die Straße darf höchstens 100 Zeichen lang sein.',
          },
          'house-number': {
            required: 'Bitte gib eine Hausnummer ein.',
            'too-long': 'Die Hausnummer darf höchstens 20 Zeichen lang sein.',
          },
          'postal-code': {
            required: 'Bitte gib eine PLZ ein.',
            invalid: 'PLZ muss aus 5 Ziffern bestehen.',
          },
          city: {
            required: 'Bitte gib eine Stadt ein.',
            'too-long': 'Die Stadt darf höchstens 80 Zeichen lang sein.',
          },
          country: {
            required: 'Bitte gib ein Land ein.',
            'too-long': 'Das Land darf höchstens 80 Zeichen lang sein.',
          },
          'location-manager-first-name': {
            required: 'Bitte gib den Vornamen der Standortleitung ein.',
            'too-long': 'Der Vorname darf höchstens 80 Zeichen lang sein.',
          },
          'location-manager-last-name': {
            required: 'Bitte gib den Nachnamen der Standortleitung ein.',
            'too-long': 'Der Nachname darf höchstens 80 Zeichen lang sein.',
          },
          'location-manager-email': {
            required: 'Bitte gib die E-Mail der Standortleitung ein.',
            invalid: 'Bitte gib eine gültige E-Mail-Adresse ein.',
          },
          'internal-website': {
            invalid: 'Bitte gib eine gültige URL ein (https://…).',
          },
        },
      },
    },
    properties: {
      title: 'Objekte',
      subtitle: 'Alle Objekte deines Workspaces.',
      'create-cta': 'Neues Objekt',
      'import-is24-cta': 'ImmoScout24-Inserate importieren',
      'success-toast': 'Objekt „{title}" angelegt.',
      'success-dismiss': 'Schließen',
      'empty-title': 'Noch keine Objekte',
      'empty-description': 'Hier landen alle Objekte deines Workspaces.',
      'list-load-error': 'Objekte konnten nicht geladen werden.',
      'list-load-retry': 'Erneut versuchen',
      'show-archived': 'Archivierte anzeigen',
      'status-view': {
        active: 'Bestand',
        'in-acquisition': 'In Akquise',
      },
      'no-match-title': 'Keine Treffer',
      'no-match-description': 'Passe Suche oder Filter an, um Objekte zu finden.',
      // IMM-685 — the workspace holds Objekte, but the current view hides all of
      // them. Distinct from „Keine Treffer": there is no filter to relax here,
      // only a partition to switch, so the copy names the switch instead.
      'hidden-title': 'Keine Objekte in dieser Ansicht',
      'hidden-description': '{count} Objekte liegen unter „{label}".',
      'hidden-description-one': 'Ein Objekt liegt unter „{label}".',
      'hidden-cta': '„{label}" anzeigen',
      'hidden-culprit': {
        acquisition: 'In Akquise',
        archived: 'Archiv',
      },
      unassigned: 'Nicht zugewiesen',
      filters: {
        'marketing-label': 'Vermarktungsart',
        'status-view-label': 'Ansicht',
        'search-placeholder': 'Titel, Anschrift oder Objekt-Nr. suchen…',
        'search-label': 'Objekte suchen',
        'status-all': 'Alle Status',
        'type-all': 'Alle Objekttypen',
        'reason-all': 'Alle Gründe',
        'reason-label': 'Archivierungsgrund',
        reset: 'Zurücksetzen',
      },
      sort: {
        label: 'Sortieren',
        updated: 'Zuletzt aktualisiert',
        newest: 'Neueste zuerst',
        oldest: 'Älteste zuerst',
        'price-desc': 'Preis: hoch → niedrig',
        'price-asc': 'Preis: niedrig → hoch',
        'area-desc': 'Fläche: groß → klein',
        'area-asc': 'Fläche: klein → groß',
      },
      pagination: {
        range: '{from}–{to} von {total}',
        'per-page': 'Pro Seite:',
        prev: 'Zurück',
        next: 'Weiter',
      },
      columns: {
        'object-number': 'Objekt-Nr.',
        title: 'Titel',
        address: 'Anschrift',
        type: 'Immobilientyp',
        object: 'Objekt',
        status: 'Status',
        price: 'Preis',
        // IMM-788.6 — the price column's header when a visible commercial
        // rental row switches the cell to €/m² (Decision 5).
        'price-per-sqm': 'Miete/m²',
        area: 'Primärfläche',
        rooms: 'Zimmer',
        'available-from': 'Verfügbar ab',
        'min-divisible-area': 'Teilbar ab',
        assignee: 'Zugewiesen',
        'broker-team': 'Makler-Team',
        'match-score': 'Match-Score',
        'last-activity': 'Letzte Aktivität',
        'last-modified': 'Letzte Änderung',
        'created-at': 'Erfasst am',
      },
      'price-on-request': 'Preis auf Anfrage',
      'ancillary-costs-suffix': '+ {amount} NK',
      // IMM-788.6 — retightened from the unused '/ m² mt.' to the design's
      // '/m²' (Decision 4); the monthly sense is carried by the column
      // header and the RENTAL tab the row can only appear under.
      'price-per-sqm-rent-suffix': '/m²',
      totals: {
        'shown-of-total': '{shown} von {total}',
        'price-sum': 'Σ {amount}',
        'area-avg': 'ø {amount}',
        'broker-count': '{count} Makler',
        'score-avg': 'ø {amount}',
        empty: '—',
      },
      'last-activity-fallback': 'noch keine Aktivität',
      'marketing-type': MARKETING_TYPE_LABELS,
      status: PROPERTY_STATUS_LABELS,
      'archive-reason': ARCHIVE_REASON_LABELS,
      'status-archived-with-reason': 'Archiviert · {reason}',
      reference: {
        badge: 'Referenz',
      },
      reservation: {
        badge: {
          RESERVED: 'Reserviert',
          SOLD: 'Verkauft',
        },
        tooltip: {
          'reserved-sales-pipeline': 'Reserviert über die Verkaufs-Pipeline',
          'reserved-by-hand': 'Von Hand reserviert',
          'reserved-both': 'Von Hand und über die Verkaufs-Pipeline reserviert',
          'sold-sales-pipeline': 'Verkauft über die Verkaufs-Pipeline',
        },
      },
      // IMM-788.1 — one shared VAT namespace: the treatment labels, the "zzgl.
      // USt" suffix and the net-price info note appear in three sections and
      // three dialogs (D10). One place to change the copy.
      vat: {
        treatment: VAT_TREATMENT_LABELS,
        'net-plus-vat-suffix': 'zzgl. USt',
        'net-plus-vat-note': 'Alle Preise verstehen sich zzgl. USt.',
        'treatment-placeholder': 'Bitte wählen',
        'per-sqm-unit': '€/m²',
      },
      detail: {
        'id-prefix': 'ID',
        capture: {
          start: 'Objektaufnahme starten',
          continue: 'Aufnahme fortsetzen',
          finish: 'Aufnahme abschließen',
          'completed-title': 'Objektaufnahme vom {date} ({viewingKind})',
          'open-line': {
            both: '{fields, plural, one {# Angabe} other {# Angaben}} und {photos, plural, one {# Foto} other {# Fotos}} offen',
            fields: '{fields, plural, one {# Angabe} other {# Angaben}} offen',
            photos: '{photos, plural, one {# Foto} other {# Fotos}} offen',
          },
          confirm: {
            text: {
              both: 'Es fehlen noch {fields, plural, one {# Pflichtangabe} other {# Pflichtangaben}} und {photos, plural, one {# Pflichtfoto} other {# Pflichtfotos}}. Trotzdem abschließen?',
              fields:
                'Es fehlen noch {fields, plural, one {# Pflichtangabe} other {# Pflichtangaben}}. Trotzdem abschließen?',
              photos:
                'Es fehlen noch {photos, plural, one {# Pflichtfoto} other {# Pflichtfotos}}. Trotzdem abschließen?',
            },
            complete: 'Abschließen',
            continue: 'Weiter erfassen',
          },
          title: 'Objektaufnahme',
          offline: 'Keine Verbindung. Eingaben ab jetzt werden nicht gespeichert.',
          'change-viewing-kind-label': 'Art der Besichtigung ändern',
          fields: {
            heading: 'Fehlt noch für die Bewertung 1b',
            known: 'Bereits bekannt · {count}',
            need: { REQUIRED: 'Pflicht', RECOMMENDED: 'Empfohlen' },
            'not-determinable': 'nicht feststellbar',
            'not-applicable': 'trifft nicht zu',
            'add-unit': 'Weitere Einheit hinzufügen',
            'add-occurrence': '{occurrence} hinzufügen',
            'select-placeholder': 'Bitte wählen',
            boolean: { true: 'Ja', false: 'Nein' },
            groups: FACT_KEY_GROUP_LABELS,
          },
          photos: {
            heading: 'Fotos',
            required: 'Pflicht',
            conditional: 'Empfohlen',
            'taken-of-minimum': '{taken} von {minimum}',
            'take-label': 'Foto aufnehmen',
            'load-error': 'Die Fotos konnten nicht geladen werden.',
            errors: { type: 'Nur JPG, PNG und WebP sind erlaubt' },
          },
          'viewing-kind': {
            heading: 'Wie besichtigst du das Objekt?',
            options: {
              INTERIOR_AND_EXTERIOR: 'Innen- und Außenbesichtigung',
              EXTERIOR_ONLY: 'nur Außenbesichtigung',
              PARTIAL: 'Teilbesichtigung',
              VIDEO: 'Videobesichtigung',
            },
          },
        },
        'immoos-iq': {
          load: 'Daten von immoOS IQ laden',
          loading: 'Daten werden geladen …',
          'last-loaded': 'Zuletzt geladen am {date}',
          'address-incomplete': 'Für den Abruf braucht das Objekt eine vollständige Adresse.',
          'no-data': 'Für diese Adresse liefert immoOS IQ keine Daten.',
          'status-failed': 'Der Stand des Abrufs konnte nicht geladen werden.',
          'status-retry': 'Erneut versuchen',
        },
        'header-price-per-sqm-suffix': ' · {amount} / m²',
        'archived-by-deal-loss': {
          text: 'Archiviert, weil der Deal am {date} verloren ging.',
          'open-deal': 'Deal öffnen',
        },
        'header-actions': 'Aktionen',
        'edit-address-action': 'Anschrift bearbeiten',
        reference: {
          yes: 'Ja',
          no: 'Nein',
          title: 'Referenz',
          'toggle-label': 'Als Referenzobjekt markieren',
          'toggle-helper':
            'Markiert diesen erfolgreichen Verkauf als Referenz – z. B. für Präsentationen und Akquise-Unterlagen.',
          'homepage-label': 'Auf Homepage veröffentlichen',
          'homepage-helper':
            'Erlaubt, dieses Objekt als Referenz auf Ihrer Homepage anzuzeigen. Die Veröffentlichung erfolgt über eine spätere Anbindung.',
          'homepage-disabled-hint': 'Zuerst als Referenzobjekt markieren.',
          'save-error': 'Änderung konnte nicht gespeichert werden.',
        },
        tabs: {
          overview: 'Übersicht',
          prospects: 'Kaufinteressenten',
          media: 'Medien',
          marketing: 'Vermarktung',
          documents: 'Dokumente',
          activity: 'Aktivität',
          property: 'Objekt',
          contacts: 'Verknüpfungen',
          expose: 'Exposé',
          emails: 'E-Mails',
          calls: 'Anrufe',
          team: 'Team',
          notes: 'Notizen',
          appointments: 'Termine',
        },
        identity: {
          'title-label': 'Titel',
          'object-type-label': 'Immobilientyp',
          'fine-type-label': 'Feintyp',
          'marketing-type-label': 'Vermarktungsart',
          'object-number-label': 'Objekt-Nr.',
          'reference-number-label': 'Exposé-Nr.',
          'available-from-label': 'Verfügbar ab',
          'edit-cta': 'Bearbeiten',
        },
        // IMM-788.6 — the six facts a Gewerbe enquiry opens with, rendered above
        // the sections on a COMMERCIAL detail page (Decisions 9–11).
        'fact-review': {
          title: 'Zu prüfen ({count})',
          heading: 'Zu prüfen',
          columns: {
            field: 'Merkmal',
            value: 'Wert',
            occurrence: 'Vorkommen',
            origin: 'Quelle',
            evidence: 'Beleg',
            recorded: 'Stand',
          },
          confirm: 'Bestätigen',
          'confirm-selected': 'Ausgewählte bestätigen',
          empty: 'Alles geprüft.',
          filter: {
            all: 'Alle',
            group: 'Gruppe',
            origin: 'Quelle',
          },
          'select-row': 'Zeile auswählen',
          'select-visible': 'Alle sichtbaren Zeilen auswählen',
          'sort-label': 'Nach {column} sortieren',
          origin: {
            OWNER_STATEMENT: 'Eigentümerangabe',
            EXTERNAL: 'Extern',
            ON_SITE: 'Vor-Ort-Aufnahme',
            DOCUMENT: 'Dokument',
            PHOTO: 'Foto',
            MEMBER: 'Mitglied',
          },
          status: {
            ASSUMED: 'angenommen',
            CONTRADICTED: 'widersprüchlich',
            UNKNOWN: 'unbekannt',
            NOT_APPLICABLE: 'nicht anwendbar',
          },
          group: FACT_KEY_GROUP_LABELS,
          'enum-values': DOSSIER_FACT_ENUM_LABELS,
          boolean: { true: 'Ja', false: 'Nein' },
          'structured-values': {
            parcel: '{number} (Flur {flur})',
            'land-value-zone': 'Zone {zone}',
            'land-value': '{amount} €/m²',
          },
          evidence: {
            page: 'S. {page}',
            'document-deleted': 'Dokument gelöscht',
            external: '{source}, {date}',
          },
          replace: {
            title: 'Objektfelder ersetzen?',
            line: '{field}: {old} wird durch {new} ersetzt.',
            confirm: 'Ersetzen und bestätigen',
            cancel: 'Abbrechen',
          },
          'not-taken-over': 'Bestätigt, aber nicht ins Objekt übernommen:',
          'dismiss-notice': 'Hinweis schließen',
          'load-failed': 'Die Werte zum Prüfen konnten nicht geladen werden.',
          retry: 'Erneut versuchen',
          'confirm-failed': 'Das Bestätigen hat nicht geklappt. Versuch es noch einmal.',
          marker: {
            'ai-detected': 'KI erkannt',
            'member-confirmed': 'Makler bestätigt',
            confirm: 'Bestätigen',
            withdraw: 'Bestätigung zurücknehmen',
            'field-prefix': '{label}: {value}',
            'withdraw-failed': 'Das Zurücknehmen hat nicht geklappt. Versuch es noch einmal.',
          },
        },
        'commercial-headline': {
          'subtype-label': 'Art',
          'usable-area-label': 'Nutzfläche',
          'price-per-sqm-label': 'Miete pro m²/Monat',
          'purchase-price-label': 'Kaufpreis',
          'vat-label': 'Umsatzsteuer',
          'vat-with-rate': 'zzgl. {rate} %',
          'available-from-label': 'Verfügbar ab',
          'min-divisible-area-label': 'teilbar ab',
        },
        address: {
          // Composites — each covers two inputs and has no 1:1 edit counterpart,
          // so they keep their own declaration and are NOT shared (IMM-614.2).
          'street-label': 'Straße & Nr.',
          'postal-city-label': 'PLZ & Ort',
          'district-label': ADDRESS_FIELD_LABELS.district,
          'city-label': ADDRESS_FIELD_LABELS.city,
          'country-label': ADDRESS_FIELD_LABELS.country,
          'hide-address-label': 'Adresse verbergen',
          yes: 'Ja',
          no: 'Nein',
          'edit-cta': 'Bearbeiten',
        },
        stats: {
          'days-on-market-label': 'Tage auf Markt',
          'inquiries-label': 'Anfragen',
          'appointments-label': 'Termine',
        },
        assigned: {
          'reassign-cta': 'Neu zuweisen',
        },
        // Die schreibgeschützte Bewertung am Objekt (IMM-407.6, AC 4) — das Objekt
        // zeigt die Bewertung an, erzeugt sie aber nie (keine Bearbeitungsaktion).
        valuation: {
          'price-range-label': 'Preisspanne',
          'recommended-start-price-label': 'Empfohlener Startpreis',
          'valuation-date-label': 'Bewertungsdatum',
          'report-hint': 'Das vollständige Wertgutachten liegt in den Dokumenten des Objekts.',
          'report-hint-agent': 'Die vollständige Bewertung liegt in den Dokumenten des Objekts.',
          'report-documents-link': 'Zu den Dokumenten',
        },
        'reassign-dialog': {
          title: 'Objekt neu zuweisen',
          'member-label': 'Zuständiges Teammitglied',
          cancel: 'Abbrechen',
          submit: 'Zuweisen',
          submitting: 'Wird zugewiesen…',
          success: 'Objekt neu zugewiesen',
          'not-found-error': 'Objekt nicht gefunden. Bitte lade die Seite neu.',
          'generic-error': 'Die Zuweisung konnte nicht gespeichert werden. Bitte versuche es erneut.',
        },
        'delete-dialog': {
          title: 'Objekt löschen?',
          intro:
            '„{title}" wird aus allen Listen entfernt, auch aus dem Archiv. Diese Aktion kann nur durch den Support rückgängig gemacht werden.',
          cancel: 'Abbrechen',
          submit: 'Löschen',
          submitting: 'Wird gelöscht…',
          success: 'Objekt gelöscht',
          'not-found-error': 'Objekt nicht gefunden. Bitte lade die Seite neu.',
          'generic-error': 'Das Objekt konnte nicht gelöscht werden. Bitte versuche es erneut.',
        },
        'archive-dialog': {
          title: 'Objekt archivieren',
          'edit-title': 'Archivierungsgrund ändern',
          intro:
            '„{title}" wird archiviert und verschwindet aus den aktiven Listen. Du kannst die Archivierung jederzeit wieder aufheben.',
          'reason-label': 'Warum wird das Objekt archiviert?',
          'reason-placeholder': 'Grund wählen …',
          'note-label-optional': 'Notiz (optional)',
          'note-label-required': 'Notiz',
          'note-placeholder': 'z. B. an Familie Müller verkauft',
          counter: '{count} / {max}',
          'history-hint': 'Die Änderung wird in der Status-Historie festgehalten.',
          submit: 'Archivieren',
          'edit-submit': 'Speichern',
          submitting: 'Wird gespeichert…',
          cancel: 'Abbrechen',
          'success-toast': 'Objekt archiviert.',
          'success-toast-with-deal-loss': 'Objekt archiviert und Deal als verloren markiert.',
          'open-deal-note-one': 'Dieses Objekt hat einen offenen Deal. Beim Archivieren wird er als verloren markiert.',
          'open-deal-note-many':
            'Dieses Objekt hat {count} offene Deals. Beim Archivieren werden sie als verloren markiert.',
          'edit-success-toast': 'Archivierungsgrund aktualisiert.',
          'validation-no-reason': 'Bitte wähle einen Archivierungsgrund.',
          'validation-note-required': 'Bitte gib bei „Sonstiges“ eine kurze Notiz an.',
          'generic-error': 'Die Aktion konnte nicht ausgeführt werden. Bitte versuche es erneut.',
        },
        'unarchive-dialog': {
          title: 'Archivierung aufheben',
          intro:
            'Das Objekt wird aus dem Archiv geholt und erhält den Status „Pausiert“. Archivierungsgrund und Notiz werden gelöscht. Beim erneuten Archivieren müssen sie neu gewählt werden.',
          'intro-with-deal':
            'Das Objekt erhält wieder den Status „{status}“, und der Deal wird wieder geöffnet. Archivierungsgrund und Notiz werden gelöscht.',
          'success-with-deal': 'Archivierung aufgehoben und Deal wieder geöffnet.',
          'current-reason-label': 'Aktueller Archivierungsgrund',
          'will-be-deleted': 'wird gelöscht',
          cancel: 'Abbrechen',
          submit: 'Archivierung aufheben',
          submitting: 'Wird aufgehoben…',
          'generic-error': 'Die Archivierung konnte nicht aufgehoben werden. Bitte versuche es erneut.',
        },
        prospects: {
          title: 'Kaufinteressenten',
          'open-in-pipeline': 'In der Pipeline öffnen →',
          'reserved-for': 'Reserviert für {name} — {price}',
          'reserved-for-no-price': 'Reserviert für {name}',
          'without-process': 'Ohne Kaufprozess · {count}',
          'add-to-process': 'In Kaufprozess aufnehmen',
          empty: 'Noch keine Kaufinteressenten für dieses Objekt.',
          'load-error': 'Die Kaufinteressenten konnten nicht geladen werden.',
          loading: 'Kaufinteressenten werden geladen…',
          retry: 'Erneut versuchen',
        },
        buyers: {
          title: 'Kaufinteressenten ({count})',
          'title-unknown': 'Kaufinteressenten',
          'without-process': 'Ohne Kaufprozess',
        },
        deals: {
          title: '{count, plural, one {Deal} other {Deals}}',
          empty: 'Kein Deal zu diesem Objekt.',
          'load-error': 'Die Deals konnten nicht geladen werden.',
          retry: 'Erneut versuchen',
        },
        'deal-link': {
          open: 'Deal: {phase} →',
          other: 'Deal: {phase} · {status} →',
        },
        auftrag: {
          'open-deal': 'Deal öffnen',
          'last-deal': 'Letzter Deal',
          'load-error': 'Die Auftragsdaten konnten nicht geladen werden.',
          retry: 'Erneut versuchen',
        },
        'linked-contacts': {
          'link-cta': 'Kontakt verknüpfen',
          empty: 'Noch keine Kontakte verknüpft.',
        },
        actions: {
          trigger: 'Aktionen',
          edit: 'Bearbeiten',
          'copy-link': 'Link kopieren',
          'link-copied': 'Link kopiert',
          activate: 'Vermarktung starten',
          'set-inactive': 'Vermarktung pausieren',
          archive: 'Archivieren',
          reactivate: 'Archivierung aufheben',
          'change-archive-reason': 'Archivierungsgrund ändern',
          'mark-reserved': 'Als reserviert markieren',
          'unmark-reserved': 'Reservierung aufheben',
          'reservation-marked': 'Objekt als reserviert markiert.',
          'reservation-unmarked': 'Reservierung aufgehoben.',
          'stage-in-acquisition': 'Auf „In Akquise“ setzen',
          'revert-to-draft': 'Auf „Entwurf“ setzen',
          'change-status': '{status}, Status ändern',
          assign: 'Zuweisen',
          delete: 'Löschen',
          'denial-title': 'Aktion nicht möglich',
          'cannot-activate-intro':
            'Die Vermarktung kann nicht gestartet werden. Bitte ergänze die folgenden Felder auf der Objekt-Detailseite:',
          'invalid-transition':
            'Dieser Statuswechsel ist nicht möglich. Bitte lade die Seite neu und versuche es erneut.',
          'denial-generic': 'Die Aktion konnte nicht ausgeführt werden. Bitte versuche es erneut.',
          'archive-denied-open-deal':
            'Du kannst dieses Objekt nicht archivieren, weil du den offenen Deal nicht als verloren markieren darfst.',
          'denial-close': 'Verstanden',
          'denial-close-label': 'Schließen',
          'publish-fields': {
            STREET: 'Straße',
            HOUSE_NUMBER: 'Hausnummer',
            POSTAL_CODE: 'PLZ',
            CITY: 'Ort',
            PURCHASE_PRICE: 'Kaufpreis',
            BASE_RENT: 'Kaltmiete',
            LIVING_AREA: 'Wohnfläche',
            USABLE_AREA: 'Nutzfläche',
            TOTAL_AREA: 'Gesamtfläche',
            PLOT_AREA: 'Grundstücksfläche',
            PARKING_SPACE_TYPE: 'Stellplatzart',
            VAT_TREATMENT: 'Umsatzsteuer-Angabe',
            AVAILABLE_FROM: 'Verfügbar ab',
            ANCILLARY_COSTS_TYPE: 'Nebenkosten-Art',
          },
          'publish-field-hints': {
            BASE_RENT: 'Eine Vermietung braucht eine Kaltmiete.',
            AVAILABLE_FROM: 'Gib an, ab wann das Objekt verfügbar ist.',
            ANCILLARY_COSTS_TYPE: 'Gib an, ob die Nebenkosten eine Vorauszahlung oder eine Pauschale sind.',
            VAT_TREATMENT: 'Gib an, wie die Umsatzsteuer auf den Preis behandelt wird.',
          },
        },
        contacts: {
          'section-title': 'Kontakte',
          loading: 'Kontakte werden geladen…',
          empty: 'Noch keine Kontakte verknüpft.',
          'load-error': 'Kontakte konnten nicht geladen werden.',
          'load-retry': 'Erneut versuchen',
          'open-in-new-tab': 'In neuem Tab öffnen',
          'add-cta': 'Kontakt verknüpfen',
          'row-actions': 'Aktionen',
          'open-contact': 'Kontakt öffnen',
          'send-email': 'E-Mail senden',
          'send-message': 'Nachricht senden',
          'not-implemented': 'Diese Aktion ist noch nicht verfügbar.',
          role: PROPERTY_CONTACT_ROLE_LABELS,
          groups: {
            owner: 'Eigentümer',
            'prospects-sale': 'Kaufinteressenten',
            'prospects-rental': 'Mietinteressenten',
            others: 'Weitere Kontakte',
          },
          'owner-empty': 'Noch kein Eigentümer verknüpft.',
          'owner-empty-cta': 'Eigentümer verknüpfen',
          'edit-link': 'Verknüpfung bearbeiten',
          'unlink-link': 'Verknüpfung lösen',
          'link-dialog': {
            title: 'Kontakt verknüpfen',
            'contact-label': 'Kontakt',
            'create-contact-cta': 'Neuen Kontakt anlegen',
            'role-label': 'Rolle',
            'role-placeholder': 'Rolle wählen …',
            'note-label': 'Notiz',
            'note-hint': 'Max. 500 Zeichen',
            'submit-cta': 'Verknüpfen',
            'submit-loading': 'Wird verknüpft…',
            'cancel-cta': 'Abbrechen',
            errors: {
              'contact-required': 'Bitte wähle einen Kontakt aus.',
              'role-required': 'Bitte wähle eine Rolle aus.',
              'not-found': 'Kontakt oder Immobilie wurde nicht gefunden.',
              'already-linked':
                'Diese Verknüpfung besteht bereits. Du kannst die Notiz der bestehenden Verknüpfung bearbeiten.',
              'note-too-long': 'Notiz darf höchstens 500 Zeichen lang sein.',
              'role-marketing-mismatch': 'Diese Rolle passt nicht zur Vermarktungsart der Immobilie.',
              forbidden: 'Du bist für diese Aktion nicht berechtigt.',
              generic: 'Verknüpfung konnte nicht angelegt werden. Bitte versuche es erneut.',
            },
          },
          'edit-dialog': {
            title: 'Verknüpfung bearbeiten',
            'role-label': 'Rolle',
            'note-label': 'Notiz',
            'note-hint': 'Max. 500 Zeichen',
            'submit-cta': 'Speichern',
            'submit-loading': 'Wird gespeichert…',
            'cancel-cta': 'Abbrechen',
            errors: {
              'already-linked':
                'Diese Verknüpfung besteht bereits. Du kannst die Notiz der bestehenden Verknüpfung bearbeiten.',
              'note-too-long': 'Notiz darf höchstens 500 Zeichen lang sein.',
              'role-marketing-mismatch': 'Diese Rolle passt nicht zur Vermarktungsart der Immobilie.',
              'not-found': 'Diese Verknüpfung wurde nicht gefunden.',
              forbidden: 'Du bist für diese Aktion nicht berechtigt.',
              generic: 'Änderung konnte nicht gespeichert werden. Bitte versuche es erneut.',
            },
          },
          'unlink-dialog': {
            title: 'Verknüpfung lösen',
            body: 'Möchtest du die Verknüpfung mit „{name}“ wirklich lösen? Der Kontakt und die Immobilie bleiben erhalten.',
            'confirm-cta': 'Verknüpfung lösen',
            'confirm-loading': 'Wird gelöst…',
            'cancel-cta': 'Abbrechen',
            'error-forbidden': 'Du bist für diese Aktion nicht berechtigt.',
            error: 'Verknüpfung konnte nicht gelöst werden. Bitte versuche es erneut.',
          },
        },
        sections: {
          'key-facts': 'Eckdaten',
          pipeline: 'Pipeline',
          activity: 'Aktivität',
          tasks: 'Aufgaben',
          details: 'Details',
          building: 'Gebäude',
          energy: 'Energie',
          'investment-yield': 'Ertrag',
          'plot-details': 'Grundstück',
          amenities: 'Ausstattung',
          'pricing-sale': 'Preis (Kauf)',
          'pricing-rental': 'Preis (Miete)',
          'rental-terms': 'Mietkonditionen',
          'additional-costs': 'Nebenkosten',
          areas: 'Flächen',
          identity: 'Identität',
          address: 'Adresse',
          valuation: 'Bewertung',
          assigned: 'Zugewiesen',
          'linked-contacts': 'Verknüpfte Kontakte',
          owner: 'Eigentümer',
          'linked-deals': 'Verknüpfte Deals',
          categories: 'Kategorien',
        },
        'owner-empty': 'Noch kein Eigentümer verknüpft.',
        building: {
          'year-built-label': 'Baujahr',
          'last-renovated-label': 'Letzte Sanierung',
          'condition-label': 'Zustand',
          'interior-quality-label': 'Ausstattungsqualität',
          'construction-phase-label': 'Bauphase',
          'edit-cta': 'Bearbeiten',
          condition: CONDITION_LABELS,
          'interior-quality': INTERIOR_QUALITY_LABELS,
          'construction-phase': CONSTRUCTION_PHASE_LABELS,
        },
        'pricing-sale': {
          title: 'Preis (Kauf)',
          'purchase-price-label': 'Kaufpreis',
          'price-per-sqm-label': 'Preis pro m²',
          'price-on-inquiry-label': 'Preis auf Anfrage',
          'has-commission-label': 'Provision aktiv',
          'commission-label': 'Provision',
          'commission-note-label': 'Provisionshinweis',
          'commission-basis-label': 'Angabe als',
          'commission-rate-label': 'Satz (netto)',
          'commission-amount-label': 'Betrag (netto)',
          'commission-statement-label': 'Anzeigetext',
          'commission-legacy-label': 'Bisherige Angabe',
          'net-purchase-price-label': 'Nettokaufpreis',
          'vat-treatment-label': 'USt-Behandlung',
          'vat-rate-label': 'USt-Satz',
          'commission-includes-vat-label': 'Provision zzgl. USt',
          yes: 'Ja',
          no: 'Nein',
          'edit-cta': 'Bearbeiten',
        },
        'pricing-rental': {
          title: 'Preis (Miete)',
          'tenant-free-label': 'Provision',
          'has-commission-label': 'Provision',
          'commission-label': 'Provisionshöhe',
          'commission-note-label': 'Provisionshinweis',
          'base-rent-label': 'Kaltmiete',
          'price-per-sqm-label': 'Miete pro m²/Monat',
          'total-rent-label': 'Gesamtmiete',
          'ancillary-costs-label': 'Nebenkosten',
          'ancillary-costs-type-label': 'Nebenkosten-Art',
          'heating-costs-label': 'Heizkosten',
          'heating-costs-included-value': 'in den Nebenkosten enthalten',
          'deposit-label': 'Kaution',
          'deposit-months-value': '{count, plural, one {# Monatsmiete} other {# Monatsmieten}}',
          'legacy-deposit-label': 'Kaution (Altbestand)',
          'parking-rent-label': 'Stellplatzmiete',
          'ancillary-costs-type': ANCILLARY_COSTS_TYPE_LABELS,
          'vat-treatment-label': 'USt-Behandlung',
          'vat-rate-label': 'USt-Satz',
          'derivation-hint': '{rate} × {area} m² = {total} Kaltmiete',
          'compensation-payment-label': 'Abstandszahlung',
          'rent-free-months-label': 'Mietfreie Zeit',
          'rent-free-months-value': '{count, plural, one {# Monat} other {# Monate}}',
          'fit-out-contribution-label': 'Baukostenzuschuss',
          'renewal-option-label': 'Verlängerungsoption',
          'price-on-inquiry-label': 'Preis auf Anfrage',
          yes: 'Ja',
          no: 'Nein',
          'edit-cta': 'Bearbeiten',
        },
        'rental-terms': {
          title: 'Mietkonditionen',
          'furnishing-label': 'Möblierung',
          'lease-term-label': 'Mietdauer',
          'fixed-term-until-label': 'befristet bis',
          'minimum-term-label': 'Mindestmietdauer',
          'minimum-term-value': '{count, plural, one {# Monat} other {# Monate}}',
          'rent-type-label': 'Mietart',
          'subletting-label': 'Untervermietung',
          furnishing: FURNISHING_LABELS,
          'lease-term': LEASE_TERM_LABELS,
          'rent-type': RENT_TYPE_LABELS,
          subletting: SUBLETTING_LABELS,
          'edit-cta': 'Bearbeiten',
        },
        'rental-headline': {
          title: 'Mietübersicht',
          'available-from-label': 'Verfügbar ab',
          'lease-term-fixed-until-value': 'befristet bis {date}',
        },
        'additional-costs': {
          title: 'Nebenkosten',
          'edit-cta': 'Bearbeiten',
          'per-sqm-suffix': ' · {amount} / m²/Monat',
          type: ADDITIONAL_COST_TYPE_LABELS,
        },
        energy: {
          'certificate-basis-label': 'Energieausweis-Typ',
          'certificate-year-label': 'Energieausweis-Jahrgang',
          'energy-class-label': 'Energieeffizienzklasse',
          'energy-value-label': 'Energiekennwert',
          'energy-value-unit': 'kWh/(m²·a)',
          'primary-energy-value-label': 'Primärenergiebedarf',
          'includes-warm-water-label': 'Warmwasser im Verbrauch enthalten',
          'certificate-issued-at-label': 'Ausstelldatum',
          'certificate-valid-until-label': 'Gültig bis',
          'heating-type-label': 'Heizungsart',
          'firing-type-label': 'Befeuerungsart',
          'edit-cta': 'Bearbeiten',
          'boolean-yes': 'Ja',
          'boolean-no': 'Nein',
          'certificate-basis': ENERGY_CERTIFICATE_BASIS_LABELS,
          'certificate-year': ENERGY_CERTIFICATE_YEAR_LABELS,
          'energy-class': ENERGY_CLASS_LABELS,
          'heating-type': HEATING_TYPE_LABELS,
          'firing-type': FIRING_TYPE_LABELS,
        },
        'investment-yield': {
          'is-rented-label': 'Vermietet',
          'rental-income-label': 'Mieteinnahmen',
          'rental-income-target-label': 'Soll-Mieteinnahmen',
          'rental-income-actual-label': 'Ist-Mieteinnahmen',
          'price-multiplier-label': 'Faktor',
          'number-of-apartments-label': 'Anzahl Wohnungen',
          'number-of-commercial-units-label': 'Anzahl Gewerbeeinheiten',
          'occupancy-rate-label': 'Vermietungsquote',
          'number-of-vacancies-label': 'Anzahl Leerstände',
          'tenant-structure-label': 'Mieterstruktur',
          'walt-label': 'WALT',
          'walt-unit': 'Jahre',
          'occupancy-rate-unit': '%',
          'currency-unit': '€',
          'edit-cta': 'Bearbeiten',
          'boolean-yes': 'Ja',
          'boolean-no': 'Nein',
        },
        'plot-details': {
          'ground-area-ratio-label': 'GRZ (Grundflächenzahl)',
          'floor-area-ratio-label': 'GFZ (Geschossflächenzahl)',
          'building-mass-ratio-label': 'BMZ (Baumassenzahl)',
          'has-building-permission-label': 'Baugenehmigung liegt vor',
          'short-term-buildable-label': 'Kurzfristig bebaubar',
          'requires-demolition-label': 'Abriss erforderlich',
          'site-development-type-label': 'Erschließungsart',
          'site-constructible-type-label': 'Bebauungsart',
          'edit-cta': 'Bearbeiten',
          'boolean-yes': 'Ja',
          'boolean-no': 'Nein',
          'site-development-type': SITE_DEVELOPMENT_TYPE_LABELS,
          'site-constructible-type': SITE_CONSTRUCTIBLE_TYPE_LABELS,
        },
        amenities: {
          'feature-column': 'Merkmal',
          'value-column': 'Wert',
          'edit-cta': 'Bearbeiten',
          'group-residential': 'Wohnen',
          'group-commercial': 'Gewerbe',
          'empty-state': 'Noch keine Ausstattungsmerkmale erfasst.',
          residential: RESIDENTIAL_AMENITY_LABELS,
          commercial: COMMERCIAL_AMENITY_LABELS,
        },
        areas: {
          'code-column-header': 'Fläche',
          'value-column-header': 'Wert',
          'edit-cta': 'Bearbeiten',
          'empty-state': 'Noch keine Flächen erfasst.',
          'unit-sqm': 'm²',
          'unit-cbm': 'm³',
          'use-split-remainder-label': 'weitere Fläche',
          'use-split-exceeded-label': 'Summe der Teilflächen',
          'use-split-exceeded-value': '{sum} m² von {usableArea} m²',
          code: AREA_CODE_LABELS,
        },
        cards: {
          'status-label': 'Status',
          'status-since-day': 'seit 1 Tag',
          'status-since-days': 'seit {days} Tagen',
          'asking-price-label': 'Asking-Preis',
          'asking-price-per-sqm': '€ {amount} / m²',
          'expected-revenue-label': 'Erwarteter Erlös',
          'match-score-label': 'Match-Score',
          'last-activity-label': 'Letzte Aktivität',
          'last-activity-empty': 'Noch keine Aktivität',
          'broker-team-label': 'Makler-Team',
          'member-singular': '1 Mitglied',
          'member-plural': '{count} Mitglieder',
        },
        pipeline: {
          'first-contact': 'Erstkontakt',
          viewing: 'Besichtigung',
          offer: 'Angebot',
          negotiation: 'Verhandlung',
          notary: 'Notar',
        },
        details: {
          'type-label': 'Objekt-Typ',
          'marketing-type-label': 'Vermarktungsart',
          'location-label': 'Lage',
          'living-area-label': 'Wohnfläche',
          'rooms-label': 'Zimmer',
          'year-built-label': 'Baujahr',
          'energy-label': 'Energie',
          'asking-label': 'Asking',
          'expected-label': 'Erwartet',
          'show-all-fields': 'Alle Felder ansehen',
        },
        timeline: {
          'add-cta': 'Aktivität hinzufügen',
          save: 'Speichern',
          saving: 'Speichern …',
          cancel: 'Abbrechen',
          'content-label': 'Inhalt',
          'content-counter': '{count, number} / {max, number}',
          'content-placeholder': 'Was ist passiert?',
          'occurred-at-label': 'Stattgefunden am',
          'now-cta': 'Jetzt',
          'occurred-at-time-label': 'Uhrzeit',
          'type-label': 'Typ',
          'type-note': 'Notiz',
          'type-appointment': 'Termin',
          'appointment-type-label': 'Termin-Art',
          'appointment-type-placeholder': 'Bitte wählen',
          'appointment-type-valuation': 'Bewertungstermin',
          'appointment-type-on-site': 'Vor-Ort-Termin',
          'appointment-type-property-survey': 'Objektaufnahme',
          'appointment-type-contract-signing': 'Vertragstermin',
          'appointment-type-other': 'Sonstiger Termin',
          'appointment-type-viewing-on-site': 'Besichtigung vor Ort',
          'appointment-type-viewing-virtual': 'Besichtigung virtuell',
          'appointment-type-consultation': 'Beratungsgespräch',
          'appointment-type-financing-consultation': 'Finanzierungsgespräch',
          'appointment-type-negotiation': 'Verhandlungsgespräch',
          'appointment-type-notary': 'Notartermin',
          'appointment-type-handover': 'Übergabe',
          'error-no-appointment-type': 'Bitte wähle eine Termin-Art.',
          'type-call': 'Anruf',
          'type-sms': 'SMS',
          'type-whatsapp': 'WhatsApp',
          'type-fax': 'Fax',
          'type-letter': 'Brief',
          'type-email': 'E-Mail',
          'direction-label': 'Richtung',
          'direction-inbound': 'Eingehend',
          'direction-outbound': 'Ausgehend',
          'subject-label': 'Betreff',
          'entry-note': 'Notiz',
          'entry-call-inbound': 'Eingehender Anruf',
          'entry-call-outbound': 'Ausgehender Anruf',
          'entry-sms-inbound': 'Eingehende SMS',
          'entry-sms-outbound': 'Ausgehende SMS',
          'entry-whatsapp-inbound': 'Eingehende WhatsApp',
          'entry-whatsapp-outbound': 'Ausgehende WhatsApp',
          'entry-fax-inbound': 'Eingehendes Fax',
          'entry-fax-outbound': 'Ausgehendes Fax',
          'entry-letter-inbound': 'Eingehender Brief',
          'entry-letter-outbound': 'Ausgehender Brief',
          'recorder-unknown': 'Unbekanntes Mitglied',
          'group-today': 'Heute',
          'group-yesterday': 'Gestern',
          'relative-just-now': 'Gerade eben',
          'relative-minutes': 'Vor {count} Minuten',
          'relative-hours': 'Vor {count} Stunden',
          'relative-yesterday': 'Gestern',
          'empty-heading': 'Noch keine Aktivitäten',
          'empty-body': 'Hier erscheint alles, was mit diesem Objekt passiert — Anrufe, Notizen, Nachrichten und mehr.',
          'empty-cta': 'Aktivität hinzufügen',
          'error-empty-content': 'Bitte gib einen Inhalt ein.',
          'error-content-too-long': 'Eine Aktivität darf höchstens 10.000 Zeichen lang sein.',
          'error-no-direction': 'Bitte gib die Richtung an (eingehend oder ausgehend).',
          'error-subject-too-long': 'Der Betreff darf höchstens 200 Zeichen lang sein.',
          'error-future': 'Eine Aktivität kann nicht in der Zukunft liegen.',
          'error-occurred-at-incomplete': 'Bitte gib Datum und Uhrzeit gemeinsam an.',
          'save-error': 'Die Aktivität konnte nicht gespeichert werden.',
          loading: 'Aktivitäten werden geladen …',
          'load-error': 'Aktivitäten konnten nicht geladen werden.',
          'load-retry': 'Erneut versuchen',
          edited: 'bearbeitet',
          'edit-cta': 'Bearbeiten',
          'delete-cta': 'Löschen',
          'delete-dialog-title': 'Aktivität löschen?',
          'delete-dialog-body': 'Diese Aktion kann nicht rückgängig gemacht werden.',
          'delete-confirm': 'Löschen',
          'delete-cancel': 'Abbrechen',
          'delete-error': 'Die Aktivität konnte nicht gelöscht werden.',
          'update-error': 'Die Aktivität konnte nicht gespeichert werden.',
          'contact-picker-label': 'Kontakt',
          'no-contacts': 'Noch kein Kontakt mit diesem Objekt verknüpft. Bitte zuerst einen Kontakt verknüpfen.',
          'outcome-label': 'Ergebnis',
          'outcome-none': 'Kein Ergebnis',
          'outcome-reached': 'Erreicht',
          'outcome-not-reached': 'Nicht erreicht',
          'outcome-voicemail': 'Mailbox',
          'outcome-callback-agreed': 'Rückruf vereinbart',
          'outcome-email-reached': 'Antwort erhalten',
          'outcome-email-not-reached': 'Keine Antwort',
        },
        'tab-empty': 'Hier landen alle {tab}-Einträge zu diesem Objekt.',
        'tasks-empty': 'Noch keine Aufgaben.',
        'tasks-open': '{count} offen',
        'edit-permission-reason': 'Du bist nicht berechtigt, dieses Objekt zu bearbeiten.',
        'coming-soon': 'Demnächst',
        'add-cta': 'Hinzufügen',
        empty: '—',
        documents: {
          'section-title': 'Dokumente',
          loading: 'Dokumente werden geladen…',
          'load-error': 'Dokumente konnten nicht geladen werden.',
          'load-retry': 'Erneut versuchen',
          'analyses-load-error': 'Der Analysestatus der Dokumente konnte nicht geladen werden.',
          'add-cta': 'Alles hochladen',
          // IMM-638.1 — the page-level drop affordance. Deliberately shorter than
          // the dialog's own `drop-hint`: it appears mid-gesture with the pointer
          // already moving, so it states the outcome and nothing else.
          'drop-overlay': 'Dateien hier ablegen',
          'type-fallback': 'Datei',
          'uploaded-by-valuation-agent': 'Bewertungs-Agent',
          'superseded-at': 'Ersetzt am {date}',
          'uploaded-by-unknown': 'Unbekanntes Mitglied',
          selection: {
            'header-label': 'Alle Dokumente auswählen',
            hint: 'Hake Dokumente an, um sie gesammelt für Käufer freizugeben.',
            count: '{count} ausgewählt',
            clear: 'Auswahl aufheben',
            marked:
              '{count, plural, one {# Dokument für Käufer freigegeben.} other {# Dokumente für Käufer freigegeben.}}',
            unmarked:
              '{count, plural, one {# Dokument nicht mehr für Käufer.} other {# Dokumente nicht mehr für Käufer.}}',
            'not-changed': '{count} nicht geändert.',
            'disabled-personal': 'Persönliche Unterlagen können nicht für Käufer freigegeben werden.',
            'disabled-valuation':
              'Bewertungen, Wertgutachten und Akquisebriefings können nicht für Käufer freigegeben werden.',
            'disabled-right': 'Dir fehlt die Berechtigung, dieses Dokument zu bearbeiten.',
          },
          forBuyers: {
            action: 'Für Käufer freigeben',
            actionMarked: 'Nicht mehr für Käufer',
            badge: 'Für Käufer',
            counter: '{viewedBy} von {activeReleases} angesehen',
            'locked-valuation':
              'Bewertungen, Wertgutachten und Akquisebriefings können nicht für Käufer freigegeben werden.',
          },
          buyerReleases: {
            button: 'Freigaben verwalten',
            title: 'Freigaben verwalten — {title}',
            released: 'Freigegeben am {date}',
            withdrawn: 'Freigabe entzogen am {date}',
            'not-released': 'Noch nicht freigegeben',
            'will-release': 'Wird freigegeben',
            'will-withdraw': 'Wird entzogen',
            'select-all': 'Alle auswählen',
            'deselect-all': 'Alle abwählen',
            cancel: 'Abbrechen',
            save: 'Speichern',
            'confirm-title-both': '{released} freigeben, {withdrawn} entziehen?',
            'confirm-title-release': '{released} freigeben?',
            'confirm-title-withdraw': '{withdrawn} entziehen?',
            'confirm-body': 'Neu freigegebene Interessenten bekommen je eine E-Mail mit ihrem Link.',
            confirm: 'Bestätigen',
            saved: 'Freigaben gespeichert.',
            partial: '{count, plural, one {# Änderung nicht gespeichert.} other {# Änderungen nicht gespeichert.}}',
            'failed-inactive': 'Der Interessent ist nicht mehr aktiv.',
            empty: 'Für dieses Objekt gibt es keine aktiven Kaufinteressenten.',
          },
          columns: {
            name: 'Name',
            category: 'Kategorie',
            size: 'Größe',
            'uploaded-by': 'Hochgeladen von',
            date: 'Datum',
          },
          filters: {
            all: 'Alle',
            uncategorized: 'Unkategorisiert',
          },
          'uncategorized-hint': '{count, plural, one {# Dokument ohne Kategorie} other {# Dokumente ohne Kategorie}}',
          'uncategorized-hint-cta': 'Kategorie zuordnen',
          'classification-run': {
            started: '{count, plural, one {# Dokument wird kategorisiert…} other {# Dokumente werden kategorisiert…}}',
            'already-running': 'Die Kategorisierung läuft bereits.',
            none: 'Keine Dokumente ohne Kategorie.',
            error: 'Die Kategorisierung konnte nicht gestartet werden. Bitte erneut versuchen.',
          },
          'analysis-backlog-hint': '{count, plural, one {# Dokument ohne Analyse} other {# Dokumente ohne Analyse}}',
          'analysis-backlog-hint-cta': 'Jetzt analysieren',
          'analysis-run': {
            confirm: {
              title: '{count, plural, one {# Dokument analysieren?} other {# Dokumente analysieren?}}',
              body: 'Jedes Dokument wird einzeln ausgewertet — das kann ein paar Minuten dauern. Bereits analysierte Dokumente bleiben unverändert.',
              confirm: 'Analyse starten',
              cancel: 'Abbrechen',
            },
            started: '{count, plural, one {# Dokument wird analysiert…} other {# Dokumente werden analysiert…}}',
            'already-running': 'Die Analyse läuft bereits.',
            none: 'Keine Dokumente ohne Analyse.',
            error: 'Die Analyse konnte nicht gestartet werden. Bitte erneut versuchen.',
          },
          'empty-first-time': {
            line: 'Noch keine Dokumente zu diesem Objekt',
            cta: 'Alles hochladen',
          },
          'section-switcher': {
            label: 'Angezeigte Unterlagen',
            all: 'Alle',
          },
          'empty-no-match': {
            line: 'Keine Dokumente in dieser Kategoriegruppe',
            'section-line': 'Keine Dokumente unter „{section}“',
            cta: 'Filter zurücksetzen',
          },
          'category-group': DOCUMENT_CATEGORY_GROUP_LABELS,
          'document-section': DOCUMENT_SECTION_LABELS,
          category: DOCUMENT_CATEGORY_LABELS,
          classification: {
            ...DOCUMENT_CLASSIFICATION_MARKERS,
            promote: {
              action: 'In die Bilder verschieben',
              errors: {
                'gallery-full': 'Die Bilder-Galerie ist voll (max. 100). Bitte zuerst Platz schaffen.',
                forbidden: 'Sie sind nicht berechtigt, dieses Dokument zu verschieben.',
                generic: 'Das Dokument konnte nicht verschoben werden. Bitte erneut versuchen.',
              },
            },
          },
          readiness: {
            title: 'Unterlagen-Check',
            signal: {
              SALE_READY: 'Vollständig',
              ALMOST_COMPLETE: 'Fast vollständig',
              INCOMPLETE: 'Unvollständig',
            },
            counter: '{present} von {required} Pflichtdokumenten vorhanden',
            'missing-count': '{count, plural, one {# Pflichtdokument fehlt} other {# Pflichtdokumente fehlen}}',
            'documents-counter': '{present} von {total} Unterlagen vorhanden',
            'checklist-toggle': 'Checkliste',
            group: {
              MANDATORY: 'Pflicht',
              OPTIONAL: 'Optional',
            },
            'optional-hint': 'Diese Unterlagen blockieren die Vermarktung nicht.',
            // IMM-1361 — a letting files a draft lease and a cost overview under two
            // reused catalogue categories; its checklist names them as the letting does.
            'letting-category': {
              RENTAL_CONTRACT: 'Mietvertragsentwurf',
              UTILITY_STATEMENT: 'Betriebskostenaufstellung',
            },
            'conditional-hint': 'nur wenn zutreffend',
            obligation: {
              MANDATORY: 'Pflicht',
              OPTIONAL: 'Optional',
            },
            status: {
              PRESENT: 'Vorhanden',
              MISSING: 'Fehlt',
              EXPIRING: 'Läuft ab am {date}',
              EXPIRED: 'Abgelaufen seit {date}',
            },
            request: {
              action: 'Beim Eigentümer anfordern',
              bulk: 'Alle fehlenden anfordern',
              chip: 'Vorgemerkt am {date}',
              'disabled-reason': 'Für dieses Objekt ist kein Eigentümer mit E-Mail-Adresse hinterlegt',
              'permission-reason': 'Du bist nicht berechtigt, Unterlagen beim Eigentümer anzufordern.',
              'recipient-fallback': 'den Eigentümer',
              'no-owner-fix':
                'Verknüpfe im Tab „Verknüpfungen“ einen Eigentümer oder ergänze seine E-Mail-Adresse im Kontakt.',
              draft: {
                subject: 'Unterlagen für Ihre Immobilie',
                intro: 'für die weitere Vermarktung Ihrer Immobilie benötige ich noch die folgenden Unterlagen:',
                ask: 'Dürfte ich Sie herzlich bitten, mir diese zukommen zu lassen, sobald es Ihnen möglich ist? Falls Ihnen einzelne Unterlagen nicht vorliegen, geben Sie mir gern kurz Bescheid, dann finden wir gemeinsam eine Lösung.',
                thanks: 'Vielen Dank für Ihre Unterstützung.',
                closing: 'Mit freundlichen Grüßen',
              },
              dialog: {
                'mailbox-block': {
                  NOT_CONNECTED:
                    'Dein Postfach ist nicht verbunden, deshalb kann immoOS keine E-Mail an den Eigentümer öffnen. Verbinde dein Postfach, dann öffnet sich beim Anfordern eine fertige E-Mail an ihn. Bis dahin kannst du die Anforderung hier als Vormerkung speichern.',
                  EXPIRED:
                    'Die Verbindung zu deinem Postfach ist abgelaufen, deshalb kann immoOS keine E-Mail an den Eigentümer öffnen. Verbinde dein Postfach erneut, dann öffnet sich beim Anfordern eine fertige E-Mail an ihn. Bis dahin kannst du die Anforderung hier als Vormerkung speichern.',
                  LOAD_FAILED:
                    'Der Verbindungsstatus deines Postfachs konnte nicht geladen werden, deshalb öffnet sich keine E-Mail. Versuche es gleich noch einmal, oder speichere die Anforderung hier als Vormerkung.',
                  NO_PERMISSION:
                    'Du bist nicht berechtigt, E-Mails zu senden, deshalb kann immoOS keine E-Mail an den Eigentümer öffnen. Bitte wende dich an einen Admin. Bis dahin kannst du die Anforderung hier als Vormerkung speichern.',
                },
                'title-single': 'Beim Eigentümer anfordern',
                'title-bulk': 'Alle fehlenden anfordern',
                'description-single':
                  'Die Anforderung wird als Vormerkung gespeichert. Der Eigentümer wird dabei nicht automatisch benachrichtigt — bitte kontaktiere ihn selbst.',
                'description-bulk':
                  'Die unten aufgeführten Unterlagen werden als Vormerkung gespeichert. Der Eigentümer wird dabei nicht automatisch benachrichtigt — bitte kontaktiere ihn selbst.',
                // IMM-749 — the mock's „Empfänger" block. Not a dispatch claim: the
                // description directly above says nothing is sent automatically, so
                // the address is here precisely so the agent can contact the owner.
                'recipient-label': 'Empfänger',
                'recipient-unknown': 'Kein Eigentümer mit E-Mail-Adresse hinterlegt',
                'categories-label': 'Angeforderte Unterlagen',
                'note-label': 'Notiz zur Anforderung (optional)',
                'note-placeholder': 'z. B. Bräuchte das noch vor dem Fototermin',
                submit: 'Anfordern',
                'submit-loading': 'Wird gespeichert …',
                cancel: 'Abbrechen',
              },
              toast: {
                success: 'Anforderung für {name} vorgemerkt.',
                'sent-not-recorded': 'Die E-Mail wurde gesendet, die Anforderung konnte aber nicht vorgemerkt werden.',
                forbidden: 'Du bist für diese Aktion nicht berechtigt.',
                'no-owner-email': 'Für dieses Objekt ist kein Eigentümer mit E-Mail-Adresse hinterlegt',
                error: 'Die Anforderung konnte nicht gespeichert werden. Bitte versuche es erneut.',
              },
            },
          },
          'upload-dialog': {
            title: 'Alles hochladen',
            subtitle:
              'Zieh einfach alle Dateien rein — Dokumente und Fotos gemischt. Wir sortieren automatisch in die passenden Kategorien.',
            'file-picker-cta': 'Dateien auswählen',
            'drop-hint': 'Dateien hier ablegen oder durchsuchen · PDF, JPG, PNG · viele auf einmal',
            'size-hint': 'Auch Word · max. 20 MB pro Datei',
            steps: {
              upload: 'Hochladen',
              categorise: 'Kategorisieren',
              done: 'Fertig',
              // Visually hidden. Marks a finished step for assistive tech, which
              // never reaches the check mark on the decorative badge.
              completed: 'abgeschlossen',
            },
            progress: '{done} von {total}',
            'done-title': 'Fertig',
            'done-body':
              'Deine Dateien sind hochgeladen. Sie werden im Hintergrund einsortiert — du siehst das direkt in der Übersicht.',
            'done-cta': 'Zur Übersicht',
            'display-name-label': 'Anzeigename',
            'category-label': 'Kategorie (optional)',
            'category-placeholder': 'Kategorie wählen oder später zuordnen',
            'remove-file': 'Datei entfernen',
            'retry-file': 'Erneut versuchen',
            status: {
              pending: 'Wartet',
              uploading: 'Wird hochgeladen …',
              done: 'Hochgeladen',
              failed: 'Fehlgeschlagen',
            },
            'submit-cta': 'Hochladen',
            'submit-loading': 'Wird hochgeladen …',
            'cancel-cta': 'Abbrechen',
            errors: {
              'disallowed-type': 'Nur PDF, Word, JPG und PNG sind erlaubt',
              'too-large': 'Die Datei darf maximal 20 MB groß sein',
              forbidden: 'Du bist für diese Aktion nicht berechtigt.',
              generic: 'Das Dokument konnte nicht hochgeladen werden. Bitte versuche es erneut.',
            },
          },
          'category-picker': {
            'search-placeholder': 'Kategorie suchen…',
            'no-results': 'Keine Kategorie gefunden.',
          },
          'row-actions': 'Aktionen',
          'action-preview': 'Vorschau',
          'action-download': 'Herunterladen',
          'action-rename': 'Umbenennen',
          'action-categorize': 'Bearbeiten',
          'action-delete': 'Löschen',
          'delete-locked-signed': 'Unterschriebene Dokumente können nicht gelöscht werden.',
          'download-error': 'Der Download konnte nicht gestartet werden. Bitte versuche es erneut.',
          'read-only-info': {
            title: 'Du kannst Dokumente nur ansehen und herunterladen.',
            body: 'Zum Hochladen oder Bearbeiten brauchst du eine höhere Berechtigung.',
          },
          rename: {
            label: 'Dateiname',
            save: 'Speichern',
            cancel: 'Abbrechen',
            errors: {
              empty: 'Bitte gib einen Namen ein.',
              forbidden: 'Du bist für diese Aktion nicht berechtigt.',
              'not-found': 'Dieses Dokument existiert nicht mehr.',
              generic: 'Das Dokument konnte nicht umbenannt werden. Bitte versuche es erneut.',
            },
          },
          'recategorize-modal': {
            title: 'Kategorie bearbeiten',
            save: 'Speichern',
            'save-loading': 'Wird gespeichert…',
            cancel: 'Abbrechen',
            errors: {
              forbidden: 'Du bist für diese Aktion nicht berechtigt.',
              'not-found': 'Dieses Dokument existiert nicht mehr.',
              generic: 'Die Kategorie konnte nicht gespeichert werden. Bitte versuche es erneut.',
            },
          },
          'delete-dialog': {
            title: 'Dokument löschen?',
            body: 'Das Dokument „{name}“ wird dauerhaft entfernt. Das kann nicht rückgängig gemacht werden.',
            confirm: 'Löschen',
            'confirm-loading': 'Wird gelöscht…',
            cancel: 'Abbrechen',
            errors: {
              forbidden: 'Du bist für diese Aktion nicht berechtigt.',
              'not-found': 'Dieses Dokument existiert nicht mehr.',
              generic: 'Das Dokument konnte nicht gelöscht werden. Bitte versuche es erneut.',
            },
          },
          'preview-modal': {
            title: 'Vorschau',
            loading: 'Vorschau wird geladen…',
            error: 'Die Vorschau konnte nicht geladen werden. Bitte versuche es erneut.',
            unsupported: 'Keine Vorschau verfügbar. Lade das Dokument herunter, um es zu öffnen.',
          },
          analyse: {
            action: 'Dokument analysieren',
            back: 'Dokumente',
            again: 'Erneut analysieren',
            apply: 'Übernehmen',
            disclaimer:
              'Automatisch aus dem Dokument ausgelesen — bitte gegen das Original prüfen. Das Dokument bleibt maßgeblich.',
            'not-determined': 'nicht ermittelt',
            'please-check': 'bitte prüfen',
            conflict: {
              label: 'Konflikt',
              message: 'Konflikt - Ausgelesener Wert <{extracted}> weicht vom ursprünglichen Wert <{original}> ab',
            },
            running: {
              label: 'Wird analysiert …',
              'toast-title': 'Analyse läuft …',
              'toast-body': 'Du kannst die Seite verlassen und später zurückkommen.',
            },
            done: {
              label: 'Analysiert',
            },
            status: {
              loading: 'Wird geladen …',
              PENDING: 'In Warteschlange',
              ANALYZING: 'Wird analysiert …',
              COMPLETED: 'Abgeschlossen',
              NO_DATA: 'Keine Daten',
              FAILED: 'Fehler',
            },
            meta: {
              'completed-at': 'Abgeschlossen am {at}',
              running: 'Die Analyse läuft noch …',
            },
            confidence: {
              high: 'hoch',
              medium: 'mittel',
              low: 'niedrig',
            },
            sections: {
              address: 'Adresse',
              areas: 'Flächen',
              building: 'Gebäude',
              energy: 'Energie',
              price: 'Preis',
              other: 'Weitere',
            },
            fields: {
              street: 'Straße',
              houseNumber: 'Hausnummer',
              postalCode: 'PLZ',
              city: ADDRESS_FIELD_LABELS.city,
              district: ADDRESS_FIELD_LABELS.district,
              state: ADDRESS_FIELD_LABELS.state,
              country: ADDRESS_FIELD_LABELS.country,
              parcelNumber: 'Flurstück',
              parcelSubdivision: 'Flur',
              livingArea: 'Wohnfläche',
              plotArea: 'Grundstücksfläche',
              usableArea: 'Nutzfläche',
              totalArea: 'Gesamtfläche',
              rooms: 'Zimmer',
              bedrooms: 'Schlafzimmer',
              bathrooms: 'Badezimmer',
              floor: 'Etage',
              floorsTotal: 'Etagen gesamt',
              yearBuilt: 'Baujahr',
              lastRenovated: 'Letzte Modernisierung',
              condition: 'Zustand',
              interiorQuality: 'Ausstattungsqualität',
              constructionPhase: 'Bauphase',
              certificateBasis: 'Ausweisart',
              energyClass: 'Energieeffizienzklasse',
              energyValue: 'Energiekennwert',
              primaryEnergyValue: 'Primärenergiebedarf',
              includesWarmWater: 'Warmwasser enthalten',
              certificateIssuedAt: 'Ausgestellt am',
              certificateValidUntil: 'Gültig bis',
              purchasePrice: 'Kaufpreis',
              baseRent: 'Kaltmiete',
              totalRent: 'Warmmiete',
              deposit: 'Kaution',
            },
            'no-data': {
              title: 'Keine Daten',
              body: 'In diesem Dokument wurden keine auslesbaren Objektdaten gefunden.',
              'no-text-body': 'Dieses Dokument enthält keinen lesbaren Text zum Auslesen.',
            },
            'load-failed': {
              title: 'Analyse konnte nicht geladen werden',
              body: 'Das Ergebnis konnte nicht gelesen werden. Bitte versuche es erneut.',
              retry: 'Erneut versuchen',
            },
            failed: {
              title: 'Fehler',
              body: 'Die Analyse ist fehlgeschlagen. Bitte versuche es erneut.',
              retry: 'Erneut versuchen',
            },
            errors: {
              forbidden: 'Du bist für diese Aktion nicht berechtigt.',
              generic: 'Die Analyse konnte nicht gestartet werden. Bitte versuche es erneut.',
            },
            'field-edit': {
              pencil: 'Wert korrigieren',
              save: 'Übernehmen',
              cancel: 'Abbrechen',
              saving: 'Wird gespeichert …',
              'error-forbidden': 'Du bist für diese Aktion nicht berechtigt.',
              'error-generic': 'Die Korrektur konnte nicht gespeichert werden. Bitte versuche es erneut.',
            },
            corrected: {
              badge: 'manuell korrigiert',
              'read-from': 'Ausgelesen',
              by: 'Korrigiert von {name} · {at}',
            },
            'rerun-warning': {
              title: 'Erneut analysieren?',
              body: 'Diese Analyse enthält manuelle Korrekturen. Eine erneute Analyse ersetzt die ausgelesenen Werte — deine Korrekturen können dabei verloren gehen. Fortfahren?',
              confirm: 'Erneut analysieren',
              cancel: 'Abbrechen',
            },
            'apply-confirm': {
              title: '{section}-Werte übernehmen?',
              body: 'Beim Übernehmen werden die Objektfelder mit den ausgelesenen Werten aktualisiert. Konflikte werden nicht automatisch ausgewählt; wenn du einen Konflikt auswählst, wird der aktuelle Objektwert überschrieben.',
              'conflict-label': 'Konflikt',
              'conflict-message':
                'Konflikt - Ausgelesener Wert <{extracted}> weicht vom ursprünglichen Wert <{original}> ab',
              confirm: 'Übernehmen',
              cancel: 'Abbrechen',
            },
            'apply-result': {
              success: '{count} Felder übernommen',
              'success-one': 'Ein Feld übernommen',
              // A partial apply is a normal outcome, not a failure: some values
              // cannot reach the Objekt (see the `skipped` reasons below).
              partial: '{applied} von {total} Feldern übernommen',
              'none-applied': 'Keine Felder übernommen',
              // A re-run replaced the reviewed analysis before confirm — nothing
              // was written; the panel reloads to show the new values.
              superseded: 'Die Analyse wurde inzwischen aktualisiert. Bitte prüfe die neuen Werte.',
              forbidden: 'Du darfst dieses Objekt nicht bearbeiten.',
              error: 'Die Werte konnten nicht übernommen werden. Bitte versuche es erneut.',
              skipped: {
                title: 'Nicht übernommen:',
                'not-extracted': '{field}: kein ausgelesener Wert',
                'unsupported-field': '{field}: kein passendes Objektfeld',
                'invalid-value': '{field}: Wert passt nicht zum Feldformat',
                'section-incomplete':
                  '{field}: Abschnitt am Objekt noch unvollständig — bitte zuerst am Objekt anlegen',
                'section-not-applicable': '{field}: passt nicht zur Vermarktungsart des Objekts',
                'analysis-superseded': '{field}: Analyse wurde aktualisiert',
                'certificate-not-gmodg': '{field}: gilt nur für einen Energieausweis ab 2027',
                'energy-class-off-scale': '{field}: liegt nicht auf der Skala A bis G für Nichtwohngebäude',
              },
            },
            'read-only': {
              'banner-title': 'Du kannst diese Analyse ansehen, aber nicht ändern.',
              'banner-body': 'Analysieren und Korrigieren sind Mitgliedern mit Schreibrechten vorbehalten.',
              denied: 'Du bist für diese Aktion nicht berechtigt.',
            },
          },
          redaction: {
            marker: 'Geschwärzt',
            'copies-toggle': '{count, plural, one {# geschwärzte Kopie} other {# geschwärzte Kopien}}',
            'original-deleted': 'Original gelöscht',
            action: 'Schwärzen',
            unsupported: 'Nur PDFs und Bilder können geschwärzt werden',
            title: 'Dokument schwärzen',
            hint: 'Markiere die Bereiche, die du unkenntlich machen möchtest.',
            save: 'Schwärzen & speichern',
            saving: 'Wird geschwärzt …',
            cancel: 'Abbrechen',
            'remove-box': 'Markierung entfernen',
            'no-box': 'Markiere mindestens einen Bereich zum Schwärzen',
            loading: 'Dokument wird geladen …',
            'load-error': 'Das Dokument konnte nicht geladen werden. Bitte versuche es erneut.',
            'page-label': 'Seite {number}',
            forbidden: 'Du bist für diese Aktion nicht berechtigt.',
            confirm: {
              title: 'Schwärzung ist endgültig',
              body: 'Die markierten Inhalte werden dauerhaft entfernt und können nicht wiederhergestellt werden. Das Original bleibt unverändert erhalten.',
              'name-label': 'Name der Kopie',
              'name-suffix': '{base} (geschwärzt)',
              'name-required': 'Bitte gib einen Namen für die Kopie an.',
            },
            success: 'Geschwärzte Kopie wurde erstellt.',
            failed: {
              engine: 'Das Schwärzen ist fehlgeschlagen. Bitte versuche es erneut.',
              check:
                'Die Schwärzung konnte nicht sicher geprüft werden. Es wurde keine Kopie erstellt. Bitte versuche es erneut oder wende dich an den Support.',
            },
          },
        },
        media: {
          'section-title': 'Medien',
          'drop-overlay': 'Dateien hier ablegen zum Hochladen',
          loading: 'Medien werden geladen…',
          'load-error': 'Medien konnten nicht geladen werden.',
          'load-retry': 'Erneut versuchen',
          'add-cta': 'Medien hochladen',
          'uploaded-by-unknown': 'Unbekanntes Mitglied',
          'thumbnail-pending': 'Vorschau wird erstellt …',
          'thumbnail-unavailable': 'Vorschau nicht verfügbar',
          areas: {
            photos: 'Fotos',
            'floor-plans': 'Grundrisse',
            videos: 'Videos',
          },
          type: {
            image: 'Bild',
            video: 'Video',
          },
          'row-actions': 'Aktionen',
          'action-set-cover-image': 'Als Titelbild',
          'action-als-grundriss': 'Als Grundriss',
          'action-als-foto': 'Als Foto',
          demote: {
            action: 'In die Dokumente verschieben',
            errors: {
              'not-an-image': 'Nur Bilder können in die Dokumente verschoben werden.',
            },
          },
          'action-umbenennen': 'Umbenennen',
          'action-loeschen': 'Löschen',
          'cover-image-badge': 'Titelbild',
          'delete-dialog': {
            title: 'Medium löschen?',
            body: 'Dieses Medium wird dauerhaft entfernt. Das kann nicht rückgängig gemacht werden.',
            confirm: 'Löschen',
            'confirm-loading': 'Wird gelöscht…',
            cancel: 'Abbrechen',
            errors: {
              forbidden: 'Du bist für diese Aktion nicht berechtigt.',
              'not-found': 'Dieses Medium existiert nicht mehr.',
              generic: 'Das Medium konnte nicht gelöscht werden. Bitte versuche es erneut.',
            },
          },
          selection: {
            select: 'Auswählen',
            cancel: 'Abbrechen',
            'select-all': 'Alle auswählen',
            'delete-selected': 'Ausgewählte löschen ({n})',
            'bulk-delete': {
              title: {
                photos: '{n, plural, one {# Foto löschen?} other {# Fotos löschen?}}',
                'floor-plans': '{n, plural, one {# Grundriss löschen?} other {# Grundrisse löschen?}}',
              },
              body: 'Diese Bilder werden dauerhaft entfernt. Das kann nicht rückgängig gemacht werden.',
              'partial-failure': {
                photos:
                  '{count, plural, one {# Foto konnte} other {# Fotos konnten}} nicht gelöscht werden. Bitte versuche es erneut.',
                'floor-plans':
                  '{count, plural, one {# Grundriss konnte} other {# Grundrisse konnten}} nicht gelöscht werden. Bitte versuche es erneut.',
              },
            },
            'move-failure': {
              photos:
                '{count, plural, one {# Foto konnte} other {# Fotos konnten}} nicht verschoben werden. Bitte versuche es erneut.',
              'floor-plans':
                '{count, plural, one {# Grundriss konnte} other {# Grundrisse konnten}} nicht verschoben werden. Bitte versuche es erneut.',
            },
          },
          rename: {
            title: 'Medium umbenennen',
            label: 'Name',
            hint: '1–255 Zeichen · der ursprüngliche Dateiname bleibt für den Download erhalten',
            save: 'Speichern',
            cancel: 'Abbrechen',
            errors: {
              empty: 'Bitte gib einen Namen ein.',
              forbidden: 'Du bist für diese Aktion nicht berechtigt.',
              'not-found': 'Dieses Medium existiert nicht mehr.',
              generic: 'Das Medium konnte nicht umbenannt werden. Bitte versuche es erneut.',
            },
          },
          errors: {
            'cover-image-not-a-photo': 'Nur ein Foto kann als Titelbild gesetzt werden',
          },
          'empty-area': {
            photos: 'Noch keine Fotos',
            'floor-plans': 'Noch keine Grundrisse',
            videos: 'Noch keine Videos',
          },
          'empty-first-time': {
            line: 'Noch keine Medien zu diesem Objekt',
            cta: 'Medien hochladen',
          },
          'capture-photos': {
            heading: 'Aufnahmefotos',
            hint: 'Nur für die Bewertung. Diese Fotos erscheinen nie in der Galerie, im Exposé oder im Datenraum.',
            'tile-meta': '{takenAt}, {time} · {member}',
          },
          tours: {
            heading: '360°-Rundgänge',
            'add-cta': 'Rundgang hinzufügen',
            empty: 'Noch keine Rundgänge',
            'limit-hint': 'Höchstens {limit} Rundgänge pro Objekt.',
            loading: 'Rundgänge werden geladen…',
            'load-error': 'Rundgänge konnten nicht geladen werden.',
            'reorder-error': 'Die Reihenfolge konnte nicht gespeichert werden. Bitte versuche es erneut.',
            entry: '{provider} · {title}',
            'default-title': '360°-Rundgang',
            actions: {
              open: 'Öffnen',
              edit: 'Bearbeiten',
              delete: 'Löschen',
            },
            dialog: {
              'add-title': 'Rundgang hinzufügen',
              'edit-title': 'Rundgang bearbeiten',
              'link-label': 'Link',
              'link-placeholder': 'https://…',
              'title-label': 'Titel',
              save: 'Speichern',
              cancel: 'Abbrechen',
              errors: {
                'not-found': 'Dieser Rundgang existiert nicht mehr.',
                generic: 'Der Rundgang konnte nicht gespeichert werden. Bitte versuche es erneut.',
              },
            },
            providers: {
              MATTERPORT: 'Matterport',
              OGULO: 'Ogulo',
              IMMOVIEWER: 'immoviewer',
              THREE_D_VISTA: '3DVista',
              GIRAFFE360: 'Giraffe360',
              KUULA: 'Kuula',
              PANOEE: 'Panoee',
              NODALVIEW: 'Nodalview',
            },
          },
          'cap-error': 'Maximal 100 Medien je Objekt – {remaining} können noch hinzugefügt werden',
          'video-link': {
            'add-cta': 'Video-Link hinzufügen',
            dialog: {
              title: 'Video-Link hinzufügen',
              'link-label': 'Link',
              'link-hint': 'YouTube oder Vimeo',
              save: 'Speichern',
              cancel: 'Abbrechen',
              errors: {
                generic: 'Der Video-Link konnte nicht gespeichert werden. Bitte versuche es erneut.',
              },
            },
            providers: {
              YOUTUBE: 'YouTube',
              VIMEO: 'Vimeo',
            },
            actions: {
              open: 'Öffnen',
            },
            'open-aria': '{title} auf {provider} öffnen (neuer Tab)',
            'rename-hint': '1–120 Zeichen',
          },
          handles: {
            preview: 'Vorschau',
            download: 'Herunterladen',
          },
          lightbox: {
            close: 'Schließen',
            previous: 'Vorheriges Medium',
            next: 'Nächstes Medium',
            download: 'Herunterladen',
            'cover-badge': 'Titelbild',
            position: {
              photos: 'Foto {i} von {n}',
              'floor-plans': 'Grundriss {i} von {n}',
              videos: 'Video {i} von {n}',
            },
            'download-error': 'Der Download konnte nicht gestartet werden. Bitte versuche es erneut.',
            'image-unavailable': 'Bild nicht verfügbar',
          },
          'upload-dialog': {
            title: 'Medien hochladen',
            'file-picker-cta': 'Fotos oder Videos auswählen',
            'drop-hint': 'JPG, PNG, WebP bis 20 MB · MP4 bis 200 MB',
            'remove-file': 'Datei entfernen',
            'submit-cta': 'Hochladen',
            'submit-loading': 'Wird hochgeladen …',
            'cancel-cta': 'Abbrechen',
            errors: {
              type: 'Nur JPG, PNG, WebP und MP4 sind erlaubt',
              'image-too-large': 'Bilder dürfen maximal 20 MB groß sein',
              'video-too-large': 'Videos dürfen maximal 200 MB groß sein',
              generic: 'Das Medium konnte nicht hochgeladen werden. Bitte versuche es erneut.',
            },
          },
        },
        portals: {
          heading: 'Portalinserate',
          'load-error': 'Die Portalinserate konnten nicht geladen werden.',
          'load-retry': 'Erneut versuchen',
          'enter-commission-cta': 'Käuferprovision eintragen',
          expired: {
            body: 'Die ImmoScout24-Verbindung ist abgelaufen. Bestehende Inserate bleiben online, lassen sich aber erst nach dem Erneuern wieder ändern.',
            'ask-admin': 'Ein Eigentümer oder Admin kann die Verbindung unter ‚Portale‘ erneuern.',
            'renew-cta': 'Verbindung erneuern',
          },
          'openimmo-expired': {
            manager:
              'Der Zugang zu {portal} ist ungültig. Inserate können dort nicht veröffentlicht, aktualisiert oder zurückgezogen werden.',
            member:
              'Der Zugang zu {portal} ist ungültig. Bestehende Inserate bleiben online, lassen sich aber erst wieder ändern, wenn ein Eigentümer oder Admin den Zugang unter ‚Portale‘ korrigiert hat.',
            'change-cta': 'Zugang ändern',
          },
          extension: {
            send: 'Auf ImmoScout24 übertragen',
            complete: 'Inseratsangaben vervollständigen',
            update: 'Änderungen veröffentlichen',
            retry: 'Erneut versuchen',
            running: 'Wird an ImmoScout24 übertragen …',
            progress: 'Schritt {done} von {total}',
            draft: 'Entwurf auf ImmoScout24',
            online: 'Online auf ImmoScout24',
            offline: 'Offline auf ImmoScout24',
            failed: 'Die Übertragung an ImmoScout24 ist fehlgeschlagen.',
            'not-kept': 'ImmoScout24 hat {fields} nicht übernommen. Trag sie im ScoutManager von Hand ein.',
            'no-extension': 'Für ImmoScout24 brauchst du die immoOS-Erweiterung.',
            outdated: 'Die immoOS-Erweiterung hat nicht geantwortet. Aktualisiere sie und versuche es noch einmal.',
            'install-cta': 'Erweiterung installieren',
            'not-connected': 'Verbinde die immoOS-Erweiterung, dann kannst du übertragen.',
            'connect-cta': 'Erweiterung verbinden',
            'signed-out':
              'ImmoScout24 erkennt deine Anmeldung gerade nicht. Öffne ImmoScout24, melde dich bei Bedarf an und versuche es dann noch einmal.',
            'sign-in-cta': 'ImmoScout24 öffnen',
            client: {
              pending: {
                SEND: 'Dein KI-Client möchte das Objekt an ImmoScout24 übertragen.',
                UPDATE: 'Dein KI-Client möchte die Änderungen auf ImmoScout24 veröffentlichen.',
                GO_LIVE: 'Dein KI-Client möchte das Inserat auf ImmoScout24 online stellen.',
                TAKE_OFFLINE: 'Dein KI-Client möchte das Inserat auf ImmoScout24 offline nehmen.',
                DELETE: 'Dein KI-Client möchte das Inserat auf ImmoScout24 löschen.',
              },
              'options-label': 'Veröffentlichen auf',
              confirm: 'Bestätigen',
              decline: 'Ablehnen',
              'decide-error': 'Deine Entscheidung konnte nicht gespeichert werden. Bitte versuche es noch einmal.',
              confirmed: 'Bestätigt. Wird ausgeführt, sobald dein Browser mit der immoOS-Erweiterung aktiv ist.',
              declined: 'Abgelehnt. An ImmoScout24 wurde nichts übertragen.',
              lapsed:
                'Nicht ausgeführt: Dein Browser mit der immoOS-Erweiterung war 24 Stunden nicht aktiv. Frag bitte noch einmal.',
            },
            'go-live': {
              cta: 'Veröffentlichen',
              title: 'Auf ImmoScout24 online stellen?',
              body: 'Wähle, wie ImmoScout24 das Inserat veröffentlicht. Das kann ein Kontingent deiner Agentur verbrauchen.',
              confirm: 'Veröffentlichen',
              cancel: 'Abbrechen',
            },
            end: {
              TAKE_OFFLINE: {
                cta: 'Veröffentlichung beenden',
                title: 'Inserat auf ImmoScout24 offline nehmen?',
                body: 'Das Inserat ist dann für Suchende nicht mehr sichtbar. Du kannst es später wieder online stellen.',
                confirm: 'Veröffentlichung beenden',
              },
              DELETE: {
                cta: 'Löschen',
                title: 'Inserat auf ImmoScout24 endgültig löschen?',
                body: 'Gebuchte Produkte verlieren ihre Gültigkeit, die Statistiken enden und Nachrichten sind nicht mehr verfügbar. Das lässt sich nicht rückgängig machen.',
                confirm: 'Endgültig löschen',
              },
              cancel: 'Abbrechen',
              failed: 'Das hat auf ImmoScout24 nicht geklappt.',
              deleted: 'Auf ImmoScout24 gelöscht am {date}.',
            },
          },
          row: {
            'not-published': 'nicht veröffentlicht',
            'coming-soon': 'Demnächst',
            publish: 'Veröffentlichen',
            status: {
              PUBLISHED: 'veröffentlicht',
              TRANSMITTED: 'übertragen',
              STALE: 'veraltet',
              FAILED: 'fehlgeschlagen',
              WITHDRAWN: 'zurückgezogen',
            },
            'in-flight': 'Wird übertragen …',
            since: 'seit {date}',
            'portal-id': 'IS24-ID {id}',
            contact: 'Ansprechpartner: {name}',
            disconnected: 'Verbindung getrennt — Inserat ist weiter online, aber nicht mehr aus immoOS steuerbar.',
            'disconnected-openimmo':
              'Verbindung getrennt — Inserat ist weiter auf {portal}, aber nicht mehr aus immoOS steuerbar.',
            'transmitted-note':
              'Übertragen am {date}, {time}. {portal} bestätigt die Übernahme per E-Mail an die bei {portal} hinterlegte Adresse deiner Agentur.',
            failure: {
              heading: 'Die Übertragung ist fehlgeschlagen.',
              verbatim: 'Meldung des Portals, unverändert.',
              'verbatim-server': 'Meldung des Servers, unverändert.',
              at: 'am {datetime}',
            },
            'retry-cta': 'Erneut übertragen',
            'withdraw-cta': 'Inserat zurückziehen',
            'withdraw-retry-cta': 'Erneut zurückziehen',
            'withdrawn-note': 'Zurückgezogen am {date}. {portal} bestätigt das Löschen per E-Mail.',
            'withdrawn-at': 'am {date}',
            'withdrawal-failure': {
              heading: 'Das Portal hat das Zurückziehen abgelehnt. Das Inserat ist weiterhin online.',
              'no-call': 'Das Inserat konnte nicht zurückgezogen werden und ist weiterhin online.',
              openimmo:
                'Das Zurückziehen konnte nicht an {portal} übertragen werden. Das Inserat ist weiterhin online.',
            },
            'online-despite': {
              ARCHIVED: `online trotz ${PROPERTY_STATUS_LABELS.ARCHIVED}`,
              INACTIVE: `online trotz ${PROPERTY_STATUS_LABELS.INACTIVE}`,
            },
            stale: {
              notice: 'Das Objekt hat sich seit der letzten Übertragung geändert.',
              'update-cta': 'Inserat aktualisieren',
              'update-error': 'Das Inserat konnte nicht aktualisiert werden. Bitte versuche es erneut.',
            },
          },
          'transmitted-picture': {
            title: 'Bild aus der Vermarktung nehmen?',
            notice: 'Das Bild verschwindet erst mit der nächsten Übertragung von {portals}.',
            confirm: 'Aus der Vermarktung nehmen',
            cancel: 'Abbrechen',
          },
          'withdraw-dialog': {
            title: 'Inserat zurückziehen?',
            body: 'Das Objekt bleibt in immoOS unverändert und kann jederzeit erneut veröffentlicht werden.',
            'pair-body': 'Beide ImmoScout24-Inserate dieses Objekts werden gemeinsam zurückgezogen.',
            confirm: 'Zurückziehen',
            cancel: 'Zurück',
            'generic-error': 'Das Inserat konnte nicht zurückgezogen werden. Bitte versuche es noch einmal.',
          },
          dialog: {
            title: 'Auf {portal} veröffentlichen',
            loading: 'Bereitschaft wird geprüft …',
            'load-error': 'Die Bereitschaft konnte nicht geprüft werden.',
            'load-retry': 'Erneut versuchen',
            'show-address': 'Adresse anzeigen',
            'energy-paragraph-label': 'Wird an die Objektbeschreibung angehängt:',
            'attach-label': 'Bestehendes ImmoScout24-Inserat übernehmen (IS24-ID)',
            'attach-warning':
              'Die Übertragung ersetzt Texte, Bilder und Ansprechpartner des bestehenden Inserats durch den Stand in immoOS.',
            'publish-cta': 'Veröffentlichen',
            'cancel-cta': 'Abbrechen',
            channel: 'Kanal: {portal}',
            contact: 'Ansprechpartner: {name}',
            'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
            'generic-error': 'Die Übertragung konnte nicht gestartet werden. Bitte versuche es noch einmal.',
          },
          readiness: {
            'not-ready-title': 'Noch nicht bereit für {portal}',
            'not-ready-intro': 'Diese Angaben fehlen {portal} noch:',
            'hints-heading': 'Hinweise',
            ready: 'Bereit für {portal}.',
            'unknown-member': 'das zuständige Mitglied',
            'contact-fields': {
              EMAIL: 'E-Mail',
              LAST_NAME: 'Nachname',
            },
            'company-fields': {
              COMPANY_NAME: 'Firmenname',
              LEGAL_FORM: 'Rechtsform',
              ADDRESS: 'Adresse',
              EMAIL: 'E-Mail',
              PHONE: 'Telefon',
              MANAGING_DIRECTORS: 'Geschäftsführer',
              COMMERCIAL_REGISTER_COURT: 'Handelsregister',
              COMMERCIAL_REGISTER_NUMBER: 'Registernummer',
              SUPERVISORY_AUTHORITY: 'Aufsichtsbehörde (§34c GewO)',
            },
            items: {
              NOT_ACTIVE:
                'Dieses Objekt ist noch nicht in Vermarktung. Veröffentlichen ist erst nach Erhalt des Maklervertrags möglich.',
              NOT_CONNECTED:
                '{portalToken, select, IMMOSCOUT24 {Der Workspace ist nicht mit ImmoScout24 verbunden. Ein Eigentümer oder Admin kann das Konto unter ‚Portale‘ verbinden.} other {Der Workspace ist nicht mit {portal} verbunden. Ein Eigentümer oder Admin kann den Zugang unter ‚Portale‘ eintragen.}}',
              CONNECTION_EXPIRED:
                '{portalToken, select, IMMOSCOUT24 {Die ImmoScout24-Verbindung ist abgelaufen. Veröffentlichen ist erst nach dem Erneuern wieder möglich.} other {Der Zugang zu {portal} ist ungültig. Veröffentlichen ist erst wieder möglich, wenn ein Eigentümer oder Admin den Zugang unter ‚Portale‘ korrigiert hat.}}',
              SUBTYPE_MISSING: 'Für {portal} fehlt der Untertyp.',
              CONTACT_INCOMPLETE:
                '{portalToken, select, IMMOSCOUT24 {Für {name} fehlen Angaben für den ImmoScout24-Kontakt: {fields}.} other {Das zuständige Mitglied ist nicht mehr aktiv. Weise das Objekt einem aktiven Mitglied zu, bevor du es auf {portal} veröffentlichst.}}',
              COMMERCIAL_FINE_TYPE: 'Feintyp',
              STREET: 'Straße',
              HOUSE_NUMBER: 'Hausnummer',
              POSTAL_CODE: 'PLZ',
              CITY: 'Ort',
              HOUSE_NUMBER_TOO_LONG: 'Hausnummer ist länger, als ImmoScout24 erlaubt (10 Zeichen).',
              CITY_TOO_LONG: 'Ort ist länger, als ImmoScout24 erlaubt (50 Zeichen).',
              TITLE_TOO_LONG:
                '{portalToken, select, IMMOSCOUT24 {Titel ist zu lang für ImmoScout24 (höchstens 100 Zeichen).} other {Titel ist für {portal} zu lang (höchstens {n} Zeichen). Bitte kürze den Text.}}',
              DESCRIPTION_TOO_LONG:
                '{portalToken, select, IMMOSCOUT24 {Objektbeschreibung ist zu lang für ImmoScout24. Kürze den Text.} other {Objektbeschreibung ist für {portal} zu lang (höchstens {n} Zeichen). Bitte kürze den Text.}}',
              EQUIPMENT_DESCRIPTION_TOO_LONG:
                '{portalToken, select, IMMOSCOUT24 {Ausstattung ist zu lang für ImmoScout24. Kürze den Text.} other {Ausstattung ist für {portal} zu lang (höchstens {n} Zeichen). Bitte kürze den Text.}}',
              LOCATION_DESCRIPTION_TOO_LONG:
                '{portalToken, select, IMMOSCOUT24 {Lage ist zu lang für ImmoScout24. Kürze den Text.} other {Lage ist für {portal} zu lang (höchstens {n} Zeichen). Bitte kürze den Text.}}',
              MISCELLANEOUS_TOO_LONG:
                '{portalToken, select, IMMOSCOUT24 {Sonstiges ist zu lang für ImmoScout24. Kürze den Text.} other {Sonstiges ist für {portal} zu lang (höchstens {n} Zeichen). Bitte kürze den Text.}}',
              LIVING_AREA: 'Wohnfläche',
              ROOMS: 'Zimmer',
              PLOT_AREA: 'Grundstücksfläche',
              NET_FLOOR_SPACE: 'Nutzfläche (Büro-, Verkaufs- oder Lagerfläche)',
              TOTAL_FLOOR_SPACE: 'Gesamtfläche',
              PRICE: 'Preis',
              RENTAL_COMMISSION: 'Provision',
              RENTAL_COMMISSION_AMOUNT: 'Provisionshöhe',
              BUYER_COMMISSION_MISSING: 'Käuferprovision fehlt',
              COMMISSION_REENTRY_REQUIRED: 'Provision neu erfassen',
              BUYER_COMMISSION_ABOVE_SELLER: 'Käuferprovision höher als Verkäuferprovision',
              BUYER_COMMISSION_BELOW_SELLER: 'Käufer- und Verkäuferprovision ungleich',
              COMMISSION_NOTE_TOO_LONG: 'Provisionshinweis ist länger, als ImmoScout24 erlaubt (500 Zeichen).',
              GEG_CERTIFICATE_BASIS: 'Pflichtangabe laut GEG fehlt: Energieausweistyp',
              GEG_ENERGY_VALUE: 'Pflichtangabe laut GEG fehlt: Energiekennwert',
              GEG_ENERGY_SOURCE: 'Pflichtangabe laut GEG fehlt: Wesentlicher Energieträger',
              GEG_YEAR_BUILT: 'Pflichtangabe laut GEG fehlt: Baujahr',
              GEG_ENERGY_CLASS: 'Pflichtangabe laut GEG fehlt: Energieeffizienzklasse',
              GMODG_CERTIFICATE_BASIS: 'Pflichtangabe laut GModG fehlt: Energieausweistyp',
              GMODG_PRIMARY_ENERGY_VALUE: 'Pflichtangabe laut GModG fehlt: Primärenergiebedarf',
              GMODG_ENERGY_CLASS: 'Pflichtangabe laut GModG fehlt: Energieeffizienzklasse',
              GMODG_YEAR_BUILT: 'Pflichtangabe laut GModG fehlt: Baujahr',
              GMODG_ENERGY_SOURCE: 'Pflichtangabe laut GModG fehlt: Wesentlicher Energieträger',
              CERTIFICATE_EXPIRED:
                'Der Energieausweis ist seit {date} abgelaufen. Für eine Anzeige brauchst du einen gültigen Ausweis.',
              PRICE_ON_INQUIRY: 'Kleinanzeigen verlangt einen Preis. ‚Preis auf Anfrage‘ ist dort nicht möglich.',
              COMPANY_DATA_INCOMPLETE: 'Für {portal} fehlen Firmendaten: {fields}.',
              CATEGORY_UNSUPPORTED: '{portal} bietet für diese Objektart keine passende Rubrik.',
              PICTURES_OVER_LIMIT:
                '{portal} übernimmt höchstens {n} Bilder. Übertragen werden die ersten {n} in der Reihenfolge der Vermarktung.',
            },
            explanations: {
              BUYER_COMMISSION_MISSING:
                'Gib an, ob die Käuferprovision als Prozentsatz oder fester Betrag gilt, und trag den Wert ein. Ohne vollständige Angabe darf die Anzeige nicht veröffentlicht werden.',
              COMMISSION_REENTRY_REQUIRED:
                'Die Provision steht noch als Freitext am Objekt. Erfasse sie neu, damit die Anzeige „inkl. USt.“ und die zahlende Seite richtig nennt.',
              BUYER_COMMISSION_ABOVE_SELLER:
                'Bei Wohnungen und Einfamilienhäusern darf der Käufer nicht mehr zahlen als der Verkäufer (§ 656c/d BGB). Sonst ist die Provisionsvereinbarung mit dem Käufer unwirksam.',
              BUYER_COMMISSION_BELOW_SELLER:
                'Schließt du auch mit dem Käufer einen Maklervertrag, müssen beide Provisionen gleich hoch sein (§ 656c BGB).',
            },
          },
          'web-expose': {
            name: 'Web-Exposé',
            status: {
              NOT_PUBLISHED: 'nicht veröffentlicht',
              PUBLISHED: 'veröffentlicht',
              WITHDRAWN: 'zurückgezogen',
            },
            since: 'seit {date}',
            'withdrawn-at': 'am {date}',
            'link-label': 'Link zum Web-Exposé',
            copy: 'Link kopieren',
            copied: 'Link kopiert',
            'publish-cta': 'Veröffentlichen',
            'withdraw-cta': 'Zurückziehen',
            preview: 'Vorschau öffnen',
            'share-refused': {
              editor: 'Die Käuferprovision ist nicht vollständig erfasst. Trag sie ein, bevor du das Exposé teilst.',
              viewer:
                'Die Käuferprovision ist nicht vollständig erfasst. Du kannst das Exposé teilen, sobald sie eingetragen ist.',
            },
            openings:
              '{count, plural, =0 {Noch nicht geöffnet} one {<strong>1 Öffnung</strong> · zuletzt am {date} um {time}} other {<strong>{count} Öffnungen</strong> · zuletzt am {date} um {time}}}',
            dialog: {
              title: 'Web-Exposé veröffentlichen',
              link: 'Link: {url}',
              'show-address-help': 'Ohne Adresse zeigt das Exposé nur PLZ, Ort und Stadtteil.',
              'generic-error': 'Das Web-Exposé konnte nicht veröffentlicht werden. Bitte versuche es noch einmal.',
            },
            items: {
              WEB_ADDRESS_MISSING: {
                manager: 'Für den Workspace ist noch keine Web-Adresse festgelegt.',
                member: 'Ein Eigentümer oder Admin muss zuerst die Web-Adresse unter ‚Unternehmensdaten‘ festlegen.',
              },
              MARKETING_TEXT_MISSING: 'Es fehlt ein Vermarktungstext.',
              NO_PUBLISHED_PHOTO: 'Es ist kein Foto für die Vermarktung freigegeben.',
            },
            'withdraw-dialog': {
              title: 'Web-Exposé zurückziehen?',
              body: 'Der Link zeigt danach ‚Dieses Exposé ist nicht mehr verfügbar.‘ Du kannst das Exposé später unter demselben Link wieder veröffentlichen.',
              confirm: 'Zurückziehen',
              cancel: 'Abbrechen',
              'generic-error': 'Das Web-Exposé konnte nicht zurückgezogen werden. Bitte versuche es noch einmal.',
            },
          },
          'listing-type': {
            APARTMENT_BUY: 'Wohnung',
            APARTMENT_RENT: 'Wohnung',
            HOUSE_BUY: 'Haus',
            HOUSE_RENT: 'Haus',
            LIVING_BUY_SITE: 'Grundstück',
            LIVING_RENT_SITE: 'Grundstück',
            TRADE_SITE: 'Grundstück',
            GARAGE_BUY: 'Garage/Stellplatz',
            GARAGE_RENT: 'Garage/Stellplatz',
            OFFICE: 'Büro/Praxis',
            STORE: 'Einzelhandel',
            GASTRONOMY: 'Gastronomie/Hotel',
            INDUSTRY: 'Halle/Produktion',
            SPECIAL_PURPOSE: 'Spezialgewerbe',
            INVESTMENT: 'Anlageobjekt',
            OPENIMMO: 'Inserat',
          },
        },
        marketing: {
          'empty-hint': 'Hier legst du fest, womit dieses Objekt beworben wird.',
          loading: 'Vermarktung wird geladen…',
          'load-error': 'Vermarktung konnte nicht geladen werden.',
          'load-retry': 'Erneut versuchen',
          outputs: {
            portals: {
              heading: 'Online-Portale',
              empty: 'Noch auf keinem Portal veröffentlicht.',
            },
            print: {
              heading: 'Print / PDF',
              loading: 'Exposés werden geladen…',
              'load-error': 'Die Exposés konnten nicht geladen werden.',
              'load-retry': 'Erneut versuchen',
              versions: {
                FULL: { title: 'Vollversion', hint: 'Mit Adresse und allen Angaben' },
                SHORT: { title: 'Kurzexposé', hint: 'Ohne Adresse und sensible Angaben' },
              },
              empty: 'Noch kein Exposé hochgeladen.',
              uploaded: 'Hochgeladen am {date} von {name}',
              'uploaded-by-unknown': 'Unbekanntes Mitglied',
              download: 'Herunterladen',
              'download-error': 'Der Download konnte nicht gestartet werden. Bitte versuche es erneut.',
              upload: 'Hochladen',
              replace: 'Ersetzen',
              uploading: 'Wird hochgeladen…',
              'upload-error': 'Das Exposé konnte nicht hochgeladen werden. Bitte versuche es erneut.',
              'replace-dialog': {
                title: '{version} ersetzen?',
                body: 'Das bisherige Exposé wird gelöscht und ist danach nicht mehr verfügbar.',
                cancel: 'Abbrechen',
                confirm: 'Ersetzen',
              },
              remove: 'Entfernen',
              'remove-dialog': {
                title: '{version} entfernen?',
                body: 'Das Exposé wird gelöscht und ist danach nicht mehr verfügbar.',
                cancel: 'Abbrechen',
                confirm: 'Entfernen',
                error: 'Das Exposé konnte nicht entfernt werden. Bitte versuche es erneut.',
              },
            },
          },
          readiness: {
            ready: 'Bereit zur Vermarktung',
            'incomplete-title': 'Für die Vermarktung fehlt noch:',
            gap: {
              'expose-title': 'Exposé-Titel',
              description: 'mindestens eine Beschreibung',
              'published-image': 'mindestens ein veröffentlichtes Bild',
            },
          },
          texts: {
            heading: 'Texte',
            labels: {
              'expose-title': 'Exposé-Titel',
              highlights: 'Highlights',
              description: 'Objektbeschreibung',
              equipment: 'Ausstattung',
              location: 'Lage',
              miscellaneous: 'Sonstiges',
            },
            'highlights-subtitle': 'Besonderheiten',
            helpers: {
              'expose-title': 'Optional. Ein knackiger Einzeiler als Aufmacher fürs Exposé.',
              highlights: 'Optional. 3–5 kurze Verkaufsargumente, die im Exposé hervorgehoben werden.',
            },
            'expose-title-counter': '{n, number}/120 Zeichen',
            'highlights-add': 'Highlight hinzufügen',
            'highlights-empty': 'Noch keine Highlights',
            'highlights-nudge':
              'Tipp: 3–5 Highlights wirken am stärksten. Du kannst mehr hinzufügen, weniger ist hier aber oft mehr.',
            'highlights-entry': 'Highlight {n, number}',
            'highlights-move-up': 'Highlight {n, number} nach oben verschieben',
            'highlights-move-down': 'Highlight {n, number} nach unten verschieben',
            'highlights-remove': 'Highlight {n, number} entfernen',
            'highlights-drag': 'Highlight {n, number} ziehen, um die Reihenfolge zu ändern',
            placeholders: {
              'expose-title': 'z. B. Lichtdurchflutetes Penthouse mit Elbblick',
              highlight: 'z. B. Direkter Elbblick',
              description: 'Wie würdest du dieses Objekt jemandem beschreiben, der davor steht?',
              equipment: 'Was ist verbaut, in welchem Zustand, aus welchem Jahr?',
              location: 'Was ist in der Umgebung wichtig — Verkehr, Einkaufen, Schulen, Ruhe?',
              miscellaneous: 'Was sonst noch wichtig ist',
            },
            counter: '{n, number} Zeichen',
            'portal-hint': 'ImmoScout24 kürzt ab {n, number} Zeichen.',
            save: 'Speichern',
            saved: 'Vermarktungstexte gespeichert.',
            'saved-state': 'Gespeichert',
            discard: 'Verwerfen',
            'unsaved-prompt': {
              description: 'Du hast Änderungen, die noch nicht gespeichert sind. Verwerfen?',
            },
            errors: {
              forbidden: 'Du bist für diese Aktion nicht berechtigt.',
              'not-found': 'Dieses Objekt existiert nicht mehr.',
              generic: 'Die Texte konnten nicht gespeichert werden. Bitte versuche es erneut.',
            },
          },
          images: {
            'section-title': 'Bilder für die Vermarktung',
            explanation: 'Nur Ausgewähltes erscheint im Exposé. Rundgänge und Video-Links gehen nicht an Portale.',
            areas: {
              photos: 'Fotos',
              'floor-plans': 'Grundrisse',
              videos: 'Videos',
              tours: '360°-Rundgänge',
            },
            counter: {
              photos: '{k} von {n} Fotos werden veröffentlicht.',
              'floor-plans': '{k} von {n} Grundrissen werden veröffentlicht.',
              videos: '{k} von {n} Videos werden veröffentlicht.',
              tours: '{k} von {n} Rundgängen werden veröffentlicht.',
            },
            'tours-permission-reason': 'Du bist nicht berechtigt, Rundgänge zu veröffentlichen.',
            'publish-label': 'veröffentlichen',
            'publish-all': 'Alle veröffentlichen',
            'cover-image-badge': 'Titelbild',
            empty: 'Noch kein Bild für die Vermarktung ausgewählt.',
            errors: {
              generic: 'Die Änderung konnte nicht gespeichert werden. Bitte versuche es erneut.',
            },
          },
        },
      },
      'edit-address': {
        title: 'Anschrift bearbeiten',
        'search-label': 'Adresse suchen',
        'search-helper': 'Tippe eine Adresse und wähle einen Vorschlag, um die Felder zu füllen.',
        'search-placeholder': 'Straße, Hausnummer, Ort …',
        'street-label': 'Straße',
        'street-placeholder': 'z. B. Eppendorfer Landstraße',
        'house-number-label': 'Hausnummer',
        'house-number-placeholder': 'z. B. 12a',
        'postal-code-label': 'PLZ',
        'postal-code-placeholder': 'z. B. 20249',
        'city-label': ADDRESS_FIELD_LABELS.city,
        'city-placeholder': 'z. B. Hamburg',
        'district-label': ADDRESS_FIELD_LABELS.district,
        'district-placeholder': 'z. B. Eppendorf',
        'state-label': ADDRESS_FIELD_LABELS.state,
        'state-placeholder': 'z. B. Hamburg',
        'country-label': 'Land',
        'country-placeholder': 'z. B. Deutschland',
        'parcel-number-label': 'Flurstück',
        'parcel-number-placeholder': 'optional',
        'parcel-subdivision-label': 'Flur',
        'parcel-subdivision-placeholder': 'optional',
        'latitude-label': 'Breitengrad',
        'latitude-placeholder': 'z. B. 53,5896',
        'longitude-label': 'Längengrad',
        'longitude-placeholder': 'z. B. 9,9876',
        'coordinate-helper': 'Wird automatisch aus der Adresssuche gefüllt.',
        'submit-cta': 'Speichern',
        'submit-loading': 'Wird gespeichert…',
        'cancel-cta': 'Abbrechen',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'not-found-error': 'Dieses Objekt existiert nicht mehr.',
        'generic-error': 'Anschrift konnte nicht gespeichert werden. Bitte versuche es erneut.',
        errors: {
          street: { required: 'Bitte gib eine Straße ein.', invalid: 'Ungültige Straße.' },
          'house-number': { required: 'Bitte gib eine Hausnummer ein.', invalid: 'Ungültige Hausnummer.' },
          'postal-code': { required: 'Bitte gib eine PLZ ein.', invalid: 'PLZ muss aus 5 Ziffern bestehen.' },
          city: { required: 'Bitte gib einen Ort ein.', invalid: 'Ungültiger Ort.' },
          // Wording follows the shared label (Stadtteil), but the message is a
          // sentence, not the label constant itself (IMM-614.2).
          district: { required: 'Bitte gib einen Stadtteil ein.', invalid: 'Ungültiger Stadtteil.' },
          state: { required: 'Bitte gib ein Bundesland ein.', invalid: 'Ungültiges Bundesland.' },
          country: { required: 'Bitte gib ein Land ein.', invalid: 'Ungültiges Land.' },
          'parcel-number': { required: 'Bitte gib ein Flurstück ein.', invalid: 'Ungültiges Flurstück.' },
          'parcel-subdivision': { required: 'Bitte gib eine Flur ein.', invalid: 'Ungültige Flur.' },
          latitude: {
            required: 'Bitte gib einen Breitengrad ein.',
            invalid: 'Breitengrad muss zwischen -90 und 90 liegen.',
          },
          longitude: {
            required: 'Bitte gib einen Längengrad ein.',
            invalid: 'Längengrad muss zwischen -180 und 180 liegen.',
          },
        },
      },
      'edit-identity': {
        title: 'Eckdaten bearbeiten',
        'title-label': 'Titel',
        'title-placeholder': 'z. B. Schöne Altbauwohnung in Eppendorf',
        'object-type-label': 'Immobilientyp',
        'marketing-type-label': 'Vermarktungsart',
        'marketing-type-option': MARKETING_TYPE_LABELS,
        'subtype-label': 'Untertyp',
        'subtype-unset': 'Nicht festgelegt',
        'fine-type-label': 'Feintyp',
        'fine-type-unset': '—',
        'reference-number-label': 'Exposé-Nr.',
        'reference-number-placeholder': 'z. B. EXP-2026-001',
        'reference-number-helper': 'Optional. Muss innerhalb des Workspace eindeutig sein.',
        'available-from-label': 'Verfügbar ab',
        'submit-cta': 'Speichern',
        'submit-loading': 'Wird gespeichert…',
        'cancel-cta': 'Abbrechen',
        'edit-cta': 'Bearbeiten',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'not-found-error': 'Dieses Objekt existiert nicht mehr.',
        'generic-error': 'Eckdaten konnten nicht gespeichert werden. Bitte versuche es erneut.',
        errors: {
          title: {
            required: 'Bitte gib einen Titel ein.',
            'too-long': 'Der Titel darf höchstens 160 Zeichen lang sein.',
          },
          'object-type': {
            locked: 'Der Objekttyp kann nur im Entwurf geändert werden.',
            blocked: 'Entferne zuerst die typspezifischen Daten ({sections}), um den Objekttyp zu ändern.',
            section: {
              ertrag: 'Ertrag',
              grundstueck: 'Grundstück',
              'ausstattung-wohnen': 'Ausstattung (Wohnen)',
              'ausstattung-gewerbe': 'Ausstattung (Gewerbe)',
            },
          },
          'reference-number': {
            'too-long': 'Die Exposé-Nr. darf höchstens 64 Zeichen lang sein.',
            invalid: 'Exposé-Nr. ist ungültig.',
            conflict: 'Diese Exposé-Nr. wird bereits verwendet — bitte wähle eine eindeutige Nummer.',
          },
        },
        'marketing-type-switch': {
          title: 'Vermarktungsart ändern?',
          body: 'Die Unterlagen-Checkliste und die Preisfelder wechseln mit. Bereits erfasste Angaben bleiben erhalten.',
          'back-cta': 'Zurück',
          'confirm-cta': {
            SALE: 'Auf Verkauf umstellen',
            RENTAL: 'Auf Vermietung umstellen',
          },
        },
      },
      'edit-building': {
        title: 'Gebäudedaten bearbeiten',
        'year-built-label': 'Baujahr',
        'year-built-placeholder': '1912',
        'last-renovated-label': 'Letzte Sanierung',
        'last-renovated-placeholder': '2018',
        'condition-label': 'Zustand',
        'interior-quality-label': 'Ausstattungsqualität',
        'construction-phase-label': 'Bauphase',
        'submit-cta': 'Speichern',
        'submit-loading': 'Wird gespeichert…',
        'cancel-cta': 'Abbrechen',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'not-found-error': 'Dieses Objekt existiert nicht mehr.',
        'generic-error': 'Gebäudedaten konnten nicht gespeichert werden. Bitte versuche es erneut.',
        condition: CONDITION_LABELS,
        'interior-quality': INTERIOR_QUALITY_LABELS,
        'construction-phase': CONSTRUCTION_PHASE_LABELS,
        errors: {
          'year-built': {
            required: 'Bitte gib das Baujahr ein.',
            invalid: 'Baujahr muss zwischen 1700 und heute + 5 Jahren liegen.',
          },
          'last-renovated': {
            required: 'Bitte gib das Jahr der letzten Sanierung ein.',
            invalid: 'Sanierungsjahr muss zwischen 1700 und heute liegen.',
          },
          condition: {
            required: 'Bitte wähle einen Zustand.',
            invalid: 'Ungültiger Zustand.',
          },
          'interior-quality': {
            required: 'Bitte wähle eine Ausstattungsqualität.',
            invalid: 'Ungültige Ausstattungsqualität.',
          },
          'construction-phase': {
            required: 'Bitte wähle eine Bauphase.',
            invalid: 'Ungültige Bauphase.',
          },
        },
      },
      'edit-pricing-sale': {
        title: 'Preis (Kauf) bearbeiten',
        'purchase-price-label': 'Kaufpreis',
        'price-per-sqm-label': 'Preis pro m²',
        'price-per-sqm-needs-area': 'Wohnfläche eingeben für Berechnung',
        'price-on-inquiry-label': 'Preis auf Anfrage',
        'has-commission-label': 'Provision aktiv',
        'commission-group': 'Käuferprovision',
        'commission-legacy': 'Bisherige Angabe: {text}',
        'commission-basis-label': 'Angabe als',
        'commission-basis-placeholder': 'Bitte wählen',
        'commission-basis': {
          PERCENT_OF_PRICE: 'Prozent vom Kaufpreis',
          FIXED_AMOUNT: 'Fester Betrag',
        },
        'commission-rate-label': 'Satz (netto)',
        'commission-amount-label': 'Betrag (netto)',
        'commission-helper': 'Netto eingeben. immoOS rechnet 19 % USt. dazu, in der Anzeige steht der Bruttowert.',
        'commission-preview': 'So steht es in der Anzeige: {statement}',
        'commission-note-label': 'Provisionshinweis',
        'commission-note-placeholder': 'z. B. Vom Käufer zu zahlen, fällig bei Beurkundung.',
        'net-purchase-price-label': 'Nettokaufpreis',
        'vat-treatment-label': 'USt-Behandlung',
        'vat-rate-label': 'USt-Satz',
        'decimal-placeholder': '0,00',
        'submit-cta': 'Speichern',
        'submit-loading': 'Wird gespeichert…',
        'cancel-cta': 'Abbrechen',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'not-found-error': 'Dieses Objekt existiert nicht mehr.',
        'not-applicable-error': 'Kauf-Preise gibt es nur für Verkaufsobjekte.',
        'generic-error': 'Preise konnten nicht gespeichert werden. Bitte versuche es erneut.',
      },
      'edit-pricing-rental': {
        title: 'Preis (Miete) bearbeiten',
        'tenant-free': 'Für den Mieter provisionsfrei (Bestellerprinzip)',
        'commission-legacy':
          'Bisherige Angabe: {text}. Wird nicht mehr veröffentlicht: Bei Wohnraum zur Miete zahlt der Mieter keine Provision, wenn der Vermieter dich beauftragt hat.',
        'has-commission-label': 'Provision',
        'commission-label': 'Provisionshöhe',
        'commission-placeholder': 'z. B. 2 Nettokaltmieten zzgl. USt.',
        'commission-note-label': 'Provisionshinweis',
        'commission-note-placeholder': 'z. B. Fällig bei Abschluss des Mietvertrags.',
        'base-rent-label': 'Kaltmiete',
        'price-per-sqm-label': 'Miete pro m²/Monat',
        'price-per-sqm-needs-area': 'Fläche eingeben für Berechnung',
        'total-rent-label': 'Gesamtmiete',
        'ancillary-costs-label': 'Nebenkosten',
        'ancillary-costs-type-label': 'Nebenkosten-Art',
        'ancillary-costs-type-placeholder': 'Bitte wählen',
        'ancillary-costs-type': ANCILLARY_COSTS_TYPE_LABELS,
        'heating-costs-label': 'Heizkosten',
        'heating-costs-included-label': 'Heizkosten in den Nebenkosten enthalten',
        'heating-costs-included-error': 'Heizkosten sind in den Nebenkosten enthalten — lass das Feld leer.',
        'total-rent-hint': 'Vorschlag aus Kaltmiete + Nebenkosten + Heizkosten',
        'total-rent-divergence': 'Weicht von der Summe ab ({difference}).',
        'deposit-mode-label': 'Kaution',
        'deposit-mode': { AMOUNT: 'in Euro', MONTHS: 'in Monatsmieten' },
        'deposit-equivalent-amount': 'entspricht {amount}',
        'deposit-equivalent-months':
          '{count, plural, one {entspricht # Monatsmiete} other {entspricht # Monatsmieten}}',
        'deposit-months-max-error': 'Höchstens 24 Monatsmieten.',
        'deposit-cap-warning':
          'Bei Wohnraum sind höchstens drei Monatskaltmieten als Kaution zulässig (§ 551 BGB). Die Angabe wird gespeichert.',
        'legacy-deposit-label': 'Kaution (Altbestand)',
        'parking-rent-label': 'Stellplatzmiete',
        'parking-rent-hint': 'nicht Teil der Kaltmiete',
        'vat-treatment-label': 'USt-Behandlung',
        'vat-rate-label': 'USt-Satz',
        'derivation-hint': '{rate} × {area} m² = {total} Kaltmiete',
        'compensation-payment-label': 'Abstandszahlung',
        'rent-free-months-label': 'Mietfreie Zeit',
        'rent-free-months-suffix': 'Monate',
        'fit-out-contribution-label': 'Baukostenzuschuss',
        'renewal-option-label': 'Verlängerungsoption',
        'renewal-option-placeholder': 'z. B. 2 × 5 Jahre',
        'price-on-inquiry-label': 'Preis auf Anfrage',
        'decimal-placeholder': '0,00',
        'submit-cta': 'Speichern',
        'submit-loading': 'Wird gespeichert…',
        'cancel-cta': 'Abbrechen',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'not-found-error': 'Dieses Objekt existiert nicht mehr.',
        'generic-error': 'Preise konnten nicht gespeichert werden. Bitte versuche es erneut.',
      },
      'edit-rental-terms': {
        title: 'Mietkonditionen bearbeiten',
        'furnishing-label': 'Möblierung',
        'lease-term-label': 'Mietdauer',
        'fixed-term-until-label': 'befristet bis',
        'minimum-term-label': 'Mindestmietdauer (Monate)',
        'rent-type-label': 'Mietart',
        'subletting-label': 'Untervermietung',
        'unset-option': 'Keine Angabe',
        furnishing: FURNISHING_LABELS,
        'lease-term': LEASE_TERM_LABELS,
        'rent-type': RENT_TYPE_LABELS,
        subletting: SUBLETTING_LABELS,
        'fixed-term-until-required-error': 'Gib an, bis wann die Miete befristet ist.',
        'fixed-term-until-past-error': 'Das Datum darf nicht in der Vergangenheit liegen.',
        'minimum-term-max-error': 'Höchstens 600 Monate.',
        'not-applicable-error': 'Dieses Objekt wird nicht vermietet — Mietkonditionen lassen sich hier nicht erfassen.',
        'submit-cta': 'Speichern',
        'submit-loading': 'Wird gespeichert…',
        'cancel-cta': 'Abbrechen',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'not-found-error': 'Dieses Objekt existiert nicht mehr.',
        'generic-error': 'Mietkonditionen konnten nicht gespeichert werden. Bitte versuche es erneut.',
      },
      'edit-additional-costs': {
        title: 'Nebenkosten bearbeiten',
        'type-column': 'Position',
        'amount-column': 'Betrag',
        'per-sqm-column': 'pro m²/Monat',
        'vat-treatment-column': 'USt',
        'note-column': 'Hinweis',
        'type-placeholder': 'Position wählen…',
        'amount-placeholder': '0,00',
        'per-sqm-placeholder': 'optional',
        'note-placeholder': 'optional',
        'add-row-cta': 'Eintrag hinzufügen',
        'remove-row-cta': 'Eintrag entfernen',
        'empty-state': 'Noch keine Einträge — füge einen hinzu.',
        'submit-cta': 'Speichern',
        'submit-loading': 'Wird gespeichert…',
        'cancel-cta': 'Abbrechen',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'not-found-error': 'Dieses Objekt existiert nicht mehr.',
        'generic-error': 'Nebenkosten konnten nicht gespeichert werden. Bitte versuche es erneut.',
        type: ADDITIONAL_COST_TYPE_LABELS,
      },
      'edit-energy': {
        title: 'Energiedaten bearbeiten',
        'certificate-basis-label': 'Energieausweis-Typ',
        'certificate-year-label': 'Energieausweis-Jahrgang',
        'energy-class-label': 'Energieeffizienzklasse',
        'energy-value-label': 'Energiekennwert',
        'energy-value-placeholder': '87,5',
        'primary-energy-value-label': 'Primärenergiebedarf',
        'primary-energy-value-placeholder': '84,3',
        'energy-class-non-residential-helper': 'Nichtwohngebäude: Skala A bis G',
        'energy-value-unit': 'kWh/(m²·a)',
        'includes-warm-water-label': 'Warmwasser im Verbrauch enthalten',
        'no-certificate-hint': 'Die Ausweisdaten werden beim Speichern entfernt.',
        'certificate-issued-at-label': 'Ausstelldatum',
        'certificate-valid-until-label': 'Gültig bis',
        'heating-type-label': 'Heizungsart',
        'firing-type-label': 'Befeuerungsart',
        'heating-type-helper': 'Mehrfachauswahl möglich.',
        'firing-type-helper': 'Mehrfachauswahl möglich.',
        'submit-cta': 'Speichern',
        'submit-loading': 'Wird gespeichert…',
        'cancel-cta': 'Abbrechen',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'not-found-error': 'Dieses Objekt existiert nicht mehr.',
        'generic-error': 'Energiedaten konnten nicht gespeichert werden. Bitte versuche es erneut.',
        'certificate-basis': ENERGY_CERTIFICATE_BASIS_LABELS,
        'certificate-year': ENERGY_CERTIFICATE_YEAR_LABELS,
        'energy-class': ENERGY_CLASS_LABELS,
        'heating-type': HEATING_TYPE_LABELS,
        'firing-type': FIRING_TYPE_LABELS,
        errors: {
          'certificate-basis': {
            required: 'Bitte wähle einen Energieausweis-Typ.',
            invalid: 'Ungültiger Energieausweis-Typ.',
          },
          'certificate-year': {
            required: 'Bitte wähle einen Energieausweis-Jahrgang.',
            invalid: 'Ungültiger Energieausweis-Jahrgang.',
          },
          'energy-class': {
            required: 'Bitte wähle eine Energieeffizienzklasse.',
            invalid: 'Ungültige Energieeffizienzklasse.',
          },
          'energy-value': {
            required: 'Bitte gib den Energiekennwert ein.',
            invalid: 'Energiekennwert ist ungültig.',
          },
          'primary-energy-value': {
            required: 'Bitte gib den Primärenergiebedarf ein.',
            invalid: 'Primärenergiebedarf ist ungültig.',
          },
          'certificate-issued-at': {
            required: 'Bitte gib das Ausstelldatum ein.',
            invalid: 'Ausstelldatum ist ungültig.',
          },
          'certificate-valid-until': {
            required: 'Bitte gib das Gültigkeitsdatum ein.',
            invalid: 'Gültigkeitsdatum ist ungültig.',
          },
        },
      },
      'edit-investment-yield': {
        title: 'Ertragsdaten bearbeiten',
        'is-rented-label': 'Vermietet',
        'rental-income-label': 'Mieteinnahmen',
        'rental-income-target-label': 'Soll-Mieteinnahmen',
        'rental-income-actual-label': 'Ist-Mieteinnahmen',
        'price-multiplier-label': 'Faktor',
        'number-of-apartments-label': 'Anzahl Wohnungen',
        'number-of-commercial-units-label': 'Anzahl Gewerbeeinheiten',
        'occupancy-rate-label': 'Vermietungsquote',
        'number-of-vacancies-label': 'Anzahl Leerstände',
        'tenant-structure-label': 'Mieterstruktur',
        'tenant-structure-placeholder': 'z. B. 8 Wohn-, 2 Gewerbemieter',
        'walt-label': 'WALT',
        'walt-unit': 'Jahre',
        'occupancy-rate-unit': '%',
        'currency-unit': '€',
        'integer-placeholder': '0',
        'decimal-placeholder': '0,00',
        'submit-cta': 'Speichern',
        'submit-loading': 'Wird gespeichert…',
        'cancel-cta': 'Abbrechen',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'not-found-error': 'Dieses Objekt existiert nicht mehr.',
        'generic-error': 'Ertragsdaten konnten nicht gespeichert werden. Bitte versuche es erneut.',
        errors: {
          'rental-income': {
            required: 'Bitte gib die Mieteinnahmen ein.',
            invalid: 'Mieteinnahmen sind ungültig.',
          },
          'rental-income-target': {
            required: 'Bitte gib die Soll-Mieteinnahmen ein.',
            invalid: 'Soll-Mieteinnahmen sind ungültig.',
          },
          'rental-income-actual': {
            required: 'Bitte gib die Ist-Mieteinnahmen ein.',
            invalid: 'Ist-Mieteinnahmen sind ungültig.',
          },
          'price-multiplier': {
            required: 'Bitte gib den Faktor ein.',
            invalid: 'Faktor ist ungültig.',
          },
          'number-of-apartments': {
            required: 'Bitte gib die Anzahl der Wohnungen ein.',
            invalid: 'Anzahl Wohnungen muss eine ganze Zahl sein.',
          },
          'number-of-commercial-units': {
            required: 'Bitte gib die Anzahl der Gewerbeeinheiten ein.',
            invalid: 'Anzahl Gewerbeeinheiten muss eine ganze Zahl sein.',
          },
          'occupancy-rate': {
            required: 'Bitte gib die Vermietungsquote ein.',
            invalid: 'Vermietungsquote ist ungültig.',
          },
          'number-of-vacancies': {
            required: 'Bitte gib die Anzahl der Leerstände ein.',
            invalid: 'Anzahl Leerstände muss eine ganze Zahl sein.',
          },
          'tenant-structure': {
            required: 'Bitte beschreibe die Mieterstruktur.',
            invalid: 'Mieterstruktur ist ungültig.',
          },
          walt: {
            required: 'Bitte gib den WALT ein.',
            invalid: 'WALT ist ungültig.',
          },
        },
      },
      'edit-plot-details': {
        title: 'Grundstücksdaten bearbeiten',
        'ground-area-ratio-label': 'GRZ (Grundflächenzahl)',
        'ground-area-ratio-placeholder': '0,4',
        'floor-area-ratio-label': 'GFZ (Geschossflächenzahl)',
        'floor-area-ratio-placeholder': '1,2',
        'building-mass-ratio-label': 'BMZ (Baumassenzahl)',
        'building-mass-ratio-placeholder': '3,5',
        'has-building-permission-label': 'Baugenehmigung liegt vor',
        'short-term-buildable-label': 'Kurzfristig bebaubar',
        'requires-demolition-label': 'Abriss erforderlich',
        'site-development-type-label': 'Erschließungsart',
        'site-constructible-type-label': 'Bebauungsart',
        'submit-cta': 'Speichern',
        'submit-loading': 'Wird gespeichert…',
        'cancel-cta': 'Abbrechen',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'not-found-error': 'Dieses Objekt existiert nicht mehr.',
        'generic-error': 'Grundstücksdaten konnten nicht gespeichert werden. Bitte versuche es erneut.',
        'site-development-type': SITE_DEVELOPMENT_TYPE_LABELS,
        'site-constructible-type': SITE_CONSTRUCTIBLE_TYPE_LABELS,
        errors: {
          'ground-area-ratio': {
            required: 'Bitte gib die GRZ ein.',
            invalid: 'GRZ ist ungültig.',
          },
          'floor-area-ratio': {
            required: 'Bitte gib die GFZ ein.',
            invalid: 'GFZ ist ungültig.',
          },
          'building-mass-ratio': {
            required: 'Bitte gib die BMZ ein.',
            invalid: 'BMZ ist ungültig.',
          },
          'site-development-type': {
            required: 'Bitte wähle eine Erschließungsart.',
            invalid: 'Ungültige Erschließungsart.',
          },
          'site-constructible-type': {
            required: 'Bitte wähle eine Bebauungsart.',
            invalid: 'Ungültige Bebauungsart.',
          },
        },
      },
      'edit-amenities': {
        title: 'Ausstattung bearbeiten',
        'feature-column': 'Merkmal',
        'value-column': 'Wert',
        'amenity-code-placeholder': 'Merkmal wählen…',
        'value-placeholder': 'Wert eingeben…',
        'group-residential': 'Wohnen',
        'group-commercial': 'Gewerbe',
        'add-row-cta': 'Eintrag hinzufügen',
        'remove-row-cta': 'Eintrag entfernen',
        'empty-state': 'Noch keine Einträge — füge einen hinzu.',
        'submit-cta': 'Speichern',
        'submit-loading': 'Wird gespeichert…',
        'cancel-cta': 'Abbrechen',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'not-found-error': 'Dieses Objekt existiert nicht mehr.',
        'generic-error': 'Ausstattung konnte nicht gespeichert werden. Bitte versuche es erneut.',
        residential: RESIDENTIAL_AMENITY_LABELS,
        commercial: COMMERCIAL_AMENITY_LABELS,
      },
      'edit-areas': {
        title: 'Flächen bearbeiten',
        'code-column-header': 'Fläche',
        'value-column-header': 'Wert',
        'add-row-cta': 'Eintrag hinzufügen',
        'remove-row-cta': 'Eintrag entfernen',
        'code-placeholder': 'Fläche auswählen…',
        'value-placeholder': '0',
        'empty-state': 'Noch keine Einträge — füge einen hinzu.',
        'submit-cta': 'Speichern',
        'submit-loading': 'Wird gespeichert…',
        'cancel-cta': 'Abbrechen',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'not-found-error': 'Dieses Objekt existiert nicht mehr.',
        'generic-error': 'Flächen konnten nicht gespeichert werden. Bitte versuche es erneut.',
        'unit-sqm': 'm²',
        'unit-cbm': 'm³',
        'use-split-remainder-label': 'weitere Fläche',
        'use-split-exceeded-label': 'Summe der Teilflächen',
        'use-split-exceeded-value': '{sum} m² von {usableArea} m²',
        'use-split-exceeded-message':
          'Die Teilflächen übersteigen die Nutzfläche. Reduziere einen Teil oder erhöhe die Nutzfläche, um zu speichern.',
        code: AREA_CODE_LABELS,
      },
      create: {
        title: 'Neues Objekt anlegen',
        'standort-label': 'Standort',
        'title-label': 'Objekt-Titel',
        'title-placeholder': 'z. B. Gründerzeit-Etage Lehel',
        'title-helper': 'Wird in Tabellen, Exposé und Suche verwendet',
        'type-label': 'Immobilientyp',
        'status-label': 'Status',
        'status-helper': 'Wähle „In Akquise" für ein Objekt aus einem Lead-Kontakt.',
        'status-option': {
          DRAFT: 'Entwurf',
          IN_ACQUISITION: 'In Akquise',
        },
        'marketing-type-label': 'Vermarktungsart',
        'marketing-type-option': MARKETING_TYPE_LABELS,
        'reference-number-label': 'Exposé-Nr.',
        'reference-number-placeholder': 'z. B. EXP-2026-001',
        'living-area-label': 'Wohnfläche',
        'living-area-placeholder': '176',
        'living-area-unit': 'm²',
        'rooms-label': 'Zimmer',
        'rooms-placeholder': '5',
        'rooms-unit': 'Zi.',
        'usable-area-label': 'Nutzfläche',
        'usable-area-placeholder': '1.550',
        'usable-area-unit': 'm²',
        'parking-spaces-label': 'Stellplätze',
        'parking-spaces-placeholder': '24',
        'parking-spaces-unit': 'Stpl.',
        'asking-price-label': 'Asking-Preis',
        'asking-price-placeholder': '2.180.000',
        'asking-price-unit': '€',
        'owner-label': 'Eigentümer',
        'owner-placeholder': 'Person auswählen…',
        'owner-loading': 'Kontakte werden geladen…',
        'owner-no-results': 'Keine passenden Kontakte gefunden.',
        'owner-load-error': 'Kontakte konnten nicht geladen werden.',
        'owner-clear': 'Auswahl löschen',
        'broker-team-label': 'Makler-Team',
        'broker-team-placeholder': 'Personen auswählen…',
        'tags-label': 'Tags',
        'tags-placeholder': 'Tags hinzufügen…',
        'coming-soon': 'Demnächst',
        'street-house-label': 'Straße & Hausnummer',
        'street-house-placeholder': 'Liebigstraße 9',
        'postal-code-label': 'PLZ',
        'postal-code-placeholder': '80538',
        'city-label': 'Ort',
        'city-placeholder': 'München',
        'submit-cta': 'Anlegen',
        'submit-loading': 'Wird angelegt…',
        'cancel-cta': 'Abbrechen',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'not-a-member-error': 'Du gehörst diesem Workspace nicht an.',
        'generic-error': 'Objekt konnte nicht angelegt werden. Bitte versuche es erneut.',
        errors: {
          title: {
            required: 'Titel ist erforderlich.',
            'too-long': 'Der Titel darf höchstens 160 Zeichen lang sein.',
            invalid: 'Titel ist ungültig.',
          },
          type: {
            required: 'Bitte wähle einen Objekttyp.',
            invalid: 'Ungültiger Objekttyp.',
          },
          'marketing-type': {
            required: 'Bitte wähle eine Vermarktungsart.',
            invalid: 'Ungültige Vermarktungsart.',
          },
          'street-house': {
            required: 'Bitte gib Straße und Hausnummer ein.',
            'too-long': 'Straße und Hausnummer sind zu lang.',
            invalid: 'Straße und Hausnummer sind ungültig.',
          },
          'postal-code': {
            required: 'Bitte gib eine PLZ ein.',
            invalid: 'PLZ muss aus 5 Ziffern bestehen.',
          },
          city: {
            required: 'Bitte gib einen Ort ein.',
            'too-long': 'Der Ort darf höchstens 80 Zeichen lang sein.',
            invalid: 'Ort ist ungültig.',
          },
          'living-area-sqm': {
            required: 'Bitte gib die Wohnfläche ein.',
            'too-long': 'Wohnfläche ist zu groß.',
            invalid: 'Wohnfläche ist ungültig.',
          },
          rooms: {
            required: 'Bitte gib die Zimmeranzahl ein.',
            'too-long': 'Zimmeranzahl ist zu hoch.',
            invalid: 'Zimmeranzahl ist ungültig.',
          },
          'asking-price': {
            required: 'Bitte gib einen Preis ein.',
            'too-long': 'Preis ist zu hoch.',
            invalid: 'Preis ist ungültig.',
          },
          'reference-number': {
            'too-long': 'Die Exposé-Nr. darf höchstens 64 Zeichen lang sein.',
            invalid: 'Exposé-Nr. ist ungültig.',
            conflict: 'Diese Exposé-Nr. wird bereits verwendet — bitte wähle eine eindeutige Nummer.',
          },
        },
      },
    },
    contacts: {
      type: CONTACT_TYPE_LABELS,
      source: CONTACT_SOURCE_LABELS,
      'contact-type': {
        PERSON: 'Person',
        COMPANY: 'Firma',
      },
      salutation: {
        MR: 'Herr',
        MRS: 'Frau',
        MX: 'Divers',
        NONE: 'Ohne Anrede',
      },
      'phone-type': {
        MOBILE: 'Mobil',
        OFFICE: 'Büro',
        LANDLINE: 'Festnetz',
        OTHER: 'Sonstige',
      },
      'phone-messaging': {
        WHATSAPP: 'WhatsApp',
        SMS: 'SMS',
        NONE: 'Keine',
      },
      list: {
        title: 'Kontakte',
        subtitle: 'Alle Kontakte und Firmen, mit denen dein Workspace arbeitet.',
        'create-cta': 'Neuer Kontakt',
        'list-load-error': 'Kontakte konnten nicht geladen werden.',
        'list-retry': 'Erneut versuchen',
        'empty-title': 'Noch keine Kontakte angelegt.',
        'empty-description': 'Lege deinen ersten Kontakt an, um loszulegen.',
        'no-match-title': 'Keine Treffer',
        'no-match-description': 'Passe Suche oder Filter an, um Kontakte zu finden.',
        'success-toast': 'Kontakt „{name}" angelegt.',
        'success-dismiss': 'Schließen',
        filters: {
          'search-placeholder': 'Nach Name, E-Mail, Telefon suchen…',
          'search-label': 'Kontakte suchen',
          'type-all': 'Alle Typen',
          reset: 'Zurücksetzen',
        },
        sort: {
          label: 'Sortieren',
          newest: 'Neueste zuerst',
          oldest: 'Älteste zuerst',
          'name-asc': 'Name A → Z',
          'name-desc': 'Name Z → A',
        },
        pagination: {
          range: '{from}–{to} von {total}',
          'per-page': 'Pro Seite:',
          prev: 'Zurück',
          next: 'Weiter',
        },
        columns: {
          name: 'Name',
          type: 'Typ',
          email: 'E-Mail',
          phone: 'Telefon',
          city: 'Ort',
          'updated-at': 'Aktualisiert',
        },
        empty: '—',
      },
      detail: {
        'warning-label': 'Hinweis',
        'delete-cta': 'Endgültig löschen',
        actions: {
          'trigger-label': 'Aktionen',
          'send-email': 'E-Mail senden',
          'send-message': 'Nachricht senden',
          delete: 'Endgültig löschen',
          edit: 'Bearbeiten',
          archive: 'Archivieren',
          'copy-link': 'Link kopieren',
          'link-copied': 'Link kopiert',
          'delete-permission-reason': 'Du bist nicht berechtigt, Kontakte zu löschen.',
          'reassign-permission-reason': 'Du bist nicht berechtigt, Kontakte einem anderen Standort zuzuordnen.',
        },
        stats: {
          'linked-properties-label': 'Immobilien',
        },
        'notes-empty': 'Noch keine Notizen.',
        'id-prefix': 'ID',
        'coming-soon': 'Bald verfügbar',
        'tab-empty': '„{tab}" ist bald verfügbar.',
        'tasks-empty': 'Noch keine Aufgaben.',
        'tasks-open': '· {count} offen',
        timeline: {
          'company-load-error': 'Die E-Mails der Firmenpostfächer konnten nicht geladen werden.',
          'mode-label': 'Zeitpunkt',
          'mode-log': 'Jetzt festhalten',
          'mode-plan': 'Für später planen',
          'due-at-label': 'Fällig am',
          'error-due-at-required': 'Bitte gib ein Fälligkeitsdatum in der Zukunft an.',
          'assignee-label': 'Zuständig',
          'assignee-self': 'Ich',
          'plan-hint': 'Diese Aktivität erscheint als offene Aufgabe, bis du sie erledigst.',
          'plan-submit': 'Als Aufgabe planen',
          'add-cta': 'Aktivität hinzufügen',
          'contact-picker-label': 'Kontakt',
          'deal-picker-label': 'Verknüpfte Deals',
          'deal-picker-none': 'Kein Deal',
          'deal-unnamed': 'Deal ohne Objektbezeichnung',
          save: 'Speichern',
          saving: 'Speichern …',
          cancel: 'Abbrechen',
          'content-label': 'Inhalt',
          'content-counter': '{count, number} / {max, number}',
          'content-placeholder': 'Was ist passiert?',
          'occurred-at-label': 'Stattgefunden am',
          'now-cta': 'Jetzt',
          'occurred-at-time-label': 'Uhrzeit',
          'outcome-label': 'Ergebnis',
          'outcome-none': 'Kein Ergebnis',
          'outcome-reached': 'Erreicht',
          'outcome-not-reached': 'Nicht erreicht',
          'outcome-voicemail': 'Mailbox',
          'outcome-callback-agreed': 'Rückruf vereinbart',
          'outcome-email-reached': 'Antwort erhalten',
          'outcome-email-not-reached': 'Keine Antwort',
          'outcome-attended': 'Wahrgenommen',
          'outcome-not-attended': 'Nicht wahrgenommen',
          'outcome-rescheduled': 'Verschoben',
          'type-label': 'Typ',
          'type-note': 'Notiz',
          'type-appointment': 'Termin',
          'appointment-type-label': 'Termin-Art',
          'appointment-type-placeholder': 'Bitte wählen',
          'appointment-type-valuation': 'Bewertungstermin',
          'appointment-type-on-site': 'Vor-Ort-Termin',
          'appointment-type-property-survey': 'Objektaufnahme',
          'appointment-type-contract-signing': 'Vertragstermin',
          'appointment-type-other': 'Sonstiger Termin',
          'appointment-type-viewing-on-site': 'Besichtigung vor Ort',
          'appointment-type-viewing-virtual': 'Besichtigung virtuell',
          'appointment-type-consultation': 'Beratungsgespräch',
          'appointment-type-financing-consultation': 'Finanzierungsgespräch',
          'appointment-type-negotiation': 'Verhandlungsgespräch',
          'appointment-type-notary': 'Notartermin',
          'appointment-type-handover': 'Übergabe',
          'error-no-appointment-type': 'Bitte wähle eine Termin-Art.',
          'type-call': 'Anruf',
          'type-sms': 'SMS',
          'type-whatsapp': 'WhatsApp',
          'type-fax': 'Fax',
          'type-letter': 'Brief',
          'type-email': 'E-Mail',
          'direction-label': 'Richtung',
          'direction-inbound': 'Eingehend',
          'direction-outbound': 'Ausgehend',
          'subject-label': 'Betreff',
          'entry-note': 'Notiz',
          'entry-call-inbound': 'Eingehender Anruf',
          'entry-call-outbound': 'Ausgehender Anruf',
          'entry-sms-inbound': 'Eingehende SMS',
          'entry-sms-outbound': 'Ausgehende SMS',
          'entry-whatsapp-inbound': 'Eingehende WhatsApp',
          'entry-whatsapp-outbound': 'Ausgehende WhatsApp',
          'entry-fax-inbound': 'Eingehendes Fax',
          'entry-fax-outbound': 'Ausgehendes Fax',
          'entry-letter-inbound': 'Eingehender Brief',
          'entry-letter-outbound': 'Ausgehender Brief',
          'recorder-unknown': 'Unbekanntes Mitglied',
          'group-today': 'Heute',
          'group-yesterday': 'Gestern',
          'relative-just-now': 'Gerade eben',
          'relative-minutes': 'Vor {count} Minuten',
          'relative-hours': 'Vor {count} Stunden',
          'relative-yesterday': 'Gestern',
          'empty-heading': 'Noch keine Aktivitäten',
          'empty-body':
            'Hier erscheint alles, was mit diesem Kontakt passiert — Anrufe, Notizen, Nachrichten und mehr.',
          'empty-cta': 'Aktivität hinzufügen',
          'error-empty-content': 'Bitte gib einen Inhalt ein.',
          'error-content-too-long': 'Eine Aktivität darf höchstens 10.000 Zeichen lang sein.',
          'error-no-link': 'Eine Aktivität muss mit mindestens einem Kontakt, Deal oder Objekt verknüpft sein.',
          'error-no-direction': 'Bitte gib die Richtung an (eingehend oder ausgehend).',
          'error-subject-too-long': 'Der Betreff darf höchstens 200 Zeichen lang sein.',
          'error-future': 'Eine Aktivität kann nicht in der Zukunft liegen.',
          'error-occurred-at-incomplete': 'Bitte gib Datum und Uhrzeit gemeinsam an.',
          'save-error': 'Die Aktivität konnte nicht gespeichert werden.',
          loading: 'Aktivitäten werden geladen …',
          'load-error': 'Aktivitäten konnten nicht geladen werden.',
          'load-retry': 'Erneut versuchen',
          edited: 'bearbeitet',
          'edit-cta': 'Bearbeiten',
          'delete-cta': 'Löschen',
          'delete-dialog-title': 'Aktivität löschen?',
          'delete-dialog-body': 'Diese Aktion kann nicht rückgängig gemacht werden.',
          'delete-confirm': 'Löschen',
          'delete-cancel': 'Abbrechen',
          'delete-error': 'Die Aktivität konnte nicht gelöscht werden.',
          'update-error': 'Die Aktivität konnte nicht gespeichert werden.',
        },
        tabs: {
          overview: 'Übersicht',
          activity: 'Aktivität',
          contact: 'Kontakt',
          emails: 'E-Mails',
          calls: 'Anrufe',
          notes: 'Notizen',
          appointments: 'Termine',
          properties: 'Objekte',
          firmen: 'Firmen',
        },
        sections: {
          identity: 'Identität',
          types: 'Typen',
          emails: 'E-Mail-Adressen',
          phones: 'Telefonnummern',
          address: 'Adresse',
          personal: 'Persönlich',
          notes: 'Notizen',
          crm: 'CRM',
          'key-facts': 'Eckdaten',
          activity: 'Aktivität',
          tasks: 'Aufgaben',
          details: 'Details',
          'linked-properties': 'Verknüpfte Objekte',
          categories: 'Kategorien',
        },
        properties: {
          'section-title': 'Immobilien',
          loading: 'Immobilien werden geladen…',
          empty: 'Noch keine Immobilien verknüpft.',
          'load-error': 'Immobilien konnten nicht geladen werden.',
          'load-retry': 'Erneut versuchen',
          'row-actions': 'Aktionen',
          'open-property': 'Immobilie öffnen',
          'add-cta': 'Immobilie verknüpfen',
          'edit-link': 'Verknüpfung bearbeiten',
          'unlink-link': 'Verknüpfung lösen',
          'archived-suffix': '(archiviert)',
          categories: {
            rentals: 'Vermietung',
            sales: 'Verkauf',
          },
          role: PROPERTY_CONTACT_ROLE_LABELS,
          'link-dialog': {
            title: 'Immobilie verknüpfen',
            'property-label': 'Immobilie',
            'property-placeholder': 'Nach Titel oder Adresse suchen…',
            'property-loading': 'Immobilien werden geladen…',
            'property-load-error': 'Immobilien konnten nicht geladen werden.',
            'property-no-results': 'Keine Immobilien gefunden.',
            'property-clear': 'Auswahl entfernen',
            'role-label': 'Rolle',
            'note-label': 'Notiz',
            'note-hint': 'Max. 500 Zeichen',
            'submit-cta': 'Verknüpfen',
            'submit-loading': 'Wird verknüpft…',
            'cancel-cta': 'Abbrechen',
            errors: {
              'property-required': 'Bitte wähle eine Immobilie aus.',
              'not-found': 'Kontakt oder Immobilie wurde nicht gefunden.',
              'already-linked':
                'Diese Verknüpfung besteht bereits. Du kannst die Notiz der bestehenden Verknüpfung bearbeiten.',
              'note-too-long': 'Notiz darf höchstens 500 Zeichen lang sein.',
              forbidden: 'Du bist für diese Aktion nicht berechtigt.',
              generic: 'Verknüpfung konnte nicht angelegt werden. Bitte versuche es erneut.',
            },
          },
          'edit-dialog': {
            title: 'Verknüpfung bearbeiten',
            'role-label': 'Rolle',
            'note-label': 'Notiz',
            'note-hint': 'Max. 500 Zeichen',
            'submit-cta': 'Speichern',
            'submit-loading': 'Wird gespeichert…',
            'cancel-cta': 'Abbrechen',
            errors: {
              'already-linked':
                'Diese Verknüpfung besteht bereits. Du kannst die Notiz der bestehenden Verknüpfung bearbeiten.',
              'note-too-long': 'Notiz darf höchstens 500 Zeichen lang sein.',
              'role-marketing-mismatch': 'Diese Rolle passt nicht zur Vermarktungsart der Immobilie.',
              'not-found': 'Diese Verknüpfung wurde nicht gefunden.',
              forbidden: 'Du bist für diese Aktion nicht berechtigt.',
              generic: 'Änderung konnte nicht gespeichert werden. Bitte versuche es erneut.',
            },
          },
          'unlink-dialog': {
            title: 'Verknüpfung lösen',
            body: 'Möchtest du die Verknüpfung mit „{name}“ wirklich lösen? Der Kontakt und die Immobilie bleiben erhalten.',
            'confirm-cta': 'Verknüpfung lösen',
            'confirm-loading': 'Wird gelöst…',
            'cancel-cta': 'Abbrechen',
            'error-forbidden': 'Du bist für diese Aktion nicht berechtigt.',
            error: 'Verknüpfung konnte nicht gelöst werden. Bitte versuche es erneut.',
          },
        },
        'companies-tab': {
          'section-title': 'Firmen',
          loading: 'Firmen werden geladen…',
          empty: 'Noch keine Firmen verknüpft.',
          'load-error': 'Firmen konnten nicht geladen werden.',
          'row-actions': 'Aktionen',
          'open-company': 'Firma öffnen',
          'add-cta': 'Firma verknüpfen',
          'edit-link': 'Verknüpfung bearbeiten',
          'unlink-link': 'Verknüpfung lösen',
          'archived-suffix': '(archiviert)',
          'primary-badge': 'primärer Arbeitgeber',
        },
        'edit-cta': 'Bearbeiten',
        'empty-state': {
          emails: 'Noch keine E-Mail-Adressen.',
          phones: 'Noch keine Telefonnummern.',
        },
        cards: {
          'source-label': 'Quelle',
          'primary-email-label': 'Primäre E-Mail',
          'primary-phone-label': 'Primäre Telefonnummer',
          'last-contacted-label': 'Letzter Kontakt',
          'last-contacted-empty': 'Noch nie',
          'types-label': 'Typen',
          'assigned-to-label': 'Verantwortlich',
        },
        details: {
          'type-label': 'Kontakttyp',
          'source-label': 'Quelle',
          'email-label': 'E-Mail',
          'phone-label': 'Telefon',
          'city-label': 'Ort',
          'last-contacted-label': 'Letzter Kontakt',
          'created-label': 'Erstellt',
          'updated-label': 'Aktualisiert',
        },
        'salutation-label': 'Anrede',
        'first-name-label': 'Vorname',
        'last-name-label': 'Nachname',
        'company-name-label': 'Firmenname',
        'types-label': 'Typen',
        'types-empty': 'Noch keine Typen zugewiesen.',
        'source-label': 'Quelle',
        'emails-label': 'E-Mail-Adressen',
        'phones-label': 'Telefonnummern',
        'primary-badge': 'Primär',
        'street-label': 'Straße',
        'postal-code-label': 'PLZ',
        'city-label': 'Ort',
        'country-label': 'Land',
        'date-of-birth-label': 'Geburtsdatum',
        'birth-name-label': 'Geburtsname',
        'birth-place-label': 'Geburtsort',
        'nationality-label': 'Nationalität',
        'language-label': 'Sprache',
        'last-contacted-at-label': 'Letzter Kontakt',
        empty: '—',
      },
      delete: {
        title: 'Diesen Kontakt endgültig löschen?',
        body: 'Diese Aktion kann nicht rückgängig gemacht werden. Die DSGVO-Löschung auf Antrag der betroffenen Person läuft separat über das DSGVO-Modul.',
        'confirm-cta': 'Endgültig löschen',
        'confirm-loading': 'Wird gelöscht…',
        'cancel-cta': 'Abbrechen',
        'generic-error': 'Kontakt konnte nicht gelöscht werden. Bitte versuche es erneut.',
      },
      'edit-personal': {
        title: 'Persönliche Angaben bearbeiten',
        'submit-cta': 'Speichern',
        'submit-loading': 'Speichern …',
        'cancel-cta': 'Abbrechen',
        'saved-toast': 'Persönliche Angaben gespeichert',
        errors: { 'too-long': 'Höchstens 80 Zeichen.' },
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'not-found-error': 'Kontakt wurde nicht gefunden.',
        'generic-error': 'Speichern fehlgeschlagen. Bitte versuche es erneut.',
      },
      'edit-types': {
        title: 'Typen bearbeiten',
        'types-label': 'Typen',
        helper: 'Wähle einen oder mehrere Typen für diesen Kontakt.',
        'submit-cta': 'Speichern',
        'submit-loading': 'Speichern …',
        'cancel-cta': 'Abbrechen',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'not-found-error': 'Kontakt wurde nicht gefunden.',
        'generic-error': 'Speichern fehlgeschlagen. Bitte versuche es erneut.',
      },
      'edit-emails': {
        title: 'E-Mail-Adressen bearbeiten',
        'empty-state': 'Noch keine E-Mail-Adressen. Klicke unten, um die erste hinzuzufügen.',
        'email-placeholder': 'name@beispiel.de',
        'primary-label': 'Primär',
        'add-row-cta': 'E-Mail hinzufügen',
        'remove-row-cta': 'Entfernen',
        'submit-cta': 'Speichern',
        'submit-loading': 'Speichern …',
        'cancel-cta': 'Abbrechen',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'not-found-error': 'Kontakt nicht gefunden.',
        'generic-error': 'Speichern fehlgeschlagen. Bitte versuche es erneut.',
        errors: {
          email: {
            required: 'E-Mail-Adresse ist erforderlich.',
            invalid: 'Bitte gib eine gültige E-Mail-Adresse ein.',
          },
        },
      },
      'edit-address': {
        title: 'Adresse bearbeiten',
        helper: 'Leere Straße, PLZ und Ort, um die Adresse zu entfernen.',
        'submit-cta': 'Speichern',
        'submit-loading': 'Speichern …',
        'cancel-cta': 'Abbrechen',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'not-found-error': 'Kontakt nicht gefunden.',
        'generic-error': 'Speichern fehlgeschlagen. Bitte versuche es erneut.',
        errors: {
          'postal-code': {
            invalid: 'PLZ muss aus genau fünf Ziffern bestehen.',
          },
        },
      },
      'edit-phones': {
        title: 'Telefonnummern bearbeiten',
        'empty-state': 'Noch keine Telefonnummern. Klicke unten, um die erste hinzuzufügen.',
        columns: {
          phone: 'Telefonnummer',
          type: 'Typ',
          messaging: 'Messaging',
          primary: 'Primär',
        },
        'phone-placeholder': '+49 151 12345678',
        'phone-helper': 'Bevorzugt im Format „+49 …" oder „0151 …".',
        'primary-label': 'Primär',
        'add-row-cta': 'Telefon hinzufügen',
        'remove-row-cta': 'Entfernen',
        'submit-cta': 'Speichern',
        'submit-loading': 'Speichern …',
        'cancel-cta': 'Abbrechen',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'not-found-error': 'Kontakt nicht gefunden.',
        'generic-error': 'Speichern fehlgeschlagen. Bitte versuche es erneut.',
        errors: {
          phone: {
            invalid: 'Telefonnummer ist ungültig.',
          },
        },
      },
      create: {
        title: 'Neuer Kontakt',
        'return-to': 'Danach geht es zurück zu „{dialog}“.',
        'standort-label': 'Standort',
        'contact-type-label': 'Kontakttyp',
        'salutation-label': 'Anrede',
        'salutation-empty': 'Ohne Anrede',
        'first-name-label': 'Vorname',
        'last-name-label': 'Nachname',
        'company-name-label': 'Firmenname',
        'email-label': 'Primäre E-Mail',
        'email-placeholder': 'name@beispiel.de',
        'email-helper': 'Optional. Weitere E-Mail-Adressen ergänzt du nach dem Anlegen.',
        'phone-label': 'Primäre Telefonnummer',
        'phone-placeholder': '+49 151 12345678',
        'phone-helper': 'Optional. Bevorzugt im Format „+49 …" oder „0151 …".',
        'phone-type-label': 'Typ',
        'address-helper': 'Optional. Die Adresse kannst du auch später ergänzen.',
        'source-label': 'Quelle',
        'types-label': 'Typen',
        'submit-cta': 'Speichern',
        'submit-loading': 'Speichern …',
        'cancel-cta': 'Abbrechen',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'not-a-member-error': 'Du gehörst diesem Workspace nicht an.',
        'generic-error': 'Kontakt konnte nicht angelegt werden. Bitte versuche es erneut.',
        errors: {
          'first-name': {
            required: 'Vorname ist erforderlich.',
            'too-long': 'Vorname ist zu lang.',
            invalid: 'Vorname ist ungültig.',
          },
          'last-name': {
            required: 'Nachname ist erforderlich.',
            'too-long': 'Nachname ist zu lang.',
            invalid: 'Nachname ist ungültig.',
          },
          'company-name': {
            required: 'Firmenname ist erforderlich.',
            'too-long': 'Firmenname ist zu lang.',
            invalid: 'Firmenname ist ungültig.',
          },
          email: {
            required: 'E-Mail-Adresse ist erforderlich.',
            invalid: 'Bitte gib eine gültige E-Mail-Adresse ein.',
            'too-long': 'E-Mail-Adresse ist zu lang.',
          },
          phone: {
            required: 'Telefonnummer ist erforderlich.',
            invalid: 'Telefonnummer ist ungültig.',
            'too-long': 'Telefonnummer ist zu lang.',
          },
          'postal-code': {
            invalid: 'PLZ muss aus genau fünf Ziffern bestehen.',
          },
        },
      },
    },
    companies: {
      'company-type': {
        PROJEKTENTWICKLER: 'Projektentwickler',
        IMMOBILIENMAKLER: 'Immobilienmakler',
        HAUSVERWALTUNG: 'Hausverwaltung',
        IMMOBILIENGUTACHTER: 'Immobiliengutachter',
        NOTARIAT: 'Notariat',
        BANK: 'Bank',
        VERSICHERUNG: 'Versicherung',
        HANDWERKER: 'Handwerker',
        ARCHITEKT: 'Architekt',
        SONSTIGE: 'Sonstige',
      },
      list: {
        title: 'Firmen',
        subtitle: 'Alle Firmen, mit denen dein Workspace zusammenarbeitet.',
        'create-cta': 'Firma erfassen',
        'list-load-error': 'Firmen konnten nicht geladen werden.',
        'empty-title': 'Noch keine Firmen angelegt.',
        'empty-description': 'Hier landen alle Firmen deines Workspaces',
        'no-match-title': 'Keine Treffer',
        'no-match-description': 'Passe Suche oder Filter an, um Firmen zu finden.',
        filters: {
          'search-placeholder': 'Suche nach Firma, Stadt, E-Mail',
          'search-label': 'Firmen suchen',
          'type-all': 'Alle Typen',
          reset: 'Zurücksetzen',
        },
        sort: {
          label: 'Sortieren',
          'name-asc': 'Name A → Z',
          'name-desc': 'Name Z → A',
          'city-asc': 'Stadt A → Z',
          'city-desc': 'Stadt Z → A',
          newest: 'Neueste zuerst',
          oldest: 'Älteste zuerst',
        },
        pagination: {
          range: '{from}–{to} von {total}',
          'per-page': 'Pro Seite:',
          prev: 'Zurück',
          next: 'Weiter',
        },
        columns: {
          name: 'Name',
          type: 'Typ',
          city: 'Stadt',
          phone: 'Primärer Telefon',
          'contact-count': 'Anzahl Kontakte',
        },
        empty: '—',
      },
      fields: {
        'sections.identity': 'Identität',
        'sections.tax-identity': 'Steuerliche Identität',
        'sections.address': 'Adresse',
        'sections.communication': 'Kommunikation',
        'sections.notes': 'Notizen',
        'sections.tags': 'Tags',
        'name-label': 'Firmenname',
        'type-label': 'Typ',
        'type-empty': 'Ohne Typ',
        'legal-form-label': 'Rechtsform',
        'register-number-label': 'Handelsregisternummer',
        'vat-id-label': 'USt-IdNr',
        'tax-number-label': 'Steuernummer',
        'street-label': 'Straße',
        'house-number-label': 'Hausnummer',
        'postal-code-label': 'PLZ',
        'city-label': 'Stadt',
        'country-label': 'Land',
        'phone-label': 'Telefon',
        'email-label': 'E-Mail',
        'email-placeholder': 'info@beispiel.de',
        'website-label': 'Website',
        'website-placeholder': 'https://…',
        'notes-label': 'Notizen',
        'tags-label': 'Tags',
        'tags-placeholder': 'Tag hinzufügen und Enter drücken',
        'tags-remove': '{label} entfernen',
        errors: {
          name: {
            required: 'Bitte gib einen Firmennamen ein.',
            'too-long': 'Firmenname darf höchstens 120 Zeichen lang sein.',
            invalid: 'Firmenname ist ungültig.',
          },
          email: {
            invalid: 'Bitte gib eine gültige E-Mail-Adresse ein.',
          },
          phone: {
            invalid: 'Telefonnummer ist ungültig.',
          },
          website: {
            invalid: 'Bitte gib eine gültige Website-Adresse ein.',
          },
          'postal-code': {
            invalid: 'Bitte gib eine gültige PLZ ein.',
          },
        },
      },
      'contact-link': {
        'section-header': 'Kontakt verknüpfen (optional)',
        helper:
          'Wenn du den ersten Ansprechpartner schon kennst, leg ihn gleich mit an. Firma und Verknüpfung werden in einem Schritt gespeichert.',
        expand: 'Kontakt verknüpfen (optional)',
        collapse: 'Kontakt-Block schließen',
        'mode-label': 'Kontakt',
        'mode-existing': 'Bestehender Kontakt',
        'mode-new': 'Neuer Kontakt',
        'contact-label': 'Kontakt',
        'contact-placeholder': 'Kontakt suchen …',
        'contact-clear': 'Auswahl entfernen',
        'contact-no-results': 'Keine passenden Kontakte.',
        'contact-searching': 'Wird gesucht …',
        'first-name-label': 'Vorname',
        'last-name-label': 'Nachname',
        'new-email-label': 'E-Mail',
        'new-email-placeholder': 'name@beispiel.de',
        'new-phone-label': 'Telefon',
        'role-label': 'Rolle',
        'role-placeholder': 'z. B. Geschäftsführer',
        'primary-employer-label': 'primärer Arbeitgeber',
        errors: {
          'contact-required': 'Bitte einen Kontakt auswählen oder Block schließen.',
          'name-required': 'Bitte Vor- und Nachnamen eingeben oder Block schließen.',
          'role-required': 'Bitte eine Rolle eingeben oder Block schließen.',
          'role-too-long': 'Rolle darf höchstens 200 Zeichen lang sein.',
        },
      },
      create: {
        title: 'Firma erfassen',
        'standort-label': 'Standort',
        'submit-cta': 'Speichern',
        'submit-loading': 'Speichern …',
        'cancel-cta': 'Abbrechen',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'not-a-member-error': 'Du gehörst diesem Workspace nicht an.',
        'generic-error': 'Firma konnte nicht angelegt werden. Bitte versuche es erneut.',
      },
      edit: {
        title: 'Firma bearbeiten',
        'submit-cta': 'Speichern',
        'submit-loading': 'Speichern …',
        'cancel-cta': 'Abbrechen',
        'contacts-heading': 'Kontakte',
        'contacts-readonly': 'Kontakte verwaltest du auf der Detailseite der Firma über „Kontakt verknüpfen".',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'not-found-error': 'Firma nicht gefunden.',
        'generic-error': 'Speichern fehlgeschlagen. Bitte versuche es erneut.',
      },
      detail: {
        empty: '—',
        'copy-link': 'Link kopieren',
        'link-copied': 'Link kopiert.',
        status: {
          ACTIVE: 'Aktiv',
          ARCHIVED: 'Archiviert',
        },
        actions: {
          'trigger-label': 'Aktionen',
          edit: 'Bearbeiten',
          archive: 'Archivieren',
          unarchive: 'Wieder aktivieren',
          delete: 'Löschen',
          'link-contact': 'Kontakt verknüpfen',
        },
        'viewer-banner':
          'Du hast nur Lesezugriff. In deiner Rolle kannst du Firmen ansehen, aber nicht bearbeiten, archivieren, löschen oder Kontakte verknüpfen.',
        'lifecycle-error': 'Aktion fehlgeschlagen. Bitte versuche es erneut.',
        sections: {
          identity: 'Identität',
          'tax-identity': 'Steuerliche Identität',
          address: 'Adresse',
          communication: 'Kommunikation',
          notes: 'Notizen',
          tags: 'Tags',
          contacts: 'Kontakte',
        },
        fields: {
          type: 'Typ',
          'legal-form': 'Rechtsform',
          'register-number': 'Handelsregisternummer',
          'vat-id': 'USt-IdNr',
          'tax-number': 'Steuernummer',
          street: 'Straße',
          'house-number': 'Hausnummer',
          'postal-code': 'PLZ',
          city: 'Stadt',
          country: 'Land',
          phone: 'Telefon',
          email: 'E-Mail',
          website: 'Website',
          status: 'Status',
          created: 'Erstellt',
          updated: 'Aktualisiert',
        },
        stats: {
          'contacts-label': 'Kontakte',
        },
        contacts: {
          'primary-badge': 'primärer Arbeitgeber',
          empty: 'Noch keine Kontakte verknüpft.',
          'load-error': 'Kontakte konnten nicht geladen werden.',
        },
      },
      delete: {
        title: 'Diese Firma endgültig löschen?',
        body: 'Diese Aktion entfernt die Firma aus allen Listen und löst alle Kontakt-Verknüpfungen. Die Kontakte selbst bleiben unverändert. Wiederherstellung ist nur über den Support möglich.',
        'confirm-cta': 'Endgültig löschen',
        'confirm-loading': 'Wird gelöscht …',
        'cancel-cta': 'Abbrechen',
        'generic-error': 'Löschen fehlgeschlagen. Bitte versuche es erneut.',
      },
      'add-contact': {
        title: 'Kontakt verknüpfen',
        'submit-cta': 'Verknüpfen',
        'submit-loading': 'Wird verknüpft …',
        'cancel-cta': 'Abbrechen',
        'duplicate-error':
          'Diese Firma ist mit dem Kontakt bereits verknüpft. Du kannst die Rolle der bestehenden Verknüpfung bearbeiten.',
        'generic-error': 'Verknüpfen fehlgeschlagen. Bitte versuche es erneut.',
      },
      link: {
        title: 'Firma verknüpfen',
        'company-label': 'Firma',
        'company-placeholder': 'Firma suchen …',
        'company-clear': 'Auswahl entfernen',
        'company-no-results': 'Keine passenden Firmen.',
        'company-searching': 'Wird gesucht …',
        'role-label': 'Rolle',
        'role-placeholder': 'z. B. Geschäftsführer',
        'primary-employer-label': 'primärer Arbeitgeber',
        'submit-cta': 'Verknüpfen',
        'submit-loading': 'Wird verknüpft …',
        'cancel-cta': 'Abbrechen',
        errors: {
          'company-required': 'Bitte wähle eine Firma aus.',
          'role-required': 'Bitte gib eine Rolle ein.',
          'role-too-long': 'Rolle darf höchstens 200 Zeichen lang sein.',
          duplicate:
            'Diese Firma ist mit dem Kontakt bereits verknüpft. Du kannst die Rolle der bestehenden Verknüpfung bearbeiten.',
          'not-found': 'Firma oder Kontakt wurde nicht gefunden.',
          forbidden: 'Du bist für diese Aktion nicht berechtigt.',
          generic: 'Verknüpfung konnte nicht angelegt werden. Bitte versuche es erneut.',
        },
      },
      'edit-link': {
        title: 'Verknüpfung bearbeiten',
        'role-label': 'Rolle',
        'role-placeholder': 'z. B. Geschäftsführer',
        'primary-employer-label': 'primärer Arbeitgeber',
        'submit-cta': 'Speichern',
        'submit-loading': 'Wird gespeichert …',
        'cancel-cta': 'Abbrechen',
        errors: {
          'role-required': 'Bitte gib eine Rolle ein.',
          'role-too-long': 'Rolle darf höchstens 200 Zeichen lang sein.',
          'not-found': 'Diese Verknüpfung wurde nicht gefunden.',
          forbidden: 'Du bist für diese Aktion nicht berechtigt.',
          generic: 'Änderung konnte nicht gespeichert werden. Bitte versuche es erneut.',
        },
      },
      unlink: {
        title: 'Verknüpfung lösen',
        body: 'Möchtest du diese Verknüpfung wirklich lösen? Der Kontakt und die Firma bleiben erhalten.',
        'confirm-cta': 'Verknüpfung lösen',
        'confirm-loading': 'Wird gelöst …',
        'cancel-cta': 'Abbrechen',
        'error-forbidden': 'Du bist für diese Aktion nicht berechtigt.',
        error: 'Verknüpfung konnte nicht gelöst werden. Bitte versuche es erneut.',
      },
    },
    salesPipeline: {
      'invite-to-viewing': {
        action: 'Zur Besichtigung einladen',
        'hint-label': 'Hinweis für den Interessenten',
        'hint-placeholder': 'z. B. Treffpunkt, Klingel oder Parken — erscheint in der Einladung',
        'hint-counter': '{count} / {max}',
        locked: 'fest',
        submit: 'Einladen',
        'preview-action': 'Vorschau anzeigen',
        'preview-loading': 'Wird geladen …',
        'preview-failed': 'Die Vorschau konnte nicht geladen werden. Bitte versuche es erneut.',
        preview: {
          title: 'Vorschau der Einladung',
          from: 'Von',
          to: 'An',
          subject: 'Betreff',
          'frame-title': 'E-Mail-Vorschau',
          back: 'Zurück',
          send: 'Einladung senden',
          sending: 'Wird gesendet …',
        },
      },
      phase: PROSPECT_PHASE_LABELS,
      dataRoom: {
        title: 'Datenraum',
        'not-released':
          '{count, plural, one {Noch nicht freigegeben. # Dokument ist am Objekt für Käufer markiert.} other {Noch nicht freigegeben. # Dokumente sind am Objekt für Käufer markiert.}}',
        release: 'Datenraum freigeben',
        'release-confirm-title': 'Datenraum für {name} freigeben?',
        'release-confirm-body': 'Er bekommt eine E-Mail mit dem Link zu den Unterlagen.',
        'release-confirm': 'Freigeben',
        'override-inside-withdrawal-period':
          'Die Widerrufsfrist läuft noch bis {withdrawalPeriodEndsOn, date, ::ddMMyyyy}, ein vorzeitiger Beginn wurde nicht verlangt. Widerruft {name} den Maklervertrag, bekommst du für die bis dahin erbrachten Leistungen keinen Wertersatz.',
        'override-not-concluded':
          'Es gibt noch keinen Maklervertrag mit {name}. Ohne Vertrag hast du keine Grundlage für einen Provisionsanspruch.',
        'override-withdrawal-received':
          '{name} hat den Maklervertrag widerrufen. Ohne wirksamen Vertrag hast du keine Grundlage für einen Provisionsanspruch.',
        'release-confirm-override': 'Trotzdem freigeben',
        cancel: 'Abbrechen',
        active: 'Freigegeben am {date} von {name}',
        withdraw: 'Freigabe entziehen',
        'withdraw-confirm-title': 'Freigabe entziehen?',
        'withdraw-confirm-body': '{name} sieht die Unterlagen dann nicht mehr.',
        'withdraw-confirm': 'Freigabe entziehen',
        withdrawn: 'Freigabe entzogen am {date}',
        'ended-lost': 'Freigabe beendet am {date} — Interessent verloren',
        renew: 'Erneut freigeben',
        'permission-denied': 'Dir fehlt die Berechtigung, diesen Interessenten zu bearbeiten.',
        'unknown-member': 'Ehemaliges Mitglied',
        'load-failed': 'Der Stand des Datenraums konnte nicht geladen werden.',
        table: {
          document: 'Dokument',
          'first-viewed': 'Zuerst angesehen',
          'last-viewed': 'Zuletzt angesehen',
          downloaded: 'Herunter­geladen',
          'never-opened': 'Noch nicht geöffnet',
          'download-count': '({count}×)',
          unshared: 'nicht mehr freigegeben',
          deleted: 'gelöscht',
        },
      },
      brokerageAgreement: {
        option: 'Widerrufsbelehrung per E-Mail senden',
        title: 'Maklervertrag & Widerruf',
        'not-sent': 'Belehrung nicht gesendet',
        sent: 'Belehrung gesendet am {date} an {email}',
        failed: 'Senden fehlgeschlagen — bitte prüfe die E-Mail-Adresse und sende erneut.',
        bounced: 'Nicht zugestellt — die Adresse hat die E-Mail abgewiesen. Bitte prüfe sie und sende erneut.',
        'not-concluded': 'Kein Maklervertrag über immoOS geschlossen',
        'receipt-failed': 'Die Eingangsbestätigung an den Interessenten konnte nicht gesendet werden.',
        'badge-waiting': 'Wartet',
        send: 'Widerrufsbelehrung senden',
        resend: 'Erneut senden',
        'permission-denied': 'Dir fehlt die Berechtigung, die Widerrufsbelehrung zu senden.',
        'ask-admin': 'Bitte wende dich an einen Admin.',
        'fix-commission': 'Käuferprovision eintragen',
        'fix-email': 'E-Mail-Adresse eintragen',
        'company-settings-link': 'Einstellungen → Unternehmen',
        'load-failed': 'Der Stand der Widerrufsbelehrung konnte nicht geladen werden.',
        'load-retry': 'Erneut versuchen',
        'send-confirm-title': 'Widerrufsbelehrung an {name} senden?',
        'send-confirm-body': 'Die E-Mail mit der Widerrufsbelehrung und dem persönlichen Link geht an {email}.',
        'resend-confirm-title': 'Widerrufsbelehrung erneut an {name} senden?',
        'resend-confirm-body': '{email} erhält die E-Mail noch einmal, mit einem neuen persönlichen Link.',
        'send-confirm-submit': 'Senden',
        'send-confirm-cancel': 'Abbrechen',
        'send-error': 'Die Widerrufsbelehrung konnte nicht gesendet werden. Bitte versuche es erneut.',
        'acceptance-pending': 'Annahme per E-Mail: ausstehend',
        'acceptance-received': 'Annahme per E-Mail: eingegangen am {date}',
        'mark-acceptance': 'Als eingegangen markieren',
        'mark-acceptance-permission-denied': 'Dir fehlt die Berechtigung, die Annahme als eingegangen zu markieren.',
        'mark-acceptance-error': 'Die Annahme konnte nicht als eingegangen markiert werden. Bitte versuche es erneut.',
        'mark-acceptance-confirm': 'Antwort von {name} mit dem Annahmetext erhalten?',
        'mark-acceptance-confirm-submit': 'Vermerken',
        'mark-acceptance-confirm-cancel': 'Abbrechen',
        reminder: 'Erinnerung gesendet am {date}',
        concluded: 'Maklervertrag online geschlossen am {date} um {time}',
        'early-start': 'Vorzeitiger Beginn verlangt: {answer}',
        yes: 'ja',
        no: 'nein',
        'period-ends': 'Widerrufsfrist endet am {date}',
        withdrawn: 'Widerrufen am {date} um {time}',
        'withdrawal-reason': 'Grund: {reason}',
        'badge-concluded': 'Abgeschlossen',
        'badge-withdrawn': 'Widerrufen',
        'show-proof': 'Nachweis ansehen',
        proof: {
          title: 'Nachweis · Maklervertrag {name}',
          facts: {
            'concluded-at': 'Abgeschlossen',
            as: 'Als',
            'notice-read': 'Widerrufsbelehrung gelesen',
            'early-start': 'Vorzeitiger Beginn verlangt',
            ip: 'IP-Adresse',
            browser: 'Browser',
            muster: 'Textfassung',
            'template-line': '{name} · Version {version} · {date} {time} · {ip}',
            'acceptance-reply': 'Annahme per E-Mail',
            'acceptance-reply-pending': 'ausstehend',
            'acceptance-reply-marked': 'eingegangen · vermerkt von {name} am {date} um {time}',
            'acceptance-reply-marked-unnamed': 'eingegangen · vermerkt am {date} um {time}',
            'withdrawal-received': 'Widerruf eingegangen',
            'withdrawal-name': 'Name',
            'withdrawal-contract': 'Vertrag',
            'withdrawal-email': 'E-Mail-Adresse',
            'withdrawal-reason': 'Grund',
            'no-user-agent': 'nicht übermittelt',
          },
          declarations: 'Erklärungen im Wortlaut',
          emails: 'Gesendete E-Mails',
          page: 'Seite, wie sie gezeigt wurde',
          'page-alt': 'Seite, wie sie gezeigt wurde, am {date} um {time}',
          'open-page': 'Vollständige Seite öffnen',
          back: 'Zurück zum Nachweis',
          close: 'Schließen',
          'email-kind': {
            NOTICE: 'Widerrufsbelehrung',
            REMINDER: 'Erinnerung',
            CONFIRMATION: 'Bestätigung',
            WITHDRAWAL_RECEIPT: 'Eingangsbestätigung',
            ACCEPTANCE_REMINDER: 'Erinnerung an die Annahme per E-Mail',
            DATA_ROOM_RELEASE: 'Datenraum-Freigabe',
          },
          'email-sent-at': 'Gesendet am {date} um {time}',
          'email-status': {
            SENT: 'Gesendet, noch keine Zustellbestätigung',
            FAILED: 'Senden fehlgeschlagen am {date} um {time}',
            PENDING: 'Noch nicht gesendet',
          },
          delivery: {
            DELIVERED: 'Zugestellt am {date} um {time}',
            BOUNCED: 'Abgewiesen am {date} um {time}',
            COMPLAINED: 'Als Spam gemeldet am {date} um {time}',
          },
          'personal-link': '[persönlicher Link]',
          tampered: 'Dieser Eintrag wurde nach dem Speichern verändert.',
          loading: 'Nachweis wird geladen …',
          'load-failed': 'Der Nachweis konnte nicht geladen werden.',
        },
      },
      status: PROSPECT_STATUS_LABELS,
      source: PROSPECT_SOURCE_LABELS,
      'source-group': PROSPECT_SOURCE_GROUP_LABELS,
      'loss-reason': PROSPECT_LOSS_REASON_LABELS,
      'loss-reason-with-note': '{reason}: {note}',
      title: 'Verkaufs-Pipeline',
      subtitle: 'Alle aktiven Kaufprozesse deines Workspaces.',
      'create-cta': 'Interessent anlegen',
      'create-cta-no-permission': 'Dir fehlt die Berechtigung, Interessenten anzulegen.',
      'empty-title': 'Noch keine Kaufinteressenten',
      'empty-description':
        'Lege einen Interessenten an oder nimm einen Kaufinteressenten direkt am Objekt in den Kaufprozess auf.',
      'error-title': 'Daten konnten nicht geladen werden',
      'error-description': 'Die Verbindung zum Backend ist gerade gestört. Versuche es in ein paar Sekunden erneut.',
      'error-retry': 'Erneut versuchen',
      'view-label': 'Ansicht',
      'view-kanban': 'Kanban',
      'view-list': 'Tabelle',
      'no-match-title': 'Keine Treffer für deine Filter',
      'no-match-description':
        'Wir konnten keine Interessenten finden, die alle aktiven Filter erfüllen. Versuche, einen Filter zu lockern oder zurückzusetzen.',
      'reset-filters': 'Filter zurücksetzen',
      'sort-label': 'Nach {column} sortieren',
      filters: {
        'status-label': 'Status',
        'status-all': 'Alle',
        'contact-search-label': 'Kontakt suchen',
        'contact-search-placeholder': 'Kontakt suchen …',
        'assigned-to-me': 'Mir zugewiesen',
        'active-label': 'Aktive Filter:',
        'clear-all': 'Alle zurücksetzen',
        'remove-aria': '{label} entfernen',
        'chip-status': 'Status: {value}',
        'chip-contact': 'Kontakt: „{query}“',
        'chip-assigned-to-me': 'Mir zugewiesen',
      },
      columns: {
        contact: 'Kontakt',
        property: 'Objekt',
        phase: 'Phase',
        status: 'Status',
        days: 'Tage',
        'next-activity': 'Nächste Aktivität',
        responsible: 'Verantwortlich',
        'offered-price': 'Angebotener Kaufpreis',
        'buyer-commission': 'Käuferprovision',
        source: 'Quelle',
        'created-at': 'Erstellt am',
      },
      'property-selection': {
        label: 'Objekt',
        all: 'Alle Objekte',
        'search-label': 'Objekt suchen',
        'search-placeholder': 'Objekt suchen …',
        options: 'Objekte',
        'reserved-suffix': ' · reserviert',
      },
      'property-strip': {
        count: '· {count, plural, one {# Interessent} other {# Interessenten}}',
        'asking-price': '· Angebotspreis {price}',
        'reserved-for': 'Reserviert für {name}',
        'to-property': 'Zum Objekt',
      },
      board: {
        'aria-label': 'Verkaufs-Pipeline nach Phase',
        'empty-column': 'Keine Interessenten',
      },
      'viewing-assessment': {
        action: 'Besichtigung auswerten',
        'edit-action': 'Auswertung bearbeiten',
        title: 'Besichtigung auswerten',
        'edit-title': 'Auswertung bearbeiten',
        'card-hint': 'Besichtigung vom {date} auswerten',
        subtitle: '{prospect} · {weekday}, {date}, {time}',
        'subtitle-all-day': '{prospect} · {weekday}, {date}',
        'attendance-label': 'Teilnahme',
        attendance: { ATTENDED: 'Erschienen', NO_SHOW: 'Nicht erschienen' },
        'impression-label': 'Eindruck',
        impression: {
          INTERESTED: 'Hat Interesse',
          NOT_INTERESTED: 'Eher kein Interesse',
          UNDECIDED: 'Noch unklar',
        },
        'comment-label': 'Kommentar (optional)',
        'comment-placeholder': 'z. B. Fragen, Einwände, nächste Schritte',
        save: 'Speichern',
        cancel: 'Abbrechen',
        'attendance-required': 'Bitte wähle, ob der Interessent erschienen ist.',
        'impression-required': 'Bitte wähle den Eindruck.',
        forbidden: 'Du bist für diese Aktion nicht berechtigt.',
      },
      'viewing-type': {
        ON_SITE: 'Besichtigung vor Ort',
        VIRTUAL: 'Besichtigung virtuell',
      },
      'viewing-type-short': {
        ON_SITE: 'vor Ort',
        VIRTUAL: 'virtuell',
      },
      'next-viewing': {
        card: 'Besichtigung {date} · {time}',
        'card-typed': '{type} {date} · {time}',
        label: 'Nächste Besichtigung',
        value: '{date} · {time}',
        'value-typed': '{type} · {date} · {time}',
        'all-day': 'ganztägig',
      },
      notary: {
        card: 'Notartermin {date}',
        'card-missing': 'Notartermin: Datum fehlt',
        'card-passed': 'Notartermin vom {date} ist vorbei',
      },
      'next-activity': {
        overdue: 'Überfällig seit {date}',
        viewing: 'Besichtigung',
        label: 'Nächste Aktivität',
        due: 'Fällig am {date}',
      },
      card: {
        'no-offer': 'Noch kein Angebot',
        'no-open-offer': 'Kein offenes Angebot',
        'days-in-phase': '{days, plural, one {# Tag} other {# Tage}}',
        empty: '—',
        advance: 'In die nächste Phase: {phase}',
      },
      move: {
        offer: {
          title: 'Kaufangebot erfassen',
          hint: 'Weitere Angebote und Gegenangebote erfasst du in der Karte ‚Kaufprozess‘.',
          'asking-price': 'Angebotspreis des Objekts: {price}',
          confirm: 'Speichern und weiter',
        },
        purchase: {
          title: 'Kauf abschließen',
          body: 'Mit dem Abschluss ist der Kaufprozess gewonnen und kann nicht mehr geändert werden.',
          field: 'Kaufpreis (Kaufvertrag)',
          hint: 'Vorbelegt mit dem letzten Angebot: {price}',
          'hint-accepted': 'Vorbelegt mit dem angenommenen Angebot: {price}',
          confirm: 'Kauf abschließen',
        },
        notary: {
          title: 'Notartermin erfassen',
          field: 'Datum des Notartermins',
          hint: 'Du kannst das Datum später in der Karte ‚Kaufprozess‘ ändern.',
          confirm: 'Speichern und weiter',
          'edit-title': 'Notartermin ändern',
          save: 'Speichern',
        },
        cancel: 'Abbrechen',
        'reserved-link': '{name} öffnen',
        forbidden: 'Dir fehlt die Berechtigung, die Phase zu ändern.',
        'generic-error': 'Die Phase konnte nicht geändert werden. Bitte versuche es erneut.',
      },
      revert: {
        title: 'Phase zurücksetzen',
        'phase-label': 'Zurück zu Phase',
        'phase-placeholder': 'Bitte wählen',
        'phase-required': 'Bitte wähle eine Phase.',
        'reason-label': 'Begründung',
        confirm: 'Zurücksetzen',
        cancel: 'Abbrechen',
        'success-toast': 'Phase zurückgesetzt.',
        forbidden: 'Dir fehlt die Berechtigung, die Phase zurückzusetzen.',
        'generic-error': 'Die Phase konnte nicht zurückgesetzt werden. Bitte versuche es erneut.',
      },
      lost: {
        title: 'Als verloren markieren',
        'reason-label': 'Verlustgrund',
        'reason-placeholder': 'Bitte wählen',
        'reason-required': 'Bitte wähle einen Verlustgrund',
        'note-label': 'Notiz (optional)',
        confirm: 'Als verloren markieren',
        cancel: 'Abbrechen',
        'success-toast': 'Als verloren markiert.',
        forbidden: 'Dir fehlt die Berechtigung, Interessenten zu bearbeiten.',
        'generic-error': 'Der Interessent konnte nicht als verloren markiert werden. Bitte versuche es erneut.',
      },
      reopen: {
        'success-toast': 'Wieder aufgenommen.',
        forbidden: 'Dir fehlt die Berechtigung, Interessenten zu bearbeiten.',
        'generic-error': 'Der Interessent konnte nicht wieder aufgenommen werden. Bitte versuche es erneut.',
      },
      'notary-edit': {
        'success-toast': 'Notartermin gespeichert.',
        'generic-error': 'Der Notartermin konnte nicht gespeichert werden. Bitte versuche es erneut.',
      },
      'offer-record': {
        'title-offer': 'Angebot erfassen',
        'title-counter-offer': 'Gegenangebot des Eigentümers erfassen',
        amount: 'Betrag',
        date: 'Datum',
        'financing-reservation': 'Angebot unter Finanzierungsvorbehalt',
        note: 'Notiz',
        save: 'Speichern',
        forbidden: 'Dir fehlt die Berechtigung, Interessenten zu bearbeiten.',
        'generic-error': 'Der Eintrag konnte nicht gespeichert werden. Bitte versuche es erneut.',
      },
      actions: {
        revert: 'Phase zurücksetzen',
        'mark-lost': 'Als verloren markieren',
        reopen: 'Wieder aufnehmen',
        'move-back-forbidden': 'Dir fehlt die Berechtigung, die Phase zurückzusetzen.',
        'update-forbidden': 'Dir fehlt die Berechtigung, Interessenten zu bearbeiten.',
      },
      create: {
        title: 'Interessent anlegen',
        confirm: 'Interessent anlegen',
        loading: 'Wird angelegt …',
        cancel: 'Abbrechen',
        'standort-label': 'Standort',
        'contact-label': 'Kontakt',
        'contact-placeholder': 'Kontakt suchen…',
        'contact-clear': 'Kontakt-Auswahl entfernen',
        'contact-no-results': 'Keine Kontakte gefunden.',
        'create-contact-cta': 'Neuen Kontakt anlegen',
        'contact-searching': 'Kontakte werden gesucht …',
        'property-label': 'Objekt',
        'property-hint':
          'Nur Objekte mit Vermarktungsart Kauf, die nicht archiviert sind. Aus dem Tab am Objekt ist es vorbelegt.',
        'source-label': 'Quelle (optional)',
        'existing-link': 'Zum laufenden Kaufprozess',
        'contact-required': 'Bitte wähle einen Kontakt.',
        'property-required': 'Bitte wähle ein Objekt.',
        'property-not-found-error':
          'Das gewählte Objekt gehört nicht zum ausgewählten Standort. Bitte wähle es erneut aus.',
        'contact-not-found-error': 'Ein gewählter Kontakt wurde nicht gefunden.',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'generic-error': 'Interessent konnte nicht angelegt werden. Bitte versuche es erneut.',
      },
      detail: {
        empty: '—',
        header: {
          'aria-label': 'Kopfbereich des Interessenten',
          'strip-aria-label': 'Phasen des Kaufprozesses',
          'offer-label': 'Angebot',
          'purchase-label': 'Kaufpreis',
          'change-phase': 'Phase ändern',
          'change-phase-forbidden': 'Dir fehlt die Berechtigung, die Phase zu ändern.',
          'won-marker': 'Kaufprozess gewonnen',
          'lost-marker': 'Kaufprozess verloren',
          more: 'Weitere Aktionen',
          'days-in-phase': '{days, plural, one {# Tag} other {# Tage}} in Phase',
          closed: 'Abgeschlossen',
        },
        banner: {
          won: 'Kaufprozess gewonnen — Phase und Preise sind gesperrt.',
          lost: 'Kaufprozess verloren — wähle „Wieder aufnehmen", um ihn erneut zu bearbeiten.',
          'lost-reason': 'Verlustgrund: {reason}',
          'lost-note': 'Verlust-Notiz: {note}',
          'read-only': 'Du kannst diesen Kaufprozess ansehen, aber nicht bearbeiten.',
        },
        activity: {
          title: 'Aktivität',
          'no-access': 'Kein Zugriff — dir fehlen die Leserechte für diese Angaben.',
          'add-cta': 'Notiz, Wiedervorlage oder Besichtigung hinzufügen …',
          'cancel-confirm':
            'Die Aktivität wird abgesagt und verschwindet aus „Anstehend". Im Verlauf bleibt sie sichtbar.',
        },
        rail: {
          contact: { title: 'Kontakt', name: 'Name', email: 'E-Mail', phone: 'Telefon' },
          property: {
            title: 'Objekt',
            name: 'Objekt',
            address: 'Adresse',
            'asking-price': 'Angebotspreis',
            prospects: 'Kaufinteressenten',
            'prospects-value': '{count} aktiv',
            reservation: 'Reservierung',
            reserved: 'reserviert',
            'reserved-won': 'reserviert · gekauft',
            free: 'frei',
          },
          process: {
            title: 'Kaufprozess',
            offer: 'Angebot',
            'offer-accepted': '{amount} · angenommen',
            notary: 'Notartermin',
            'notary-missing': 'Datum fehlt',
            'purchase-price': 'Kaufpreis (Kaufvertrag)',
            source: 'Quelle',
            responsible: 'Verantwortlich',
            created: 'Erstellt',
            edit: 'Bearbeiten',
            'notary-edit-aria': 'Notartermin bearbeiten',
            'edit-forbidden': 'Dir fehlt die Berechtigung, Interessenten zu bearbeiten.',
          },
          offers: {
            title: 'Angebote',
            'record-offer': 'Angebot erfassen',
            'record-counter-offer': 'Gegenangebot erfassen',
            kind: { OFFER: 'Angebot', COUNTER_OFFER: 'Gegenangebot' },
            status: { OPEN: 'Offen', ACCEPTED: 'Angenommen', REJECTED: 'Abgelehnt', WITHDRAWN: 'Zurückgezogen' },
            'financing-reservation': 'Finanzierungsvorbehalt',
            'actions-label': 'Aktionen für diesen Eintrag',
            actions: { ACCEPTED: 'Annehmen', REJECTED: 'Ablehnen', WITHDRAWN: 'Zurückziehen', OPEN: 'Wieder öffnen' },
          },
        },
      },
    },
    deals: {
      phase: DEAL_PHASE_LABELS,
      status: DEAL_STATUS_LABELS,
      type: DEAL_TYPE_LABELS,
      source: DEAL_SOURCE_LABELS,
      probability: DEAL_PROBABILITY_LABELS,
      'completion-horizon': DEAL_COMPLETION_HORIZON_LABELS,
      'loss-reason': DEAL_LOSS_REASON_LABELS,
      'mandate-type': DEAL_MANDATE_TYPE_LABELS,
      auftrag: {
        card: {
          title: 'Auftrag',
          edit: 'Bearbeiten',
          empty: 'Noch keine Auftragsdaten erfasst.',
          'mandate-type-label': 'Auftragsart',
          'start-label': 'Tätigkeitsaufnahme',
          'duration-label': 'Laufzeit',
          'duration-value': '{months, plural, one {# Monat} other {# Monate}}',
          'ends-on': 'Läuft bis {date}',
          'seller-commission-label': 'Provision Verkäufer',
          'price-label': 'Erwarteter Verkaufspreis',
          'percent-value': '{value} %',
        },
        dialog: {
          title: 'Auftrag bearbeiten',
          'mandate-type-label': 'Auftragsart',
          'mandate-type-placeholder': 'Bitte wählen',
          'start-label': 'Tätigkeitsaufnahme',
          'duration-label': 'Laufzeit (Monate)',
          'duration-suffix': 'Monate',
          'seller-commission-label': 'Provision Verkäufer (%)',
          'commission-hint': 'Netto, zzgl. MwSt.',
          'price-label': 'Erwarteter Verkaufspreis',
          save: 'Speichern',
          cancel: 'Abbrechen',
          saved: 'Auftrag gespeichert',
          'generic-error': 'Der Auftrag konnte nicht gespeichert werden. Bitte versuche es erneut.',
          errors: {
            commission: 'Gib einen Wert zwischen 0 und 100 mit höchstens zwei Nachkommastellen ein.',
            duration: 'Gib eine Laufzeit zwischen 1 und 24 Monaten ein.',
          },
        },
      },
      'buyer-commission-difference': {
        banner:
          '{count, plural, one {Bei # Deal weicht} other {Bei # Deals weicht}} die Käuferprovision im Auftrag vom Objekt ab.',
        'banner-action': 'Ansehen',
        'dialog-title': 'Abweichende Käuferprovision',
        'dialog-body':
          'Die Käuferprovision gehört jetzt zum Objekt und gilt für Deal, Auftrag und Maklervertrag. Bei diesen Deals stand im Auftrag ein anderer Wert. Öffne den Deal und entscheide, welcher Wert gilt.',
        row: 'Auftrag: {mandate} · Objekt: {objekt}',
        'mandate-value': '{percent, number, ::.00} % netto',
        'open-deal': 'Deal öffnen',
        'card-notice': 'Im Auftrag stand {mandate}. Es gilt jetzt der Wert des Objekts: {objekt}.',
        'keep-objekt': 'Objektwert behalten',
        'use-mandate': 'Auftragswert ans Objekt übernehmen',
        'dismiss-error': 'Das hat nicht geklappt. Bitte versuche es erneut.',
      },
      list: {
        title: 'Akquisitionen',
        // IMM-1333 — follows the status filter, so the header never contradicts the
        // list (e.g. „aktiven“ above lost deals). `ALL` is the „Alle“ option (`''`).
        subtitle: {
          ALL: 'Alle Akquisitions-Pipelines deines Workspaces.',
          DRAFT: 'Alle Akquisitions-Pipelines deines Workspaces im Entwurf.',
          ACTIVE: 'Alle aktiven Akquisitions-Pipelines deines Workspaces.',
          DEFERRED: 'Alle zurückgestellten Akquisitions-Pipelines deines Workspaces.',
          WON: 'Alle gewonnenen Akquisitions-Pipelines deines Workspaces.',
          LOST: 'Alle verlorenen Akquisitions-Pipelines deines Workspaces.',
        } as const satisfies EnumTranslation<DealStatus | 'ALL'>,
        'create-cta': 'Deal anlegen',
        // IMM-836 — eigener Grund-Schlüssel für die gesperrte CTA. Im gleichen
        // Register wie `phase-lock.no-permission` auf derselben Seite,
        // damit beide Sperren als ein System gelesen werden.
        'create-cta-no-permission': 'Du hast keine Berechtigung, Deals anzulegen.',
        'row-actions': 'Aktionen',
        'list-load-error': 'Deals konnten nicht geladen werden.',
        'empty-title': 'Noch keine Deals',
        'empty-description':
          'Hier landen alle deine aktiven Deals. Lege einen Deal direkt an oder qualifiziere einen Eigentümer-Lead.',
        'empty-leads-cta': 'Zu den Eigentümer-Leads',
        'no-match-title': 'Keine Treffer für deine Filter',
        'no-match-description':
          'Wir konnten keine Deals finden, die alle aktiven Filter erfüllen. Versuche, einen Filter zu lockern oder zurückzusetzen.',
        'reset-filters': 'Filter zurücksetzen',
        'error-title': 'Daten konnten nicht geladen werden',
        'error-description': 'Die Verbindung zum Backend ist gerade gestört. Versuche es in ein paar Sekunden erneut.',
        'error-retry': 'Erneut versuchen',
        'success-toast': 'Deal angelegt.',
        filters: {
          'status-label': 'Status',
          'status-all': 'Alle',
          'assigned-to-me': 'Mir zugewiesen',
          orphaned: 'Nicht zugewiesen / verwaiste Deals',
          'contact-search-label': 'Kontakt suchen',
          'contact-search-placeholder': 'Kontakt suchen …',
          'property-search-label': 'Objekt suchen',
          'property-search-placeholder': 'Objekt suchen …',
          'price-min-label': 'Verkaufspreis ab',
          'price-max-label': 'Verkaufspreis bis',
          'price-min-placeholder': 'Min. €',
          'price-max-placeholder': 'Max. €',
          'active-label': 'Aktive Filter:',
          'clear-all': 'Alle zurücksetzen',
          'remove-aria': '{label} entfernen',
          'chip-status': 'Status: {value}',
          'chip-lost-in-phase': 'Verloren in: {phase}',
          'lost-in-phase-label': 'Verloren in Phase',
          'lost-in-phase-all': 'Alle Phasen',
          'chip-assigned-to-me': 'Mir zugewiesen',
          'chip-orphaned': 'Nicht zugewiesen / verwaiste Deals',
          'chip-contact': 'Kontakt: „{query}“',
          'chip-property': 'Objekt: „{query}“',
          'chip-price-range': 'Verkaufspreis: {min} – {max}',
          'chip-price-min': 'Verkaufspreis ab {min}',
          'chip-price-max': 'Verkaufspreis bis {max}',
        },
        'orphan-banner':
          'Diese Deals haben derzeit kein verantwortliches Mitglied. Weise sie explizit zu — es wird nichts automatisch übertragen.',
        columns: {
          contact: 'Kontakt(e)',
          property: 'Objekt',
          type: 'Geschäftsart',
          phase: 'Phase',
          status: 'Status',
          commission: 'Provisionshöhe',
          'days-in-phase': 'Tage',
          probability: 'Wahrsch.',
          'completion-horizon': 'Horizont',
          responsible: 'Verantwortlich',
          'former-responsible': 'Früher verantwortlich',
          'created-by': 'Erstellt von',
        },
        'sort-label': 'Nach {column} sortieren',
        'created-by-coming-soon': 'Bald verfügbar',
        'not-assigned': 'Nicht zugewiesen',
        'unknown-member': 'Unbekanntes Mitglied',
        'former-responsible-deactivated': '{name} (deaktiviert)',
        'assign-cta': 'Zuweisen',
        'days-in-phase-value': '{days, plural, one {# Tag} other {# Tage}}',
        'days-of-target': '{days, plural, one {# Tag} other {# Tage}} in Phase, Ziel {target}',
        'owners-suffix': '+{count}',
        'owners-hover-label': 'Verknüpfte Kontakte',
        'view-label': 'Ansicht',
        'view-kanban': 'Kanban',
        'view-list': 'Tabelle',
        board: {
          'aria-label': 'Deal-Pipeline nach Phase',
          'empty-column': 'Keine Deals',
          'not-valued': 'Noch nicht bewertet',
          commission: 'Courtage {amount}',
          'commission-estimated': 'Courtage ~{amount} geschätzt',
          'commission-label': 'Courtage',
          'commission-missing': 'Keine erwartete Courtage',
          estimate: '~{amount} geschätzt',
          'column-commission': 'Erwartete Courtage',
          'next-activity': {
            none: 'Kein nächster Schritt',
            overdue: 'Überfällig seit {date}',
          },
          'hold-lane': 'Zurückgestellt',
          'lost-lane': 'Verloren',
          'hold-empty': 'Keine zurückgestellten Deals',
          // TODO(IMM-22): wire to a specific backend 409/conflict error code
          'concurrent-change': 'Der Deal wurde inzwischen geändert — die Ansicht wurde aktualisiert.',
          'drag-column-drop-hint': 'Hier ablegen',
        },
        'deferred-until-card': 'Wiedervorlage {date}',
        empty: '—',
      },
      // Deal-Überblick (IMM-430.7) — die Standortleitungs-Sicht über alle Deals.
      overview: {
        title: 'Deal-Überblick',
        // IMM-726.1 — „aktiven", wie der Design-Run es setzt und wie der Read es
        // ohnehin tut: Entwürfe erscheinen hier nicht.
        subtitle: 'Alle aktiven Deals deiner Standorte — sortiert nach Dringlichkeit.',
        empty: 'Keine Deals in diesem Filter.',
        'load-failed': 'Der Überblick konnte nicht geladen werden.',
        'load-failed-hint': 'Bitte lade die Seite neu.',
        retry: 'Erneut versuchen',
        // IMM-665 — der Server hat den Filter abgelehnt, nicht die Seite. Die
        // Liste bleibt stehen, deshalb sagt der Hinweis, was gerade gilt.
        'filter-rejected': 'Dieser Filter konnte nicht angewendet werden — es werden alle Deals angezeigt.',
        truncated: 'Es werden nur die ersten 2.000 Deals angezeigt.',
        filter: {
          label: 'Filter:',
          phase: 'Phase',
          // IMM-1312 — die letzte Option des Phasen-Filters; bringt die verlorenen Deals zurück.
          lost: 'Verloren',
          attention: 'Aufmerksamkeit',
          // IMM-629 — eigener Schlüssel statt `column.assignee`: die Spalte und
          // der Filter tragen heute dasselbe Wort, sind aber unabhängig
          // umformulierbar.
          assignee: 'Zuständig',
          'assignee-all': 'Alle Zuständigen',
          // IMM-726.2 — der vierte Filter aus dem Design-Run, plus die
          // „Alle …"-Einträge, mit denen ein Filter zurückgesetzt wird. Als
          // Dropdown-Pille braucht jeder Filter einen solchen Eintrag; der bloße
          // Filtername hat als Menüeintrag keine Bedeutung.
          standort: 'Standort',
          'standort-all': 'Alle Standorte',
          'phase-all': 'Alle Phasen',
          'attention-all': 'Alle Zustände',
        },
        // IMM-726.2 — der Seiten-Scope. Überschreibt für diese Seite die
        // Einschränkung des globalen Standort-Wechslers.
        scope: {
          // Der zugängliche Name des Bedienelements. Getrennt von `all`/`one`,
          // weil die sichtbare Beschriftung der aktuelle WERT ist — ein
          // Bedienelement, dessen Name sein Wert ist, lässt sich nach der ersten
          // Auswahl nicht mehr über denselben Namen finden. Zudem trägt der
          // Standort-Filter daneben bereits „Standort".
          label: 'Standort wählen',
          all: 'Standort: Alle',
          one: 'Standort: {name}',
        },
        // IMM-726.2 — eigener Schlüssel statt `pages.workspace.deals.list.create-cta`,
        // nach dem Vorbild, das IMM-629 auf dieser Seite mit `filter.assignee`
        // gesetzt hat: zwei Oberflächen, die sich heute ein Wort teilen, müssen
        // unabhängig umformulierbar bleiben.
        'create-cta': 'Deal anlegen',
        // IMM-836 — eigener Grund-Schlüssel, aus demselben Grund, aus dem
        // `create-cta` hier einer ist: zwei Oberflächen, die sich heute einen
        // Satz teilen, müssen unabhängig umformulierbar bleiben.
        'create-cta-no-permission': 'Du hast keine Berechtigung, Deals anzulegen.',
        // IMM-629 — die Umschaltung zwischen flacher Liste und Gruppierung nach
        // Phase. Steht in der URL, damit eine gruppierte Ansicht teilbar ist.
        view: {
          label: 'Ansicht',
          list: 'Liste',
          phase: 'Nach Phase',
        },
        // IMM-629 — die Zahl im Gruppenkopf kommt aus `phaseCounts` (ohne den
        // Phasenfilter gezählt) und kann deshalb von der Zeilenzahl abweichen.
        'group-count': '{count, plural, one {# Deal} other {# Deals}}',
        // IMM-726.1 — mehrere Zuständige kollabieren zu „erster Name +N", damit
        // die Zeile einzeilig bleibt. Der Design-Run zeigt nur den Einzelfall.
        'assignee-more': '+{count}',
        column: {
          object: 'Objekt',
          // IMM-802.1 — its own column, directly after Objekt. „Eigentümer" is
          // the word the deal surface already uses for this person.
          owner: 'Eigentümer',
          phase: 'Phase',
          days: 'Tage in Phase',
          assignee: 'Zuständig',
          'next-activity': 'Nächste Aktivität',
          status: 'Status',
        },
      },
      detail: {
        'id-prefix': 'Deal',
        'acquisition-briefing': {
          start: 'Akquisebriefing erstellen',
          preparing: 'Akquisebriefing wird erstellt … (gestartet {time})',
          delivered: 'Akquisebriefing vom {date}',
          view: 'Ansehen',
          renew: 'Neu erstellen',
          retry: 'Erneut versuchen',
          'object-type-unsupported': 'Für diese Objektart gibt es noch kein Akquisebriefing.',
          'address-incomplete': 'Für ein Briefing braucht das Objekt eine vollständige Adresse.',
          'renew-dialog': {
            title: 'Neues Akquisebriefing erstellen?',
            description: 'Das bisherige wird ersetzt.',
            confirm: 'Neu erstellen',
            cancel: 'Abbrechen',
          },
        },
        'load-error': 'Deal konnte nicht geladen werden.',
        empty: '—',
        'owners-suffix': '+ {count} Eigentümer',
        'owner-unknown': 'Unbekannter Kontakt',
        'no-access': 'Kein Zugriff — dir fehlen die Leserechte für diese Angaben.',
        // Der persistente Deal-Header (IMM-456.2). Die Sekundäraktionen im
        // „Weitere"-Menü tragen ihre eigenen, bereits vorhandenen Labels; hier
        // steht nur, was der Header selbst beschriftet.
        header: {
          'aria-label': 'Deal-Kopfzeile',
          'commission-label': 'Erw. Maklergebühr',
          'days-in-phase': 'Tag {days} in Phase',
          closed: 'Akquise abgeschlossen',
          'lost-in': 'Verloren in: {phase}',
          'won-marker': 'Deal gewonnen',
          'lost-marker': 'Deal verloren',
          more: 'Weitere',
          valuation: 'Bewertung',
          'back-to-deal': 'Zurück zum Deal',
          'open-object': 'Objekt öffnen: {address}',
          'publish-to-immoscout24': 'Jetzt auf ImmoScout24 veröffentlichen',
          'publish-to-portal': 'Jetzt auf {portal} veröffentlichen',
        },
        // Die „Zuständig"-Auswahl im Aktivitäts-Composer. Auf dem Deal heißt „keine
        // Auswahl" NICHT „ich": der Server setzt die verantwortliche Person des
        // Deals ein (IMM-430.2). Auf dem Lead bleibt es „Ich".
        'activity-assignee-default': 'Verantwortliche:r des Deals',
        // Das Aufmerksamkeits-Signal eines Deals (IMM-430.6). Genau eines gilt,
        // in dieser Reihenfolge, und nur für einen aktiven Deal — ein
        // zurückgestellter, gewonnener oder verlorener Deal stockt nicht.
        attention: {
          OVERDUE: 'Überfällig',
          NO_NEXT_STEP: 'Kein nächster Schritt',
          TOO_LONG_IN_PHASE: 'Zu lange in Phase',
          ON_TRACK: 'Auf Kurs',
        },
        playbook: {
          heading: 'Empfohlen für diese Phase',
          dismiss: 'Ausblenden',
          'in-exchange-first-contact': 'Erstkontakt',
          'in-exchange-on-site': 'Vor-Ort-Termin vereinbaren',
          'in-exchange-call-outcome': 'Gesprächsergebnis festhalten',
          'appointment-scheduled-on-site': 'Vor-Ort-Termin planen',
          'appointment-scheduled-confirm': 'Terminbestätigung',
          'appointment-scheduled-preparation': 'Vorbereitung notieren',
          'appointment-completed-survey': 'Objektaufnahme planen',
          'appointment-completed-request-valuation': 'Bewertung anfragen',
          'appointment-completed-price-expectation': 'Preisvorstellung notieren',
          'valuation-completed-present': 'Bewertung präsentieren',
          'valuation-completed-call': 'Eigentümer anrufen',
          'valuation-completed-prepare-mandate': 'Maklervertrag vorbereiten',
          'mandate-received-send-mandate': 'Maklervertrag versenden',
          'mandate-received-signing': 'Unterzeichnung',
          'mandate-received-identification': 'GwG-Identifizierung',
          'contract-sent-follow-up': 'Nachfassen',
          'contract-received-survey': 'Objektaufnahme planen',
          'contract-received-marketing-strategy': 'Exposé · Vermarktungsstrategie',
          'expose-review-seek-approval': 'Freigabe einholen',
          'expose-review-letter': 'Freigabe schriftlich anfordern',
          'expose-review-approval-recorded': 'Freigabe dokumentiert',
          'online-handover': 'Übergabe an Vermarktung',
        },
        // Der „Absagen"-Bestätigungstext. Auf dem Lead verschwindet die Aktivität
        // ganz; auf dem Deal bleibt sie im Verlauf stehen (IMM-430.3), damit
        // nachvollziehbar bleibt, was geplant und wieder abgesagt wurde.
        'activity-cancel-confirm':
          'Die Aktivität wird abgesagt und verschwindet aus „Anstehend". Im Verlauf bleibt sie sichtbar.',
        // Bewertung (valuation) tab — IMM-407.1. Only the quiet states in this
        // slice; the Anfrage form and populated states arrive with IMM-407.2.x.
        valuation: {
          'empty-title': 'Noch keine Bewertung angefragt',
          'empty-hint': 'Vieles kennen wir schon aus dem Objekt. Du prüfst es nur noch.',
          'request-action': 'Bewertung anfragen',
          'read-only-hint': 'Für diesen Deal wurde noch keine Bewertung angefragt.',
          // A Deal always has ein Objekt (property_id ist NOT NULL); ein fehlendes
          // Objekt bedeutet hier, dass es nicht geladen werden konnte, nicht dass
          // der Deal keins hat.
          'unavailable-title': 'Objekt konnte nicht geladen werden',
          'unavailable-hint':
            'Das mit diesem Deal verknüpfte Objekt ist derzeit nicht verfügbar. Lade die Seite neu oder versuche es später erneut.',
          loading: 'Bewertung wird geladen …',
          'load-error-title': 'Bewertung konnte nicht geladen werden',
          'load-error-hint':
            'Die Bewertungsanfrage dieses Deals konnte gerade nicht gelesen werden. Es wurde nichts verändert.',
          'load-retry': 'Erneut versuchen',
          'status-label': 'Status der Anfrage',
          // The Anfrage Entwurf form (IMM-407.2.1): the three always-visible
          // blocks, the honest pre-fill marker, and the save/delete actions.
          form: {
            'status-label': 'Status der Anfrage',
            'status-value': VALUATION_STATUS_LABELS,
            save: 'Speichern',
            saving: 'Wird gespeichert…',
            'save-success': 'Entwurf gespeichert.',
            delete: 'Entwurf löschen',
            deleting: 'Wird gelöscht…',
            'delete-success': 'Entwurf gelöscht.',
            'delete-confirm': {
              title: 'Entwurf löschen?',
              body: 'Der Entwurf für {address} und seine hochgeladenen Unterlagen werden gelöscht. Das lässt sich nicht rückgängig machen.',
              'body-unnamed':
                'Der Entwurf und seine hochgeladenen Unterlagen werden gelöscht. Das lässt sich nicht rückgängig machen.',
              cancel: 'Abbrechen',
            },
            creating: 'Wird angelegt…',
            'prefill-marker': 'Aus dem Objekt übernommen',
            // Vorschau + Absenden + Zurückziehen (IMM-407.3).
            'to-preview': 'Weiter zur Vorschau',
            withdraw: 'Zurückziehen',
            withdrawing: 'Wird zurückgezogen…',
            'withdraw-success': 'Anfrage zurückgezogen.',
            'requested-by': 'Angefragt von {name}',
            'requested-at': 'Angefragt am {date}',
            // The Pflichtfeld banner shown on return from the Vorschau when submit
            // is refused server-side (IMM-407.3 AC 29). The offending fields are
            // listed by name below the intro.
            missing: {
              title: 'Pflichtangaben fehlen',
              intro: 'Bitte ergänze diese Pflichtangaben, bevor du die Anfrage absendest:',
            },
            'not-saved': {
              title: 'Deine Änderungen wurden nicht gespeichert',
              body: 'Die Anfrage war bereits abgesendet. Das Formular zeigt weiter deine Eingaben, nicht den Stand der abgesendeten Anfrage.',
            },
            errors: {
              forbidden: 'Dafür fehlt dir die Berechtigung.',
              create: 'Die Anfrage konnte nicht angelegt werden. Bitte versuche es erneut.',
              save: 'Der Entwurf konnte nicht gespeichert werden. Bitte versuche es erneut.',
              // A business-rule refusal (IMM-1198) — the request was already submitted;
              // not retryable, so no „Bitte versuche es erneut".
              'save-not-draft':
                'Die Anfrage wurde bereits abgesendet und kann nicht mehr bearbeitet werden. Zum Ändern zuerst zurückziehen.',
              // A specific value refusal — not retryable. The Vermietungsquote is a
              // percentage (0–100); an out-of-range value is caught inline before save.
              'occupancy-rate-range': 'Die Vermietungsquote muss zwischen 0 und 100 % liegen.',
              delete: 'Der Entwurf konnte nicht gelöscht werden. Bitte versuche es erneut.',
              submit: 'Die Anfrage konnte nicht gesendet werden. Bitte versuche es erneut.',
              withdraw: 'Die Anfrage konnte nicht zurückgezogen werden. Bitte versuche es erneut.',
              // A business-rule refusal (IMM-511) — permanent, not retryable. No
              // „Bitte versuche es erneut": the request is being worked on / closed.
              'withdraw-taken-on': 'Diese Anfrage wird bereits bearbeitet und kann nicht mehr zurückgezogen werden.',
              'withdraw-not-possible': 'Diese Anfrage kann nicht mehr zurückgezogen werden.',
              // A business-rule refusal (IMM-1198) — permanent, not retryable.
              'not-draft-withdraw': 'Diese Anfrage wurde bereits abgesendet. Zum Ändern zuerst zurückziehen.',
              'not-draft': 'Diese Anfrage ist kein Entwurf mehr und kann nicht mehr geändert werden.',
            },
            objectType: VALUATION_OBJEKTART_LABELS,
            usage: VALUATION_NUTZUNG_LABELS,
            'reason-enum': VALUATION_ANLASS_LABELS,
            timeHorizon: VALUATION_ZEITHORIZONT_LABELS,
            priceType: VALUATION_PREISVORSTELLUNG_ART_LABELS,
            priceExpectationKind: VALUATION_PRICE_EXPECTATION_KIND_LABELS,
            // Type-specific enum option tables + field-name labels (IMM-407.2.2).
            heatingType: VALUATION_HEIZUNGSART_LABELS,
            energyEfficiencyClass: VALUATION_ENERGIEEFFIZIENZKLASSE_LABELS,
            houseType: VALUATION_HAUSTYP_LABELS,
            basement: VALUATION_UNTERKELLERT_LABELS,
            developmentPlan: VALUATION_BEBAUUNGSPLAN_LABELS,
            servicingStatus: VALUATION_ERSCHLIESSUNGSZUSTAND_LABELS,
            contaminationSuspicion: VALUATION_ALTLASTENVERDACHT_LABELS,
            'graduated-index-rent': VALUATION_STAFFEL_INDEX_MIETE_LABELS,
            'unit-breakdown': VALUATION_ANGABE_DER_EINHEITEN_LABELS,
            // Gesamtzustand + collection enum option tables (IMM-407.2.3).
            quality: VALUATION_QUALITAET_LABELS,
            condition: VALUATION_ZUSTAND_LABELS,
            buildingElement: VALUATION_GEBAEUDEELEMENT_LABELS,
            parkingSpaceType: VALUATION_STELLPLATZART_LABELS,
            lettingStatus: VALUATION_EINHEIT_VERMIETUNGSSTATUS_LABELS,
            field: VALUATION_FIELD_NAME_LABELS,
            // The names the switch-warning uses for the two discardable collections.
            collection: {
              parkingSpaces: 'Stellplätze',
              units: 'Einheiten',
            },
            'select-placeholder': 'Bitte wählen',
            'boolean-ja': 'Ja',
            'boolean-nein': 'Nein',
            'income-annual-rent-label': 'Jahresnettokaltmiete',
            'income-annual-rent-hint': 'Ist-Miete × 12 · berechnet',
            block: {
              condominium: 'Angaben zur Eigentumswohnung',
              house: 'Angaben zum Haus',
              multiFamily: 'Angaben zum Mehrfamilienhaus',
              plot: 'Angaben zum Grundstück',
              income: 'Ertrag (IST)',
              hoa: 'WEG',
              overallCondition: 'Qualität und Zustand',
              parkingSpaces: 'Stellplätze',
              units: 'Einheiten',
            },
            // Gesamtzustand block (IMM-407.2.3) — the two star axes + a hint.
            overallCondition: {
              'axis-quality': 'Qualität',
              'axis-condition': 'Zustand',
              hint: 'Keine Sterne bedeutet „keine Angabe" — nicht die niedrigste Stufe.',
            },
            // Stellplätze block (IMM-407.2.3) — repeatable Art + Anzahl rows.
            parkingSpaces: {
              'type-label': 'Art',
              'count-label': 'Anzahl',
              add: 'Stellplatz hinzufügen',
              remove: 'Entfernen',
              empty: 'Noch keine Stellplätze erfasst.',
            },
            // Mehrfamilienhaus per-unit rent roll (IMM-407.2.3).
            units: {
              'unit-heading': 'Einheit {index}',
              label: 'Bezeichnung',
              lettingStatus: 'Vermietungsstatus',
              'let-since': 'Vermietet seit',
              livingArea: 'Wohnfläche',
              'current-rent': 'Aktuelle Miete',
              'last-rent-increase': 'Letzte Mieterhöhung',
              add: 'Einheit hinzufügen',
              remove: 'Entfernen',
              empty: 'Noch keine Einheiten erfasst.',
            },
            unavailable: {
              title: 'Erfassung noch nicht verfügbar',
              body: 'Für Gewerbeobjekte und Garagen/Stellplätze ist die Erfassung noch nicht verfügbar. Diese Objektarten folgen in einem späteren Schritt.',
            },
            'switch-warning': {
              title: 'Angaben gehen verloren',
              intro: 'Durch diese Änderung gehen deine Angaben zu diesen Feldern verloren. Fortfahren?',
              confirm: 'Fortfahren',
              cancel: 'Abbrechen',
            },
            basics: {
              title: 'Basis',
              'objectType-label': 'Bewertungsobjektart',
              'objectType-placeholder': 'Bitte wählen',
              'usage-label': 'Nutzung',
              'usage-placeholder': 'Bitte wählen',
              'address-label': 'Adresse',
              'address-placeholder': 'Straße, PLZ, Ort',
            },
            reason: {
              title: 'Anlass & Erwartung',
              'reason-label': 'Bewertungsanlass',
              'reason-placeholder': 'Bitte wählen',
              'timeHorizon-label': 'Zeithorizont',
              'timeHorizon-placeholder': 'Bitte wählen',
              'completion-label': 'Gewünschte Fertigstellung',
              'price-expectation-from-label': 'Betrag / von',
              'price-expectation-to-label': 'bis',
              'price-expectation-hint':
                'Nur zur Info für die Gutachterin oder den Gutachter — die Bewertung entsteht unabhängig davon.',
              'note-label': 'Anmerkung',
              'note-placeholder': 'Freitext für den Gutachter — alles, was das Formular nicht abfragt.',
            },
            documents: {
              title: 'Unterlagen',
              // Seit IMM-501.1 ist die hochgeladene Unterlage das Objekt-Dokument
              // selbst — keine Kopie. Die Kategorie steht seit IMM-501.2 an jeder
              // Zeile, deshalb darf der Hinweis sie nicht mehr behaupten.
              'hand-off-hint':
                'Hochgeladene Dokumente sind zugleich Unterlagen des Objekts und werden dort automatisch einsortiert.',
              empty: 'Noch keine Unterlagen. Lade Dokumente hoch oder wähle sie aus dem Objekt.',
              'upload-action': 'Hochladen',
              uploading: 'Wird hochgeladen…',
              'select-action': 'Aus dem Objekt wählen',
              'upload-success': 'Dokument hinzugefügt.',
              'reference-success': 'Objekt-Dokument verknüpft.',
              remove: '{name} entfernen',
              source: {
                UPLOADED: 'Hochgeladen',
                REFERENCED: 'Aus dem Objekt',
              },
              errors: {
                disallowed: 'Dateityp oder Größe nicht erlaubt (PDF, DOCX, JPEG, PNG; max. 20 MB).',
                upload: 'Das Dokument konnte nicht hochgeladen werden. Bitte versuche es erneut.',
                reference: 'Das Objekt-Dokument konnte nicht verknüpft werden. Bitte versuche es erneut.',
                remove: 'Das Dokument konnte nicht entfernt werden. Bitte versuche es erneut.',
              },
              picker: {
                title: 'Aus dem Objekt wählen',
                loading: 'Objekt-Dokumente werden geladen…',
                'load-error': 'Die Objekt-Dokumente konnten nicht geladen werden.',
                empty: 'Das Objekt hat noch keine Dokumente.',
                select: 'Übernehmen',
              },
            },
            // Der Bilder-Block (IMM-447.2) — direkt unter den Unterlagen, nur
            // Bilddateien, mit eigener Liste zum Hochladen und Entfernen.
            images: {
              title: 'Bilder',
              'policy-hint': 'JPG, PNG · max. 25 MB',
              // Die Objekt-Übernahme (IMM-447.4) — hochgeladene Bilder landen
              // auch in der Galerie des Objekts, aber nie als Titelbild.
              'hand-off-hint': 'Hochgeladene Bilder landen auch in den Bildern des Objekts (nicht als Titelbild).',
              empty: 'Noch keine Bilder. Lade Fotos des Objekts hoch.',
              'upload-action': 'Hochladen',
              'upload-input-label': 'Bilder hochladen',
              uploading: 'Wird hochgeladen…',
              'upload-success': 'Bild hinzugefügt.',
              remove: '{name} entfernen',
              errors: {
                disallowed: 'Nur Bilddateien sind erlaubt (JPG, PNG; max. 25 MB).',
                upload: 'Das Bild konnte nicht hochgeladen werden. Bitte versuche es erneut.',
                remove: 'Das Bild konnte nicht entfernt werden. Bitte versuche es erneut.',
              },
            },
          },
          // Für diesen Deal läuft bereits eine offene Anfrage (IMM-407.3 AC 34) —
          // ein zweites Anlegen wird abgelehnt.
          'one-open': 'Für diesen Deal läuft bereits eine Bewertungsanfrage.',
          // Die Wartezeile des Agenten, solange die Bewertung zur Freigabe liegt
          // (IMM-475.4, Eltern-AC 2). Der Agent sieht nur Status + diese Zeile —
          // weder die zurückgehaltenen Zahlen noch den Bericht.
          'awaiting-release-hint':
            'Die Bewertung liegt zur Prüfung vor. Du wirst benachrichtigt, sobald sie freigegeben ist.',
          // Überschrift des Verlaufsblocks auf dem Bewertung-Tab (IMM-475.5) —
          // dieselben Einträge wie im Aktivität-Tab des Deals, gefiltert auf die
          // Bewertung. Bewusst identisch benannt, damit dieselben Datensätze auf
          // einer Seite nicht zwei Namen tragen.
          'activity-title': 'Aktivität',
          // Die Prüfansicht des Freigebenden (IMM-475.4) — nur für Mitglieder mit
          // `organization:valuation:valuation-request-release`. Wer die
          // Berechtigung nicht hat, sieht den Status und sonst nichts; es gibt
          // hier bewusst keine deaktivierten Schaltflächen.
          review: {
            title: 'Preisspanne und Startpreis prüfen',
            'price-range-label': 'Preisspanne',
            'recommended-start-price-label': 'Empfohlener Startpreis',
            'valuation-date-label': 'Bewertungsdatum',
            'produced-by-label': 'Erstellt von',
            'submitted-at-label': 'Eingereicht am',
            'not-provided': '—',
            // Anpassen ist bewusst ein eigener Schritt VOR der Freigabe (erst
            // speichern, dann freigeben) — nie ein Feld im Freigabe-Dialog.
            'adjust-title': 'Zahlen anpassen',
            'price-from-label': 'Preisspanne von',
            'price-to-label': 'Preisspanne bis',
            save: 'Zahlen speichern',
            saving: 'Wird gespeichert…',
            'save-success': 'Zahlen gespeichert.',
            release: 'Freigeben',
            releasing: 'Wird freigegeben…',
            'release-success': 'Bewertung freigegeben. Der Agent wurde benachrichtigt.',
            return: 'Zur Überarbeitung zurückgeben',
            returning: 'Wird zurückgegeben…',
            'return-success': 'Bewertung zur Überarbeitung zurückgegeben.',
            'return-cancel': 'Abbrechen',
            'return-submit': 'Zurückgeben',
            'return-reason-label': 'Grund der Rückgabe',
            // Freigegeben wird immer der GESPEICHERTE Stand — die Freigabe sendet
            // keine Zahlen mit. Solange die Felder davon abweichen, ist „Freigeben"
            // gesperrt und diese Zeile sagt, warum.
            'unsaved-figures':
              'Die geänderten Zahlen sind noch nicht gespeichert. Speichere sie zuerst — freigegeben wird immer der gespeicherte Stand.',
            errors: {
              forbidden: 'Du hast keine Berechtigung, Bewertungen freizugeben.',
              // Eine fachliche Ablehnung (BL-020) — dauerhaft, kein „erneut
              // versuchen": die Bewertung hat den Freigabezustand verlassen.
              'not-awaiting-release': 'Diese Bewertung wartet nicht auf Freigabe.',
              // 404 — die Anfrage ist gelöscht oder liegt nicht mehr im Zugriff.
              // Ebenfalls dauerhaft: kein „erneut versuchen", sondern neu lesen.
              'not-found': 'Diese Bewertungsanfrage ist nicht mehr verfügbar.',
              'result-invalid': 'Die Preisspanne ist ungültig: Der Startpreis muss innerhalb der Spanne liegen.',
              'return-reason-required': 'Bitte gib einen Grund für die Rückgabe an.',
              'prices-required': 'Bitte gib die Preisspanne von und bis an.',
              save: 'Die Zahlen konnten nicht gespeichert werden. Bitte versuche es erneut.',
              release: 'Die Bewertung konnte nicht freigegeben werden. Bitte versuche es erneut.',
              return: 'Die Bewertung konnte nicht zurückgegeben werden. Bitte versuche es erneut.',
            },
          },
          // Die read-only Vorschau (IMM-407.3 AC 28) — alle erfassten Angaben nach
          // Block gruppiert, mit „Zurück zum Formular" und „Absenden".
          preview: {
            title: 'Vorschau der Anfrage',
            intro: 'Prüfe alle Angaben, bevor die Anfrage an den Gutachter geht.',
            back: 'Zurück zum Formular',
            submit: 'Absenden',
            submitting: 'Wird gesendet…',
            'not-provided': '—',
            // Ein nicht erfasster Preis liest sich als „Keine Angabe", nicht als
            // der Gedankenstrich der übrigen Felder.
            'price-expectation-none': 'Keine Angabe',
            'documents-empty': 'Keine Unterlagen',
            'images-empty': 'Keine Bilder',
            'parking-empty': 'Keine Stellplätze erfasst',
            'units-empty': 'Keine Einheiten erfasst',
            'unit-heading': 'Einheit {index}',
            // Blocktitel für alle Blöcke der Vorschau (das Layout stammt aus der
            // gemeinsamen `valuationBlockLayout`-Funktion).
            block: {
              basics: 'Basis',
              reason: 'Anlass & Erwartung',
              condominium: 'Angaben zur Eigentumswohnung',
              house: 'Angaben zum Haus',
              multiFamily: 'Angaben zum Mehrfamilienhaus',
              plot: 'Angaben zum Grundstück',
              overallCondition: 'Qualität und Zustand',
              parkingSpaces: 'Stellplätze',
              income: 'Ertrag (IST)',
              units: 'Einheiten',
              hoa: 'WEG',
              documents: 'Unterlagen',
              images: 'Bilder',
            },
            // Feldnamen für die Basis- und Anlass-Blöcke (die typenspezifischen
            // Felder nutzen `form.field`).
            field: {
              objectType: 'Bewertungsobjektart',
              usage: 'Nutzung',
              address: 'Adresse',
              valuationReason: 'Bewertungsanlass',
              timeHorizon: 'Zeithorizont',
              desiredCompletion: 'Gewünschte Fertigstellung',
              note: 'Anmerkung',
            },
          },
          // Das gelieferte Ergebnis auf dem Tab (IMM-407.6, AC 3/52) — schreibgeschützt,
          // sobald das Objekt eine Bewertung trägt. Führt mit der Preisspanne, dann
          // empf. Startpreis + Bewertungsdatum, und bietet das Wertgutachten zur
          // Vorschau/zum Download.
          result: {
            title: 'Bewertungsergebnis',
            'completed-at': 'Abgeschlossen am {date}',
            'price-range-label': 'Preisspanne',
            'recommended-start-price-label': 'Empfohlener Startpreis',
            'valuation-date-label': 'Bewertungsdatum',
            'report-label': 'Wertgutachten',
            'report-fallback-name': 'Wertgutachten',
            'report-preview': 'Vorschau',
            'report-download': 'Herunterladen',
            'report-in-documents': 'In den Dokumenten des Objekts',
            'report-error': 'Das Wertgutachten konnte nicht geöffnet werden. Bitte versuche es erneut.',
            // Der Bericht-Viewer (IMM-475.3): Umschalter zwischen der gesandboxten
            // HTML-Vorschau und dem bestehenden PDF-Viewer, plus der Hinweis unter
            // der Vorschau, dass im Bericht nichts ausgeführt wird.
            viewer: {
              'format-label': 'Format des Berichts',
              html: 'HTML-Vorschau',
              pdf: 'PDF',
              'frame-title': 'Vorschau des Berichts',
              // Öffnet den Bericht im Vollbild-Dialog (IMM-609): die Vorschau
              // bleibt der schnelle Blick, gelesen wird er in voller Breite.
              expand: 'Vollbild',
              close: 'Schließen',
              note: 'Vorschau des Berichts — Skripte und externe Inhalte sind deaktiviert.',
              loading: 'Vorschau wird geladen …',
              error: 'Die Vorschau konnte nicht geladen werden. Bitte versuche es erneut.',
            },
            // Wird gezeigt, wenn diese Bewertung eine frühere ersetzt hat (AC 47).
            superseded: 'Diese Bewertung ersetzt die vorherige Bewertung des Objekts vom {date}.',
          },
          // Aktion auf dem Abgeschlossen-Screen (IMM-407.6) — die einzige Aktion; die
          // Deal-Phase wird über die normale Phasensteuerung gesetzt, nicht hier.
          'request-again': 'Neue Bewertung anfragen',
          'revalue-hint': 'Du kannst jederzeit eine neue Bewertung für dieses Objekt anfragen.',
          // Die Ergebnisse des Bewertungs-Agenten über dem Tab-Inhalt (IMM-1391.4).
          'agent-results': {
            title: VALUATION_PRODUCT_LABELS,
            author: 'Erstellt vom Bewertungs-Agent am {date}',
            superseded: 'Ersetzt am {date}',
            'price-range': 'Preisspanne',
            'recommended-start-price': 'Empfohlener Startpreis',
            view: 'Ansehen',
            download: 'Herunterladen',
          },
          // Die Verlaufsliste der abgeschlossenen Anfragen auf dem Tab
          // (IMM-407.3 AC 35).
          history: {
            title: 'Abgeschlossene Bewertungen',
            'requested-by': 'Angefragt von {name}',
            'requested-at': 'Angefragt am {date}',
          },
          // Übersetzte Statuslabels der Bewertungsanfrage (IMM-407).
          status: {
            DRAFT: 'Entwurf',
            REQUESTED: 'Angefragt',
            IN_PROGRESS: 'In Bearbeitung',
            COMPLETED: 'Abgeschlossen',
            WITHDRAWN: 'Zurückgezogen',
          },
        },
        sections: {
          object: 'Objekt',
          contacts: 'Kontakte',
          commercial: 'Kommerzielle Daten',
          activity: 'Aktivität',
          notes: 'Notizen',
          progress: 'Phasen-Fortschritt',
          metadata: 'Metadaten',
          responsible: 'Verantwortliche Mitglieder',
        },
        lifecycle: {
          responsible: {
            label: 'Verantwortliche Mitglieder',
            empty: 'Niemandem zugewiesen',
            'rbac-hint': 'Nur Owner, Workspace-Admin und Standortleitung können Deals zuweisen.',
          },
          assign: {
            cta: 'Zuweisen',
            title: 'Deal zuweisen',
            'members-label': 'Verantwortliche Mitglieder',
            'empty-error': 'Bitte mindestens ein verantwortliches Mitglied auswählen.',
            'no-results': 'Keine Mitglieder gefunden.',
            remove: '{name} entfernen',
            submit: 'Zuweisen',
            submitting: 'Wird zugewiesen…',
            cancel: 'Abbrechen',
            success: 'Deal zugewiesen.',
            'forbidden-error': 'Du darfst diesen Deal nicht zuweisen.',
            'not-found-error': 'Dieser Deal existiert nicht mehr.',
            'generic-error': 'Der Deal konnte nicht zugewiesen werden. Bitte versuche es erneut.',
          },
        },
        pipeline: {
          'aria-label': 'Pipeline-Fortschritt',
        },
        // Die Referenz-Leiste (IMM-456.3). Sie ist lesend: jede „ändern"-/
        // „hinzufügen"-Aktion öffnet den bereits vorhandenen Dialog, deshalb
        // stehen hier nur die Kartentitel und Feldbeschriftungen — die Aktionen
        // tragen die Labels, die sie schon haben.
        rail: {
          'aria-label': 'Referenzdaten',
          'standort-title': 'Standort & Verantwortung',
          'standort-label': 'Standort',
          'standort-no-access': 'Kein Zugriff',
          'responsible-label': 'Verantwortlich',
          'owners-title': 'Eigentümer',
          'phone-label': 'Telefon',
          'email-label': 'E-Mail',
          'object-title': 'Objekt',
          'object-type-label': 'Objekttyp',
          'year-built-label': 'Baujahr',
          maps: 'Auf Karte',
          // Valuation card (IMM-456.4). Its status labels are DELIBERATELY their
          // own strings rather than the shared VALUATION_STATUS_LABELS: there
          // AWAITING_RELEASE reads „Warte auf Freigabe“, naming an internal immoOS
          // review the customer must never see. On the card the only true
          // statement is that it is being worked on.
          'valuation-title': 'Bewertung',
          'valuation-empty': 'Noch keine Bewertung angefragt.',
          'valuation-request': 'Bewertung anfragen',
          'valuation-status-requested': 'Angefragt',
          'valuation-status-in-progress': 'In Bearbeitung',
          'valuation-status-done': 'Abgeschlossen',
          'valuation-requested-at-label': 'Angefragt am',
          'valuation-price-range-label': 'Preisspanne',
          'valuation-start-price-label': 'Empf. Startpreis',
          'valuation-completed-at-label': 'Erstellt am',
          'valuation-report': 'Bewertungsdokument öffnen',
          'valuation-previous-result': 'Vorherige Bewertung',
          // Unterlagen-Check (IMM-456.5). Die Zeilen-Vermerke sind BEWUSST kürzer
          // als die des Dokumente-Tabs („Läuft ab am {date}“): in einer 360-px-
          // Spalte trägt das Datum nicht, der Zustand schon. Zähler, „Angefordert
          // am“-Chip und der fehlende-E-Mail-Hinweis kommen dagegen unverändert
          // aus dem Dokumente-Tab — zwei Kopien wären genau der Weg, auf dem die
          // beiden Oberflächen auseinanderlaufen.
          'unterlagen-title': 'Unterlagen-Check',
          'unterlagen-count': '{present} / {required}',
          'unterlagen-request': 'Fehlende anfordern',
          'unterlagen-status-missing': 'fehlt',
          'unterlagen-status-expiring': 'läuft ab',
          'unterlagen-status-expired': 'abgelaufen',
          'unterlagen-complete': 'Datenraum vollständig — alle Pflichtunterlagen liegen vor.',
          'unterlagen-more': 'und {count} weitere Unterlagen im Datenraum des Objekts',
          // Herkunft & Notizen (IMM-456.6). „Quelle“ heißt zweierlei, und das ist
          // Absicht: mit Lead-Herkunft ist es der Akquisekanal des Leads, ohne die
          // Quelle des Deals selbst. Beide Tabellen sind über `EnumTranslation`
          // vollständig, damit ein neues Token den Build bricht statt einen rohen
          // Schlüssel zu rendern.
          'origin-title': 'Herkunft & Notizen',
          'origin-source-label': 'Quelle',
          'origin-received-label': 'Eingegangen',
          'origin-qualified-label': 'Aus Lead',
          'origin-qualified-by': 'Qualifiziert · {name}',
          'origin-qualified-anonymous': 'Qualifiziert',
          'origin-concern-label': 'Anliegen',
          'origin-created-label': 'Erstellt am',
          'origin-lead-source': LEAD_SOURCE_LABELS,
          'origin-deal-source': DEAL_SOURCE_LABELS,
          // Deal-Eckdaten (IMM-456.6). Maklergebühr und Tage in Phase fehlen hier
          // bewusst — beide stehen dauerhaft im Sticky-Header (IMM-456.2), und die
          // 360-px-Spalte zweimal dasselbe sagen zu lassen ist verschenkter Platz.
          'facts-title': 'Deal-Eckdaten',
          'facts-probability-label': 'Wahrscheinlichkeit',
          'facts-horizon-label': 'Abschluss-Horizont',
          'facts-created-label': 'Erstellt am',
          'facts-created-by-label': 'Erstellt von',
          // Maklervertrag (IMM-1171.8): die Karte ganz oben im Deal.
          'brokerage-contract': {
            title: 'Maklervertrag',
            empty: 'Noch kein Maklervertrag erstellt.',
            create: 'Maklervertrag erstellen',
            'load-error': 'Der Maklervertrag konnte nicht geladen werden.',
            'sub-line': '{templateName} · Erstellt am {date}',
            'status-not-sent': 'Noch nicht gesendet',
            'status-sent': 'Zur Unterschrift gesendet',
            'status-partially-signed': 'Teilweise unterschrieben',
            'status-signed': 'Unterschrieben',
            'status-revoked': 'Widerrufen',
            'status-withdrawn': 'Zurückgezogen',
            progress: '{signed} von {total} unterschrieben',
            'owner-signed': 'Unterschrieben am {date}',
            'owner-not-signed': 'Noch nicht unterschrieben',
            'owner-not-delivered': 'Nicht zugestellt',
            'acceptance-pending': 'Annahme per E-Mail: ausstehend',
            'acceptance-received': 'Annahme per E-Mail: eingegangen am {date}',
            'acceptance-mark': 'Als eingegangen markieren',
            'acceptance-dialog-title': 'Annahme per E-Mail vermerken',
            'acceptance-dialog-body': 'Antwort von {name} mit dem Annahmetext erhalten?',
            'acceptance-dialog-confirm': 'Vermerken',
            'acceptance-dialog-cancel': 'Abbrechen',
            'revoked-by': 'Widerrufen von {name} am {date}',
            'signed-on': 'Unterschrieben am {date}',
            send: 'Zur Unterschrift senden',
            remind: 'Erinnerung senden',
            'download-signed': 'Unterschriebenes PDF herunterladen',
            'view-pdf': 'PDF ansehen',
            'view-proof': 'Nachweis ansehen',
            withdraw: 'Zurückziehen',
            'pending-file': 'Unterschrieben — PDF wird erstellt …',
          },
          // Dokumente (IMM-268.4): die generierten Dokumente des Deals.
          documents: {
            title: 'Dokumente',
            empty: 'Noch kein Dokument erstellt.',
            'row-title': '{documentType} · {templateName}',
            'row-meta': '{createdBy}, {createdAt}',
            generating: 'Wird erzeugt …',
            failed: 'Erstellung fehlgeschlagen.',
            retry: 'Erneut versuchen',
            download: 'Herunterladen',
            delete: 'Löschen',
            'manually-adjusted': 'Manuell angepasst',
            'manually-adjusted-tooltip': 'Der Text wurde für dieses Dokument abweichend von der Vorlage bearbeitet.',
            'delete-title': 'Dokument löschen',
            'delete-body': 'Dokument ‚{fileName}‘ löschen? Die Kopie in den Objekt-Dokumenten wird ebenfalls gelöscht.',
            'delete-confirm': 'Löschen',
            'delete-cancel': 'Abbrechen',
            'original-received': 'Original eingegangen',
            'original-received-on': 'Original eingegangen am {date}',
            'document-type': {
              BROKER_CONTRACT: DOCUMENT_CATEGORY_LABELS.BROKER_CONTRACT,
              POWER_OF_ATTORNEY: DOCUMENT_CATEGORY_LABELS.POWER_OF_ATTORNEY,
            } satisfies Record<DocumentTemplateCategory, string>,
            'load-error': 'Die Dokumente konnten nicht geladen werden.',
            'generic-error': 'Das hat nicht geklappt. Bitte versuche es erneut.',
            signing: {
              send: 'Zur Unterschrift senden',
              'dialog-title':
                '{documentType, select, BROKER_CONTRACT {Maklervertrag} other {Vollmacht}} zur Unterschrift senden',
              'dialog-subtitle': '{templateName} · erstellt am {date}',
              'dialog-hint':
                'Jeder Eigentümer bekommt eine eigene E-Mail mit dem Dokument als PDF und einem persönlichen Link zum Unterschreiben.',
              'email-label': 'E-Mail-Adresse',
              'email-hint': 'Wird beim Senden am Kontakt gespeichert.',
              'email-missing-no-permission':
                'Keine E-Mail-Adresse hinterlegt. Bitte jemanden mit Zugriff auf den Kontakt, sie zu ergänzen.',
              'agency-signature-label': 'Deine Unterschrift für {companyName}',
              'agency-signature-required': 'Unterschrift und Bestätigung sind erforderlich.',
              'agency-signature-checkbox': 'Ich unterschreibe für {companyName}',
              'company-data-missing': 'Bitte einen Owner oder Admin, die Unternehmensdaten zu ergänzen.',
              'power-of-attorney-hint':
                'Grundbuchämter und manche Behörden verlangen die Vollmacht im Original. Lass sie dafür zusätzlich auf Papier unterschreiben.',
              submit: 'Senden',
              cancel: 'Abbrechen',
              'sent-notice':
                '{documentType, select, BROKER_CONTRACT {Maklervertrag} other {Vollmacht}} an {count} Eigentümer zur Unterschrift gesendet',
              'state-sent': 'Zur Unterschrift gesendet · {signed} von {total} unterschrieben',
              'state-partially-signed': 'Teilweise unterschrieben · {signed} von {total}',
              'state-signed-pending-file': 'Unterschrieben — PDF wird erstellt …',
              'state-withdrawn': 'Zurückgezogen',
              'state-revoked': 'Widerrufen',
              'delete-locked-open': 'Zieh zuerst die Unterschriftsanfrage zurück.',
              'delete-locked-signed': 'Unterschriebene Dokumente können nicht gelöscht werden.',
              'state-signed': 'Unterschrieben',
              'download-signed': 'Unterschriebenes PDF herunterladen',
              'retry-load': 'Erneut versuchen',
              'email-invalid': 'Bitte gib eine gültige E-Mail-Adresse ein.',
              'draft-load-error': 'Der Dialog konnte nicht geladen werden. Bitte versuche es erneut.',
              'generic-error': 'Das hat nicht geklappt. Bitte versuche es erneut.',
              remind: 'Erinnerung senden',
              withdraw: 'Zurückziehen',
              'reminder-automatic': 'Erinnerung automatisch am {date}',
              'reminder-last': 'Letzte Erinnerung: {date}, {time}',
              'reminded-notice': 'Erinnerung an {count} Eigentümer gesendet',
              'withdraw-title': 'Unterschriftsanfrage zurückziehen',
              'withdraw-body':
                'Unterschriftsanfrage für „{templateName}“ zurückziehen? Die Links der Eigentümer funktionieren danach nicht mehr.',
              'withdraw-confirm': 'Zurückziehen',
              'withdraw-cancel': 'Abbrechen',
              'withdrawn-notice': 'Unterschriftsanfrage zurückgezogen',
              'signer-sent': 'Gesendet am {date} an {email}',
              'signer-delivered': 'Zugestellt',
              'signer-not-delivered': 'Nicht zugestellt — die Adresse hat die E-Mail abgewiesen.',
              'signer-signed': 'Unterschrieben am {date} um {time}',
              'signer-not-signed': 'Noch nicht unterschrieben',
              'signer-early-start': 'Vorzeitiger Beginn verlangt',
              'signer-revoked': 'Widerrufen von {name} am {date}',
              'proof-open': 'Nachweis ansehen',
              'proof-title': 'Nachweis · {documentType}',
              'proof-subtitle': '{templateName} · {address}',
              'proof-sent-by': 'Gesendet von {name} am {date}, {time}',
              'proof-sent-at': 'Gesendet am {date}, {time}',
              'proof-agency-signature': 'Unterschrift für die Firma:',
              'proof-signed': 'Unterschrieben',
              'proof-early-start': 'Vorzeitiger Beginn verlangt',
              'proof-acceptance': 'Annahme per E-Mail',
              'proof-acceptance-received': 'eingegangen am {date}, {time} · vermerkt von {name}',
              'proof-acceptance-received-at': 'eingegangen am {date}, {time}',
              'proof-acceptance-pending': 'ausstehend',
              'proof-yes': 'ja',
              'proof-no': 'nein',
              'proof-ip': 'IP-Adresse',
              'proof-browser': 'Browser',
              'proof-no-browser': 'Nicht übermittelt',
              'proof-link-sent-to': 'Link gesendet an',
              'proof-typed-name': 'Getippter Name, wie unterschrieben',
              'proof-emails': 'Gesendete E-Mails',
              'proof-email-meta': '{kind} an {recipient} · {date}, {time}',
              'proof-email-delivered': 'Zugestellt',
              'proof-email-not-delivered': 'Nicht zugestellt',
              'proof-personal-link': '[persönlicher Link]',
              'proof-declarations': 'Erklärungen im Wortlaut',
              'proof-checked': 'angekreuzt',
              'proof-unchecked': 'nicht angekreuzt',
              'proof-page': 'Seite, wie angezeigt',
              'proof-not-signed': 'Noch nicht unterschrieben',
              'proof-close': 'Schließen',
              'proof-loading': 'Nachweis wird geladen …',
              'proof-load-failed': 'Der Nachweis konnte nicht geladen werden.',
              'email-kind': {
                INVITATION: 'Einladung',
                REMINDER: 'Erinnerung',
                CONFIRMATION: 'Bestätigung',
                COMPLETION: 'Vollständig unterschrieben',
                ACCEPTANCE_REMINDER: 'Erinnerung zur Annahme',
                WITHDRAWAL_RECEIPT: 'Widerrufsbestätigung',
              } satisfies Record<SigningProofEmail['kind'], string>,
            },
          },
        },
        // „Dokument erstellen" (IMM-268.5): Vorlage wählen und fehlende Angaben ergänzen.
        'documents-dialog': {
          open: 'Dokument erstellen',
          title: 'Dokument erstellen',
          'document-type-label': 'Dokumentart',
          'template-label': 'Vorlage',
          'template-placeholder': 'Bitte wählen',
          'no-template': 'Für diese Dokumentart gibt es noch keine Vorlage.',
          'no-template-link': 'Zu den Vorlagen',
          'no-template-ask': 'Bitte einen Owner oder Admin, eine Vorlage anzulegen.',
          'owner-coverage': 'Diese Vorlage nennt nur {named} von {total} Eigentümern.',
          'templates-load-error': 'Die Vorlagen konnten nicht geladen werden.',
          'generic-error': 'Das hat nicht geklappt. Bitte versuche es erneut.',
          next: 'Weiter',
          cancel: 'Abbrechen',
          'missing-title': 'Fehlende Angaben',
          'missing-hint':
            'Für dieses Dokument fehlen noch Angaben. Was du hier einträgst, wird beim Erstellen am Kontakt, Objekt, Deal oder in den Unternehmensdaten gespeichert.',
          'group-owner': 'Eigentümer {name}',
          'group-property': 'Objekt',
          'group-deal': 'Deal',
          'group-location': 'Standort {name}',
          'group-broker': 'Makler {name}',
          'group-document-only-hint': 'Gilt nur für dieses Dokument — Standort und Profil bleiben unverändert.',
          'group-company': 'Unternehmensdaten',
          'optional-hint': 'Optional — bleibt es leer, druckt das Dokument eine Linie zum Ausfüllen.',
          'required-empty': 'Fülle alle Pflichtangaben aus.',
          'company-locked': 'Nur Owner und Admins können Unternehmensdaten ändern.',
          'company-blocked': 'Bitte einen Owner oder Admin, die Unternehmensdaten zu ergänzen.',
          'source-locked': 'Du darfst diese Angabe nicht ändern.',
          'source-blocked': 'Bitte jemanden mit Bearbeitungsrecht, die fehlenden Angaben zu ergänzen.',
          'not-applicable': 'Bei einem Unternehmen als Eigentümer gibt es diese Angabe nicht.',
          'not-applicable-blocked':
            'Markiere das Feld in der Vorlage als „Darf leer bleiben“, um das Dokument zu erstellen.',
          'to-preview': 'Weiter zur Vorschau',
          back: 'Zurück',
          'preview-title': 'Vorschau',
          'preview-hint':
            'Prüfe das Dokument. Du kannst Werte ändern, Kästchen ankreuzen und den Text für dieses Dokument anpassen. Die Vorlage bleibt unverändert.',
          'save-hint': {
            contact: 'Wird beim Erstellen am Kontakt gespeichert.',
            property: 'Wird beim Erstellen am Objekt gespeichert.',
            deal: 'Wird beim Erstellen am Deal gespeichert.',
            company: 'Wird beim Erstellen in den Unternehmensdaten gespeichert.',
            'document-only': 'Gilt nur für dieses Dokument — Standort und Profil bleiben unverändert.',
          },
          'apply-value': 'Übernehmen',
          'text-edited': 'Text manuell angepasst — die Vorlage bleibt unverändert.',
          'reset-text': 'Textänderungen zurücksetzen',
          'reset-confirm': 'Alle Textänderungen an diesem Dokument zurücksetzen?',
          'reset-confirm-ok': 'Zurücksetzen',
          'reset-confirm-cancel': 'Abbrechen',
          create: 'Erstellen',
          creating: '{documentType} wird erstellt …',
          'creating-hint': 'Das dauert nur einen Moment.',
          'create-failed': 'Erstellung fehlgeschlagen. Bitte versuche es erneut.',
          created: '{documentType} erstellt',
          'open-pdf': 'PDF öffnen',
          'box-toggle': 'Kästchen ankreuzen',
          'change-value': '{field} ändern',
          'owner-row': 'Eigentümer {name}: {input}',
          'broker-row': 'Makler {name}: {input}',
          'location-no-manager': 'Dieser Standort hat keine Standortleitung.',
          'location-no-manager-blocked': 'Bitte einen Owner oder Admin, dem Standort eine Standortleitung zuzuweisen.',
          'location-option-label': 'Makler-Block',
          'location-option': {
            LOCATION: 'Standort inkl. Standortleiter',
            HEADQUARTERS: 'Hauptstandort',
          },
          'location-option-replace-title': 'Standort-Angaben ersetzen?',
          'location-option-replace-body': 'Deine Änderungen an den Standort-Angaben gehen verloren.',
          'location-option-replace-confirm': 'Ersetzen',
          'location-option-replace-cancel': 'Abbrechen',
          part: {
            'first-name': 'Vorname',
            'last-name': 'Nachname',
            'street-and-number': 'Straße und Hausnummer',
            street: 'Straße',
            'house-number': 'Hausnummer',
            'postal-code': 'PLZ',
            city: 'Ort',
          },
          input: {
            OWNER: {
              NAME: 'Name',
              ADDRESS: 'Wohnanschrift',
              DATE_OF_BIRTH: 'Geburtsdatum',
              NATIONALITY: 'Nationalität',
              BIRTH_NAME: 'Geburtsname',
              BIRTH_PLACE: 'Geburtsort',
              EMAIL: 'E-Mail',
              PHONE: 'Telefon',
            },
            LOCATION: { NAME: 'Name', ADDRESS: 'Adresse', MANAGERS: 'Standortleitung' },
            BROKER: { NAME: 'Name', EMAIL: 'E-Mail', PHONE: 'Telefon' },
            PROPERTY: { ADDRESS: 'Adresse' },
            DEAL: {
              EXPECTED_SALE_PRICE: 'Erwarteter Verkaufspreis',
              MANDATE_TYPE: 'Auftragsart',
              MANDATE_START: 'Tätigkeitsaufnahme',
              MANDATE_DURATION: 'Laufzeit',
              SELLER_COMMISSION: 'Provision Verkäufer',
              BUYER_COMMISSION: 'Provision Käufer',
            },
            COMPANY: {
              COMPANY_NAME: 'Firmenname',
              LEGAL_FORM: 'Rechtsform',
              ADDRESS: 'Adresse',
              MANAGING_DIRECTORS: 'Geschäftsführer',
              REGISTER_COURT: 'Handelsregister',
              REGISTER_NUMBER: 'Registernummer',
              VAT_ID: 'USt-IdNr.',
              TRADE_PERMIT_AUTHORITY: 'Aufsichtsbehörde §34c',
              PROFESSIONAL_INDEMNITY: 'Berufshaftpflicht',
            },
          } satisfies {
            [S in keyof typeof DOCUMENT_SOURCE_INPUTS]: Record<(typeof DOCUMENT_SOURCE_INPUTS)[S][number], string>;
          },
        },
        object: {
          open: 'Objekt öffnen',
        },
        contacts: {
          count: '({count})',
          'primary-badge': 'Primär',
          add: 'Eigentümer hinzufügen',
          remove: '{name} entfernen',
        },
        'add-dialog': {
          title: 'Eigentümer hinzufügen',
          intro: 'Wähle einen oder mehrere weitere Eigentümer-Kontakte für diesen Deal.',
          submit: 'Hinzufügen',
          submitting: 'Wird hinzugefügt …',
          cancel: 'Abbrechen',
        },
        errors: {
          forbidden: 'Du bist für diese Aktion nicht berechtigt.',
          duplicate: 'Dieser Kontakt ist bereits mit dem Deal verknüpft.',
          'non-owner-link-conflict':
            'Dieser Kontakt ist bereits mit einer anderen Rolle mit dem Objekt verknüpft. Ein Deal setzt eine Eigentümer-Beziehung voraus.',
          'not-owner':
            'Dieser Kontakt ist kein Eigentümer des Objekts. Füge ihn zuerst als Eigentümer am Objekt hinzu.',
          'active-deal-overlap': 'Für diesen Eigentümer + Objekt existiert bereits ein aktiver Deal.',
          'won-immutable': 'Gewonnene Deals sind unveränderlich.',
          'last-contact':
            'Mindestens ein Eigentümer-Kontakt ist erforderlich. Füge zuerst einen weiteren Eigentümer hinzu, bevor du diesen entfernst.',
          'defer-not-active': 'Gewonnene oder verlorene Deals können nicht zurückgestellt werden.',
          'resume-not-deferred': 'Dieser Deal ist nicht zurückgestellt.',
          'mark-lost-won-immutable': 'Gewonnene Deals sind unveränderlich.',
          'mark-lost-already-lost': 'Dieser Deal ist bereits verloren.',
          'reopen-not-lost': 'Dieser Deal ist nicht verloren.',
          'delete-won-immutable': 'Gewonnene Deals sind unveränderlich.',
          'no-loss-reason': 'Bitte wähle einen Verlustgrund',
          generic: 'Aktion fehlgeschlagen. Bitte versuche es erneut.',
        },
        commercial: {
          'fee-label': 'Provisionshöhe',
          'fee-helper': '{percent} % vom Verkaufspreis',
          'provision-helper': '{percent} % vom Verkaufspreis · netto, zzgl. MwSt. · Standard Bestellerprinzip',
          'sale-price-label': 'Erwarteter Verkaufspreis',
          'commission-label': 'Provision',
          'commission-percent-label': 'Provision',
          'expected-fee-label': 'Erwartete Maklergebühr',
          'probability-label': 'Wahrscheinlichkeit',
          'completion-horizon-label': 'Abschluss-Horizont',
          'vot-label': 'VOT vereinbart',
          'vot-yes': 'Ja',
          'vot-no': 'Nein',
        },
        actions: {
          defer: 'Zurückstellen',
          'mark-lost': 'Als verloren markieren',
          resume: 'Wieder aufnehmen',
          reopen: 'Wieder öffnen',
          'no-update-permission': 'Du hast keine Berechtigung, diesen Deal zu bearbeiten.',
          delete: 'Löschen',
          more: 'Weitere Aktionen',
          'coming-soon-hint': 'Bald verfügbar',
        },
        'defer-dialog': {
          title: 'Zurückstellen',
          intro: 'Wähle, wann dieser Deal automatisch wieder aufgenommen werden soll.',
          'presets-label': 'Schnellauswahl',
          'date-label': 'Zurückgestellt bis',
          'preset-week': '1 Woche',
          'preset-month': '1 Monat',
          'preset-quarter': '3 Monate',
          'preset-custom': 'Eigenes Datum',
          submit: 'Zurückstellen',
          submitting: 'Wird zurückgestellt …',
          cancel: 'Abbrechen',
          'error-no-date': 'Bitte wähle ein Datum, an dem der Deal wieder aufgenommen werden soll.',
          'error-past-date': 'Das Wiedervorlage-Datum muss heute oder in der Zukunft liegen.',
        },
        hold: {
          title: 'Zurückstellung',
          'deferred-until-label': 'Zurückgestellt bis',
          edit: 'Bearbeiten',
          'edit-aria': 'Wiedervorlage-Datum bearbeiten',
          save: 'Speichern',
          saving: 'Wird gespeichert …',
          cancel: 'Abbrechen',
        },
        'defer-success': 'Deal zurückgestellt bis {date}.',
        'resume-success': 'Deal wieder aufgenommen.',
        'resume-error': 'Deal konnte nicht wieder aufgenommen werden.',
        'auto-resume-banner':
          'Der Deal wurde am {date} automatisch wieder aufgenommen — du hattest ihn bis dahin zurückgestellt.',
        'auto-resume-dismiss': 'Alles klar',
        'lost-dialog': {
          title: 'Als verloren markieren',
          intro: '„{title}“ als verloren markieren. Wähle einen Verlustgrund. Eine Notiz ist optional.',
          'reason-label': 'Verlustgrund',
          'reason-placeholder': 'Verlustgrund wählen …',
          'note-label': 'Verlust-Notiz',
          'note-placeholder': 'Optionale Notiz zum Verlust …',
          'note-recommended': 'Bei „Sonstiges“ empfohlen — bitte kurz erläutern.',
          submit: 'Als verloren markieren',
          submitting: 'Wird markiert …',
          cancel: 'Abbrechen',
          'error-no-reason': 'Bitte wähle einen Verlustgrund',
        },
        lost: {
          'reason-label': 'Verlustgrund',
          'note-label': 'Verlust-Notiz',
          banner: 'Deal verloren — wähle „Wieder öffnen“, um ihn erneut zu bearbeiten.',
        },
        'mark-lost-success': 'Deal als verloren markiert.',
        'mark-lost-error': 'Deal konnte nicht als verloren markiert werden. Bitte versuche es erneut.',
        'reopen-success': 'Deal wieder geöffnet.',
        'reopen-error': 'Deal konnte nicht wieder geöffnet werden.',
        'delete-dialog': {
          title: 'Deal löschen',
          intro: 'Möchtest du den Deal „{name}“ wirklich löschen? Er verschwindet aus allen Ansichten und Listen.',
          submit: 'Löschen',
          submitting: 'Wird gelöscht …',
          cancel: 'Abbrechen',
        },
        'delete-success': 'Deal gelöscht.',
        'delete-error': 'Deal konnte nicht gelöscht werden.',
        activity: {
          empty: 'Noch keine Aktivität.',
          'action-created': 'Deal angelegt',
          'action-forward': 'Phase vorwärts',
          'action-backward': 'Phase zurück',
          'action-contact-added': 'Eigentümer hinzugefügt',
          'action-contact-removed': 'Eigentümer entfernt',
          transition: '{from} → {to}',
          'actor-system': 'System',
          'actor-unknown': 'Unbekanntes Mitglied',
          'contact-unknown': 'Unbekannter Kontakt',
          'reason-label': 'Grund',
          'load-more': 'Mehr laden',
          'loading-more': 'Wird geladen …',
        },
        notes: {
          'obstacles-label': 'Hindernisse',
          'next-steps-label': 'Nächste Schritte',
          'coming-soon': 'Bald verfügbar',
          empty: 'Noch nichts erfasst.',
          edit: 'Bearbeiten',
          'edit-aria': '{label} bearbeiten',
          save: 'Speichern',
          saving: 'Wird gespeichert …',
          cancel: 'Abbrechen',
          'save-error': 'Notiz konnte nicht gespeichert werden.',
          'obstacles-placeholder': 'Z. B. Ehefrau muss noch zustimmen, Erbengemeinschaft uneinig …',
          'next-steps-placeholder': 'Z. B. Bewertungstermin Donnerstag 14:00 …',
        },
        kpis: {
          'days-since-creation-label': 'Tage seit Anlage',
          'probability-label': 'Wahrscheinlichkeit',
          'days-value': '{days, plural, one {# Tag} other {# Tage}}',
        },
        progress: {
          count: '{done} von {total} Phasen abgeschlossen',
          'horizon-hint': 'Abschluss-Horizont · {value}',
        },
        metadata: {
          'created-at-label': 'Erstellt am',
          'days-since-creation-label': 'Tage seit Anlage',
          'created-by-label': 'Erstellt von',
          'created-by-coming-soon': 'Bald verfügbar',
          'source-label': 'Quelle',
        },
        won: {
          banner:
            'Deal gewonnen — kommerzielle Daten sind gesperrt. Verkaufspreis und Provision können nicht mehr geändert werden.',
          'lock-hint': 'Gesperrt — Deal gewonnen',
        },
        rbac: {
          banner:
            'Du hast Leserechte für diesen Deal. Änderungen sind nur für Owner, Admin oder den Ersteller möglich.',
          'read-only': 'Nur Lesezugriff',
        },
      },
      create: {
        title: 'Deal anlegen',
        'standort-label': 'Standort',
        intro: 'Eigentümer + Objekt verknüpfen. Optionale kommerzielle Daten kannst du auch später ergänzen.',
        sections: {
          contacts: 'Kontakte',
          commercial: 'Kommerzielle Daten',
          notes: 'Notizen',
        },
        'property-label': 'Objekt',
        'property-placeholder': 'Objekt suchen…',
        'property-helper': 'Genau ein Objekt. Die Geschäftsart wird vom Objekt übernommen.',
        'property-clear': 'Objekt-Auswahl entfernen',
        'property-loading': 'Objekte werden geladen …',
        'property-load-error': 'Objekte konnten nicht geladen werden.',
        'property-no-results': 'Keine Objekte gefunden.',
        'owners-label': 'Eigentümer',
        'owners-placeholder': 'Kontakt suchen…',
        'owners-helper':
          'Wähle alle Eigentümer dieses Objekts. Bei Miteigentum (z. B. Ehepaar oder Erbengemeinschaft) fügst du alle Personen hier hinzu — sie teilen sich diesen einen Deal. Der erste ist der primäre Kontakt.',
        'owners-primary-badge': 'Primär',
        'owners-remove': '{name} entfernen',
        'owners-loading': 'Kontakte werden geladen …',
        'owners-load-error': 'Kontakte konnten nicht geladen werden.',
        'owners-no-results': 'Keine Kontakte gefunden.',
        'source-label': 'Quelle',
        'source-value': 'Manuell',
        'source-hint': 'Auto-gesetzt · nicht editierbar',
        'expected-sale-price-label': 'Erwarteter Verkaufspreis',
        'expected-sale-price-placeholder': 'z. B. 450000',
        'expected-sale-price-helper': 'Geschätzter Verkaufspreis',
        'commission-label': 'Provision Verkäufer (%)',
        'commission-hint': 'Netto, zzgl. MwSt.',
        'fee-label': 'Erwartete Maklergebühr',
        'fee-helper': 'Berechnet aus Verkaufspreis × Provision',
        'fee-empty': '—',
        'notes-obstacles-label': 'Hindernisse',
        'notes-obstacles-placeholder': 'Z. B. Ehefrau muss noch zustimmen, Erbengemeinschaft uneinig …',
        'notes-next-steps-label': 'Nächste Schritte',
        'notes-next-steps-placeholder': 'Z. B. Bewertungstermin Donnerstag 14:00 …',
        'probability-label': 'Wahrscheinlichkeit',
        'probability-empty': 'Keine Angabe',
        'completion-horizon-label': 'Abschluss-Horizont',
        'completion-horizon-empty': 'Keine Angabe',
        'submit-cta': 'Deal anlegen',
        'submit-loading': 'Wird angelegt …',
        'cancel-cta': 'Abbrechen',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'property-not-found-error':
          'Das gewählte Objekt gehört nicht zum ausgewählten Standort. Bitte wähle es erneut aus.',
        'contact-not-found-error': 'Ein gewählter Kontakt wurde nicht gefunden.',
        'type-mismatch-error': 'Die Geschäftsart passt nicht zum gewählten Objekt.',
        'link-conflict-error': 'Ein Kontakt ist bereits mit einer anderen Rolle mit dem Objekt verknüpft.',
        'active-deal-exists-error': 'Für dieses Objekt existiert bereits ein aktiver Deal dieser Geschäftsart.',
        'property-archived-error':
          'Dieses Objekt ist archiviert. Hebe zuerst die Archivierung auf, um einen Deal anzulegen.',
        'generic-error': 'Deal konnte nicht angelegt werden. Bitte versuche es erneut.',
        errors: {
          property: {
            required: 'Bitte wähle ein Objekt.',
          },
          owners: {
            required: 'Bitte wähle mindestens einen Eigentümer-Kontakt.',
          },
          'expected-sale-price': {
            invalid: 'Bitte gib einen Betrag von 0 oder mehr ein.',
          },
          commission: {
            invalid: 'Die Provision muss zwischen 0 und 100 liegen.',
          },
        },
      },
      // Warum ein Deal die Phase nicht wechseln kann — geteilt vom Lock-Popover
      // des Boards und vom Banner der Detailseite, damit beide gleich erklären.
      'lost-phase': {
        label: 'Verloren',
        in: 'in: {phase}',
      },
      'phase-lock': {
        won: 'Dieser Deal ist gewonnen und seine Phase ist gesperrt. Gewonnene Deals sind unveränderlich.',
        deferred: 'Dieser Deal ist zurückgestellt. Setze ihn fort, um ihn durch die Pipeline zu bewegen.',
        lost: 'Dieser Deal ist als verloren markiert. Öffne ihn erneut, um die Phase zu ändern.',
        'final-phase': 'Dieser Deal ist bereits in der letzten Phase und kann nicht weiter vorrücken.',
        'no-permission': 'Du hast keine Berechtigung, diesen Deal in eine andere Phase zu verschieben.',
        // Kurze Tags neben dem Schloss-Symbol auf den Board-Karten.
        short: {
          won: 'Gewonnen',
          deferred: 'Zurückgestellt',
          lost: 'Verloren',
          'final-phase': 'Letzte Phase',
          'no-permission': 'Keine Berechtigung',
        },
      },
      advance: {
        cta: 'In nächste Phase verschieben',
        'dialog-title': 'Phase wechseln',
        'dialog-transition': '{from} → {to}',
        'note-label': 'Notiz',
        'note-placeholder': 'Was ist in dieser Phase passiert? (optional)',
        'note-helper': 'Die Notiz erscheint in der Phasen-Historie.',
        'note-too-long': 'Die Notiz darf höchstens 1000 Zeichen lang sein.',
        submit: 'Phase wechseln',
        submitting: 'Wird gewechselt …',
        cancel: 'Abbrechen',
        'success-toast': 'Deal in die nächste Phase verschoben.',
        'forecast-nudge':
          'Bitte ergänze Wahrscheinlichkeit und Abschluss-Horizont, damit dein Funnel-Forecast aussagekräftig bleibt.',
        'contract-received':
          'Vertrag erhalten. Die Vermarktung des Objekts startet automatisch, sobald alle Pflichtfelder vorliegen – andernfalls ergänze sie auf der Objektseite.',
        promotion: {
          'activated-toast': 'Vermarktung automatisch gestartet.',
          pending: 'Die Vermarktung wird gestartet — das kann einen Moment dauern.',
          'gate-failed':
            'Die Vermarktung konnte nicht gestartet werden — folgende Felder fehlen: {fields}. Bitte ergänze sie auf der Objekt-Detailseite.',
          'subscriber-failed':
            'Die Vermarktung konnte nicht automatisch gestartet werden — bitte starte sie manuell über »Vermarktung starten« oder wiederhole den Vorgang.',
          cta: 'Zur Objekt-Detailseite',
        },
        'won-banner': 'Glückwunsch! Dieser Deal ist gewonnen und als »Gewonnen« markiert.',
        'banner-dismiss': 'Schließen',
        'skip-error': 'Phasen können nicht übersprungen werden',
        'not-active-error': 'Nur aktive Deals können in die nächste Phase verschoben werden.',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'not-found-error': 'Dieser Deal konnte nicht gefunden werden. Bitte lade die Seite neu.',
        'generic-error': 'Die Phase konnte nicht geändert werden. Bitte versuche es erneut.',
      },
      revert: {
        cta: 'Phase zurücksetzen',
        'success-toast': 'Deal in eine frühere Phase zurückgesetzt.',
        'dialog-title': 'Phase rückwärts setzen',
        'dialog-intro': 'Wähle eine frühere Phase und begründe den Rückschritt.',
        'phase-label': 'Frühere Phase',
        'phase-placeholder': 'Phase wählen',
        'reason-label': 'Grund',
        'reason-placeholder': 'Warum wird dieser Deal zurückgesetzt?',
        submit: 'Zurücksetzen',
        submitting: 'Wird zurückgesetzt …',
        cancel: 'Abbrechen',
        'reason-required': 'Bitte begründe den Rückschritt',
        'not-earlier-error': 'Es kann nur in eine frühere Phase zurückgesetzt werden.',
        'not-active-error': 'Nur aktive Deals können zurückgesetzt werden.',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'not-found-error': 'Dieser Deal konnte nicht gefunden werden. Bitte lade die Seite neu.',
        'generic-error': 'Die Phase konnte nicht zurückgesetzt werden. Bitte versuche es erneut.',
      },
    },
    leads: {
      title: 'Eigentümer',
      'create-cta': 'Neuer Lead',
      status: {
        NEW: 'Neu',
        CONTACTED: 'Kontaktiert',
        CONVERTED: 'Konvertiert',
        LOST: 'Verloren',
        ARCHIVED: 'Archiviert',
      },
      source: LEAD_SOURCE_LABELS,
      salutation: {
        MRS: 'Frau',
        MR: 'Herr',
        DIVERSE: 'Divers',
        FAMILY: 'Familie',
        OTHER: 'Sonstiges',
      },
      // The lead shares the deal's Verlustgrund set (IMM-429), so the labels
      // are the deal's too.
      'loss-reason': DEAL_LOSS_REASON_LABELS,
      'legal-basis': {
        CONSENT: 'Einwilligung',
        CONTRACT_INITIATION: 'Vertragsanbahnung',
        PRE_CONTRACTUAL_MEASURES: 'Vorvertragliche Maßnahmen',
        LEGAL_OBLIGATION: 'Rechtliche Verpflichtung',
        LEGITIMATE_INTEREST: 'Berechtigtes Interesse',
      },
      // The owner's Absicht (IMM-792.1) — two values, never a third. `null` is
      // "not yet asked" and is rendered separately (list `intent-unanswered`),
      // never as a choice here.
      intent: {
        SALE: 'verkaufen',
        RENTAL: 'vermieten',
      },
      list: {
        subtitle:
          'Jeder eingehende Eigentümer-Lead landet hier — erfasse Eigentümer aus Partnern, Portalen, deiner Website, Empfehlungen und Kaltakquise in einem Posteingang.',
        'load-error': 'Eigentümer-Leads konnten nicht geladen werden.',
        'load-error-hint': 'Versuche es erneut, um die Liste neu zu laden.',
        'load-retry': 'Erneut versuchen',
        'filter-save-error': 'Filter konnten nicht gespeichert werden — bitte erneut versuchen.',
        'empty-title': 'Noch keine Eigentümer-Leads.',
        'empty-description':
          'Jeder eingehende Eigentümer-Lead landet hier. Erfasse deinen ersten — von einem Partner, einer Portalanfrage, deiner Website, einer Empfehlung oder einem Kaltanruf.',
        'success-toast': 'Lead „{name}“ erfasst.',
        'no-match': 'Keine Leads entsprechen deiner Suche.',
        filters: {
          'search-label': 'Leads suchen',
          'search-placeholder': 'Name, Telefon, Adresse suchen …',
          'status-label': 'Status',
          'status-all': 'Alle Status',
          'source-label': 'Quelle',
          'source-all': 'Alle Quellen',
          'partner-label': 'Lead-Partner',
          'partner-all': 'Alle Partner',
          'assigned-to-me': 'Mir zugewiesen',
          orphaned: 'Verwaiste Leads',
          'show-archived': 'Archivierte anzeigen',
          'clear-all': 'Alle zurücksetzen',
        },
        'sort-label': 'Sortieren nach {column}',
        columns: {
          name: 'Eigentümer',
          address: 'Adresse',
          intent: 'Absicht',
          value: 'Immobilienwert',
          source: 'Quelle',
          partner: 'Lead-Partner',
          standort: 'Standort',
          assigned: 'Zugewiesen',
          status: 'Status',
          received: 'Empfangen',
          'last-contact': 'Letzter Kontakt',
        },
        'no-address': '—',
        'intent-unanswered': 'nicht erfasst',
        'no-value': '—',
        'no-partner': '—',
        'no-standort': '—',
        'no-assignee': '–',
        'no-last-contact': '—',
      },
      'deactivated-suffix': '(deaktiviert)',
      create: {
        title: 'Eigentümer-Lead erfassen',
        subtitle:
          'Nur Nachname und Quelle sind erforderlich — den Rest kannst du später auf der Lead-Detailseite ergänzen.',
        'step-indicator': 'Schritt {current} von {total}',
        'address-search-label': 'Adresse suchen',
        'address-search-placeholder': 'Straße oder Ort eingeben…',
        'address-search-helper': 'Wähle einen Vorschlag, um Straße, PLZ, Ort und Land zu füllen.',
        sections: {
          owner: 'Eigentümer',
          property: 'Objekt',
          'source-gdpr': 'Quelle & DSGVO',
        },
        fields: {
          salutation: 'Anrede',
          'first-name': 'Vorname',
          'last-name': 'Nachname',
          standort: 'Standort',
          email: 'E-Mail',
          phone: 'Telefon',
          mobile: 'Mobil',
          street: 'Straße',
          'house-number': 'Hausnummer',
          'postal-code': 'PLZ',
          city: 'Ort',
          country: 'Land',
          'cadastral-district': 'Gemarkung',
          'cadastral-parcel': 'Flurstück',
          'object-type': 'Immobilientyp',
          subtype: 'Untertyp',
          intent: 'Absicht',
          'construction-year': 'Baujahr',
          'living-area': 'Wohnfläche (m²)',
          'plot-area': 'Grundstücksfläche (m²)',
          'estimated-value': 'Geschätzter Wert',
          source: 'Quelle',
          'lead-partner': 'Lead-Partner',
          'received-at': 'Empfangen am',
          'legal-basis': 'Rechtsgrundlage',
          notes: 'Notizen',
        },
        'lead-partner-placeholder': 'Aktiven Lead-Partner wählen',
        'optional-hint': 'optional',
        'unset-option': 'Keine Angabe',
        'intent-placeholder': 'Bitte wählen',
        'intent-hint': 'Bleibt leer, solange niemand danach gefragt hat.',
        back: 'Zurück',
        next: 'Weiter',
        submit: 'Lead erfassen',
        submitting: 'Wird erfasst…',
        cancel: 'Abbrechen',
        errors: {
          'last-name-required': 'Nachname ist erforderlich.',
          'last-name-too-long': 'Der Nachname darf höchstens 80 Zeichen lang sein.',
          'first-name-too-long': 'Der Vorname darf höchstens 80 Zeichen lang sein.',
          'email-invalid': 'Bitte gib eine gültige E-Mail-Adresse ein.',
          'phone-invalid': 'Telefonnummer ist ungültig.',
          'mobile-invalid': 'Mobilnummer ist ungültig.',
          'received-at-bounds': 'Empfangen-am muss zwischen heute und vor 90 Tagen liegen.',
          'source-required': 'Quelle ist erforderlich.',
          'partner-required': 'Bei Quelle »Partner« ist ein Lead-Partner erforderlich.',
          generic: 'Der Lead konnte nicht erfasst werden – bitte versuche es erneut.',
          forbidden: 'Du darfst in diesem Workspace keine Leads erfassen.',
          'not-a-member': 'Du bist kein Mitglied dieses Workspace.',
        },
      },
      dedup: {
        title: 'Möglicher Doppeleintrag gefunden',
        body: '{count, plural, one {# vorhandener Lead passt zu diesem Eintrag.} other {# vorhandene Leads passen zu diesem Eintrag.}} Trotzdem speichern oder den vorhandenen Lead öffnen?',
        'matched-on': {
          email: 'E-Mail',
          address: 'Adresse',
          cadastral: 'Flurstück',
        },
        'matched-label': 'Übereinstimmung bei {dimensions}',
        'save-anyway': 'Trotzdem speichern',
        'open-existing': 'Vorhandenen Lead öffnen',
        saving: 'Wird gespeichert…',
      },
      detail: {
        'load-error': 'Dieser Lead konnte nicht geladen werden.',
        'load-retry': 'Erneut versuchen',
        'back-to-inbox': 'Zurück zum Posteingang',
        'no-value': '—',
        'notes-show-more': 'Mehr anzeigen',
        'notes-show-less': 'Weniger anzeigen',
        'address-open-maps': 'In Google Maps öffnen',
        standort: {
          heading: 'Standort',
          unassigned: 'Kein Standort',
          change: 'Ändern',
        },
        timeline: {
          'add-cta': 'Aktivität hinzufügen',
          save: 'Speichern',
          saving: 'Speichern …',
          cancel: 'Abbrechen',
          'type-label': 'Typ',
          'type-note': 'Notiz',
          'type-appointment': 'Termin',
          'appointment-type-label': 'Termin-Art',
          'appointment-type-placeholder': 'Bitte wählen',
          'appointment-type-valuation': 'Bewertungstermin',
          'appointment-type-on-site': 'Vor-Ort-Termin',
          'appointment-type-property-survey': 'Objektaufnahme',
          'appointment-type-contract-signing': 'Vertragstermin',
          'appointment-type-other': 'Sonstiger Termin',
          'appointment-type-viewing-on-site': 'Besichtigung vor Ort',
          'appointment-type-viewing-virtual': 'Besichtigung virtuell',
          'appointment-type-consultation': 'Beratungsgespräch',
          'appointment-type-financing-consultation': 'Finanzierungsgespräch',
          'appointment-type-negotiation': 'Verhandlungsgespräch',
          'appointment-type-notary': 'Notartermin',
          'appointment-type-handover': 'Übergabe',
          'error-no-appointment-type': 'Bitte wähle eine Termin-Art.',
          'type-call': 'Anruf',
          'type-sms': 'SMS',
          'type-whatsapp': 'WhatsApp',
          'type-fax': 'Fax',
          'type-letter': 'Brief',
          'type-email': 'E-Mail',
          'direction-label': 'Richtung',
          'direction-inbound': 'Eingehend',
          'direction-outbound': 'Ausgehend',
          'subject-label': 'Betreff',
          'content-label': 'Inhalt',
          'content-counter': '{count, number} / {max, number}',
          'content-placeholder': 'Was ist passiert?',
          'occurred-at-label': 'Stattgefunden am',
          'now-cta': 'Jetzt',
          'occurred-at-time-label': 'Uhrzeit',
          'error-empty-content': 'Bitte gib einen Inhalt ein.',
          'error-content-too-long': 'Eine Aktivität darf höchstens 10.000 Zeichen lang sein.',
          'error-no-direction': 'Bitte gib die Richtung an (eingehend oder ausgehend).',
          'error-subject-too-long': 'Der Betreff darf höchstens 200 Zeichen lang sein.',
          'error-future': 'Eine Aktivität kann nicht in der Zukunft liegen.',
          'error-occurred-at-incomplete': 'Bitte gib Datum und Uhrzeit gemeinsam an.',
          'save-error': 'Die Aktivität konnte nicht gespeichert werden.',
          'update-error': 'Die Aktivität konnte nicht gespeichert werden.',
          'mode-label': 'Zeitpunkt',
          'mode-log': 'Jetzt festhalten',
          'mode-plan': 'Für später planen',
          'due-at-label': 'Fällig am',
          'assignee-label': 'Zuständig',
          'assignee-self': 'Ich',
          'plan-hint': 'Diese Aktivität erscheint als offene Aufgabe, bis du sie erledigst.',
          'plan-submit': 'Als Aufgabe planen',
          'error-due-at-required': 'Bitte gib ein Fälligkeitsdatum in der Zukunft an.',
          'upcoming-heading': 'Anstehend',
          'history-heading': 'Verlauf',
          'badge-planned': 'Geplant',
          'badge-overdue': 'Überfällig',
          'due-prefix': 'Fällig am {date}',
          'assignee-prefix': 'Zuständig: {name}',
          'created-by-prefix': 'Erstellt von {name}',
          'upcoming-empty': 'Keine anstehenden Aktivitäten.',
          'history-empty': 'Noch keine erledigten Aktivitäten.',
          'activity-empty': 'Noch keine Aktivität — halte den ersten Kontakt fest oder plane eine Wiedervorlage.',
          'action-complete': 'Als erledigt markieren',
          'action-reschedule': 'Verschieben',
          'action-cancel': 'Absagen',
          'outcome-label': 'Ergebnis',
          'outcome-none': 'Kein Ergebnis',
          'outcome-reached': 'Erreicht',
          'outcome-not-reached': 'Nicht erreicht',
          'outcome-voicemail': 'Mailbox',
          'outcome-callback-agreed': 'Rückruf vereinbart',
          'outcome-email-reached': 'Antwort erhalten',
          'outcome-email-not-reached': 'Keine Antwort',
          'outcome-attended': 'Wahrgenommen',
          'outcome-not-attended': 'Nicht wahrgenommen',
          'outcome-rescheduled': 'Verschoben',
          'complete-heading': 'Aktivität erledigen',
          'complete-note-label': 'Wie ist das Gespräch gelaufen?',
          'complete-note-placeholder': 'Optionale Notiz zum Ergebnis',
          'complete-submit': 'Erledigt',
          'complete-submit-follow-up': 'Erledigt & Wiedervorlage',
          'complete-error': 'Die Aktivität konnte nicht abgeschlossen werden.',
          'follow-up-heading': 'Folgeaktivität wird angelegt',
          'follow-up-auto': 'automatisch',
          'follow-up-due-label': 'Fällig am',
          'follow-up-footer': 'Standard: nächster Tag, selbe Uhrzeit — anpassbar.',
          'follow-up-reset': 'Standard',
          'reschedule-heading': 'Aktivität verschieben',
          'reschedule-due-label': 'Neues Fälligkeitsdatum',
          'reschedule-submit': 'Verschieben',
          'reschedule-error': 'Die Aktivität konnte nicht verschoben werden.',
          'cancel-heading': 'Aktivität absagen?',
          'cancel-confirm-text':
            'Die Aktivität wird abgesagt und verschwindet aus „Anstehend". Sie erscheint nicht im Verlauf.',
          'cancel-confirm': 'Absagen',
          'cancel-keep': 'Behalten',
          'cancel-error': 'Die Aktivität konnte nicht abgesagt werden.',
          'reassign-label': 'Zuständig',
          'reassign-error': 'Die Zuständigkeit konnte nicht geändert werden.',
          // The reason the Anstehend actions carry when the member may not
          // perform them (IMM-804.1). The controls stay visible and disabled
          // with their own reason — never hidden, and never borrowing a string
          // that says something else.
          'action-no-permission': 'Du hast keine Berechtigung, Aktivitäten zu bearbeiten.',
          'reassign-no-permission': 'Du hast keine Berechtigung, die Zuständigkeit zu ändern.',
          'complete-instead-prefix': 'Oder stattdessen:',
        },
        sections: {
          owner: 'Eigentümer',
          property: 'Objekt',
          address: 'Adresse',
          notes: 'Notizen',
          gdpr: 'DSGVO & Herkunft',
        },
        fields: {
          name: 'Name',
          'street-no': 'Straße & Nr.',
          'postcode-city': 'PLZ & Ort',
          'created-at': 'Erstellt am',
        },
        kpi: {
          'days-in-inbox': 'Tage im Posteingang',
          'est-value': 'Geschätzter Wert',
        },
        'status-heading': 'Status',
        'status-since': 'seit {date}',
        'tab-activity': 'Aktivität',
        edit: 'Bearbeiten',
        'edit-coming-soon': 'Das Bearbeiten von Eigentümer-Leads ist bald verfügbar.',
        'edit-owner': {
          title: 'Eigentümer bearbeiten',
          save: 'Speichern',
          saving: 'Speichern…',
          cancel: 'Abbrechen',
          success: 'Eigentümer aktualisiert.',
          'forbidden-error': 'Du hast keine Berechtigung, diesen Lead zu bearbeiten.',
          'not-found-error': 'Dieser Lead existiert nicht mehr.',
          'generic-error': 'Eigentümer konnte nicht gespeichert werden. Bitte erneut versuchen.',
        },
        'edit-notes': {
          title: 'Notizen bearbeiten',
          field: 'Notizen',
          save: 'Speichern',
          saving: 'Speichern…',
          cancel: 'Abbrechen',
          success: 'Notizen aktualisiert.',
          'forbidden-error': 'Du hast keine Berechtigung, diesen Lead zu bearbeiten.',
          'not-found-error': 'Dieser Lead existiert nicht mehr.',
          'generic-error': 'Notizen konnten nicht gespeichert werden. Bitte erneut versuchen.',
        },
        'edit-legal-basis': {
          title: 'Rechtsgrundlage bearbeiten',
          save: 'Speichern',
          saving: 'Speichern…',
          cancel: 'Abbrechen',
          success: 'Rechtsgrundlage aktualisiert.',
          'forbidden-error': 'Du hast keine Berechtigung, diesen Lead zu bearbeiten.',
          'not-found-error': 'Dieser Lead existiert nicht mehr.',
          'generic-error': 'Rechtsgrundlage konnte nicht gespeichert werden. Bitte erneut versuchen.',
        },
        'edit-address': {
          title: 'Adresse bearbeiten',
          save: 'Speichern',
          saving: 'Speichern…',
          cancel: 'Abbrechen',
          success: 'Adresse aktualisiert.',
          'forbidden-error': 'Du hast keine Berechtigung, diesen Lead zu bearbeiten.',
          'not-found-error': 'Dieser Lead existiert nicht mehr.',
          'generic-error': 'Adresse konnte nicht gespeichert werden. Bitte erneut versuchen.',
        },
        'edit-property': {
          title: 'Objekt bearbeiten',
          save: 'Speichern',
          saving: 'Speichern…',
          cancel: 'Abbrechen',
          success: 'Objekt aktualisiert.',
          'forbidden-error': 'Du hast keine Berechtigung, diesen Lead zu bearbeiten.',
          'not-found-error': 'Dieser Lead existiert nicht mehr.',
          'generic-error': 'Objekt konnte nicht gespeichert werden. Bitte erneut versuchen.',
        },
        lifecycle: {
          heading: 'Aktionen',
          more: 'Weitere Aktionen',
          'mark-lost': {
            cta: 'Als verloren markieren',
            title: 'Lead als verloren markieren',
            'reason-label': 'Verlustgrund',
            'reason-placeholder': 'Verlustgrund wählen',
            'note-label': 'Verlust-Notiz',
            'note-placeholder': 'Optionale Notiz (max. 500 Zeichen)',
            submit: 'Als verloren markieren',
            submitting: 'Wird markiert…',
            cancel: 'Abbrechen',
            success: 'Lead als verloren markiert.',
            errors: {
              'reason-required': 'Bitte wähle einen Verlustgrund',
              'note-too-long': 'Die Verlust-Notiz darf höchstens 500 Zeichen lang sein',
              generic: 'Der Lead konnte nicht als verloren markiert werden – bitte versuche es erneut.',
            },
          },
          archive: {
            cta: 'Archivieren',
            success: 'Lead archiviert.',
            error: 'Der Lead konnte nicht archiviert werden – bitte versuche es erneut.',
          },
          reopen: {
            cta: 'Wieder öffnen',
            success: 'Lead wieder geöffnet.',
            error: 'Der Lead konnte nicht wieder geöffnet werden – bitte versuche es erneut.',
          },
          responsible: {
            label: 'Verantwortliche Mitglieder',
            empty: 'Niemandem zugewiesen',
          },
          assign: {
            cta: 'Zuweisen',
            title: 'Lead zuweisen',
            'members-label': 'Verantwortliche Mitglieder',
            'group-standort': 'Standort {name}',
            'group-other': 'Weitere Mitglieder',
            'empty-error': 'Bitte mindestens ein verantwortliches Mitglied auswählen.',
            'no-results': 'Keine Mitglieder gefunden.',
            remove: '{name} entfernen',
            submit: 'Zuweisen',
            submitting: 'Wird zugewiesen…',
            cancel: 'Abbrechen',
            success: 'Lead zugewiesen.',
            'forbidden-error': 'Du hast keine Berechtigung, diesen Lead zuzuweisen.',
            'not-found-error': 'Dieser Lead existiert nicht mehr.',
            'generic-error': 'Der Lead konnte nicht zugewiesen werden – bitte versuche es erneut.',
          },
        },
        qualify: {
          heading: 'Konvertieren',
          cta: 'Qualifizieren',
          'confirm-title': 'Lead qualifizieren',
          'confirm-body':
            'Aus diesem Eigentümer-Lead entstehen ein Kontakt, ein Objekt und ein Deal. Prüfe den Standort, wähle die Startphase und halte optional den Anlass fest.',
          confirm: 'Lead qualifizieren',
          'location-label': 'Standort',
          'location-placeholder': 'Standort auswählen',
          'startphase-label': 'Startphase',
          'startphase-hint': 'In welcher Phase startet der Deal?',
          'marketing-type-label': 'Wird das Objekt verkauft oder vermietet?',
          'marketing-type-hint': 'Die Angabe legt fest, welche Unterlagen und Preisfelder das Objekt bekommt.',
          'marketing-type-option': MARKETING_TYPE_LABELS,
          'owner-first-name-label': 'Vorname des Eigentümers',
          'note-label': 'Notiz',
          'note-placeholder': 'Was war der Anlass für die Qualifizierung? (optional)',
          'note-hint': 'Die Notiz erscheint in der Phasen-Historie des Deals.',
          'note-too-long': 'Die Notiz darf höchstens 1000 Zeichen lang sein.',
          'preview-title': 'Das wird angelegt',
          'preview-contact': 'Kontakt',
          'preview-object': 'Objekt',
          'preview-deal': 'Deal',
          submitting: 'Wird qualifiziert…',
          cancel: 'Abbrechen',
          success: 'Lead qualifiziert.',
          errors: {
            forbidden: 'Du hast keine Berechtigung, diesen Lead zu qualifizieren.',
            'not-found': 'Dieser Lead existiert nicht mehr.',
            conflict: 'Dieser Lead kann nicht mehr qualifiziert werden.',
            'missing-fields':
              'Der Immobilie fehlen Felder zur Veröffentlichung: {fields}. Ergänze sie und qualifiziere dann.',
            generic: 'Der Lead konnte nicht qualifiziert werden – bitte versuche es erneut.',
          },
          'publish-fields': {
            STREET: 'Straße',
            HOUSE_NUMBER: 'Hausnummer',
            POSTAL_CODE: 'Postleitzahl',
            CITY: 'Stadt',
            PURCHASE_PRICE: 'Kaufpreis',
            BASE_RENT: 'Kaltmiete',
            LIVING_AREA: 'Wohnfläche',
            USABLE_AREA: 'Nutzfläche',
            TOTAL_AREA: 'Gesamtfläche',
            PLOT_AREA: 'Grundstücksfläche',
            PARKING_SPACE_TYPE: 'Stellplatztyp',
            VAT_TREATMENT: 'Umsatzsteuer-Angabe',
            AVAILABLE_FROM: 'Verfügbar ab',
            ANCILLARY_COSTS_TYPE: 'Nebenkosten-Art',
          },
        },
        converted: {
          'panel-heading': 'Dieser Lead wurde qualifiziert.',
          'qualified-by': 'Von {name} am {date} qualifiziert.',
          'qualified-by-anon': 'Von einem Teammitglied am {date} qualifiziert.',
          'records-intro': 'Aus dem Lead sind folgende Datensätze entstanden:',
          contact: 'Kontakt',
          property: 'Objekt',
          deal: 'Deal',
        },
      },
    },
    // Der Aktivitäten-Tab — Anstehend (IMM-776.3), der Nachfolger von „Meine
    // Aufgaben" oben. Merged die zwei bounded reads IMM-776.1/.2 clientseitig;
    // jede Quelle trägt ihren eigenen Ausfall (BL-058).
    activities: {
      title: 'Aktivitäten',
      'subtitle-own': 'Alles, was dir zugewiesen ist — überfällige zuerst.',
      'subtitle-oversight': 'Alle Aktivitäten deiner Standorte — überfällige zuerst.',
      'subtitle-workspace': 'Alle Aktivitäten im Workspace — überfällige zuerst.',
      // Datumszelle unter „Stattgefunden am" in Vergangen: eine abgesagte Zeile
      // zeigt, wann sie abgesagt wurde; eine vor der Zeitstempel-Einführung
      // abgesagte nur ihr geplantes Datum.
      'past-when': { cancelled: 'Abgesagt am {date}', planned: 'Geplant für {date}' },
      // Tagestrenner, gezählt (AC 7–8). Eine Gruppe ohne Zeilen wird gar nicht
      // gerendert, nicht mit „(0)".
      groups: {
        overdue: 'Überfällig ({count})',
        today: 'Heute ({count})',
        tomorrow: 'Morgen ({count})',
        'this-week': 'Diese Woche ({count})',
        later: 'Später ({count})',
      },
      // {count} der Tagestrenner: ganze Gruppe, Teilstück „von", „+" ab der Gruppe, die das Limit abschneidet.
      'group-count': {
        whole: '{total}',
        partial: '{shown} von {total}',
        'whole-bounded': '{total}+',
        'partial-bounded': '{shown} von {total}+',
      },
      segments: {
        upcoming: 'Anstehend',
        past: 'Vergangen',
      },
      columns: {
        due: 'Fällig',
        occurred: 'Stattgefunden am',
        kind: 'Art',
        content: 'Aktivität',
        subject: 'Bezug',
        assignee: 'Zuständig',
        standort: 'Standort',
        status: 'Status',
        actions: 'Aktionen',
      },
      // Die Fällig-Zelle ist gruppenrelativ (Überfällig/Später vs. Heute/Morgen
      // vs. Diese Woche) — zwei ICU-Vorlagen statt String-Konkatenation (ADR 0009).
      'due-date-time': '{date}, {time}',
      'due-weekday-time': '{weekday}, {time}',
      status: {
        open: 'Offen',
        overdue: 'Überfällig',
        done: 'Erledigt',
        cancelled: 'Abgesagt',
      },
      actions: {
        complete: 'Erledigt',
        reschedule: 'Verschieben',
        cancel: 'Absagen',
        'reschedule-title': 'Aktivität verschieben',
        'reschedule-date': 'Neues Datum',
        'reschedule-time': 'Neue Uhrzeit',
        'reschedule-submit': 'Neuen Termin speichern',
        back: 'Zurück',
        'cancel-title': 'Aktivität wirklich absagen?',
        'cancel-confirm': 'Aktivität absagen',
        'context-line': '{kind} · {content}',
        completed: 'Aktivität als erledigt markiert.',
        rescheduled: 'Aktivität verschoben.',
        cancelled: 'Aktivität abgesagt.',
        refused: 'Du darfst diese Aktivität nicht ändern. Lade die Seite neu.',
        failed: 'Die Änderung konnte nicht gespeichert werden. Versuch es noch einmal.',
      },
      unassigned: 'Nicht zugewiesen',
      'no-standort': 'Kein Standort',
      // Bezug-Zelle, „Zu {Art}: {Label}" — pro Subjekttyp, mit dem neutralen Text
      // wenn kein Label vorliegt (AC 19–20). Erschöpfend über
      // ActivitySubjectType, damit ein sechster Typ hier nicht durchfällt
      // (BL-024 / BL-083).
      subject: {
        DEAL: { prefix: 'Zu Deal: {label}', neutral: 'Zu Deal' },
        LEAD: { prefix: 'Zu Lead: {label}', neutral: 'Zu Lead' },
        PROPERTY: { prefix: 'Zu Objekt: {label}', neutral: 'Zu Objekt' },
        CONTACT: { prefix: 'Zu Kontakt: {label}', neutral: 'Zu Kontakt' },
        VALUATION_REQUEST: { prefix: 'Zu Bewertungsanfrage: {label}', neutral: 'Zu Bewertungsanfrage' },
        PROSPECT: { prefix: 'Zu Interessent: {label}', neutral: 'Zu Interessent' },
      } as const satisfies Record<ActivitySubjectType, { readonly prefix: string; readonly neutral: string }>,
      filters: {
        search: 'Aktivität oder Bezug suchen',
        standort: 'Standort',
        assignee: 'Zuständig',
        kind: 'Art',
        'kind-communication': 'Kommunikation',
        subject: 'Bezug',
        'subject-types': {
          LEAD: 'Lead',
          DEAL: 'Deal',
          CONTACT: 'Kontakt',
          PROPERTY: 'Objekt',
          VALUATION_REQUEST: 'Bewertungsanfrage',
          PROSPECT: 'Interessent',
        } as const satisfies Record<ActivitySubjectType, string>,
        period: 'Zeitraum',
        periods: {
          TODAY: 'Heute',
          NEXT_7_DAYS: 'Nächste 7 Tage',
          NEXT_30_DAYS: 'Nächste 30 Tage',
          LAST_7_DAYS: 'Letzte 7 Tage',
          LAST_30_DAYS: 'Letzte 30 Tage',
          THIS_MONTH: 'Dieser Monat',
          LAST_MONTH: 'Letzter Monat',
        },
        reset: 'Filter zurücksetzen',
        'no-match': 'Keine Treffer für diese Filter.',
        'sort-label': 'Nach {column} sortieren',
      },
      'load-more': 'Mehr laden',
      'empty-title': 'Keine Aktivitäten',
      'empty-description':
        'Hier erscheinen geplante und erledigte Aktivitäten aus Leads, Deals, Kontakten und Objekten.',
      'load-error': 'Aktivitäten konnten nicht geladen werden.',
      'load-retry': 'Erneut versuchen',
      // Die eine Quelle, die dieser Tab clientseitig merged, kann unabhängig von
      // der anderen ausfallen (BL-058) — jede benennt, welche Hälfte fehlt.
      'partial-store': 'Aktivitäten konnten nicht vollständig geladen werden. Es fehlen also welche.',
      'partial-lead': 'Lead-Aktivitäten konnten nicht geladen werden. Es fehlen also welche.',
      truncated: 'Es gibt mehr Aktivitäten, als hier angezeigt werden. Grenze den Zeitraum oder die Filter ein.',
      'subject-unavailable': 'Der Bezug dieser Aktivität ist nicht mehr verfügbar.',
    },
    // Bewertungen (IMM-467.1) — the member's cross-deal list. Status, Objektart
    // and Nutzung are the SHARED dictionaries the operator queue and the Anfrage
    // form already use, mounted here rather than retyped: the same Bewertung
    // must read identically on every surface.
    // Aufträge (IMM-1152) — jedes Objekt ab „Auftrag erhalten“. Die Immobilienstatus-Wörter
    // verweisen auf die Labels des Objekts selbst, damit Liste und Objekt nie auseinanderlaufen.
    mandates: {
      title: 'Aufträge',
      subtitle: 'Jedes Objekt ab „Auftrag erhalten“ – vom Auftrag bis zum Abschluss.',
      column: {
        object: 'Objekt',
        'deal-status': 'Deal-Status',
        'property-state': 'Immobilienstatus',
        responsible: 'Zuständig',
        'mandate-since': 'Auftrag seit',
      },
      'property-state': {
        NOT_ONLINE: 'Nicht online',
        ONLINE: 'Online',
        RESERVED: 'Reserviert',
        SOLD: ARCHIVE_REASON_LABELS.SOLD,
        RENTED: ARCHIVE_REASON_LABELS.RENTED,
        INACTIVE: PROPERTY_STATUS_LABELS.INACTIVE,
        ARCHIVED: PROPERTY_STATUS_LABELS.ARCHIVED,
      } as const satisfies EnumTranslation<MandatePropertyState>,
      'property-unavailable': 'Objekt nicht verfügbar',
      empty: {
        title: 'Noch keine Aufträge',
        description: 'Hier erscheint jedes Objekt, sobald sein Deal „Auftrag erhalten“ erreicht.',
      },
      truncated: 'Es werden nur die ersten 2.000 Aufträge angezeigt.',
      'load-failed': 'Die Aufträge konnten nicht geladen werden.',
      'load-failed-hint': 'Prüfe deine Verbindung und versuche es erneut.',
      'load-retry': 'Erneut versuchen',
    },
    valuations: {
      title: 'Bewertungen',
      subtitle: 'Alle Bewertungen zu den Deals, denen du zugewiesen bist.',
      // For oversight holders. Deliberately not „alle Bewertungen": the capability
      // is resolved per Standort, so the list is a union — someone who oversees a
      // single Standort sees all of it plus their own deals elsewhere. The
      // sentence names both halves so it stays true for them.
      'subtitle-oversight':
        'Alle Bewertungen in den Standorten, die du verantwortest — dazu die Bewertungen zu deinen eigenen Deals.',
      columns: {
        object: 'Objekt',
        'object-type': 'Objektart',
        usage: 'Nutzung',
        standort: 'Standort',
        status: 'Status',
        'price-range': 'Preisspanne',
        requested: 'Angefragt',
        completed: 'Fertiggestellt',
      },
      filters: {
        all: 'Alle',
        'status-label': 'Nach Status filtern',
        'search-label': 'Bewertungen durchsuchen',
        'search-placeholder': 'Suche nach Objekt',
        'standort-label': 'Nach Standort filtern',
        'standort-all': 'Alle Standorte',
        // Der zugängliche Name der Sortier-Schaltfläche in jedem Spaltenkopf.
        'sort-label': 'Nach {column} sortieren',
      },
      status: VALUATION_STATUS_LABELS,
      objectType: VALUATION_OBJEKTART_LABELS,
      usage: VALUATION_NUTZUNG_LABELS,
      'empty-title': 'Noch keine Bewertungen',
      'empty-description':
        'Bewertungen werden aus einem Deal heraus angefragt. Sobald du einem Deal mit einer Bewertung zugewiesen bist, erscheint sie hier.',
      // The „zugewiesen" sentence is simply false for an oversight holder: they
      // see their Standorte's Bewertungen without any assignment at all.
      'empty-description-oversight':
        'Bewertungen werden aus einem Deal heraus angefragt. Sobald in einem deiner Standorte eine angefragt wird, erscheint sie hier.',
      // A truncated list that says nothing is the same defect as the narrowed list
      // that said nothing.
      truncated: 'Es werden nur die ersten 2.000 Bewertungen angezeigt.',
      'no-match': 'Keine Treffer für deine Auswahl.',
      // Ein Filter wirkt NUR auf die geladene Seite. Über einer gekürzten Liste
      // kann „keine Treffer" deshalb falsch sein statt bloß unvollständig: zu
      // diesem Standort kann es Bewertungen geben, die die Grenze abgeschnitten
      // hat, bevor der Client sie gesehen hat.
      'no-match-truncated':
        'Keine Treffer für deine Auswahl. Es werden nur die ersten 2.000 Bewertungen geladen — zu diesem Standort kann es weitere geben, die hier nicht dabei sind.',
      'load-error': 'Bewertungen konnten nicht geladen werden. Bitte versuche es noch einmal.',
      // Wenn der Standort einer Bewertung außerhalb des sichtbaren Baums liegt.
      'no-standort': 'Kein Standort',
      // Any cell without a value.
      'empty-cell': '—',
      // Eine Bewertung in voller Länge (IMM-467.2). Die Anfrage, das Ergebnis,
      // das Wertgutachten und die Aktivität kommen aus den geteilten Bausteinen
      // des Deals — hier steht nur der Rahmen und die vier Zustände ohne Zahlen.
      detail: {
        'to-deal': 'Zum Deal',
        'activity-title': 'Aktivität',
        // Fertig, aber noch nicht freigegeben — und die Leserin darf nicht
        // freigeben. Weder Zahlen noch Gutachten, dafür der Grund.
        'awaiting-release': {
          title: 'Bewertung fertig, noch nicht freigegeben',
          body: 'Die Bewertung ist erstellt und wird gerade geprüft. Sobald sie freigegeben ist, findest du hier die Preisspanne und das Wertgutachten.',
        },
        // Datum UND Dauer: das Datum ist die Tatsache, die Dauer erspart das
        // Nachrechnen. Die Frage in diesem Status ist „läuft es zu lange?".
        // Die Dauer erscheint genau einmal — hier, nie zusätzlich im Kopf.
        'in-progress': {
          title: 'immoOS erstellt gerade deine Bewertung',
          body: 'In Bearbeitung seit {date} · {duration}. Das Ergebnis erscheint hier, sobald es freigegeben ist.',
          days: '{count, plural, one {# Tag} other {# Tage}}',
          // Tag 0 heißt „heute", nicht „0 Tage".
          today: 'heute',
        },
        'load-error': {
          title: 'Bewertung konnte nicht geladen werden',
          body: 'Bitte versuche es noch einmal. Falls es weiterhin nicht klappt, öffne die Bewertung über den Deal.',
          retry: 'Erneut versuchen',
        },
      },
    },
  },
  support: {
    'not-registered': {
      title: 'Konto nicht registriert',
      body: 'Dein Konto ist noch keiner Organisation zugeordnet. Bitte kontaktiere unser Vertriebsteam — wir schalten dich frei.',
      'whatsapp-cta': 'WhatsApp Vertrieb: {number}',
      'back-to-sign-in': 'Zurück zur Anmeldung',
    },
  },
  onboarding: {
    'workspace-switcher': {
      label: 'Arbeitsbereich wechseln',
      current: 'Aktueller Arbeitsbereich',
      // Same dead-end tooltip as the in-app location selector (IMM-322).
      'not-onboarded': 'Diese Organisation ist noch nicht vollständig eingerichtet',
    },
    welcome: {
      heading: 'Willkommen bei {workspaceName}.',
      body: 'In wenigen Schritten vervollständigst du dein Profil — Foto, Position, Kontaktdaten und ein paar Angaben zu deiner Tätigkeit. So erscheinst du direkt richtig in Exposés und auf deinem Makler-Profil. Du kannst jederzeit pausieren und später über denselben Link weitermachen.',
      cta: 'Onboarding starten',
      reprompt: 'Schließe dein Onboarding ab.',
      'platform-mark': 'läuft auf immoOS',
    },
    wizard: {
      'stepper-label': 'Onboarding-Fortschritt',
      'pause-hint': 'Du kannst jederzeit pausieren — über deinen Link geht es später weiter.',
      'next-cta': 'Speichern & weiter',
      'back-cta': 'Zurück',
      'finish-cta': 'Onboarding abschließen',
      saving: 'Wird gespeichert…',
      'load-error': 'Dein Onboarding konnte nicht geladen werden. Bitte lade die Seite neu.',
      'generic-error': 'Es ist ein Fehler aufgetreten. Bitte versuche es erneut.',
      steps: {
        photo_intro: 'Foto & Vorstellung',
        contact: 'Kontakt',
        expertise: 'Fachprofil',
        consent: 'Einwilligung',
      },
      identity: {
        heading: 'Deine Angaben',
        'display-name': 'Anzeigename',
        email: 'E-Mail',
        location: 'Standort',
        'location-empty': 'Noch nicht zugeordnet',
      },
      consent: {
        text: 'Ich bin damit einverstanden, dass mein Profil inklusive Foto in Exposés und auf dem öffentlichen Makler-Profil meines Unternehmens verwendet wird.',
      },
      validation: {
        'required-core': 'Bitte fülle dieses Feld aus, um dein Onboarding abzuschließen.',
        consent: 'Bitte bestätige die Einwilligung, um fortzufahren.',
      },
      connect: {
        'step-label': 'Anmeldung',
        heading: 'Richte deine Anmeldung bei {workspaceName} ein',
        body: 'Verbinde einmalig deine Anmeldung, damit du dich künftig jederzeit wieder anmelden kannst. Wähle dazu einen Anbieter aus.',
        'email-note': 'Bitte verwende dieselbe E-Mail-Adresse wie in deiner Einladung:',
        'google-cta': 'Mit Google fortfahren',
        'microsoft-cta': 'Mit Microsoft fortfahren',
        'password-cta': 'Mit E-Mail & Passwort fortfahren',
        loading: 'Wird geöffnet…',
        error: 'Die Anmeldung konnte nicht gestartet werden. Bitte versuche es erneut.',
      },
    },
    done: {
      heading: 'Geschafft — dein Profil ist startklar.',
      body: 'Dein Profil ist gespeichert und erscheint ab sofort in Exposés und auf deinem Makler-Profil. Du kannst es jederzeit unter Mein Profil anpassen.',
      cta: 'Zu {workspaceName}',
    },
    member: {
      'platform-mark': 'läuft auf immoOS',
      loading: 'Einen Moment …',
      code: {
        heading: 'Bestätige deine E-Mail-Adresse',
        body: 'Wir haben dir einen Code per E-Mail geschickt. Gib ihn unten ein, um zu {workspaceName} fortzufahren.',
        'code-label': 'Bestätigungscode',
        'code-placeholder': '123456',
        'submit-cta': 'Bestätigen',
        submitting: 'Wird geprüft…',
        'resend-cta': 'Code erneut senden',
        errors: {
          'invalid-code': 'Dieser Code ist ungültig oder abgelaufen. Bitte versuche es erneut.',
        },
      },
      link: {
        'expired-heading': 'Dieser Link ist nicht mehr gültig.',
        'expired-body':
          'Deine Einladung ist abgelaufen oder wurde bereits verwendet. Bitte den Absender, dich erneut einzuladen.',
        'error-heading': 'Etwas ist schiefgelaufen.',
        'error-body': 'Wir konnten deine Einladung gerade nicht laden. Bitte versuche es erneut.',
        'retry-cta': 'Erneut versuchen',
      },
    },
    partner: {
      'platform-mark': 'läuft auf immoOS',
      loading: 'Einen Moment …',
      welcome: {
        heading: 'Willkommen beim Standort-Onboarding.',
        body: 'In wenigen Schritten erfasst du alles, was wir für deinen Standort brauchen — eure rechtlichen Angaben, Kontaktdaten und die nötigen Einwilligungen. Du kannst jederzeit pausieren und später über denselben Link weitermachen.',
        cta: 'Onboarding starten',
      },
      code: {
        heading: 'Bestätige deine E-Mail-Adresse',
        body: 'Wir haben dir einen sechsstelligen Code per E-Mail geschickt. Gib ihn unten ein, um fortzufahren.',
        'code-label': 'Bestätigungscode',
        'code-placeholder': '123456',
        'submit-cta': 'Bestätigen',
        submitting: 'Wird geprüft…',
        'resend-cta': 'Code erneut senden',
        errors: {
          'invalid-code': 'Dieser Code ist ungültig oder abgelaufen. Bitte versuche es erneut.',
        },
      },
      shell: {
        'stepper-label': 'Onboarding-Fortschritt',
        'changes-requested': 'Das Team hat Anmerkungen hinterlassen: {reviewNotes}',
        'save-error': 'Deine Angaben konnten nicht gespeichert werden. Bitte versuche es erneut.',
        'field-required': 'Dieses Feld ist erforderlich.',
        steps: {
          legal: 'Rechtliche Angaben',
          contact: 'Kontakt',
          consents: 'Rechte & Einwilligungen',
        },
      },
      legal: {
        heading: 'Rechtliche Angaben',
        intro:
          'Erfasse hier die rechtlichen Angaben deines Unternehmens. Du kannst Felder offen lassen und später ergänzen.',
        'next-cta': 'Speichern & weiter',
        'back-cta': 'Zurück',
        saving: 'Wird gespeichert…',
        fields: {
          'company-name': 'Firmenname',
          'legal-form': 'Rechtsform',
          'managing-directors': 'Geschäftsführer / Vertretungsberechtigte',
          'commercial-register-court': 'Handelsregister (Gericht)',
          'commercial-register-number': 'Handelsregister (Nummer)',
          'vat-id': 'USt-IdNr.',
          'trade-permit-authority': 'Erlaubnisbehörde (§ 34c GewO)',
          'supervisory-authority': 'Zuständige Aufsichtsbehörde',
          'professional-indemnity-insurance': 'Berufshaftpflichtversicherung',
          'content-responsible-person': 'Inhaltlich Verantwortliche (§ 18 Abs. 2 MStV)',
          website: 'Externe Website',
        },
        'legal-form-placeholder': 'Bitte wählen',
        'register-hint': 'Nur erforderlich für im Handelsregister eingetragene Rechtsformen.',
        errors: {
          'vat-id': 'Bitte gib eine gültige USt-IdNr. ein (Format: DE und 9 Ziffern).',
          website: 'Bitte gib eine gültige Website-URL ein.',
        },
        'legal-forms': {
          GMBH: 'GmbH',
          UG: 'UG (haftungsbeschränkt)',
          GMBH_CO_KG: 'GmbH & Co. KG',
          EINZELUNTERNEHMEN: 'Einzelunternehmen',
          EK: 'e.K.',
          GBR: 'GbR',
          PARTG_MBB: 'PartG mbB',
          OHG: 'OHG',
          KG: 'KG',
          UG_CO_KG: 'UG & Co. KG',
          AG: 'AG',
        },
        preview: {
          heading: 'Impressum-Vorschau',
          empty: 'Sobald du Angaben erfasst, erscheint hier eine Vorschau deines Impressums.',
        },
      },
      contact: {
        heading: 'Kontakt',
        intro:
          'Erfasse hier die öffentlichen Kontaktdaten deines Standorts. Du kannst Felder offen lassen und später ergänzen.',
        'next-cta': 'Speichern & weiter',
        'back-cta': 'Zurück',
        saving: 'Wird gespeichert…',
        fields: {
          email: 'E-Mail',
          phone: 'Telefon',
        },
        'address-search-label': 'Anschrift',
        'address-search-placeholder': 'Adresse suchen…',
        'remove-address': 'Adresse entfernen',
        'street-label': 'Straße',
        'street-placeholder': 'z. B. Musterstraße',
        'house-number-label': 'Hausnummer',
        'house-number-placeholder': 'z. B. 1',
        'postal-code-label': 'PLZ',
        'postal-code-placeholder': 'z. B. 10115',
        'city-label': 'Ort',
        'city-placeholder': 'z. B. Berlin',
        'country-label': 'Land',
        'country-placeholder': 'z. B. Deutschland',
        errors: {
          email: 'Bitte gib eine gültige E-Mail-Adresse ein.',
          phone: 'Bitte gib eine gültige Telefonnummer ein.',
        },
      },
      consents: {
        heading: 'Rechte & Einwilligungen',
        intro:
          'Damit wir die übermittelten Daten verarbeiten dürfen, brauchen wir deine ausdrückliche Einwilligung. Bestätige sie und gib an, wer sie erteilt.',
        'save-cta': 'Einwilligungen speichern',
        'submit-cta': 'Onboarding absenden',
        submitting: 'Wird abgesendet…',
        saving: 'Wird gespeichert…',
        consent: {
          'data-protection': {
            label: 'Datenschutz & Verarbeitung',
            description:
              'Ich willige ein, dass die hier übermittelten Unternehmens- und personenbezogenen Daten zum Zweck des Standort-Onboardings gespeichert und verarbeitet werden.',
          },
        },
        'granted-by-name-label': 'Name der einwilligenden Person',
        errors: {
          'granted-by-name': 'Bitte gib den Namen der einwilligenden Person an.',
        },
      },
      confirmation: {
        heading: 'Geschafft — dein Onboarding ist abgesendet.',
        body: 'Deine Angaben gehen ans Team und werden geprüft. Wir melden uns, sobald dein Standort freigegeben ist.',
      },
      state: {
        expired: {
          heading: 'Link abgelaufen oder ungültig',
          body: 'Dieser Link ist abgelaufen oder ungültig. Bitte fordere einen neuen an.',
        },
        'already-submitted': {
          heading: 'Bereits eingereicht',
          body: 'Dein Onboarding wurde bereits eingereicht und wird gerade geprüft.',
        },
        error: {
          heading: 'Etwas ist schiefgelaufen',
          body: 'Wir konnten dein Onboarding gerade nicht laden. Bitte versuche es in einem Moment erneut.',
          retry: 'Erneut versuchen',
        },
      },
    },
    company: {
      heading: 'Unternehmens-Onboarding für {workspaceName}',
      intro:
        'Bevor es losgeht, vervollständige die rechtlichen Angaben und Kontaktdaten deines Unternehmens. Diese erscheinen unter anderem im Impressum eurer Exposés.',
      'pause-hint': 'Du kannst jederzeit pausieren — beim nächsten Anmelden geht es hier weiter.',
      blocked: {
        heading: 'Die Einrichtung von {workspaceName} ist noch nicht abgeschlossen',
        body: 'Bevor du loslegen kannst, muss eine Inhaberin oder ein Inhaber deiner Organisation die Unternehmensangaben vervollständigen. Bitte wende dich an eine verantwortliche Person in deiner Organisation.',
        'platform-mark': 'läuft auf immoOS',
      },
    },
  },
  public: {
    webExpose: {
      title: 'Exposé',
      loading: 'Die Seite wird geladen',
      photos: '{count, plural, one {# Foto} other {# Fotos}}',
      previous: 'Vorheriges Foto',
      next: 'Nächstes Foto',
      counter: '{n} / {total}',
      'photo-alt': 'Foto {n} von {total}',
      tour: {
        mark: '360°',
        start: 'Rundgang starten',
        pill: '360°',
        'pill-label': '360°-Rundgang starten (öffnet in neuem Tab)',
      },
      video: {
        watch: {
          YOUTUBE: 'Auf YouTube ansehen',
          VIMEO: 'Auf Vimeo ansehen',
        },
      },
      'new-tab': '(öffnet in neuem Tab)',
      facts: {
        'living-area': 'Wohnfläche',
        rooms: 'Zimmer',
        'expose-number': 'Exposé-Nr.',
      },
      'price-on-request': 'Preis auf Anfrage',
      share: {
        label: 'Teilen',
        copy: 'Link kopieren',
        copied: 'Link kopiert',
        email: 'Per E-Mail',
        whatsapp: 'WhatsApp',
      },
      gallery: {
        close: 'Schließen',
      },
      footer: {
        'offered-by': 'Ein Angebot von {companyName}',
        imprint: 'Impressum',
        privacy: 'Datenschutz',
      },
      unavailable: {
        title: 'Dieses Exposé ist nicht mehr verfügbar.',
        body: 'Das Angebot wurde möglicherweise bereits verkauft oder zurückgezogen.',
      },
      failed: {
        message: 'Das hat nicht geklappt. Bitte versuchen Sie es später noch einmal.',
        retry: 'Erneut versuchen',
      },
      sections: {
        'object-data': 'Objektdaten',
        equipment: 'Ausstattung',
        video: 'Video',
        tour: '360°-Rundgang',
        'floor-plans': 'Grundriss',
        location: 'Lage',
        energy: 'Energie',
        commission: 'Provision',
      },
      'section-nav': 'Bereiche auf dieser Seite',
      'show-more': 'Mehr anzeigen',
      'show-less': 'Weniger anzeigen',
      'object-data': {
        type: 'Objektart',
        rooms: 'Zimmer',
        bedrooms: 'Schlafzimmer',
        bathrooms: 'Bäder',
        'living-area': 'Wohnfläche',
        'usable-area': 'Nutzfläche',
        'plot-area': 'Grundstücksfläche',
        floor: 'Etage',
        'floors-total': 'Etagen',
        'year-built': 'Baujahr',
        condition: 'Zustand',
        'last-renovated': 'Letzte Modernisierung',
        'available-from': 'Verfügbar ab',
        'parking-spaces': 'Stellplätze',
        'service-charge': 'Hausgeld',
        'expose-number': 'Exposé-Nr.',
        miscellaneous: 'Sonstiges',
      },
      equipment: {
        entry: '{label}: {value}',
      },
      'floor-plans': {
        name: 'Grundriss {n}',
        download: 'Grundriss herunterladen',
      },
      location: {
        'address-on-request': 'Die genaue Adresse erhalten Sie auf Anfrage.',
        'map-alt': 'Karte: Gebiet der Postleitzahl {postalCode}',
        'map-caption': 'Der markierte Bereich zeigt das Gebiet der Postleitzahl {postalCode}.',
      },
      energy: {
        badge: '{energyClass} · {energyValue} kWh/(m²·a)',
        'scale-label': 'Energieeffizienzklasse {energyClass} auf der Skala von A+ bis H',
        'scale-label-non-residential': 'Energieeffizienzklasse {energyClass} auf der Skala von A bis G',
        certificate: 'Energieausweis',
        demand: 'Endenergiebedarf',
        consumption: 'Endenergieverbrauch',
        'primary-energy': 'Primärenergiebedarf',
        value: '{value} kWh/(m²·a)',
        'warm-water': 'inkl. Warmwasser',
        'energy-class': 'Effizienzklasse',
        'firing-type': 'Energieträger',
        'heating-type': 'Heizungsart',
        'year-built': 'Baujahr lt. Energieausweis',
        'issued-at': 'Ausgestellt am',
        'not-available': 'Ein Energieausweis liegt nicht vor.',
        'at-viewing': 'Der Energieausweis wird bei der Besichtigung vorgelegt.',
      },
      commission: {
        label: 'Käuferprovision',
        'plus-vat': '{rate} zzgl. MwSt.',
        'of-price': 'vom Kaufpreis inkl. MwSt.',
        disclaimer:
          'Alle Angaben beruhen auf Informationen des Eigentümers und erfolgen ohne Gewähr. Irrtum und Zwischenverkauf vorbehalten.',
      },
      contact: {
        label: 'Kontakt',
        'purchase-price': 'Kaufpreis',
        'plus-costs': 'zzgl. Kaufnebenkosten',
        'living-area': 'Wohnfläche',
        rooms: 'Zimmer',
        energy: 'Energie',
        'energy-class': 'Klasse {energyClass}',
        kicker: 'Ihr Ansprechpartner',
        call: 'Anrufen',
        email: 'E-Mail',
      },
      'phone-bar': {
        rooms: '{rooms} Zimmer',
      },
      inquiry: {
        actions: { 'full-expose': 'Exposé anfordern', inquiry: 'Unverbindlich nachfragen' },
        heading: { 'full-expose': 'Vollständiges Exposé anfordern', inquiry: 'Unverbindlich nachfragen' },
        fields: {
          'first-name': 'Vorname',
          'last-name': 'Nachname',
          email: 'E-Mail',
          phone: 'Telefon',
          message: 'Nachricht (optional)',
        },
        privacy:
          'Ich bin einverstanden, dass {company} meine Angaben zur Bearbeitung meiner Anfrage verarbeitet. Mehr dazu in der Datenschutzerklärung.',
        'privacy-link-word': 'Datenschutzerklärung',
        'privacy-fallback-company': 'der Makler',
        submit: { 'full-expose': 'Exposé anfordern', inquiry: 'Anfrage senden' },
        hint: 'Eine Anfrage verpflichtet Sie zu nichts. Das vollständige Exposé erhalten Sie nach Abschluss des Maklervertrags.',
        close: 'Schließen',
        errors: {
          required: 'Bitte füllen Sie dieses Feld aus.',
          'email-invalid': 'Bitte geben Sie eine gültige E-Mail-Adresse an.',
          privacy: 'Bitte bestätigen Sie den Datenschutzhinweis.',
          failed: 'Das hat nicht geklappt. Bitte versuchen Sie es später noch einmal.',
        },
        sent: {
          'heading-inquiry': 'Anfrage gesendet',
          'heading-full-expose': 'Vielen Dank.',
          inquiry: 'Vielen Dank für Ihre Anfrage. {name} meldet sich in Kürze bei Ihnen.',
          'inquiry-no-name': 'Vielen Dank für Ihre Anfrage. Wir melden uns in Kürze bei Ihnen.',
          'full-expose':
            'Vielen Dank. Wir haben Ihnen eine E-Mail an {email} geschickt. Darin finden Sie die Widerrufsbelehrung und den Link, über den Sie den Maklervertrag abschließen und das vollständige Exposé erhalten.',
        },
      },
    },
    brokerageAgreement: {
      title: 'Ihr Maklervertrag',
      loading: 'Die Seite wird geladen',
      'load-failed': 'Die Seite konnte gerade nicht geladen werden.',
      'load-retry': 'Erneut versuchen',
      greeting: {
        mr: 'Guten Tag Herr {lastName},',
        mrs: 'Guten Tag Frau {lastName},',
        person: 'Guten Tag {firstName} {lastName},',
        company: 'Guten Tag,',
      },
      intro:
        '{companyName} vermittelt Ihnen dieses Objekt. Bevor wir Ihnen Adresse und vollständiges Exposé geben, schließen Sie hier den Maklervertrag ab.',
      steps: {
        label: 'Ihre nächsten Schritte',
        read: 'Unterlagen lesen',
        conclude: 'Vertrag abschließen',
        expose: 'Exposé erhalten',
        done: 'erledigt',
      },
      chips: {
        'purchase-price': 'Kaufpreis',
        'living-area': 'Wohnfläche',
        rooms: 'Zimmer',
      },
      contact: {
        heading: 'Ihr Kontakt',
        call: 'Anrufen',
        email: 'E-Mail schreiben',
      },
      commission: {
        heading: 'Provision',
      },
      documents: {
        heading: 'Ihre Vertragsunterlagen',
        subtitle: 'Dieselben Texte erhalten Sie auch per E-Mail.',
        count: '3 Dokumente',
        contract: 'Maklervertrag',
        'withdrawal-notice': 'Widerrufsbelehrung',
        'withdrawal-form': 'Muster-Widerrufsformular',
      },
      form: {
        heading: 'Vertrag abschließen',
        identity: 'Sie schließen den Vertrag als {name} ab.',
        'privacy-link-word': 'Datenschutzerklärung',
      },
      inactive: 'Dieses Angebot ist nicht mehr aktiv. Bei Fragen wenden Sie sich an {companyName}.',
      expose: {
        tag: 'Exposé · PDF',
        ready: 'Ihr Exposé ist bereit',
        details: 'Mit Adresse und allen Details zum Objekt.',
        download: 'Exposé herunterladen',
        heading: 'Ihr Exposé',
        soon: 'Das vollständige Exposé erhalten Sie in Kürze von {companyName}.',
        'not-requested': 'Sie haben keinen vorzeitigen Beginn verlangt. {companyName} meldet sich bei Ihnen.',
      },
      dataRoom: {
        heading: 'Unterlagen',
        intro:
          '{companyName} hat Ihnen diese Unterlagen zum Objekt freigegeben. Sie können sie ansehen und herunterladen, zum Beispiel für Ihre Bank.',
        view: 'Ansehen',
        download: 'Herunterladen',
        'view-label': '{name} ansehen',
        'download-label': '{name} herunterladen',
        meta: '{category} · {type} · {size}',
        ended: 'Die Unterlagen sind nicht mehr freigegeben. Bei Fragen wenden Sie sich an {companyName}.',
        privacy:
          'Wenn Sie ein Dokument ansehen oder herunterladen, speichern wir Dokument und Zeitpunkt, damit {companyName} Sie gezielt beraten und die erbrachte Leistung nachweisen kann. Mehr dazu in der Datenschutzerklärung von {companyName}.',
      },
      concluded: {
        heading: 'Ihr Maklervertrag ist abgeschlossen',
        place: '{title} · {city}',
        summary: {
          heading: 'Ihre Angaben',
          'concluded-at-label': 'Abgeschlossen am',
          'concluded-at': '{date} um {time}',
          'early-start-label': 'Vorzeitiger Beginn verlangt',
          yes: 'ja',
          no: 'nein',
          'contract-number-label': 'Vertragsnummer',
          'confirmation-to-label': 'Bestätigung an',
        },
        help: {
          heading: 'Fragen oder Besichtigung?',
        },
      },
      withdrawal: {
        heading: 'Widerruf',
        'contract-number': 'Nr. {contractNumber}',
        'period-over': 'Ihre Widerrufsfrist ist am {date} abgelaufen.',
        open: 'Vertrag widerrufen',
        'form-heading': 'Vertrag widerrufen',
        name: 'Name',
        contract: 'Vertrag',
        email: 'E-Mail-Adresse',
        reason: 'Grund (freiwillig)',
        'contract-prefill': 'Maklervertrag „{title}“ vom {date}, Nr. {contractNumber}',
        confirm: 'Widerruf bestätigen',
        cancel: 'Abbrechen',
        'receipt-heading': 'Ihr Widerruf ist eingegangen',
        'receipt-body': 'Eingegangen am {date} um {time}. Eine Eingangsbestätigung haben wir an {email} gesendet.',
        'reason-none': '—',
        revoked: 'Sie haben diesen Vertrag am {date} um {time} widerrufen.',
        'period-ended':
          'Die Frist für einen Widerruf über diese Seite ist abgelaufen. Bei Fragen wenden Sie sich an {companyName}.',
      },
      footer: {
        withdraw: 'Vertrag widerrufen',
        privacy: 'Datenschutz',
      },
      imprint: {
        phone: 'Telefon: {value}',
        email: 'E-Mail: {value}',
        'managing-directors': 'Geschäftsführung: {value}',
        register: 'Registergericht: {court} · Registernummer: {number}',
        'register-court-only': 'Registergericht: {court}',
        'register-number-only': 'Registernummer: {number}',
        'vat-id': 'USt-IdNr.: {value}',
        'supervisory-authority': 'Aufsichtsbehörde: {value}',
        'trade-permit-authority': 'Erlaubnisbehörde (§ 34c GewO): {value}',
        'professional-indemnity-insurance': 'Berufshaftpflicht: {value}',
      },
    },
    signing: {
      title: 'Unterschrift',
      loading: 'Die Seite wird geladen',
      heading: '{documentType, select, BROKER_CONTRACT {Maklervertrag} other {Vollmacht}} für {address}',
      'document-label':
        '{documentType, select, BROKER_CONTRACT {Maklervertrag} other {Vollmacht}} · {count, plural, one {# Seite} other {# Seiten}}',
      download: 'PDF herunterladen',
      'page-label': 'Seite {page} von {count}',
      'document-failed': 'Das Dokument konnte nicht geladen werden.',
      confirmation:
        'Vielen Dank, Sie haben den {documentType, select, BROKER_CONTRACT {Maklervertrag} other {Vollmacht}} unterschrieben. Eine Bestätigung mit dem Dokument ist per E-Mail unterwegs. Sobald alle Eigentümer unterschrieben haben, erhalten Sie das vollständig unterschriebene Dokument.',
      'signed-state': 'Sie haben am {date} um {time} unterschrieben.',
      'signed-at': 'Elektronisch unterschrieben am {date} um {time}',
      'form-heading': 'Unterschreiben',
      'name-label': 'Ihr vollständiger Name',
      'name-hint': 'Ihr Name wird als Unterschrift in das Dokument übernommen.',
      'contact-heading': 'Ihr Kontakt',
      'privacy-link': 'Datenschutzerklärung',
      inactive: 'Diese Unterschriftsanfrage ist nicht mehr aktiv. Bei Fragen wenden Sie sich an {companyName}.',
      close: 'Schließen',
      failed: 'Das hat nicht geklappt. Bitte versuchen Sie es später noch einmal.',
      retry: 'Erneut versuchen',
      'withdrawal-title': 'Widerruf',
      'withdrawal-text':
        'Ihre Widerrufsfrist endet am {date}. Bis dahin können Sie diesen Vertrag ohne Angabe von Gründen widerrufen.',
      'withdrawal-action': 'Vertrag widerrufen',
      'withdrawal-contract-prefill': 'Maklervertrag für {address}, unterschrieben am {date}',
    },
  },
  admin: {
    'sign-in': {
      title: 'immoOS Admin',
      subtitle: 'Plattform-Anmeldung',
      google: 'Mit Google anmelden',
      loading: 'Anmeldung läuft…',
      error: 'Anmeldung nicht möglich. Bitte überprüfen Sie Ihre Eingaben.',
    },
    callback: {
      loading: 'Anmeldung wird abgeschlossen…',
      'missing-code': 'Der Anmeldevorgang ist unvollständig. Bitte starten Sie ihn erneut.',
      failed: 'Anmeldung nicht möglich. Bitte überprüfen Sie Ihre Eingaben.',
      'back-to-sign-in': 'Zurück zur Anmeldung',
    },
    'sign-out': {
      loading: 'Abmeldung läuft…',
      failed: 'Abmeldung fehlgeschlagen — bitte erneut versuchen.',
      'back-to-sign-in': 'Zur Anmeldung',
    },
    'access-denied': {
      title: 'Kein Zugriff',
      body: 'Diese Oberfläche ist nur für Plattformpersonal. Bitte nutzen Sie app.immoos.ai für Ihre Workspace.',
      // Shown when the operator IS platform staff but holds no permission for
      // the route they opened — telling them they don't belong here would send
      // them chasing the wrong fix (IMM-494.1).
      'body-missing-permission': 'Ihnen fehlt die Berechtigung für diesen Bereich.',
      'back-to-sign-in': 'Zurück zur Anmeldung',
    },
    notifications: {
      title: 'Benachrichtigungen',
      intro: 'Wähle, worüber dich die App benachrichtigen soll.',
      events: {
        FLEET_QUESTION_PARKED: 'Agent wartet auf eine Antwort',
        FLEET_MACHINE_ENROLMENT_WAITING: 'Maschine wartet auf Freigabe',
        LANDIQ_DATA_RUN_FAILED: 'LandIQ-Lauf fehlgeschlagen',
        LANDIQ_STAGE_FINISHED: 'LandIQ-Stufe abgeschlossen',
        LANDIQ_DATA_RUN_STALE: 'LandIQ-Lauf ohne neuen Stand',
        FLEET_RELEASE_DEPLOYED: 'Deployments',
      },
      bodies: {
        FLEET_MACHINE_ENROLMENT_WAITING: '{machine} möchte der Flotte beitreten.',
        LANDIQ_DATA_RUN_FAILED:
          '{ticket}: Der Lauf ist fehlgeschlagen. Öffne ihn, um ihn fortzusetzen oder neu zu starten.',
        LANDIQ_STAGE_FINISHED: '{ticket}: {stage} ist fertig. Die nächste Stufe kann starten.',
        LANDIQ_DATA_RUN_STALE: '{ticket}: Seit 20 Minuten kein neuer Stand. Öffne den Lauf, um nachzusehen.',
        FLEET_QUESTION_PARKED: '{ticket} braucht eine Entscheidung.',
        FLEET_RELEASE_DEPLOYED: {
          immoos: '{immoos, plural, one {# immoOS-Ticket wurde deployt} other {# immoOS-Tickets wurden deployt}}',
          landiq: '{landiq, plural, one {# LandIQ-Ticket wurde deployt} other {# LandIQ-Tickets wurden deployt}}',
          both: '{immoos} immoOS- und {landiq, plural, one {# LandIQ-Ticket} other {# LandIQ-Tickets}} wurden deployt',
        },
      },
      blocked: 'Benachrichtigungen sind in den iOS-Einstellungen deaktiviert.',
      'open-settings': 'Einstellungen öffnen',
    },
    'update-prompt': {
      title: 'Neue Version verfügbar',
      body: 'Diese Version der App ist veraltet. Installiere die neueste Version über TestFlight.',
      action: 'TestFlight öffnen',
    },
    shell: {
      // Nur unter `sm` sichtbar: dort liegt die Navigation in einer Schublade.
      'open-nav': 'Navigation öffnen',
      nav: {
        workspaces: 'Workspaces',
        members: 'Mitglieder',
        organizations: 'Organisationen',
        properties: 'Objekte',
        'deleted-deals': 'Gelöschte Deals',
        'deleted-companies': 'Gelöschte Firmen',
        'api-keys': 'API-Keys',
        'api-calls': 'API-Aufrufe',
        logs: 'Logs',
        'api-key-requests': 'API-Key-Anfragen',
        emails: 'E-Mails',
        valuations: 'Bewertungen',
        'valuation-agent': 'Bewertungs-Agent',
        'messenger-chats': 'Messenger-Chats',
        admin: 'Admin',
        admins: 'Admins',
        skills: 'Skills',
        'document-analyses': 'Dokumentanalysen',
        'mcp-access': 'MCP-Zugang',
        'fact-keys': 'Merkmalschlüssel',
        'agent-skills': 'Agent-Skills',
        fleet: 'Fleet',
        machines: 'Maschinen',
        board: 'Board',
        metrics: 'Metriken',
        'fleet-accounts': 'Konten',
        landiq: 'LandIQ',
        'data-runs': 'Datenläufe',
        sandbox: 'Sandbox',
      },
      'user-menu': {
        'sign-out': 'Abmelden',
      },
      table: {
        'sort-label': 'Nach {column} sortieren',
      },
      filters: {
        search: 'Suchen…',
        'more-workspaces': 'Weitere Workspaces',
        'more-workspaces-placeholder': 'Weitere Workspaces …',
        all: 'Alle',
        status: 'Status',
        organization: 'Standort',
        'organization-all': 'Alle Standorte',
        // The operator's test-workspace filter (IMM-593.2): one pill shared by
        // every workspace-scoped list, its row badge, and the empty state shown
        // while the filter is hiding rows.
        'test-workspaces': 'Testumgebungen',
        'test-workspaces-aria': 'Testumgebungen anzeigen',
        'test-badge': 'Test',
        'test-badge-full': 'Testumgebung',
        'empty-test-hidden':
          'Keine Einträge — Testumgebungen sind ausgeblendet. Aktivieren Sie „Testumgebungen", um sie anzuzeigen.',
        'empty-test-hidden-cta': 'Testumgebungen anzeigen',
      },
    },
    // Gelöschte Deals (IMM-851.1) — cross-tenant operator restore surface.
    'deleted-by': { unknown: 'Unbekannt', system: 'System', operator: '{name} (Operator)' },
    deals: {
      deleted: {
        title: 'Gelöschte Deals',
        empty: 'Es gibt keine gelöschten Deals.',
        'unresolved-workspace': '—',
        'unresolved-property': '—',
        'property-deleted': 'Objekt gelöscht',
        phase: DEAL_PHASE_LABELS,
        status: DEAL_STATUS_LABELS,
        type: DEAL_TYPE_LABELS,
        columns: {
          workspace: 'Workspace',
          object: 'Objekt',
          type: 'Art',
          status: 'Status',
          phase: 'Phase',
          'deleted-at': 'Gelöscht',
          'deleted-by': 'Gelöscht von',
          actions: 'Aktion',
        },
        restore: 'Wiederherstellen',
        'restore-aria': 'Deal zu Objekt {reference} wiederherstellen',
        'restore-aria-unresolved': 'Deal wiederherstellen',
        confirm: {
          title: 'Deal wiederherstellen?',
          body: 'Der Deal erscheint wieder im Workspace {workspace} — mit Phase, Status, Kontakten und Notizen wie vor dem Löschen. In der Aktivität des Deals sehen die Kunden, dass immoOS ihn wiederhergestellt hat.',
          'body-property-deleted': 'Das zugehörige Objekt ist ebenfalls gelöscht und wird nicht mit wiederhergestellt.',
          cancel: 'Abbrechen',
          cta: 'Wiederherstellen',
          loading: 'Wird wiederhergestellt …',
        },
        errors: {
          'cannot-restore-not-deleted': 'Dieser Deal ist nicht gelöscht — es gibt nichts wiederherzustellen.',
          'cannot-restore-active-deal-exists':
            'Für dieses Objekt gibt es bereits einen aktiven Deal. Du kannst den gelöschten Deal erst wiederherstellen, wenn der andere abgeschlossen oder gelöscht wurde.',
          'cannot-restore-property-archived':
            'Das Objekt dieses Deals ist archiviert. Hebe zuerst die Archivierung auf, um den Deal wiederherzustellen.',
          'blocking-deal': 'Blockierender Deal: Objekt {reference}',
          forbidden: 'Dir fehlt die Berechtigung, Deals wiederherzustellen.',
          generic: 'Der Deal konnte nicht wiederhergestellt werden. Bitte versuche es erneut.',
        },
      },
    },
    // Gelöschte Firmen (IMM-1096.4) — cross-tenant operator read.
    companies: {
      deleted: {
        title: 'Gelöschte Firmen',
        empty: 'Keine gelöschten Firmen.',
        'unresolved-workspace': '—',
        columns: { name: 'Firma', workspace: 'Workspace', 'deleted-by': 'Gelöscht von', 'deleted-at': 'Gelöscht' },
      },
    },
    // Bewertungen-Queue (IMM-407.4) — the cross-tenant operator surface. Enum
    // option tables and the type-field names are the SAME shared constants the
    // web-app Anfrage form/Vorschau use, so the operator reads the identical
    // labels; only the queue/detail/take-on chrome is admin-specific.
    valuations: {
      title: 'Bewertungen',
      empty: 'Zurzeit keine offenen Bewertungsanfragen.',
      unassigned: '—',
      // IMM-572.2 — a machine holder (`takenByKind === 'ADMIN_API_KEY'`) is
      // labelled as such wherever an assignee renders. At the parent namespace,
      // NOT under `detail`: the queue page cannot reach `detail.*` keys.
      'admin-key-holder': '{name} (Admin-API-Schlüssel)',
      'admin-key-holder-unknown': 'Unbekannter Admin-API-Schlüssel',
      // `ALL` is a filter-only pill (every admin-visible status), not a request status.
      status: { ...VALUATION_STATUS_LABELS, ALL: 'Alle' },
      columns: {
        workspace: 'Workspace',
        object: 'Objekt',
        objectType: 'Objektart',
        usage: 'Nutzung',
        'requested-by': 'Angefragt von',
        'requested-at': 'Angefragt am',
        'desired-completion': 'Gewünschte Fertigstellung',
        age: 'Alter',
        'recommended-start-price': 'Empfohlener Startpreis',
        status: 'Status',
        assignee: 'Bearbeiter',
      },
      // The queue age (oldest first, age shown — the no-SLA mitigation).
      age: {
        days: '{count, plural, one {# Tag} other {# Tage}}',
        hours: '{count, plural, one {# Stunde} other {# Stunden}}',
        minutes: '{count, plural, one {# Minute} other {# Minuten}}',
        now: 'gerade eben',
      },
      filters: {
        'status-all': 'Alle Status',
        'workspace-all': 'Alle Workspaces',
        // The Bewertungen queue's overflow control for the workspace tail
        // (IMM-823.1) — „Kunden" is this queue's vocabulary, not the shell's.
        'more-workspaces': 'Weitere Kunden',
        'more-workspaces-placeholder': 'Weitere Kunden …',
      },
      detail: {
        back: 'Bewertungen',
        'not-provided': '—',
        'boolean-ja': 'Ja',
        'boolean-nein': 'Nein',
        'requester-label': 'Angefragt von',
        'requested-at-label': 'Angefragt am',
        'assignee-label': 'Bearbeiter',
        'taken-at-label': 'Übernommen am',
        'take-on': 'Übernehmen',
        'taking-on': 'Wird übernommen…',
        // AC 41: the loser of the claim race is told who holds it.
        'already-taken': '{name} bearbeitet diese Anfrage bereits.',
        'take-on-error': 'Die Anfrage konnte nicht übernommen werden. Bitte versuche es erneut.',
        'take-on-forbidden': 'Dafür fehlt dir die Berechtigung.',
        // IMM-572.2 — „Übernahme aufheben": hands an IN_PROGRESS claim back to
        // the queue. Copy pinned verbatim by the parent spec except the three
        // error/loading keys.
        'release-claim': {
          cta: 'Übernahme aufheben',
          'confirm-title': 'Übernahme durch „{name}“ aufheben?',
          'confirm-body':
            'Die Anfrage geht zurück in die Warteschlange und kann erneut übernommen werden — von einem Konsumenten oder von dir.',
          'confirm-cta': 'Übernahme aufheben',
          'cancel-cta': 'Abbrechen',
          loading: 'Wird aufgehoben…',
          'not-claimed-error': 'Diese Anfrage ist nicht mehr übernommen. Die Ansicht wurde aktualisiert.',
          'forbidden-error': 'Dafür fehlt dir die Berechtigung.',
          error: 'Die Übernahme konnte nicht aufgehoben werden. Bitte versuche es erneut.',
        },
        // RD 15: none of the three intake price expectations may read as a
        // target or as the valuation result.
        'price-expectation-note':
          'Preisvorstellungen aus der Anfrage — reine Kontextangaben, kein Zielwert und kein Ergebnis der Bewertung.',
        // An unset price reads as „Keine Angabe", not the dash the other fields use.
        'price-expectation-none': 'Keine Angabe',
        'documents-empty': 'Keine Unterlagen',
        'images-empty': 'Keine Bilder',
        // IMM-501.3 — die Kategorie des Objekt-Dokuments an jeder Unterlage. Es
        // sind dieselben Konstanten wie im Dokumente-Tab des Maklers: Operator
        // und Makler müssen dieselbe Kategorie gleich benennen.
        'category-group': DOCUMENT_CATEGORY_GROUP_LABELS,
        category: DOCUMENT_CATEGORY_LABELS,
        classification: DOCUMENT_CLASSIFICATION_MARKERS,
        // Aktionen pro Anhang in den Unterlagen- / Bilder-Zeilen. Jede erzeugt beim
        // Klick eine kurzlebige signierte URL; der Fehler deckt jeden Fehlschlag ab.
        'attachment-preview': 'Vorschau',
        'attachment-download': 'Herunterladen',
        'attachment-error': 'Die Datei konnte nicht geöffnet werden. Bitte versuche es erneut.',
        'parking-empty': 'Keine Stellplätze erfasst',
        'units-empty': 'Keine Einheiten erfasst',
        'unit-heading': 'Einheit {index}',
        // Operator production panel (IMM-407.5, renamed IMM-475.2). Shown for
        // IN_PROGRESS behind the -complete permission; producing no longer delivers
        // anything — it parks the result at the approval gate.
        submit: {
          title: 'Zur Freigabe einreichen',
          'price-from-label': 'Preisspanne von',
          'price-to-label': 'Preisspanne bis',
          'recommended-label': 'Empfohlener Startpreis (optional)',
          'date-label': 'Bewertungsdatum',
          // Two independent slots, one per report kind — the contract carries two
          // nullable ids and allows one file per kind.
          'report-html-label': 'Bericht (HTML)',
          'report-document-label': 'Bericht (PDF/DOCX)',
          'report-hint': 'Mindestens ein Bericht erforderlich · HTML oder PDF · max 20 MB',
          'field-errors': {
            priceFrom: 'Bitte gib die untere Preisspanne ein.',
            priceTo: 'Bitte gib die obere Preisspanne ein.',
            priceRange: 'Die obere Preisspanne muss mindestens so hoch sein wie die untere.',
            report: 'Bitte lade mindestens einen Bericht hoch.',
          },
          'report-selected': '„{name}" hochgeladen',
          'report-none': 'Noch keine Datei ausgewählt',
          uploading: 'Wird hochgeladen…',
          'upload-in-progress-hint': 'Bericht wird noch hochgeladen…',
          submit: 'Zur Freigabe einreichen',
          submitting: 'Wird eingereicht…',
          // AC 43: „Zum Einreichen fehlen: {Liste}." naming each missing/invalid item.
          'missing-prefix': 'Zum Einreichen fehlen: {list}.',
          // A disallowed / oversized file is refused before presigning.
          'upload-policy-error': 'Diese Datei ist nicht zulässig. HTML oder PDF · max 20 MB.',
          'upload-error': 'Die Datei konnte nicht hochgeladen werden. Bitte versuche es erneut.',
          'submit-error': 'Die Bewertung konnte nicht eingereicht werden. Bitte versuche es erneut.',
          // Non-blocking advisory: the entered recommended start price sits
          // outside the entered price range. Submitting still succeeds.
          'recommended-out-of-range':
            'Der Startpreis liegt außerhalb der Preisspanne. Das Einreichen ist trotzdem möglich.',
          // A returned request keeps its held result and reports; the producer sees
          // what the reviewer asked to be corrected.
          'returned-title': 'Grund der Rückgabe',
          'returned-by': 'Zurückgegeben von {name} am {date}',
          // Maps the backend's `missing` keys → operator-facing labels.
          'missing-labels': {
            priceFrom: 'Preisspanne von',
            priceTo: 'Preisspanne bis',
            priceRange: 'gültige Preisspanne (von ≤ bis)',
            report: 'Bericht',
            reportHtmlMediaFileId: 'Bericht (HTML)',
            reportDocumentMediaFileId: 'Bericht (PDF/DOCX)',
          },
        },
        // The approval gate (IMM-475.2). Shown for AWAITING_RELEASE: the held
        // figures and report filenames read-only, plus — behind the -release
        // permission — the two reviewer actions.
        review: {
          title: 'Warte auf Freigabe',
          'price-from-label': 'Preisspanne von',
          'price-to-label': 'Preisspanne bis',
          'recommended-label': 'Empfohlener Startpreis',
          'valuation-date-label': 'Bewertungsdatum',
          'submitted-by-label': 'Eingereicht von',
          'submitted-at-label': 'Eingereicht am',
          'reports-label': 'Berichte',
          'reports-empty': 'Keine Berichte',
          'report-kind': {
            HTML: 'HTML',
            DOCUMENT: 'Dokument',
          },
          release: 'Freigeben',
          releasing: 'Wird freigegeben…',
          'release-success': 'Bewertung freigegeben. Der Agent wurde benachrichtigt.',
          'release-error': 'Die Bewertung konnte nicht freigegeben werden. Bitte versuche es erneut.',
          // The permanent refusal (BL-020): the request already left „Warte auf
          // Freigabe" — deliberately NO retry invitation, because a retry cannot work.
          'release-conflict': 'Diese Bewertung wartet nicht mehr auf Freigabe. Der Status wurde aktualisiert.',
          return: 'Zur Überarbeitung zurückgeben',
          'return-reason-label': 'Grund der Rückgabe',
          'return-cancel': 'Abbrechen',
          'return-submit': 'Zurückgeben',
          returning: 'Wird zurückgegeben…',
          'return-success': 'Bewertung zur Überarbeitung zurückgegeben.',
          'return-reason-required': 'Bitte gib einen Grund für die Rückgabe an.',
          'return-error': 'Die Bewertung konnte nicht zurückgegeben werden. Bitte versuche es erneut.',
          'return-conflict': 'Diese Bewertung wartet nicht mehr auf Freigabe. Der Status wurde aktualisiert.',
        },
        // The report controls (IMM-598), used by BOTH the approval gate and the
        // released panel — one block, never two copies (BL-022).
        report: {
          preview: 'Vorschau',
          download: 'Herunterladen',
          // Accessible name carrying the report's own filename (IMM-828.1) — the
          // visible „Herunterladen" is identical for both reports, so a reader of
          // assistive technology needs the name to distinguish them.
          'download-aria': 'Herunterladen: {name}',
          // A permanent refusal: the report is not readable at this status, or not
          // there at all. Deliberately NO retry invitation (BL-020).
          error: 'Der Bericht konnte nicht geöffnet werden.',
          loading: 'Bericht wird geladen…',
          'frame-title': 'Wertgutachten (Vorschau)',
          // Öffnet den Bericht im Vollbild-Dialog (IMM-609). Am Freigabe-Gate
          // muss der Bericht gelesen werden, nicht nur überflogen.
          expand: 'Vollbild',
          close: 'Schließen',
          note: 'Der Bericht wird isoliert dargestellt und stellt keine externen Anfragen.',
        },
        // Read-only result shown once the request is released: the delivered
        // valuation record (price range / recommended start price / valuation date
        // / report file), read from the property's property_valuation, alongside
        // the who/when release stamp. An HTML-only release files no property
        // document, so the report row is simply absent.
        result: {
          title: 'Bewertungsergebnis',
          'price-from-label': 'Preisspanne von',
          'price-to-label': 'Preisspanne bis',
          'recommended-label': 'Empfohlener Startpreis',
          'valuation-date-label': 'Bewertungsdatum',
          'report-label': 'Wertgutachten',
          'released-by-label': 'Freigegeben von',
          'released-at-label': 'Freigegeben am',
          note: 'Das Ergebnis wurde als Bewertung des Objekts gespeichert und das Wertgutachten am Objekt abgelegt.',
        },
        block: {
          basics: 'Basis',
          reason: 'Anlass & Erwartung',
          condominium: 'Angaben zur Eigentumswohnung',
          house: 'Angaben zum Haus',
          multiFamily: 'Angaben zum Mehrfamilienhaus',
          plot: 'Angaben zum Grundstück',
          overallCondition: 'Qualität und Zustand',
          parkingSpaces: 'Stellplätze',
          income: 'Ertrag (IST)',
          units: 'Einheiten',
          hoa: 'WEG',
          documents: 'Unterlagen',
          // The Bilder set sits directly below Unterlagen. It is not part of the
          // shared `valuationBlockLayout`, so the operator detail titles it here.
          images: 'Bilder',
        },
        // Base + context field labels (the type-specific fields use `field-name`).
        field: {
          objectType: 'Bewertungsobjektart',
          usage: 'Nutzung',
          address: 'Adresse',
          valuationReason: 'Bewertungsanlass',
          timeHorizon: 'Zeithorizont',
          desiredCompletion: 'Gewünschte Fertigstellung',
          note: 'Anmerkung',
        },
        'field-name': VALUATION_FIELD_NAME_LABELS,
        objectType: VALUATION_OBJEKTART_LABELS,
        usage: VALUATION_NUTZUNG_LABELS,
        'reason-enum': VALUATION_ANLASS_LABELS,
        timeHorizon: VALUATION_ZEITHORIZONT_LABELS,
        priceType: VALUATION_PREISVORSTELLUNG_ART_LABELS,
        priceExpectationKind: VALUATION_PRICE_EXPECTATION_KIND_LABELS,
        heatingType: VALUATION_HEIZUNGSART_LABELS,
        energyEfficiencyClass: VALUATION_ENERGIEEFFIZIENZKLASSE_LABELS,
        houseType: VALUATION_HAUSTYP_LABELS,
        basement: VALUATION_UNTERKELLERT_LABELS,
        developmentPlan: VALUATION_BEBAUUNGSPLAN_LABELS,
        servicingStatus: VALUATION_ERSCHLIESSUNGSZUSTAND_LABELS,
        contaminationSuspicion: VALUATION_ALTLASTENVERDACHT_LABELS,
        'graduated-index-rent': VALUATION_STAFFEL_INDEX_MIETE_LABELS,
        'unit-breakdown': VALUATION_ANGABE_DER_EINHEITEN_LABELS,
        quality: VALUATION_QUALITAET_LABELS,
        condition: VALUATION_ZUSTAND_LABELS,
        parkingSpaceType: VALUATION_STELLPLATZART_LABELS,
        lettingStatus: VALUATION_EINHEIT_VERMIETUNGSSTATUS_LABELS,
        parkingSpaces: {
          'type-label': 'Art',
          'count-label': 'Anzahl',
        },
        units: {
          label: 'Bezeichnung',
          lettingStatus: 'Vermietungsstatus',
          'let-since': 'Vermietet seit',
          livingArea: 'Wohnfläche',
          'current-rent': 'Aktuelle Miete',
          'last-rent-increase': 'Letzte Mieterhöhung',
        },
      },
    },
    // Operator read of members' messenger chats (WhatsApp, Telegram) across all
    // workspaces (IMM-1040.6, IMM-1127.1) — read-only, gated by the same permission
    // as IMM-928.4's WhatsApp reads.
    'messenger-chats': {
      title: 'Messenger-Chats',
      description: 'Alle Chats von Mitgliedern mit dem immoOS-Assistenten, über alle Workspaces. Nur lesen.',
      experimental: 'Experimentell',
      unassigned: '—',
      columns: {
        channel: 'Kanal',
        account: 'Konto',
        member: 'Mitglied',
        workspace: 'Workspace',
        'last-message': 'Letzte Nachricht',
      },
      channels: {
        whatsapp: 'WhatsApp',
        telegram: 'Telegram',
      },
      truncated: 'Es werden die {limit} Chats mit der neuesten Aktivität angezeigt.',
      empty: 'Noch keine Messenger-Chats.',
      detail: {
        back: 'Alle Chats',
        'member-panel': 'Mitglied',
        email: 'E-Mail',
        workspace: 'Workspace',
        channel: 'Kanal',
        account: 'Konto',
        immoos: 'immoOS',
        'read-only': 'Nur lesen — Operatoren können in diesen Chat nicht schreiben.',
        truncated: 'Es werden die neuesten {limit, number} Nachrichten angezeigt.',
      },
    },
    workspaces: {
      title: 'Workspaces',
      placeholder: 'Workspace-Verwaltung erscheint hier sobald die Provisionierung gebaut ist.',
      empty: 'Noch keine Workspaces angelegt.',
      'create-cta': 'Neue Workspace',
      columns: {
        name: 'Name',
        owner: 'Inhaber',
        'created-at': 'Erstellt am',
      },
      create: {
        title: 'Neue Workspace anlegen',
        'name-label': 'Workspace-Name',
        'name-placeholder': 'Mustermakler GmbH',
        'is-test-label': 'Testumgebung (nicht produktiv)',
        'owner-email-label': 'Owner-E-Mail',
        'owner-email-placeholder': 'owner@example.com',
        'owner-first-name-label': 'Vorname',
        'owner-first-name-placeholder': 'Ada',
        'owner-last-name-label': 'Nachname',
        'owner-last-name-placeholder': 'Lovelace',
        'address-heading': 'Anschrift Hauptstandort',
        'address-search-label': 'Adresse suchen',
        'address-search-placeholder': 'Adresse eingeben…',
        'street-label': 'Straße',
        'street-placeholder': 'Eppendorfer Landstraße',
        'house-number-label': 'Hausnummer',
        'house-number-placeholder': '47a',
        'postal-code-label': 'PLZ',
        'postal-code-placeholder': '20251',
        'city-label': 'Stadt',
        'city-placeholder': 'Hamburg',
        'country-label': 'Land',
        'country-placeholder': 'Deutschland',
        'submit-cta': 'Workspace anlegen',
        'submit-loading': 'Wird angelegt…',
        'back-cta': 'Zurück',
        success: 'Workspace „{name}" angelegt. Owner: {ownerEmail}.',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'generic-error': 'Workspace konnte nicht angelegt werden. Bitte versuche es erneut.',
        errors: {
          name: {
            required: 'Bitte gib einen Namen ein.',
            'too-short': 'Der Workspace-Name muss mindestens 2 Zeichen lang sein.',
            'too-long': 'Der Workspace-Name darf höchstens 80 Zeichen lang sein.',
          },
          'owner-email': {
            required: 'Bitte gib eine E-Mail-Adresse ein.',
            invalid: 'Diese E-Mail-Adresse ist ungültig.',
          },
          'owner-first-name': {
            required: 'Bitte gib einen Vornamen ein.',
            'too-long': 'Der Vorname darf höchstens 80 Zeichen lang sein.',
          },
          'owner-last-name': {
            required: 'Bitte gib einen Nachnamen ein.',
            'too-long': 'Der Nachname darf höchstens 80 Zeichen lang sein.',
          },
          street: {
            required: 'Bitte gib eine Straße ein.',
            'too-long': 'Die Straße darf höchstens 100 Zeichen lang sein.',
          },
          'house-number': {
            required: 'Bitte gib eine Hausnummer ein.',
            'too-long': 'Die Hausnummer darf höchstens 20 Zeichen lang sein.',
          },
          'postal-code': {
            required: 'Bitte gib eine PLZ ein.',
            invalid: 'Die PLZ muss aus 5 Ziffern bestehen.',
          },
          city: {
            required: 'Bitte gib eine Stadt ein.',
            'too-long': 'Die Stadt darf höchstens 80 Zeichen lang sein.',
          },
          country: {
            required: 'Bitte gib ein Land ein.',
            'too-long': 'Das Land darf höchstens 80 Zeichen lang sein.',
          },
        },
      },
      detail: {
        title: 'Workspace',
        // Shown right after creating a test workspace while the filter hides it —
        // the one moment the default reliably surprises the operator (IMM-593.2).
        'created-test-hidden':
          'Diese Workspace ist eine Testumgebung und wird in den Listen ausgeblendet, solange „Testumgebungen" nicht aktiviert ist.',
        'id-label': 'ID',
        'created-at-label': 'Erstellt am',
        'owner-heading': 'Owner',
        'owner-name-label': 'Name',
        'owner-email-label': 'E-Mail',
        'address-heading': 'Anschrift Hauptstandort',
        'street-label': 'Straße',
        'house-number-label': 'Hausnummer',
        'postal-code-label': 'PLZ',
        'city-label': 'Stadt',
        'country-label': 'Land',
        'coordinates-label': 'Koordinaten',
        'organization-heading': 'Hauptstandort',
        'organization-name-label': 'Name',
        'status-aktiv': 'Aktiv',
        'edit-cta': 'Bearbeiten',
        'back-cta': 'Zurück zur Liste',
        'pending-heading': 'Ausstehende Einladungen',
        'pending-empty': 'Keine ausstehenden Einladungen.',
        'pending-expires-label': 'Läuft ab',
        'pending-resend-cta': 'Erneut senden',
        'pending-resending': 'Wird gesendet…',
        'pending-resent': 'Einladung wurde erneut gesendet.',
        'pending-resend-error': 'Die Einladung konnte nicht erneut gesendet werden. Bitte versuche es erneut.',
        'pending-revoke-cta': 'Zurückziehen',
        'pending-revoking': 'Wird zurückgezogen…',
        'pending-revoke-error': 'Die Einladung konnte nicht zurückgezogen werden. Bitte versuche es erneut.',
        whatsapp: {
          section: 'WhatsApp',
          'load-error': 'Die WhatsApp-Liste konnte nicht geladen werden.',
          'sort-label': 'Nach {column} sortieren',
          truncated: 'Nur die neuesten {limit} Einträge werden angezeigt.',
          failed: {
            heading: 'Fehlgeschlagene Sendungen',
            recipient: 'Empfänger',
            reason: 'Grund',
            'provider-code': 'Meta-Fehlercode',
            'sent-by': 'Gesendet von',
            'occurred-at': 'Zeitpunkt',
            empty: 'Keine fehlgeschlagenen WhatsApp-Sendungen.',
          },
          unmatched: {
            heading: 'Nicht zugeordnete Nachrichten',
            'scope-note': 'Plattformweit — diese Nachrichten gehören zu keiner Workspace.',
            phone: 'Nummer',
            name: 'Name',
            body: 'Nachricht',
            'received-at': 'Eingegangen am',
            reason: 'Grund',
            empty: 'Keine nicht zugeordneten Nachrichten.',
          },
          reason: {
            'no-match': 'Keine Zuordnung',
            ambiguous: 'Mehrdeutig',
            'window-closed': '24-Stunden-Fenster geschlossen',
            'not-on-whatsapp': 'Nummer nicht bei WhatsApp',
            'unsupported-content': 'Inhalt nicht unterstützt',
            unknown: 'Unbekannter Grund',
          },
        },
        portals: {
          section: 'Portale',
          'load-error': 'Die Portalverbindungen konnten nicht geladen werden.',
          portal: { IMMOSCOUT24: 'ImmoScout24', KLEINANZEIGEN: 'Kleinanzeigen', IMMOWELT: 'Immowelt' },
          'status-label': 'Status',
          status: {
            'not-connected': 'Nicht verbunden',
            connected: 'Verbunden',
            expired: 'Verbindung abgelaufen',
            invalid: 'Zugang ungültig',
          },
          'expired-since': 'seit {at}',
          'account-label': 'IS24-Konto',
          'connected-by-label': 'Verbunden von',
          'connected-at-label': 'Verbunden am',
          'entered-by-label': 'Eingetragen von',
          'entered-at-label': 'Eingetragen am',
          'invalid-since-label': 'Ungültig seit',
          'last-error-label': 'Letzter Verbindungsfehler',
          'last-error-note': 'Meldung des Portals, unverändert.',
          'last-error-none': 'Keiner',
        },
        'invite-heading': 'Mitglied einladen',
        'invite-email-label': 'E-Mail',
        'invite-email-placeholder': 'name@beispiel.de',
        'invite-first-name-label': 'Vorname',
        'invite-last-name-label': 'Nachname',
        'invite-organisation-role-label': 'Organisationsrolle',
        'invite-workspace-role-label': 'Workspace-Rolle',
        'invite-workspace-role-none': 'Keine',
        'invite-standorte-label': 'Standorte',
        'invite-standorte-empty': 'Für diesen Workspace sind keine Standorte verfügbar.',
        'invite-submit-cta': 'Einladung senden',
        'invite-cancel-cta': 'Abbrechen',
        'invite-submitting': 'Wird gesendet…',
        'invite-success': 'Einladung wurde gesendet.',
        'invite-error': 'Die Einladung konnte nicht gesendet werden. Bitte versuche es erneut.',
        'invite-validation-error': 'Bitte fülle alle Pflichtfelder korrekt aus und wähle mindestens einen Standort.',
        'org-role': {
          'organisation-owner': 'Organisations-Owner',
          agent: 'Makler',
          assistant: 'Assistenz',
          viewer: 'Betrachter',
        },
        'workspace-role': {
          'workspace-owner': 'Workspace-Owner',
          'workspace-admin': 'Workspace-Admin',
          billing: 'Abrechnung',
        },
      },
      edit: {
        title: 'Workspace bearbeiten',
        'name-label': 'Workspace-Name',
        'is-test-label': 'Testumgebung (nicht produktiv)',
        'is-test-confirm-title': 'Als Testumgebung markieren?',
        'is-test-confirm-body':
          'Alle Mitglieder dieser Workspace sehen ab sofort den Hinweis „Testumgebung" in der App, und die Workspace wird in den Listen ausgeblendet, solange „Testumgebungen" nicht aktiviert ist.',
        'is-test-remove-confirm-title': 'Markierung als Testumgebung entfernen?',
        'is-test-remove-confirm-body':
          'Der Hinweis „Testumgebung" verschwindet für alle Mitglieder dieser Workspace, und die Workspace erscheint wieder in allen Listen.',
        'is-test-confirm-cta': 'Bestätigen',
        'submit-cta': 'Speichern',
        'submit-loading': 'Wird gespeichert…',
        'back-cta': 'Abbrechen',
        forbidden: 'Ihnen fehlt die Berechtigung, diese Workspace zu ändern.',
        'generic-error': 'Workspace konnte nicht aktualisiert werden. Bitte versuche es erneut.',
        errors: {
          name: {
            required: 'Bitte gib einen Namen ein.',
            'too-short': 'Der Workspace-Name muss mindestens 2 Zeichen lang sein.',
            'too-long': 'Der Workspace-Name darf höchstens 80 Zeichen lang sein.',
          },
        },
      },
      'buyer-contract': {
        heading: 'Maklervertrag Käufer',
        active: 'Aktiv: {name} · Version {version}',
        'active-own': 'Aktiv: {name} · Version {version} · gespeichert am {date} {time} von {member}',
        'inactive-own': 'Eigene Vorlage (inaktiv): {name} · Version {version}',
        'load-error': 'Die Käufer-Vorlage konnte nicht geladen werden.',
      },
      'email-templates': {
        title: 'E-Mail-Vorlage',
        heading: 'E-Mail-Vorlagen',
        empty: 'Dieser Workspace hat keine E-Mail-Vorlagen.',
        failed: 'Die E-Mail-Vorlagen konnten nicht geladen werden.',
        columns: {
          name: 'Name',
          area: 'Bereich',
          subject: 'Betreff',
          'updated-at': 'Aktualisiert am',
          'updated-by': 'Aktualisiert von',
        },
        'updated-at-value': '{date} {time}',
        immoos: 'immoOS',
        'subject-label': 'Betreff',
      },
    },
    members: {
      title: 'Mitglieder',
      empty: 'Noch keine Mitglieder.',
      'empty-filtered': 'Kein Mitglied entspricht den aktuellen Filtern.',
      columns: {
        name: 'Name',
        email: 'E-Mail',
        workspace: 'Workspace',
        status: 'Status',
        onboarding: 'Onboarding',
        role: 'Rolle',
        'primary-organization': 'Standort',
        'last-active': 'Zuletzt aktiv',
        'created-at': 'Erstellt am',
      },
      'last-active-never': 'Nie',
      'sort-label': 'Nach {column} sortieren',
      'status-filter': {
        label: 'Status',
        all: 'Alle',
        active: 'Aktive',
        deactivated: 'Deaktivierte',
      },
      onboarding: {
        complete: 'Vollständig',
        open: '{count} offen',
      },
      'onboarding-filter': {
        label: 'Onboarding',
        all: 'Alle',
        complete: 'Vollständig',
        incomplete: 'Unvollständig',
      },
      roles: {
        'workspace-owner': 'Eigentümer',
        'workspace-admin': 'Admin',
        billing: 'Abrechnung',
        'organisation-owner': 'Inhaber',
        agent: 'Makler',
        assistant: 'Assistenz',
        viewer: 'Betrachter',
      },
      detail: {
        title: 'Mitglied',
        'id-label': 'Mitglieds-ID',
        'user-id-label': 'IdP-Benutzer-ID',
        'last-active': 'Zuletzt aktiv am {date}',
        'last-active-never': 'Zuletzt aktiv: Nie',
        empty: '—',
        'roles-section': {
          title: 'Rollen & Standorte',
          'workspace-roles-label': 'Workspace-Rollen',
          primary: 'Primär',
          empty: 'Keine Rollen zugewiesen.',
          'edit-cta': 'Rollen & Standorte bearbeiten',
        },
        'profile-section': {
          title: 'Profil',
          'position-label': 'Position',
          'phone-label': 'Telefon',
          'mobile-label': 'Mobil',
        },
        'technical-section': {
          title: 'Technische Angaben',
        },
        activity: {
          title: 'Aktivität',
          'active-days': 'In den letzten 30 Tagen an {n, plural, one {# Tag} other {# Tagen}} aktiv',
          filter: {
            label: 'Ereignisse filtern',
            ALL: 'Alle',
            SIGN_INS: 'Anmeldungen',
            LEADS_AND_DEALS: 'Leads & Deals',
            APPOINTMENTS: 'Termine',
            NOTES_AND_TASKS: 'Notizen & Aufgaben',
            ADMIN_ACTIONS: 'Admin-Eingriffe',
          },
          kind: {
            SIGN_IN: 'Anmeldung',
            SIGN_IN_FAILED: 'Fehlgeschlagene Anmeldung',
            LEAD_STATUS_CHANGED: 'Lead-Status geändert',
            DEAL_PHASE_CHANGED: 'Deal-Phase geändert',
            APPOINTMENT_CREATED: 'Termin angelegt',
            NOTE_CREATED: 'Notiz angelegt',
            TASK_CREATED: 'Aufgabe angelegt',
            CALL_LOGGED: 'Anruf protokolliert',
            ACTED_ON_BEHALF: 'Im Auftrag gehandelt',
            EDITED_BY_OPERATOR: 'Mitglied bearbeitet',
          },
          description: {
            transition: '{record} · {from} → {to}',
            'transition-unlabelled': '{from} → {to}',
            status: '{record} · {to}',
            'status-unlabelled': '{to}',
            appointment: '{title} · {when}',
            note: 'Zu {record}',
            'call-with': 'Mit {record}',
            'call-about': 'Zu {record}',
            'acted-on-behalf': 'Von {operator} · Grund: {reason}',
            'acted-on-behalf-no-reason': 'Von {operator}',
            'edited-by-operator': 'Von {operator} · {area}',
          },
          'on-behalf': 'im Auftrag von {operator}',
          'unknown-operator': 'Unbekannter Operator',
          'edit-area': {
            DETAILS: 'Name und E-Mail',
            PROFILE: 'Profil',
            ACCESS: 'Rollen & Standorte',
          },
          'lead-status': {
            NEW: 'Neu',
            CONTACTED: 'Kontaktiert',
            CONVERTED: 'Konvertiert',
            LOST: 'Verloren',
            ARCHIVED: 'Archiviert',
          },
          'deal-phase': { ...DEAL_PHASE_LABELS },
          empty: 'Keine Aktivität in den letzten 90 Tagen.',
          'empty-filtered': 'Keine Ereignisse dieser Art in den letzten 90 Tagen.',
          loading: 'Aktivität wird geladen…',
          error: 'Die Aktivität konnte nicht geladen werden.',
          retry: 'Erneut versuchen',
        },
        onboarding: {
          title: 'Onboarding',
          collapsed: {
            open: 'Offen: {items}',
            'expired-suffix': '{item} (abgelaufen)',
            invitation: {
              pending: 'Erste Anmeldung (Einladung ausstehend)',
              expired: 'Erste Anmeldung (Einladung abgelaufen)',
              revoked: 'Erste Anmeldung (Einladung widerrufen)',
            },
            lapsing: 'Läuft bald ab: {items}',
            'lapsing-item': '{item} ({date})',
          },
          progress: '{done} von {total} Pflichtpunkten erledigt',
          complete: 'Onboarding vollständig',
          loading: 'Onboarding-Status wird geladen …',
          error: 'Der Onboarding-Status konnte nicht geladen werden.',
          retry: 'Erneut versuchen',
          items: {
            FIRST_SIGN_IN: 'Erste Anmeldung',
            PROFILE_PHOTO: 'Profilbild',
            MOBILE: 'Mobilnummer',
            POSITION: 'Position',
            IMAGE_RIGHTS_CONSENT: 'Bildrechte-Einwilligung',
            MAILBOX: 'Postfach verbunden',
            CALENDAR: 'Kalender verbunden',
            WHATSAPP: 'WhatsApp verknüpft',
            ORGANIZATION_ROLE: 'Standort und Rolle',
            RECEIVES_LEADS: 'Empfängt Leads',
          },
          states: {
            MISSING: 'Fehlt',
            OPTIONAL: 'Optional',
            EXPIRED: 'Abgelaufen seit {date}',
            EXPIRING: 'Läuft am {date} ab',
            COMPANY_MAILBOX_ONLY: 'Nur Firmenpostfach',
            INVITATION_PENDING: 'Einladung ausstehend seit {date}',
            INVITATION_EXPIRED: 'Einladung abgelaufen am {date}',
            INVITATION_REVOKED: 'Einladung widerrufen am {date}',
            NOT_APPLICABLE: 'Entfällt (IMAP)',
          },
          details: {
            'signed-in-at': 'Am {date}',
            'photo-present': 'Vorhanden',
            'consent-granted-at': 'Erteilt am {date}',
            'mailbox-synced': '{provider} · zuletzt synchronisiert am {dateTime}',
            'whatsapp-since': 'Seit {date}',
          },
          providers: {
            GOOGLE: 'Google',
            MICROSOFT: 'Microsoft',
            IMAP: 'IMAP',
          },
          'imap-providers': {
            GMX: 'GMX',
            WEB_DE: 'WEB.DE',
            T_ONLINE: 'T-Online',
            IONOS: 'IONOS',
            STRATO: 'STRATO',
            MAILBOX_ORG: 'mailbox.org',
            POSTEO: 'Posteo',
            FREENET: 'freenet',
          },
        },
        'created-at-label': 'Erstellt am',
        'edit-cta': 'Bearbeiten',
        'back-cta': 'Zurück zur Liste',
        'impersonate-cta': 'Im Auftrag handeln',
        'technical-cta': 'Technische Angaben',
        'technical-dialog': {
          close: 'Schließen',
        },
        impersonate: {
          title: 'Impersonation starten?',
          body: 'Du arbeitest als {name} im zugehörigen Workspace, mit den Rechten dieses Mitglieds. Du wirst dafür in die Kundenanwendung weitergeleitet. Jede Aktion wird im Audit-Log mit deinem Konto und dem imitierten Mitglied erfasst.',
          confirm: 'Impersonation starten',
          cancel: 'Abbrechen',
          submitting: 'Wird gestartet…',
          'forbidden-error': 'Dir fehlt die Berechtigung, um Mitglieder zu impersonifizieren.',
          'generic-error': 'Impersonation konnte nicht gestartet werden. Bitte versuche es erneut.',
        },
        status: {
          active: 'Aktiv',
          deactivated: 'Deaktiviert',
        },
        'deactivate-cta': 'Deaktivieren',
        'reactivate-cta': 'Reaktivieren',
        deactivate: {
          title: '{name} deaktivieren?',
          body: 'Deaktivierte Mitglieder verlieren den Zugriff auf den Workspace.',
          cancel: 'Abbrechen',
          confirm: 'Deaktivieren',
        },
        'status-errors': {
          'last-owner':
            'Dieses Mitglied ist das letzte aktive Mitglied mit workspaceweiten Rechten. Vergib die Rolle Eigentümer oder Admin zuerst an ein anderes Mitglied, bevor du es deaktivierst.',
          generic: 'Aktion fehlgeschlagen — bitte erneut versuchen.',
        },
        emails: {
          title: 'E-Mails',
          empty: 'Keine E-Mails in den letzten 90 Tagen.',
          loading: 'E-Mails werden geladen…',
          error: 'Die E-Mails konnten nicht geladen werden.',
          retry: 'Erneut versuchen',
          columns: { kind: 'Art', subject: 'Betreff', 'sent-at': 'Zeitpunkt', status: 'Status' },
        },
      },
      access: {
        title: 'Rollen & Standorte bearbeiten',
        'workspace-role-label': 'Workspace-Rolle',
        'workspace-role-none': 'Keine',
        'organizations-label': 'Standorte & Rollen',
        'organizations-hint': 'Das Mitglied gehört zu jedem Standort, an dem es mindestens eine Rolle hat.',
        'main-suffix': 'Hauptstandort',
        'primary-label': 'Primärer Standort',
        'primary-none': '—',
        'organizations-load-error': 'Die Standorte des Workspace konnten nicht geladen werden.',
        'submit-cta': 'Speichern',
        'submit-loading': 'Wird gespeichert…',
        'back-cta': 'Abbrechen',
        errors: {
          'workspace-must-retain-owner':
            'Der Workspace braucht mindestens ein aktives Mitglied mit der Rolle Eigentümer oder Admin. Vergib die Rolle zuerst an ein anderes Mitglied.',
          'affiliation-required':
            'Das Mitglied braucht mindestens einen Standort. Wähle an mindestens einem Standort eine Rolle.',
          'primary-not-in-affiliation': 'Wähle einen primären Standort unter den Standorten des Mitglieds.',
          'affiliation-requires-role':
            'In einem Workspace ohne weitere Standorte gehört jedes Mitglied zum Hauptstandort. Wähle dort mindestens eine Rolle.',
          'organization-not-found':
            'Ein gewählter Standort ist nicht mehr verfügbar. Lade die Seite neu und versuche es erneut.',
          generic: 'Rollen und Standorte konnten nicht gespeichert werden. Bitte versuche es erneut.',
          forbidden: 'Du bist für diese Aktion nicht berechtigt.',
        },
      },
      edit: {
        title: 'Mitglied bearbeiten',
        'email-label': 'E-Mail',
        'first-name-label': 'Vorname',
        'last-name-label': 'Nachname',
        'position-label': 'Position',
        'phone-label': 'Telefon',
        'mobile-label': 'Mobil',
        'phone-placeholder': '+49 40 123456',
        'mobile-placeholder': '+49 151 23456789',
        'submit-cta': 'Speichern',
        'submit-loading': 'Wird gespeichert…',
        'back-cta': 'Abbrechen',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'generic-error': 'Mitglied konnte nicht aktualisiert werden. Bitte versuche es erneut.',
        photo: {
          label: 'Profilbild',
          'upload-cta': 'Foto hochladen',
          'replace-cta': 'Foto ersetzen',
          'remove-cta': 'Entfernen',
          uploading: 'Wird hochgeladen…',
          'upload-error': 'Das Foto konnte nicht hochgeladen werden. Bitte versuche es erneut.',
        },
        errors: {
          email: {
            required: 'Bitte gib eine E-Mail-Adresse ein.',
            invalid: 'Diese E-Mail-Adresse ist ungültig.',
          },
          'first-name': {
            required: 'Vorname ist erforderlich.',
            'too-long': 'Vorname ist zu lang.',
            invalid: 'Vorname ist ungültig.',
          },
          'last-name': {
            required: 'Nachname ist erforderlich.',
            'too-long': 'Nachname ist zu lang.',
            invalid: 'Nachname ist ungültig.',
          },
          position: { 'too-long': 'Position ist zu lang.' },
          phone: { invalid: 'Telefonnummer ist ungültig. Gib sie mit Ländervorwahl ein, z. B. +49 40 123456.' },
          mobile: { invalid: 'Mobilnummer ist ungültig. Gib sie mit Ländervorwahl ein, z. B. +49 151 23456789.' },
        },
      },
    },
    organizations: {
      title: 'Organisationen',
      empty: 'Noch keine Organisationen.',
      'empty-filtered': 'Keine Organisation entspricht den aktuellen Filtern.',
      columns: {
        name: 'Name',
        type: 'Typ',
        workspace: 'Workspace',
        'created-at': 'Erstellt am',
      },
      types: {
        main: 'Hauptstandort',
        subsidiary: 'Standort',
      },
      'setup-status': {
        label: 'Einrichtungsstatus',
        IN_SETUP: 'In Einrichtung',
        COMPLETED: 'Eingerichtet',
      },
      detail: {
        title: 'Organisation',
        'id-label': 'ID',
        'name-label': 'Name',
        'type-label': 'Typ',
        'workspace-label': 'Workspace',
        'created-at-label': 'Erstellt am',
        'address-heading': 'Anschrift',
        'street-label': 'Straße',
        'house-number-label': 'Hausnummer',
        'postal-code-label': 'PLZ',
        'city-label': 'Stadt',
        'country-label': 'Land',
        'coordinates-label': 'Koordinaten',
        'edit-cta': 'Bearbeiten',
        'edit-address-cta': 'Anschrift bearbeiten',
        'edit-name-cta': 'Name bearbeiten',
        'back-cta': 'Zurück zur Liste',
        'members-heading': 'Mitglieder',
        'members-empty': 'Diesem Standort sind keine Mitglieder zugeordnet.',
        'members-columns': {
          name: 'Name',
          email: 'E-Mail',
        },
      },
      records: {
        heading: 'Datensätze',
        'load-failed': 'Die Datensätze dieses Standorts konnten nicht geladen werden.',
        tabs: { deals: 'Deals', properties: 'Objekte', contacts: 'Kontakte', leads: 'Leads' },
        empty: {
          deals: 'Dieser Standort hat keine Deals.',
          properties: 'Dieser Standort hat keine Objekte.',
          contacts: 'Dieser Standort hat keine Kontakte.',
          leads: 'Dieser Standort hat keine Leads.',
        },
        'select-all': 'Alle auswählen',
        'select-row': '{label} auswählen',
        selected:
          '{count, plural, =0 {Nichts ausgewählt} one {# Datensatz ausgewählt} other {# Datensätze ausgewählt}}',
        'move-cta': 'Verschieben nach…',
        'no-targets': 'Dieser Workspace hat keinen anderen aktiven Standort.',
        unassigned: 'Nicht zugewiesen',
        columns: {
          object: 'Objekt/Adresse',
          phase: 'Phase',
          status: 'Status',
          responsible: 'Zuständig',
          'object-number': 'Objekt-Nr.',
          address: 'Adresse',
          name: 'Name',
          type: 'Typ',
          owner: 'Eigentümer',
          received: 'Eingegangen',
        },
        'contact-type': { PERSON: 'Person', COMPANY: 'Firma' },
        target: {
          title: 'Ziel-Standort wählen',
          label: 'Ziel-Standort',
          placeholder: 'Standort wählen…',
          cancel: 'Abbrechen',
          cta: 'Weiter zur Prüfung',
        },
      },
      move: {
        title: 'Datensätze verschieben',
        heading: 'Von „{source}“ nach „{target}“',
        'back-cta': 'Zurück zum Standort',
        totals:
          '{deals, plural, one {# Deal} other {# Deals}} · {properties, plural, one {# Objekt} other {# Objekte}} · {contacts, plural, one {# Kontakt} other {# Kontakte}} · {leads, plural, one {# Lead} other {# Leads}}',
        'brings-heading': 'Wird mitverschoben',
        'brings-none': 'Bringt keine weiteren Datensätze mit.',
        kind: { DEAL: 'Deal', PROPERTY: 'Objekt', CONTACT: 'Kontakt', LEAD: 'Lead' },
        relation: {
          DEAL_PROPERTY: 'Objekt von Deal {label}',
          DEAL_CONTACT: 'Kontakt von Deal {label}',
          PROPERTY_CONTACT: 'Kontakt von Objekt {label}',
          LEAD_CONTACT: 'Kontakt von Lead {label}',
          LEAD_PROPERTY: 'Objekt von Lead {label}',
        },
        shared: 'Geteilt – {source} behält Zugriff über die Verknüpfung',
        'linked-to': 'Verknüpft mit: {records}',
        'shared-note':
          'Geteilte Datensätze ziehen mit in den neuen Standort. Der bisherige Standort behält den Zugriff über ihre Verknüpfungen.',
        responsibility: {
          heading: 'Zuständigkeit',
          note: 'Zuständig bleibt, wer den Ziel-Standort sieht. Alle anderen verlieren die Zuständigkeit und damit den Zugriff über sie. Bleibt niemand, übernimmt die Standortleitung des Ziel-Standorts, ohne Standortleitung der Workspace-Owner.',
          summary:
            '{kept, plural, one {# bleibt zuständig} other {# bleiben zuständig}} · {removed, plural, one {# wird entfernt} other {# werden entfernt}} · {assigned, plural, one {# neu zuständig} other {# neu zuständig}}',
          kept: 'Zuständig bleibt: {members}',
          removed: 'Entfernt: {members}',
          assigned: {
            LOCATION_MANAGER: 'Neu zuständig (Standortleitung): {members}',
            OWNER_FALLBACK: 'Neu zuständig (Workspace-Owner): {members}',
          },
          'unknown-member': 'Ehemaliges Mitglied',
        },
        confirm: 'Verschieben bestätigen',
        confirming: 'Wird verschoben…',
        outdated: 'Die Vorschau hat sich geändert. Prüfe die neue Vorschau und bestätige erneut.',
        failed: 'Die Datensätze konnten nicht verschoben werden.',
        'plan-failed': 'Die Vorschau konnte nicht erstellt werden.',
        'nothing-selected': 'Es sind keine Datensätze ausgewählt.',
        success: {
          title: 'Datensätze verschoben',
          body: 'Die Datensätze gehören jetzt zu „{target}“.',
          'to-source': 'Zu {source}',
          'to-target': 'Zu {target}',
        },
      },
      edit: {
        title: 'Organisation bearbeiten',
        'name-label': 'Name',
        'address-search-label': 'Adresse suchen',
        'address-search-placeholder': 'Adresse eingeben…',
        'street-label': 'Straße',
        'house-number-label': 'Hausnummer',
        'postal-code-label': 'PLZ',
        'city-label': 'Stadt',
        'country-label': 'Land',
        'submit-cta': 'Speichern',
        'submit-loading': 'Wird gespeichert…',
        'back-cta': 'Abbrechen',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'generic-error': 'Organisation konnte nicht aktualisiert werden. Bitte versuche es erneut.',
        errors: {
          name: {
            required: 'Bitte gib einen Namen ein.',
            'too-long': 'Der Name darf höchstens 80 Zeichen lang sein.',
          },
          street: {
            required: 'Bitte gib eine Straße ein.',
            'too-long': 'Die Straße darf höchstens 100 Zeichen lang sein.',
          },
          'house-number': {
            required: 'Bitte gib eine Hausnummer ein.',
            'too-long': 'Die Hausnummer darf höchstens 20 Zeichen lang sein.',
          },
          'postal-code': { required: 'Bitte gib eine PLZ ein.', invalid: 'Die PLZ muss aus 5 Ziffern bestehen.' },
          city: { required: 'Bitte gib eine Stadt ein.', 'too-long': 'Die Stadt darf höchstens 80 Zeichen lang sein.' },
          country: { required: 'Bitte gib ein Land ein.', 'too-long': 'Das Land darf höchstens 80 Zeichen lang sein.' },
        },
      },
    },
    'email-log': {
      title: 'E-Mails',
      'search-placeholder': 'Nach Empfänger suchen…',
      columns: {
        'sent-at': 'Zeitpunkt',
        kind: 'Art',
        recipient: 'Empfänger',
        workspace: 'Workspace',
        subject: 'Betreff',
        status: 'Status',
      },
      'empty-filtered': 'Keine E-Mail entspricht den aktuellen Filtern.',
      empty: 'Noch keine E-Mails versendet.',
      filters: {
        workspace: 'Workspace',
        kind: 'Art',
        all: 'Alle',
        'workspaces-unavailable': 'Workspaces konnten nicht geladen werden.',
      },
      placeholder: '—',
      'load-more': 'Mehr laden',
      'load-more-loading': 'Wird geladen…',
      'sort-label': 'Nach {column} sortieren',
      kinds: {
        MEMBER_INVITE: 'Einladung ins Team',
        NEW_LEAD: 'Neuer Lead',
        LEAD_QUALIFIED: 'Lead qualifiziert',
        VALUATION_RELEASED: 'Bewertung fertig',
        VIEWING_DECLINED: 'Absage zur Besichtigung',
        ONBOARDING_INVITE: 'Einladung zum Onboarding',
        ONBOARDING_SUBMITTED: 'Onboarding eingereicht',
        ONBOARDING_CHANGES_REQUESTED: 'Onboarding: Änderungen erforderlich',
        ONBOARDING_APPROVED: 'Standort freigeschaltet',
        OPERATOR_INVITE: 'Einladung als Plattform-Admin',
        BROKERAGE_CONCLUDED: 'Maklervertrag abgeschlossen',
        BROKERAGE_WITHDRAWN: 'Maklervertrag widerrufen',
        VIEWING_INVITATION: 'Einladung zur Besichtigung',
        NEW_INQUIRY: 'Neue Anfrage',
        SIGNING_SIGNED: 'Unterschrift eingegangen',
        SIGNING_COMPLETED: 'Vollständig unterschrieben',
        SIGNING_REVOKED: 'Widerruf eingegangen',
      } satisfies EnumTranslation<EmailLogKind>,
      statuses: {
        SENT: 'Gesendet',
        DELIVERED: 'Zugestellt',
        BOUNCED: 'Abgewiesen',
        COMPLAINED: 'Als Spam gemeldet',
        FAILED: 'Fehlgeschlagen',
      } satisfies EnumTranslation<EmailLogStatus>,
    },
    // Global inbound-call log (IMM-457.2) — the cross-workspace browse over the
    // universal api_call_log store. Reuses the api-keys.requests outcome
    // vocabulary; adds credential-class and rejection-reason labels.
    'api-calls': {
      title: 'API-Aufrufe',
      subtitle:
        'Alle eingehenden Anfragen an die öffentliche API — unabhängig von der Authentifizierung. Für Wiederholungen siehe API-Key-Anfragen.',
      // IMM-494.3 retired the second store and moved replay here, so the log is
      // no longer a disposable stopgap — the old notice told operators not to
      // build processes on something a recovery path now depends on.
      'temporary-notice':
        'Dieses Protokoll ist die einzige Aufzeichnung eingehender Anfragen und trägt die Wiederholungs-Historie. Aufbewahrung: 14 Tage.',
      empty: 'Keine passenden API-Aufrufe gefunden.',
      // Neutral placeholder for an absent key, member or workspace.
      placeholder: '—',
      'load-more': 'Mehr laden',
      'load-more-loading': 'Wird geladen…',
      columns: {
        'occurred-at': 'Zeitpunkt',
        workspace: 'Workspace',
        'credential-class': 'Anmeldeart',
        origin: 'Herkunft',
        endpoint: 'Endpunkt',
        'mcp-method': 'MCP-Methode',
        status: 'Status',
        outcome: 'Ergebnis',
      },
      // The three tables below render contract enums verbatim
      // (`t(\`rejection-reasons.${call.rejectionReason}\`)`), so a missing
      // label puts the raw token on the operator's screen. `satisfies
      // EnumTranslation<E>` makes that a `tsc` failure instead (ADR 0009).
      'credential-classes': {
        'api-key': 'API-Key',
        'member-session': 'Mitglieds-Sitzung',
        impersonation: 'Impersonation',
        // The `adm_` bearer for the operator MCP tool endpoint (IMM-522.1).
        // Labelled as the backoffice half of the MCP pair below, so the two
        // sit together in the filter row.
        'backoffice-credential': 'MCP (Backoffice)',
        'operator-session': 'Operator-Sitzung',
        // An MCP-capable `immo_` key on the customer MCP endpoint (IMM-601.2).
        // Same credential kind as `API-Key`, labelled apart so MCP tool traffic
        // is distinguishable from plain HTTP API traffic at a glance.
        'workspace-mcp': 'MCP (Kunden)',
        // An OAuth access token from a verbundener Client on the customer MCP
        // endpoint (IMM-601.8.1). Its own label because there is no API key
        // behind it — the call is answerable to the member who consented, not
        // to a key they issued.
        'workspace-mcp-oauth': 'MCP (Verbundener Client)',
        'valuation-agent': 'Bewertungs-Agent',
        anonymous: 'Ohne Anmeldung',
        malformed: 'Ungültiger Header',
      } satisfies EnumTranslation<ApiCallCredentialClass>,
      outcomes: {
        success: 'Erfolg',
        denied: 'Abgelehnt',
        'rate-limited': 'Rate-Limit',
        'client-error': 'Client-Fehler',
        'server-error': 'Server-Fehler',
        'auth-failed': 'Authentifizierung fehlgeschlagen',
      } satisfies EnumTranslation<ApiKeyRequestOutcomeDto>,
      'rejection-reasons': {
        'credential-absent': 'Keine Anmeldedaten gesendet',
        'credential-malformed': 'Anmeldedaten fehlerhaft',
        'api-key-unknown': 'API-Key unbekannt oder widerrufen',
        'api-key-member-inactive': 'Mitglied des API-Keys ist deaktiviert',
        'service-key-admin-inactive': 'Operator des Service-Keys ist deaktiviert',
        'session-expired': 'Sitzung abgelaufen',
        'session-invalid': 'Sitzung ungültig',
        'impersonation-invalid': 'Impersonation-Sitzung ungültig',
        'workspace-mismatch': 'API-Key gehört zu einem anderen Workspace',
        'backoffice-credential-invalid': 'Backoffice-Zugangsdaten ungültig oder widerrufen',
        'backoffice-credential-wrong-route': 'Backoffice-Zugangsdaten auf falscher Route verwendet',
        // The backoffice twin of the containment reason below (IMM-522.4) —
        // same flat 401 on the wire, same "this was a deliberate platform
        // action, not a wave of bad credentials" reading in the log.
        'backoffice-surface-disabled': 'MCP-Zugang (Backoffice) ist plattformweit deaktiviert',
        // The containment reason (IMM-601.6). The wire response is the same flat
        // 401 a bad key gets, so this label is the ONLY place an operator sees
        // that the call was refused by a deliberate platform action.
        'workspace-mcp-surface-disabled': 'MCP-Zugang (Kunden) ist plattformweit deaktiviert',
        'tool-not-exposed': 'Tool plattformweit nicht freigegeben',
        // One label for every OAuth refusal (IMM-601.8.1) — Signatur, Ablauf,
        // fremder Issuer, falsche Audience, widerrufene Freigabe. Deliberately
        // undifferentiated: the surface must not be probeable, and the wire
        // response is the same flat 401 an unknown key gets.
        'oauth-token-invalid': 'OAuth-Token ungültig oder Freigabe widerrufen',
        // Die Übergabe-Endpunkte (IMM-751.2.2) nehmen ausschließlich einen
        // Workspace-Key; eine Browser-Session oder ein `svc_`-Key landet hier.
        'workspace-key-required': 'Endpunkt erfordert einen Workspace-API-Key',
        // Der Key ist gültig, die Konfiguration unvollständig: seine ID wurde
        // noch keiner freigegebenen Anwendung zugeordnet. Das ist die einzige
        // Stelle, an der eine halb eingerichtete Integration sichtbar wird.
        'app-handoff-key-unbound': 'API-Key ist keiner freigegebenen Anwendung zugeordnet',
        'valuation-agent-token-invalid': 'Bewertungs-Agent: Token ungültig',
      } satisfies EnumTranslation<ApiCallRejectionReason>,
      filters: {
        'workspace-id': 'Workspace-ID',
        'workspace-placeholder': 'Nach Workspace filtern…',
        // The per-key narrowing an incident needs: MCP traffic is excluded from
        // the API-Key-Anfragen page (which pins `credentialClass: 'api-key'`),
        // so this is the only place MCP calls can be pinned to one key.
        'api-key-id': 'API-Key-ID',
        'api-key-placeholder': 'Nach API-Key filtern…',
        'credential-class': 'Anmeldeart',
        method: 'Methode',
        'endpoint-placeholder': 'Nach Endpunkt filtern…',
        from: 'Von',
        to: 'Bis',
      },
      detail: {
        title: 'API-Aufruf',
        'id-label': 'ID',
        'request-id-label': 'Anfrage-ID',
        'occurred-at-label': 'Zeitpunkt',
        'credential-class-label': 'Anmeldeart',
        'rejection-reason-label': 'Ablehnungsgrund',
        'method-label': 'Methode',
        'endpoint-label': 'Endpunkt',
        'path-params-label': 'Pfad-Parameter',
        'mcp-method-label': 'MCP-Methode',
        'tool-name-label': 'MCP-Tool',
        'status-label': 'Statuscode',
        'outcome-label': 'Ergebnis',
        'duration-label': 'Dauer',
        'duration-value': '{ms} ms',
        'permission-label': 'Berechtigung',
        'origin-heading': 'Herkunft',
        'workspace-label': 'Workspace',
        'api-key-label': 'API-Key',
        'member-label': 'Mitglied',
        'operating-admin-label': 'Handelnder Operator',
        'client-ip-label': 'Client-IP',
        'user-agent-label': 'User-Agent',
        'request-body-heading': 'Anfrage-Body',
        'response-body-heading': 'Antwort-Body',
        'no-body': 'Kein Body vorhanden.',
        'suppressed-auth-route':
          'Inhalte werden für Anmelde-Endpunkte nicht gespeichert, da sie Zugangsdaten enthalten.',
        'suppressed-unmatched-anonymous':
          'Inhalte werden für nicht zugeordnete Anfragen ohne Anmeldung nicht gespeichert.',
        'stored-request-only':
          'Der Antwort-Body wird für diese Aufrufe nicht gespeichert; die Anfrage bleibt erhalten.',
      },
    },
    'api-key-requests': {
      title: 'API-Key-Anfragen',
      subtitle: 'Alle über einen API-Key authentifizierten Anfragen — workspace-übergreifend, mit Wiederholung.',
      empty: 'Keine passenden Anfragen gefunden.',
      placeholder: '—',
      'load-more': 'Mehr laden',
      'load-more-loading': 'Wird geladen…',
      columns: {
        'occurred-at': 'Zeitpunkt',
        workspace: 'Workspace',
        'api-key': 'API-Key',
        endpoint: 'Endpunkt',
        status: 'Status',
        outcome: 'Ergebnis',
        replay: 'Wiederholung',
      },
      outcomes: {
        success: 'Erfolg',
        denied: 'Abgelehnt',
        'rate-limited': 'Rate-Limit',
        'client-error': 'Client-Fehler',
        'server-error': 'Server-Fehler',
        'auth-failed': 'Authentifizierung fehlgeschlagen',
      },
      filters: {
        workspace: 'Workspace',
        'workspace-placeholder': 'Nach Workspace filtern…',
        // The NULL branch: requests whose workspace or key never resolved — the
        // auth-failed traffic that no id filter can reach.
        'workspace-unresolved': 'Ohne Workspace',
        'api-key': 'API-Key',
        'api-key-placeholder': 'Nach API-Key filtern…',
        'api-key-unresolved': 'Ohne API-Key',
        method: 'Methode',
        'endpoint-placeholder': 'Nach Endpunkt filtern…',
        from: 'Von',
        to: 'Bis',
      },
      detail: {
        title: 'API-Anfrage',
      },
    },
    // MCP-Zugang (IMM-522.1) — the operator mints, lists and revokes the
    // read-only cross-tenant credential an MCP client (e.g. Claude Code) uses
    // to read the platform. Copy derived from the parent PRD's `## Copy` section.
    'valuation-agent': {
      title: 'Bewertungs-Agent',
      empty: 'Kein Token ausgestellt',
      issue: 'Token ausstellen',
      replace: 'Token ersetzen',
      revoke: 'Token entziehen',
      status: { active: 'Aktiv', replaced: 'Ersetzt (noch gültig)' },
      'valid-until': 'Alter Token gültig bis {validUntil}',
      'issued-at-by': 'Ausgestellt am {date} von {name}',
      'issued-at': 'Ausgestellt am {date}',
      'permissions-label': 'Berechtigungen',
      'last-used': 'Zuletzt genutzt: {dateTime}',
      'once-hint': 'Der Token wird nur jetzt angezeigt. Bewahre ihn sicher auf.',
      copy: 'Kopieren',
      'revoke-dialog': {
        title: 'Token entziehen?',
        body: 'Der Bewertungs-Agent kann danach sofort nichts mehr lesen oder liefern.',
        confirm: 'Entziehen',
      },
      'no-permission-chosen': 'Wähle mindestens eine Berechtigung.',
      'load-error': 'Der Token konnte nicht geladen werden.',
      'issue-error': 'Der Token konnte nicht ausgestellt werden — bitte erneut versuchen.',
      'replace-error': 'Der Token konnte nicht ersetzt werden — bitte erneut versuchen.',
      permission: {
        'catalog-read': 'Katalog lesen',
        'property-read': 'Objektdaten lesen',
        'request-receive': 'Aufträge empfangen',
        'result-deliver': 'Ergebnisse liefern',
        'finding-write': 'Befunde schreiben',
        'capture-photo-read': 'Aufnahmefotos lesen',
      },
    },
    'mcp-access': {
      title: 'MCP-Zugang',
      description:
        'Erstelle einen Lesezugang, mit dem ein MCP-Client (z. B. Claude Code) die Plattformdaten aller Workspaces auswerten darf — für Support- und Fehleranalysen. Der Zugang kann ausschließlich lesen.',
      'load-error': 'MCP-Zugänge konnten nicht geladen werden.',
      never: 'Nie',
      empty: {
        heading: 'Noch kein MCP-Zugang',
        description:
          'Erstelle deinen ersten Zugang, um über einen MCP-Client Fragen zu Leads, Rechten und Ereignissen zu stellen.',
      },
      columns: {
        name: 'Name',
        status: 'Status',
        'expires-at': 'Läuft ab',
        'created-at': 'Erstellt',
        'last-used': 'Zuletzt verwendet',
        actions: 'Aktionen',
      },
      status: {
        active: 'Aktiv',
        revoked: 'Widerrufen',
        expired: 'Abgelaufen',
      },
      issue: {
        cta: 'Zugang erstellen',
        title: 'MCP-Zugang erstellen',
        'name-label': 'Name',
        'name-placeholder': 'Claude Code — Support-Laptop',
        'validity-label': 'Gültigkeit',
        'validity-hint': 'Jeder Zugang läuft automatisch ab. Wähle den kürzesten Zeitraum, der für dich reicht.',
        'validity-option': '{days} Tage',
        'grants-label': 'Zusätzliche Berechtigungen',
        grant: {
          'skill-read': {
            label: 'Dokumentanalyse-Skills lesen',
            hint: 'Der Client kann die Analyse-Prompts aller Dokumenttypen, den Fallback-Skill und den Merkmalkatalog lesen.',
          },
          'skill-manage': {
            label: 'Dokumentanalyse-Skills bearbeiten',
            hint: 'Der Client kann Analyse-Prompts ändern, aktivieren und deaktivieren. Jede Änderung gilt sofort für alle künftigen Dokumentanalysen in allen Workspaces und wird dir zugeordnet.',
          },
          'memory-manage': {
            label: 'Objekt-Memory bearbeiten',
            hint: 'Der Client kann Aussagen in der Memory eines Objekts korrigieren, bearbeiten, hinzufügen und löschen – in allen Workspaces. Jede Änderung fließt sofort ins Dossier und wird dir zugeordnet.',
          },
          'document-update': {
            label: 'Unterlagen umbenennen und umsortieren',
            hint: 'Der Client kann jede Unterlage eines Objekts umbenennen und einer anderen Kategorie zuordnen – in allen Workspaces. Jede Änderung wird dir zugeordnet und im Audit-Log festgehalten.',
          },
        },
        'submit-cta': 'Zugang erstellen',
        'submit-loading': 'Wird erstellt…',
        'cancel-cta': 'Abbrechen',
        'done-cta': 'Fertig',
        'secret-heading': 'Dein neuer MCP-Zugang',
        'secret-warning':
          'Kopiere den Befehl jetzt — der Zugang wird nur einmal angezeigt und kann später nicht erneut abgerufen werden.',
        'command-label': 'Befehl für Claude Code',
        'copy-cta': 'Kopieren',
        'copied-confirmation': 'Befehl in die Zwischenablage kopiert.',
        footnote: 'Der Zugang darf ausschließlich lesen. Jede Anfrage wird protokolliert und dir zugeordnet.',
        'footnote-write':
          'Der Zugang darf lesen und die angehakten Änderungen vornehmen. Jede Anfrage wird protokolliert und dir zugeordnet.',
        errors: {
          'name-required': 'Name ist erforderlich.',
          'name-too-long': 'Name ist zu lang.',
          'name-already-used': 'Du hast bereits einen Zugang mit diesem Namen.',
          'validity-required': 'Wähle eine Gültigkeit.',
          'limit-reached':
            'Du hast das Maximum von {max} aktiven Zugängen erreicht. Widerrufe einen bestehenden Zugang, um einen neuen zu erstellen.',
          generic: 'Zugang konnte nicht erstellt werden — bitte erneut versuchen.',
        },
      },
      incident: {
        heading: 'Alle Zugänge',
        description: 'Zugänge aller Operatoren — für den Fall, dass ein Zugang kompromittiert ist.',
        'issued-by': 'Erstellt von',
        'revoke-confirm-body':
          'Der Zugang „{name}" von {operator} wird sofort dauerhaft deaktiviert. Dies kann nicht rückgängig gemacht werden.',
        'disable-cta': 'MCP-Zugriff deaktivieren',
        'disable-confirm-title': 'MCP-Zugriff für die gesamte Plattform deaktivieren?',
        'disable-confirm-body':
          'Alle Zugänge werden sofort abgelehnt, bleiben aber bestehen. Du kannst den Zugriff jederzeit wieder aktivieren.',
        'disable-confirm-cta': 'Deaktivieren',
        'disable-success': 'MCP-Zugriff wurde deaktiviert.',
        'enable-cta': 'MCP-Zugriff aktivieren',
        'enable-success': 'MCP-Zugriff wurde aktiviert.',
        'disabled-banner':
          'Der MCP-Zugriff ist derzeit für die gesamte Plattform deaktiviert. Bestehende Zugänge werden abgelehnt, bis er wieder aktiviert wird.',
        'switch-error': 'Der Schalter konnte nicht geändert werden — bitte erneut versuchen.',
      },
      // Operator containment for the CUSTOMER MCP surface (IMM-601.6). A
      // SEPARATE block from `incident` above, which governs the operator's own
      // backoffice accesses — the two switches must never read alike.
      // Copy pinned verbatim in the IMM-601 PRD's `## Copy` section.
      'customer-surface': {
        heading: 'MCP-Zugang (Kunden)',
        'state-enabled': 'Aktiv — Kunden-Clients können sich verbinden.',
        'state-disabled': 'Deaktiviert — alle Kunden-Schlüssel werden abgelehnt.',
        'disable-cta': 'MCP-Zugang deaktivieren',
        'enable-cta': 'MCP-Zugang aktivieren',
        'disable-confirm-title': 'MCP-Zugang deaktivieren?',
        'disable-confirm-body':
          'Alle verbundenen Kunden-Clients werden sofort abgelehnt. Die Schlüssel selbst bleiben unverändert und funktionieren wieder, sobald du den Zugang reaktivierst.',
        'disabled-banner': 'Deaktiviert — alle Kunden-Schlüssel werden abgelehnt.',
        'switch-error': 'Der Schalter konnte nicht geändert werden — bitte erneut versuchen.',
      },
      // Per-tool exposure on the customer MCP surface (IMM-625.2). Distinct
      // from `customer-surface` above: that is the platform-wide kill switch,
      // this is one tool at a time. The withdrawal confirmation and the save
      // error are pinned verbatim in the slice's Detailed Acceptance Criteria.
      tools: {
        heading: 'Freigegebene Werkzeuge',
        description:
          'Jedes Werkzeug im Katalog mit seiner Freigabe. Änderungen greifen ab der nächsten Anfrage eines verbundenen Clients.',
        // One em dash for every „nothing to state here" cell: a read tool has
        // no side-effect class, and a tool nobody has ruled on has no author.
        none: '—',
        // Feldbeschriftungen der aufgeklappten Zeile. Name, Kennung und
        // Freigabe stehen unbeschriftet in der zugeklappten Zeile — das ist
        // genau die Auswahl, die die Liste lesbar hält.
        details: {
          description: 'Beschreibung',
          kind: 'Art',
          'side-effect': 'Nebenwirkung',
          permission: 'Berechtigung',
          'changed-by': 'Geändert von',
          'changed-at': 'Geändert am',
        },
        kind: {
          query: 'Liest',
          command: 'Schreibt',
        },
        'side-effect': {
          auto_with_audit: 'Automatisch mit Protokoll',
          approval_required: 'Freigabe erforderlich',
        },
        exposure: {
          exposed: 'Freigegeben',
          withdrawn: 'Nicht freigegeben',
          'switch-label': 'Freigabe für {name}',
        },
        'expose-cta': 'Freigeben',
        // Der Freigeben-Zweig der Auswahlleiste (IMM-832.2). Sagt den Zustand
        // statt einer Aktion, weil es in diesem Fall keine Aktion zu benennen
        // gibt: jede ausgewählte Zeile ist bereits freigegeben, der Knopf ist
        // deaktiviert, und ein leiserer Knopf gleichen Namens wäre eine
        // Unterscheidung im Stylesheet und keine, die jemand liest (BL-064).
        'already-exposed-cta': 'Bereits freigegeben',
        // Die zweite Zeilen-Entscheidung (IMM-774.1). „Freigeben" und
        // „Freigabe entziehen" bilden zusammen nur einen Umschalter, und ein nie
        // geprüftes Werkzeug ist per Konstruktion nicht freigegeben — ohne
        // diesen Knopf gäbe es keine Zeilen-Aktion, die „geprüft und bewusst
        // zugelassen? nein" aufschreibt. Benennt eine festgehaltene Prüfung,
        // KEINEN Entzug: es wird nichts weggenommen.
        'keep-closed-cta': 'Geprüft — nicht freigeben',
        'withdraw-cta': 'Freigabe entziehen',
        'withdraw-confirm-title': 'Freigabe entziehen?',
        // Benennt sein Subjekt (IMM-832.2). „dieses Werkzeug" stand über einer
        // Liste von ~36 fast gleichlautenden Kennungen; der Dialog ist die
        // letzte Stelle, an der eine danebengegriffene Zeile auffällt.
        'withdraw-confirm-body':
          'Alle Workspaces verlieren damit den Zugriff auf „{name}". Verbundene Clients sehen es ab der nächsten Anfrage nicht mehr.',
        'cancel-cta': 'Abbrechen',
        'save-error': 'Die Freigabe konnte nicht geändert werden — bitte erneut versuchen.',
        // The filter is server-driven (IMM-625.3): „Nur ungeprüfte" carries an
        // order — newest arrival first — that only the server can answer.
        filters: {
          label: 'Filter',
          all: 'Alle',
          exposed: 'Nur freigegebene',
          unreviewed: 'Nur ungeprüfte',
          writes: 'Nur schreibende',
          stale: 'Verwaiste Einträge',
          search: 'Werkzeug suchen',
          'search-placeholder': 'Name oder Kennung…',
        },
        // What the list currently holds. The catalogue is expected to grow
        // well past a screenful, so the tally answers „wie viel sehe ich hier
        // gerade, und wie viel davon ist live?" without counting rows.
        count: '{count, plural, one {# Werkzeug} other {# Werkzeuge}} · davon {exposed} freigegeben',
        'count-filtered': '{visible} von {total} Werkzeugen · davon {exposed} freigegeben',
        // Modul-Facette und Blättern (IMM-769.7). Der Katalog wächst über eine
        // Seite hinaus, also narrows der Server — die Zahlen kommen mit der
        // Antwort, damit „wie viel steht in diesem Modul?" ohne Klick lesbar ist.
        module: {
          label: 'Modul',
          all: 'Alle Module',
        },
        page: {
          range: '{from}–{to} von {total}',
          previous: 'Zurück',
          next: 'Weiter',
        },
        // Stapel-Freigabe (IMM-769.7). Bei dreihundert Werkzeugen ist eine
        // Entscheidung pro Anfrage der Engpass, an dem die Triage hängen bleibt.
        selection: {
          label: 'Auswahl',
          count: '{count, plural, one {# ausgewählt} other {# ausgewählt}}',
          clear: 'Auswahl aufheben',
          'select-page': 'Seite auswählen',
        },
        bulk: {
          'confirm-expose-title': '{count, plural, one {# Werkzeug freigeben?} other {# Werkzeuge freigeben?}}',
          'confirm-withdraw-title':
            '{count, plural, one {Freigabe für # Werkzeug entziehen?} other {Freigabe für # Werkzeuge entziehen?}}',
          // Ist in der Auswahl ohnehin nichts freigegeben, wird auch nichts
          // entzogen (IMM-774.1) — der Stapel hält dann genau dieselbe
          // Entscheidung fest wie „Geprüft — nicht freigeben" in der Zeile.
          'confirm-keep-closed-title':
            '{count, plural, one {# Werkzeug als geprüft markieren?} other {# Werkzeuge als geprüft markieren?}}',
          // Beide Richtungen fragen nach, anders als bei der Einzelzeile: das
          // Risiko im Stapel ist die ANZAHL, nicht die Richtung.
          'confirm-body':
            'Die Änderung greift ab der nächsten Anfrage eines verbundenen Clients. Sie wird als eine Aktion gespeichert — ganz oder gar nicht.',
          // Ist in der Auswahl nichts freigegeben, greift auch nichts (IMM-774.1):
          // „ab der nächsten Anfrage" würde eine Wirkung behaupten, die es hier
          // nicht gibt. Die Atomarität bleibt die Aussage, die zählt.
          'confirm-keep-closed-body':
            'Für verbundene Clients ändert sich nichts — keines dieser Werkzeuge ist freigegeben. Festgehalten wird nur die Prüfung, als eine Aktion — ganz oder gar nicht.',
          error: 'Die Auswahl konnte nicht geändert werden. Es wurde nichts gespeichert.',
        },
        // Shown against a tool that has entered the catalogue without anyone
        // ruling on it — cleared by any deliberate decision, a withdrawal
        // included.
        unreviewed: 'Neu — noch nicht geprüft',
        stale: {
          notice: 'Dieses Werkzeug gibt es nicht mehr.',
          'remove-cta': 'Eintrag entfernen',
          'confirm-title': 'Eintrag entfernen?',
          'confirm-body':
            'Der Eintrag verweist auf ein Werkzeug, das der Katalog nicht mehr kennt. Das Entfernen ändert für keinen Client etwas — das Werkzeug ist ohnehin nicht mehr erreichbar.',
          'removed-confirmation': 'Eintrag entfernt.',
          'remove-error': 'Der Eintrag konnte nicht entfernt werden — bitte erneut versuchen.',
        },
        empty: {
          heading: 'Keine Werkzeuge im Katalog',
          description: 'Sobald der Katalog ein Werkzeug enthält, erscheint es hier.',
        },
        'empty-filtered': {
          heading: 'Keine Werkzeuge gefunden',
          description: 'Kein Werkzeug entspricht den gewählten Filtern. Setze die Filter zurück, um alle zu sehen.',
        },
        'empty-unreviewed': {
          heading: 'Alle Werkzeuge sind geprüft.',
          description:
            'Kein Werkzeug wartet auf eine Entscheidung. Neue Werkzeuge erscheinen hier, sobald sie ankommen.',
        },
        'empty-stale': {
          heading: 'Keine verwaisten Einträge',
          description: 'Jeder Eintrag verweist auf ein Werkzeug, das der Katalog noch kennt.',
        },
        'kill-switch-notice':
          'Die MCP-Schnittstelle ist derzeit vollständig deaktiviert. Änderungen an Freigaben werden gespeichert und greifen, sobald die Schnittstelle wieder aktiv ist.',
        'load-error':
          'Die Werkzeugliste konnte nicht geladen werden. Lade die Seite neu — dies liegt nicht an fehlenden Berechtigungen.',
        // Erreichbarkeitsdiagnose (IMM-625.4): warum ein bestimmter Zugang ein
        // bestimmtes Werkzeug nicht erreicht. Läuft auf `mcp-surface-read` —
        // prüfen ist nicht ändern — und sitzt deshalb außerhalb der
        // Manage-Schranke der Zeilenaktionen. Die Diagnose öffnet sich
        // zusammen mit den Details der Zeile — sie hat keinen eigenen Schalter.
        reachability: {
          // The panel's own affordance inside an expanded row. It is not
          // mounted with the details, because it asks for the API-key list of
          // every workspace the moment it appears.
          cta: 'Erreichbarkeit prüfen',
          heading: 'Erreichbarkeit prüfen',
          'kind-label': 'Art des Zugangs',
          kind: {
            'api-key': 'API-Schlüssel',
            'oauth-grant': 'OAuth-Zugang',
          },
          'credential-label': 'Zugang prüfen',
          'credential-placeholder': 'Zugang wählen…',
          never: 'Nie',
          'all-tools': 'Alle Werkzeuge',
          'seat-label': 'Welcher Standort?',
          'seat-hint':
            'Dieser Zugang gilt für mehrere Standorte. Die Berechtigungen unterscheiden sich je Standort — wähle den, um den es geht.',
          'seat-placeholder': 'Standort wählen…',
          loading: 'Wird geprüft…',
          reachable: 'Dieses Werkzeug ist für diesen Zugang erreichbar.',
          // Nach Schichten benannt, in genau der Reihenfolge, in der die
          // Schnittstelle ablehnt — die erste zutreffende gewinnt.
          excluded: {
            SURFACE_DISABLED:
              'Die MCP-Schnittstelle ist derzeit vollständig deaktiviert — zurzeit erreicht kein Client ein Werkzeug.',
            NOT_EXPOSED: 'Dieses Werkzeug ist nicht freigegeben.',
            // Der Zugang ist gar nicht für MCP ausgestellt — er erreicht kein
            // einziges Werkzeug. Die Abhilfe liegt beim Kunden, nicht beim
            // Operator, deshalb nennt die Copy sie.
            CREDENTIAL_NOT_MCP:
              'Dieser Zugang ist nicht für MCP-Clients freigeschaltet und erreicht deshalb kein Werkzeug. Der Kunde muss einen neuen Schlüssel mit MCP-Freischaltung erstellen.',
            CREDENTIAL_GRANTS: 'Der Zugang hat die nötige Berechtigung nicht: {permission}.',
            MEMBER_PERMISSIONS:
              'Das Mitglied, an das der Zugang gebunden ist, hat die nötige Berechtigung nicht: {permission}.',
          },
          'credential-not-found': 'Diesen Zugang gibt es nicht mehr.',
          empty: {
            'api-key': 'Es gibt keine API-Schlüssel.',
            'oauth-grant': 'Es gibt keine OAuth-Zugänge.',
          },
          'load-error': 'Die Zugänge konnten nicht geladen werden — bitte erneut versuchen.',
          error: 'Die Prüfung konnte nicht durchgeführt werden — bitte erneut versuchen.',
        },
      },
      revoke: {
        cta: 'Widerrufen',
        'confirm-title': 'Zugang widerrufen?',
        'confirm-body':
          'Der Zugang „{name}“ wird sofort dauerhaft deaktiviert. Anfragen mit diesem Zugang werden danach abgelehnt. Dies kann nicht rückgängig gemacht werden.',
        'cancel-cta': 'Abbrechen',
        'confirm-cta': 'Widerrufen',
        loading: 'Wird widerrufen…',
        'success-confirmation': 'Zugang „{name}“ wurde widerrufen.',
        'generic-error': 'Zugang konnte nicht widerrufen werden — bitte erneut versuchen.',
      },
    },
    'api-keys': {
      title: 'API-Keys',
      // Admin API keys (IMM-556.3). Copy taken verbatim from the parent spec's
      // `## Copy` block — the dialog, the shown-once panel, the list scope
      // values and the revoke confirmation are specified there string-for-string.
      kind: {
        workspace: 'Workspace-Key',
        admin: 'Admin-API-Schlüssel',
      },
      scope: {
        all: 'Alle Workspaces',
        selected: '{count} Workspaces',
        // A workspace key has no scope: its reach is the one workspace it is
        // bound to, which the Workspace column already shows.
        none: '—',
      },
      'issue-service-cta': 'Admin-API-Schlüssel ausstellen',
      'issue-service': {
        title: 'Admin-API-Schlüssel ausstellen',
        'name-label': 'Name',
        'name-helper':
          'Wer diesen Key verwendet. Erscheint operator-seitig überall, wo Aktionen dieses Keys auftauchen — nicht beim Kunden.',
        'description-label': 'Beschreibung (optional)',
        'rate-limit-label': 'Rate-Limit (Anfragen pro Sekunde)',
        'rate-limit-helper':
          '1–60 Anfragen pro Sekunde. Voreingestellt 30 — ein Konsument, der die Warteschlange pollt, ruft häufiger auf als eine Kundenintegration.',
        'scope-label': 'Workspace-Geltungsbereich',
        'scope-helper': 'Pflichtangabe. „Alle Workspaces" bedeutet Zugriff auf jede Bewertungsanfrage der Plattform.',
        'scope-all': 'Alle Workspaces',
        'scope-selected': 'Ausgewählte Workspaces',
        'workspace-search-placeholder': 'Workspace suchen',
        'submit-cta': 'Key ausstellen',
        'submit-loading': 'Wird ausgestellt…',
        'cancel-cta': 'Abbrechen',
        'secret-heading': 'Dieser Key wird genau einmal angezeigt.',
        'secret-warning':
          'Kopiere ihn jetzt und hinterlege ihn beim Konsumenten. Wir können ihn später nicht erneut anzeigen — geht er verloren, stellst du einen neuen aus und widerrufst diesen.',
        'copy-cta': 'Key kopieren',
        'copied-cta': 'Kopiert',
        'secret-acknowledge-cta': 'Ich habe den Key gesichert',
        errors: {
          'name-required': 'Name ist erforderlich.',
          'name-too-long': 'Name ist zu lang.',
          'name-taken': 'Ein aktiver Admin-API-Schlüssel mit dem Namen „{name}" existiert bereits.',
          'description-too-long': 'Beschreibung ist zu lang.',
          'rate-limit-range': 'Rate Limit muss zwischen 1 und 60 liegen.',
          'scope-required': 'Bitte wähle einen Geltungsbereich.',
          'scope-empty': 'Bitte wähle mindestens einen Workspace.',
          'workspace-unknown': 'Einer der gewählten Workspaces existiert nicht mehr.',
          forbidden: 'Dir fehlt die Berechtigung, Keys auszustellen.',
          generic: 'Key konnte nicht ausgestellt werden — bitte erneut versuchen.',
        },
      },
      'revoke-service': {
        title: 'Admin-API-Schlüssel „{name}" widerrufen?',
        body: 'Der nächste Aufruf mit diesem Key wird abgelehnt. Bereits freigegebene Bewertungen bleiben bestehen und werden nicht zurückgenommen.',
        'confirm-cta': 'Widerrufen',
        'cancel-cta': 'Abbrechen',
      },
      empty: 'Keine passenden API-Keys gefunden.',
      unknown: 'Unbekannt',
      'customer-created': 'Vom Kunden erstellt',
      'customer-revoked': 'Vom Kunden widerrufen',
      // Generic stand-in when an operator id can't be resolved to a name
      // (deactivated + pruned admin, or an edge case) — never the raw uuid.
      'operator-fallback': 'immoOS-Operator',
      columns: {
        name: 'Name',
        workspace: 'Workspace',
        member: 'Mitglied',
        status: 'Status',
        'rate-limit': 'Rate-Limit',
        'last-used': 'Zuletzt genutzt',
        'issued-by': 'Ausgestellt von',
        scope: 'Geltungsbereich',
      },
      status: {
        active: 'Aktiv',
        revoked: 'Widerrufen',
        // Derived at read time from `expiresAt` (IMM-601.1) — never stored.
        expired: 'Abgelaufen',
      },
      revoke: {
        cta: 'Widerrufen',
        'confirm-title': 'API-Key widerrufen?',
        'confirm-body':
          '„{name}" wird sofort und dauerhaft ungültig. Laufende Integrationen, die diesen Key nutzen, brechen ab. Diese Aktion lässt sich nicht rückgängig machen.',
        'confirm-cta': 'Endgültig widerrufen',
        'cancel-cta': 'Abbrechen',
        loading: 'Wird widerrufen …',
        'generic-error': 'Der API-Key konnte nicht widerrufen werden. Bitte versuche es erneut.',
        'forbidden-error': 'Dir fehlt die Berechtigung, API-Keys zu widerrufen.',
      },
      filters: {
        'workspace-id': 'Workspace-ID',
        'uuid-placeholder': 'UUID einfügen',
        status: 'Status',
      },
      never: 'Nie',
      detail: {
        title: 'API-Key',
        'id-label': 'ID',
        'name-label': 'Name',
        'description-label': 'Beschreibung',
        // The kind and scope rows IMM-556.1 added to this list — and the two
        // labels it forgot to add here, so both rendered as their own raw
        // dotted key until IMM-596.
        'kind-label': 'Art',
        'workspace-label': 'Workspace',
        'member-label': 'Mitglied',
        'scope-label': 'Geltungsbereich',
        'status-label': 'Status',
        'rate-limit-label': 'Rate-Limit',
        'rate-limit-value': '{count} / Sekunde',
        // The one sentence that says everything an admin API key reaches
        // (IMM-587.7). ICU plural, not concatenation: a named scope of one must
        // not read »1 ausgewählte Workspaces«.
        'reach-all': 'Dieser Schlüssel arbeitet im Auftrag von {operatorName} und erreicht alle Workspaces.',
        'reach-selected':
          'Dieser Schlüssel arbeitet im Auftrag von {operatorName} und erreicht {count, plural, one {einen ausgewählten Workspace} other {# ausgewählte Workspaces}}.',
        'permissions-label': 'Berechtigungen',
        'issued-by-label': 'Ausgestellt von',
        'last-used-label': 'Zuletzt genutzt',
        'revoked-at-label': 'Widerrufen am',
        'revoked-by-label': 'Widerrufen von',
        'created-at-label': 'Erstellt am',
        'requests-heading': 'Anfragen',
        'requests-empty': 'Keine passenden Anfragen gefunden.',
        'load-more': 'Mehr laden',
        'load-more-loading': 'Wird geladen…',
      },
      requests: {
        columns: {
          'occurred-at': 'Zeitpunkt',
          endpoint: 'Endpunkt',
          status: 'Status',
          outcome: 'Ergebnis',
        },
        outcomes: {
          success: 'Erfolg',
          denied: 'Abgelehnt',
          'rate-limited': 'Rate-Limit',
          'client-error': 'Client-Fehler',
          'server-error': 'Server-Fehler',
          'auth-failed': 'Authentifizierung fehlgeschlagen',
        },
        filters: {
          'endpoint-placeholder': 'Nach Endpunkt filtern…',
          from: 'Von',
          to: 'Bis',
        },
      },
      replay: {
        heading: 'Wiederholung',
        mark: 'wiederholt',
        'in-flight': 'läuft…',
        'original-outcome': 'Ursprünglich: {status} · {outcome}',
        description:
          'Diese Anfrage erneut absenden — die gespeicherten Daten werden gegen den aktuellen Vertrag verarbeitet. Die Zuordnung zum Partner bleibt erhalten.',
        action: 'Anfrage wiederholen',
        submitting: 'Wird wiederholt…',
        'already-replayed': 'Am {date} wiederholt.',
        'forbidden-error': 'Ihnen fehlt die Berechtigung, Anfragen zu wiederholen.',
        'generic-error': 'Anfrage konnte nicht wiederholt werden — bitte erneut versuchen.',
        blocked: {
          NOT_A_FAILURE: 'Diese Anfrage war erfolgreich — es gibt nichts wiederherzustellen.',
          ALREADY_REPLAYED: 'Diese Anfrage wurde bereits wiederholt.',
          ENDPOINT_NOT_REPLAYABLE: 'Dieser Endpunkt kann nicht wiederholt werden.',
          NO_REQUEST_BODY: 'Zu dieser Anfrage wurden keine Daten gespeichert.',
          REQUEST_BODY_TRUNCATED: 'Die gespeicherten Daten sind gekürzt — eine Wiederholung wäre unvollständig.',
          API_KEY_REVOKED: 'Der zugehörige API-Schlüssel wurde widerrufen.',
          PERMISSION_NO_LONGER_HELD: 'Dem Schlüssel fehlt inzwischen die erforderliche Berechtigung.',
        },
      },
      'request-detail': {
        title: 'API-Anfrage',
        'id-label': 'ID',
        'request-id-label': 'Anfrage-ID',
        'occurred-at-label': 'Zeitpunkt',
        'method-label': 'Methode',
        'endpoint-label': 'Endpunkt',
        'status-label': 'Statuscode',
        'outcome-label': 'Ergebnis',
        'duration-label': 'Dauer',
        'duration-value': '{ms} ms',
        'permission-label': 'Berechtigung',
        'origin-heading': 'Herkunft',
        'workspace-label': 'Workspace',
        'api-key-label': 'API-Key',
        'member-label': 'Mitglied',
        'client-ip-label': 'Client-IP',
        'user-agent-label': 'User-Agent',
        'request-body-heading': 'Anfrage-Body',
        'response-body-heading': 'Antwort-Body',
        'no-body': 'Kein Body vorhanden.',
      },
      // In-place edit of an issued key (IMM-493.1). Api-key failures render from
      // page-scoped copy keyed on the error code, matching the issue flow —
      // none of the identity.api-key.* codes is in the ERROR_CODES seed registry.
      edit: {
        cta: 'Bearbeiten',
        title: 'API-Key bearbeiten',
        'rate-limit-label': 'Rate-Limit',
        'permissions-label': 'Berechtigungen',
        'submit-cta': 'Änderungen speichern',
        'submit-loading': 'Wird gespeichert …',
        'cancel-cta': 'Abbrechen',
        errors: {
          revoked: 'Dieser API-Key wurde widerrufen und lässt sich nicht mehr bearbeiten.',
          forbidden: 'Dir fehlt die Berechtigung, API-Keys zu bearbeiten.',
          generic: 'Die Änderungen konnten nicht gespeichert werden. Bitte versuche es erneut.',
        },
      },
      issue: {
        cta: 'Neuer API-Key',
        title: 'Neuen API-Key ausstellen',
        'workspace-label': 'Workspace',
        'workspace-search-placeholder': 'Workspace suchen …',
        'workspace-empty': 'Kein passender Workspace gefunden.',
        'owner-line': 'Handelt als: {email}',
        'owner-helper':
          'Der Key erbt die Rechte dieses Mitglieds. Verliert das Mitglied eine Rolle, verliert der Key sie ebenfalls.',
        'name-label': 'Name',
        'description-label': 'Beschreibung (optional)',
        'rate-limit-label': 'Rate-Limit',
        'rate-limit-suffix': 'Anfragen / Sekunde',
        'rate-limit-helper': 'Zwischen 1 und 60.',
        'permissions-label': 'Berechtigungen',
        'permissions-helper':
          'API-Key-Verwaltungsrechte lassen sich nicht vergeben — ein Key kann keine weiteren Keys erstellen oder widerrufen.',
        'permission-search-placeholder': 'Berechtigung suchen …',
        'permission-empty': 'Keine passende Berechtigung gefunden.',
        scope: {
          WORKSPACE: 'Workspace-Rechte',
          ORG: 'Standort-Rechte',
        },
        module: {
          identity: 'Verwaltung & Zugriff',
          relationship: 'Kontakte & Firmen',
          property: 'Objekte',
          deal: 'Deals',
          lead: 'Leads',
          valuation: 'Bewertungen',
          activity: 'Aktivitäten',
          communications: 'Kommunikation',
          media: 'Medien',
          assistant: 'Assistent',
          search: 'Suche',
        },
        'select-all': 'Alle auswählen',
        'deselect-all': 'Alle abwählen',
        'submit-cta': 'API-Key ausstellen',
        'submit-loading': 'Wird ausgestellt …',
        'cancel-cta': 'Abbrechen',
        'secret-heading': 'API-Key erstellt',
        'secret-warning':
          'Dieser Key wird nur ein einziges Mal angezeigt. Kopiere ihn jetzt und bewahre ihn sicher auf — er lässt sich später nicht erneut abrufen.',
        'copy-cta': 'Kopieren',
        'copied-cta': 'Kopiert',
        'done-cta': 'Fertig',
        errors: {
          'workspace-required': 'Bitte wähle einen Workspace aus.',
          'name-required': 'Bitte gib einen Namen an.',
          'name-too-long': 'Der Name darf höchstens 80 Zeichen lang sein.',
          'description-too-long': 'Die Beschreibung darf höchstens 500 Zeichen lang sein.',
          'rate-limit-range': 'Das Rate-Limit muss zwischen 1 und 60 liegen.',
          'permissions-required': 'Bitte wähle mindestens eine Berechtigung aus.',
          'no-eligible-owner':
            'Für „{workspace}" gibt es kein aktives Mitglied mit workspaceweiten Rechten. Ohne ein solches Mitglied lässt sich kein Key ausstellen.',
          generic: 'Der API-Key konnte nicht ausgestellt werden. Bitte versuche es erneut.',
          forbidden: 'Dir fehlt die Berechtigung, API-Keys auszustellen.',
          // Names the bound member but NOT the permission: the refusal's wire
          // message stopped disclosing which grant was refused (IMM-569.3),
          // because the message is part of the API response.
          'permission-not-held':
            '{member} besitzt eine der gewählten Berechtigungen nicht mehr. Bitte prüfe die Auswahl und sende erneut.',
        },
      },
    },
    properties: {
      title: 'Objekte',
      empty: 'Noch keine Objekte angelegt.',
      filters: { search: 'Nach Titel oder Adresse suchen…' },
      'empty-filtered': 'Kein Objekt entspricht den aktuellen Filtern.',
      columns: {
        title: 'Titel',
        address: 'Anschrift',
        type: 'Immobilientyp',
        object: 'Objekt',
        status: 'Status',
        price: 'Preis',
        area: 'Fläche',
        'broker-team': 'Makler-Team',
        'match-score': 'Match-Score',
        'last-activity': 'Letzte Aktivität',
        workspace: 'Workspace',
        organization: 'Standort',
        tenancy: 'Standort',
        'created-at': 'Erfasst am',
      },
      totals: {
        'shown-of-total': '{shown} von {total}',
        'price-sum': 'Σ {amount}',
        'area-avg': 'ø {amount}',
        'broker-count': '{count} Makler',
        empty: '—',
      },
      'marketing-type': MARKETING_TYPE_LABELS,
      status: PROPERTY_STATUS_LABELS,
      detail: {
        title: 'Objekt',
        deleted: { title: 'Objekt gelöscht', body: 'Gelöscht am {deletedAt} von {deletedBy}' },
        'id-prefix': 'ID',
        'header-price-per-sqm-suffix': ' · {amount} / m²',
        tabs: {
          overview: 'Übersicht',
          activity: 'Aktivität',
          property: 'Objekt',
          contacts: 'Kontakte',
          expose: 'Exposé',
          emails: 'E-Mails',
          calls: 'Anrufe',
          team: 'Team',
          notes: 'Notizen',
          appointments: 'Termine',
          dossier: 'Dossier',
          memory: 'Memory',
          inquiries: 'Anfragen',
        },
        dossier: {
          loading: 'Dossier wird geladen…',
          'no-facts-detected':
            'Noch keine Merkmale aus Dokumenten erkannt. Die folgenden Werte stammen aus den Objektdaten; Merkmale entstehen, sobald Dokumente analysiert wurden.',
          forbidden: 'Du hast keine Berechtigung, das Dossier dieses Objekts zu sehen.',
          error: 'Das Dossier konnte nicht geladen werden.',
          retry: 'Erneut versuchen',
          brief: {
            heading: 'Zusammenfassung',
            'created-at': 'Erstellt am {date}',
            stale: 'Die Zusammenfassung wird gerade neu erstellt und kann noch von den Merkmalen unten abweichen.',
            empty: 'Für dieses Objekt liegt noch keine Zusammenfassung vor.',
          },
          groups: {
            PROPERTY_INTRINSIC: 'Objektmerkmale',
            DEAL_CONTEXT: 'Deal-Kontext',
          },
          'confirmed-value': 'Bestätigter Wert',
          'extracted-values': 'Erkannte Werte',
          conflict: 'Widersprüchliche Angaben',
          'public-safe': 'Öffentlich sichtbar',
          columns: {
            feature: 'Merkmal',
            source: 'Quelle',
            confidence: 'Konfidenz',
            'extracted-at': 'Erfasst am',
          },
          modality: {
            DOCUMENT: 'Dokument',
            IMAGE: 'Bild',
            NONE: 'Ohne Vorlage',
          },
          'source-unavailable': 'Quelle nicht mehr verfügbar',
          'search-placeholder': 'Merkmal suchen…',
          'only-conflicts': 'Nur Widersprüche ({count})',
          'no-match': 'Keine Merkmale gefunden.',
          category: DOCUMENT_CATEGORY_LABELS,
          'enum-values': DOSSIER_FACT_ENUM_LABELS,
          'boolean-values': { true: 'Ja', false: 'Nein' },
        },
        memory: {
          operator: {
            by: 'von {name} · {date}',
            edited: 'bearbeitet von {name} · {date}',
            added: 'hinzugefügt von {name} · {date}',
            orphaned: 'Verwaist · steht nicht mehr in der Quelle',
            deleted: 'gelöscht von {name} · {date}',
            unknown: 'einem Operator',
          },
          loading: 'Memory wird geladen…',
          forbidden: 'Du hast keine Berechtigung, die Memory dieses Objekts zu sehen.',
          error: 'Die Memory konnte nicht geladen werden.',
          retry: 'Erneut versuchen',
          empty: 'Für dieses Objekt wurde noch nichts ausgelesen.',
          columns: {
            label: 'Aussage',
            value: 'Wert',
            correction: 'Korrigiert',
            source: 'Quelle',
            passage: 'Fundstelle',
            prompt: 'Gelesen mit',
            model: 'Modell',
            confidence: 'Konfidenz',
            'read-at': 'Datum',
          },
          'sort-label': 'Nach {column} sortieren',
          'source-type': { DOCUMENT: 'Dokument', EMAIL: 'E-Mail', CALL: 'Anruf', NOTE: 'Notiz', OPERATOR: 'Manuell' },
          filter: {
            label: 'Quellentyp',
            chip: 'Quellentyp: {type}',
            remove: 'Filter „Quellentyp“ entfernen',
          },
          confidence: { HIGH: 'Hoch', MEDIUM: 'Mittel', LOW: 'Niedrig' },
          page: 'S. {page}',
          viewer: {
            title: '{name}',
            download: 'Herunterladen',
            'no-preview': 'Keine Vorschau verfügbar.',
            'not-found': 'Dokument nicht gefunden.',
            loading: 'Vorschau wird geladen…',
            error: 'Die Vorschau konnte nicht geladen werden.',
          },
        },
        inquiries: {
          loading: 'Anfragen werden geladen…',
          forbidden: 'Du hast keine Berechtigung, die Anfragen dieses Objekts zu sehen.',
          error: 'Die Anfragen konnten nicht geladen werden.',
          retry: 'Erneut versuchen',
          empty: 'Zu diesem Objekt sind noch keine Anfragen eingegangen.',
          columns: {
            'received-at': 'Eingegangen',
            source: 'Quelle',
            name: 'Name',
            email: 'E-Mail',
            phone: 'Telefon',
            message: 'Nachricht',
            outcome: 'Ergebnis',
          },
          'sort-label': 'Nach {column} sortieren',
          source: PROSPECT_SOURCE_LABELS,
          outcome: INQUIRY_OUTCOME_LABELS,
        },
        contacts: {
          'section-title': 'Kontakte',
          loading: 'Kontakte werden geladen…',
          empty: 'Noch keine Kontakte verknüpft.',
          'load-error': 'Kontakte konnten nicht geladen werden.',
          'open-in-new-tab': 'In neuem Tab öffnen',
          'add-cta': 'Kontakt verknüpfen',
          'row-actions': 'Aktionen',
          'open-contact': 'Kontakt öffnen',
          'send-email': 'E-Mail senden',
          'send-message': 'Nachricht senden',
          'not-implemented': 'Diese Aktion ist noch nicht verfügbar.',
          role: PROPERTY_CONTACT_ROLE_LABELS,
          groups: {
            owner: 'Eigentümer',
            'prospects-sale': 'Kaufinteressenten',
            'prospects-rental': 'Mietinteressenten',
            others: 'Weitere Kontakte',
          },
          'owner-empty': 'Noch kein Eigentümer verknüpft.',
          'owner-empty-cta': 'Eigentümer verknüpfen',
          'edit-link': 'Verknüpfung bearbeiten',
          'unlink-link': 'Verknüpfung lösen',
          'link-dialog': {
            title: 'Kontakt verknüpfen',
            'contact-label': 'Kontakt',
            'create-contact-cta': 'Neuen Kontakt anlegen',
            'role-label': 'Rolle',
            'role-placeholder': 'Rolle wählen …',
            'note-label': 'Notiz',
            'note-hint': 'Max. 500 Zeichen',
            'submit-cta': 'Verknüpfen',
            'submit-loading': 'Wird verknüpft…',
            'cancel-cta': 'Abbrechen',
            errors: {
              'contact-required': 'Bitte wähle einen Kontakt aus.',
              'role-required': 'Bitte wähle eine Rolle aus.',
              'not-found': 'Kontakt oder Immobilie wurde nicht gefunden.',
              'already-linked':
                'Diese Verknüpfung besteht bereits. Du kannst die Notiz der bestehenden Verknüpfung bearbeiten.',
              'note-too-long': 'Notiz darf höchstens 500 Zeichen lang sein.',
              'role-marketing-mismatch': 'Diese Rolle passt nicht zur Vermarktungsart der Immobilie.',
              forbidden: 'Du bist für diese Aktion nicht berechtigt.',
              generic: 'Verknüpfung konnte nicht angelegt werden. Bitte versuche es erneut.',
            },
          },
          'edit-dialog': {
            title: 'Verknüpfung bearbeiten',
            'role-label': 'Rolle',
            'note-label': 'Notiz',
            'note-hint': 'Max. 500 Zeichen',
            'submit-cta': 'Speichern',
            'submit-loading': 'Wird gespeichert…',
            'cancel-cta': 'Abbrechen',
            errors: {
              'already-linked':
                'Diese Verknüpfung besteht bereits. Du kannst die Notiz der bestehenden Verknüpfung bearbeiten.',
              'note-too-long': 'Notiz darf höchstens 500 Zeichen lang sein.',
              'role-marketing-mismatch': 'Diese Rolle passt nicht zur Vermarktungsart der Immobilie.',
              'not-found': 'Diese Verknüpfung wurde nicht gefunden.',
              forbidden: 'Du bist für diese Aktion nicht berechtigt.',
              generic: 'Änderung konnte nicht gespeichert werden. Bitte versuche es erneut.',
            },
          },
          'unlink-dialog': {
            title: 'Verknüpfung lösen',
            body: 'Möchtest du diese Verknüpfung wirklich lösen? Der Kontakt und die Immobilie bleiben erhalten.',
            'confirm-cta': 'Verknüpfung lösen',
            'confirm-loading': 'Wird gelöst…',
            'cancel-cta': 'Abbrechen',
            'error-forbidden': 'Du bist für diese Aktion nicht berechtigt.',
            error: 'Verknüpfung konnte nicht gelöst werden. Bitte versuche es erneut.',
          },
        },
        sections: {
          'key-facts': 'Eckdaten',
          pipeline: 'Pipeline',
          activity: 'Aktivität',
          tasks: 'Aufgaben',
          details: 'Details',
          tenancy: 'Workspace & Standort',
          owner: 'Eigentümer',
          'linked-deals': 'Verknüpfte Deals',
          categories: 'Kategorien',
        },
        'owner-empty': 'Noch kein Eigentümer verknüpft.',
        cards: {
          'status-label': 'Status',
          'status-since-day': 'seit 1 Tag',
          'status-since-days': 'seit {days} Tagen',
          'asking-price-label': 'Asking-Preis',
          'asking-price-per-sqm': '€ {amount} / m²',
          'expected-revenue-label': 'Erwarteter Erlös',
          'match-score-label': 'Match-Score',
          'last-activity-label': 'Letzte Aktivität',
          'last-activity-empty': 'Noch keine Aktivität',
          'broker-team-label': 'Makler-Team',
          'member-singular': '1 Mitglied',
          'member-plural': '{count} Mitglieder',
        },
        pipeline: {
          'first-contact': 'Erstkontakt',
          viewing: 'Besichtigung',
          offer: 'Angebot',
          negotiation: 'Verhandlung',
          notary: 'Notar',
        },
        details: {
          'type-label': 'Objekt-Typ',
          'location-label': 'Lage',
          'living-area-label': 'Wohnfläche',
          'rooms-label': 'Zimmer',
          'year-built-label': 'Baujahr',
          'energy-label': 'Energie',
          'asking-label': 'Asking',
          'expected-label': 'Erwartet',
          'kind-label': 'Art',
          'workspace-label': 'Workspace',
          'scope-label': 'Geltungsbereich',
          'organization-label': 'Standort',
          'show-all-fields': 'Alle Felder ansehen',
        },
        'activity-empty': 'Hier landen alle Aktivitäten zu diesem Objekt.',
        'tab-empty': 'Hier landen alle {tab}-Einträge zu diesem Objekt.',
        'tasks-empty': 'Noch keine Aufgaben.',
        'tasks-open': '{count} offen',
        'coming-soon': 'Demnächst',
        'add-cta': 'Hinzufügen',
        empty: '—',
      },
    },
    admins: {
      title: 'Admins',
      'api-keys': {
        title: 'Admin-API-Schlüssel dieses Admins',
        empty: 'Dieser Admin hat noch keine Admin-API-Schlüssel.',
        'view-cta': 'Admin-API-Schlüssel ansehen',
      },
      detail: {
        title: 'Admin',
        'id-label': 'ID',
        'back-cta': 'Zurück zur Liste',
        'back-cta-heading': 'Navigation',
      },
      empty: 'Noch keine Admins angelegt.',
      'no-match-title': 'Keine Treffer',
      'no-match-description': 'Passe die Suche an, um Admins zu finden.',
      'search-reset-cta': 'Suche zurücksetzen',
      'search-placeholder': 'Name, E-Mail oder ID suchen…',
      'create-cta': 'Neuen Admin anlegen',
      status: {
        active: 'Aktiv',
        inactive: 'Inaktiv',
      },
      'deactivate-cta': 'Deaktivieren',
      'deactivate-confirm-title': 'Admin deaktivieren?',
      'deactivate-confirm-body':
        '{fullName} ({email}) verliert sofort den Zugriff auf die Admin-Konsole. Diese Aktion kann nur durch erneutes Hinzufügen rückgängig gemacht werden.',
      'deactivate-confirm-cta': 'Deaktivieren',
      'deactivate-keys-confirm-title': 'Operator deaktivieren?',
      'deactivate-keys-confirm-body':
        '{count, plural, one {Dieser Operator hält einen aktiven Admin-API-Schlüssel. Er hört sofort auf zu funktionieren, sobald du ihn deaktivierst. Betroffen: {names}. Stelle den Schlüssel vorher neu aus, wenn die Integration weiterlaufen soll.} other {Dieser Operator hält {count} aktive Admin-API-Schlüssel. Sie hören sofort auf zu funktionieren, sobald du ihn deaktivierst. Betroffen: {names}. Stelle die Schlüssel vorher neu aus, wenn die Integrationen weiterlaufen sollen.}}',
      'deactivate-keys-confirm-cta': 'Trotzdem deaktivieren',
      'deactivate-cancel-cta': 'Abbrechen',
      'deactivate-loading': 'Wird deaktiviert…',
      'deactivate-self-error': 'Du kannst dein eigenes Admin-Konto nicht deaktivieren.',
      'deactivate-last-error': 'Es muss immer mindestens ein aktiver Admin verbleiben.',
      'deactivate-generic-error': 'Admin konnte nicht deaktiviert werden. Bitte versuche es erneut.',
      'created-by-system': 'System',
      columns: {
        name: 'Name',
        email: 'E-Mail',
        status: 'Status',
        'created-at': 'Hinzugefügt am',
        'created-by': 'Hinzugefügt von',
        id: 'ID',
        actions: 'Aktionen',
      },
      create: {
        title: 'Neuen Admin anlegen',
        'full-name-label': 'Vollständiger Name',
        'full-name-placeholder': 'Erika Mustermann',
        'email-label': 'E-Mail',
        'email-placeholder': 'name@immoos.ai',
        'submit-cta': 'Admin einladen',
        'submit-loading': 'Wird eingeladen…',
        'back-cta': 'Abbrechen',
        'forbidden-error': 'Du bist für diese Aktion nicht berechtigt.',
        'duplicate-error': 'Diese E-Mail-Adresse ist bereits als Admin hinterlegt.',
        'generic-error': 'Admin konnte nicht angelegt werden. Bitte versuche es erneut.',
        errors: {
          'full-name': {
            required: 'Bitte gib einen Namen ein.',
            'too-long': 'Der Name darf höchstens 120 Zeichen lang sein.',
          },
          email: {
            required: 'Bitte gib eine E-Mail-Adresse ein.',
            invalid: 'Diese E-Mail-Adresse ist ungültig.',
          },
        },
      },
    },
    sandbox: {
      title: 'Sandbox',
      intro:
        'Hier setzt du die Daten der Sandbox (app.sandbox.immoos.ai) auf eine frische Kopie der Produktion zurück. Jeden Montag um 7:00 Uhr passiert das automatisch.',
      'latest-heading': 'Letztes Zurücksetzen',
      started: 'Gestartet: {startedAt}',
      'status-label': 'Status:',
      statuses: {
        QUEUED: 'Wartet',
        IN_PROGRESS: 'Läuft',
        SUCCEEDED: 'Erfolgreich',
        FAILED: 'Fehlgeschlagen',
      },
      'view-run': 'Lauf ansehen',
      empty: 'Noch kein Zurücksetzen gelaufen.',
      'failure-note':
        'Das letzte Zurücksetzen ist fehlgeschlagen. Die Sandbox ist pausiert, bis ein Zurücksetzen durchläuft.',
      'status-unavailable': 'Der Status von GitHub ist gerade nicht abrufbar. Lade die Seite gleich noch mal.',
      'reset-cta': 'Daten zurücksetzen',
      'busy-note': 'Gerade läuft ein Sandbox-Lauf. Lade die Seite neu, um zu sehen, ob er fertig ist.',
      'started-toast': 'Zurücksetzen gestartet. Lade die Seite neu, um den Fortschritt zu sehen.',
      dialog: {
        title: 'Sandbox-Daten zurücksetzen?',
        body: 'Die Datenbank der Sandbox wird durch das neueste Backup der Produktion ersetzt. Alles, was seit dem letzten Zurücksetzen in der Sandbox passiert ist, geht verloren, und alle müssen sich neu anmelden. Das dauert ein paar Minuten.',
        'choice-label': 'Daten',
        options: {
          ANONYMIZED: {
            label: 'Anonymisiert',
            hint: 'Personenbezogene Daten werden pseudonymisiert.',
          },
          DEBUG: {
            label: 'Debug-Daten',
            hint: 'Echte Kundendaten bleiben lesbar (Namen mit „SBX - “ markiert). Nur zum Debuggen.',
          },
        },
        confirm: 'Zurücksetzen',
        cancel: 'Abbrechen',
        'busy-refusal': 'Gerade läuft schon ein Sandbox-Lauf. Warte, bis er fertig ist, und versuch es dann noch mal.',
        'start-failed': 'Das Zurücksetzen konnte nicht gestartet werden. Versuch es gleich noch mal.',
      },
    },
    'landiq-data-runs': {
      title: 'LandIQ · Datenläufe',
      board: {
        columns: {
          PENDING: 'Ausstehend',
          UPLOAD: 'Upload',
          BUILD: 'Build',
          BACKFILL: 'Backfill',
          IMPORTED: 'Importiert',
        },
        'column-states': { RUNNING: 'Läuft', DONE: 'Fertig', FAILED: 'Fehlgeschlagen', INCOMPLETE: 'Unvollständig' },
        linear: 'In Linear öffnen',
        'linear-label': '{ticket} – In Linear öffnen',
        'move-to': 'Verschieben nach {stage}',
        'stages-label': 'Stufen: {stages}',
        empty: 'Keine Datenläufe – LandIQ hat noch keine Tickets im Manifest.',
        refusals: {
          IMPORTED_NOT_A_TARGET: 'Nach Importiert kommt ein Ticket nur, wenn seine letzte Stufe fertig ist.',
          NOT_NEXT_STAGE: 'Dieses Ticket kann nur nach {stage} – es wurde nichts gestartet.',
          NO_NEXT_STAGE: 'Dieses Ticket hat keine weitere Stufe – es wurde nichts gestartet.',
          STAGE_NOT_DONE: '{stage} ist noch nicht fertig – es wurde nichts gestartet.',
          PARTLY_STARTED:
            'Render hat nur einen Teil der Stufe angenommen – {started} von {total} Läufen wurden gestartet. Starte den Rest gleich noch einmal auf der Seite des Eintrags.',
          UNKNOWN_TICKET: 'Dieses Ticket gibt es nicht mehr. Das Board wurde neu geladen.',
        },
      },
      states: {
        PENDING: 'Ausstehend',
        BLOCKED: 'Blockiert',
        RUNNING: 'Läuft',
        DONE: 'Fertig',
        FAILED: 'Fehlgeschlagen',
      },
      'failure-reasons': { SOURCE_OUTAGE: 'Fehlgeschlagen – Quelle nicht erreichbar' },
      entries: {
        toggle: '{count} Einträge{failed, plural, =0 {} other { · # fehlgeschlagen}}',
        label: 'Einträge von {ticket}',
      },
      'waits-for': 'Wartet auf: {entries}',
      confirm: {
        title: 'Datenlauf starten?',
        body: 'Dieser Befehl läuft auf {environment}:',
        environment: 'Umgebung: {environment}',
        cancel: 'Abbrechen',
        submit: 'Starten',
      },
      refusals: {
        BLOCKED: 'Kann noch nicht starten – wartet auf: {entries}.',
        ALREADY_RUNNING: 'Dieser Eintrag läuft bereits.',
        'to-running-run': 'Zum laufenden Lauf →',
        ALREADY_DONE: 'Dieser Eintrag ist schon erledigt. Es wurde nichts gestartet.',
        ENVIRONMENT_NOT_ALLOWED: 'LandIQ läuft hier nicht auf staging – Starten ist nicht erlaubt.',
        RUNNER_UNAVAILABLE:
          'Render hat den Job nicht angenommen. Es wurde nichts gestartet – versuch es gleich noch einmal.',
        UNKNOWN_ENTRY: 'Diesen Eintrag gibt es nicht mehr. Die Liste wurde neu geladen.',
      },
    },
    'landiq-data-run': {
      breadcrumb: 'LandIQ · Datenläufe',
      sections: { command: 'Befehl', log: 'Log', entries: 'Alle Einträge von {ticket}' },
      facts: {
        state: 'Status',
        'job-status': 'Job-Status',
        started: 'Gestartet',
        finished: 'Beendet',
        duration: 'Dauer',
        error: 'Fehler',
        origin: 'Gestartet über',
        'render-job': 'Render-Job',
      },
      origins: { API: 'Über immoOS', MANUAL: 'Von Hand' },
      chunks: 'Chunks – eingehend {incoming} · verarbeitet {processed} · fehlgeschlagen {failed}',
      queue: 'Warteschlange (alle Quellen): {queueDepth}',
      'never-ran': 'Noch nie gelaufen.',
      // Kennzahlen eines Laufs (LandIQ ADR data-runs/0027): Board-Karte und Seite eines Eintrags.
      metrics: {
        progress: 'Fortschritt',
        'progress-label': 'Fortschritt: {phase}',
        'phase-since': 'seit {time}',
        'done-of': '{done} von {total} ({percent} %)',
        percent: '{percent} %',
        'done-ratio': '{done} / {total}',
        'done-only': '{done} erledigt',
        rate: '{rate}/min',
        'rate-long': '{rate} pro Minute',
        eta: 'fertig ca. {time}',
        stale: 'Kein neuer Stand seit {age}',
        'stale-banner':
          'Der letzte Stand ist {age} alt. Ein laufender Befehl meldet sich spätestens alle 10 min – er hängt womöglich.',
        labels: {
          rate: 'Tempo',
          eta: 'Voraussichtlich fertig',
          requests: 'Anfragen',
          retries: 'Wiederholungen',
          updated: 'Stand',
        },
        requests: '{live} live · {replayed} aus Speicher',
        times: 'Zeiten',
        stages: { queued: 'Warteschlange', command: 'Befehl', import: 'Import', total: 'Gesamt' },
        'stage-time': '{stage} {duration}',
        'card-chunks': '{imported} importiert · {failed} fehlgeschlagen',
        'chunk-stats': 'Chunks dieses Laufs',
        'chunk-labels': {
          seen: 'Gesehen',
          imported: 'Importiert',
          failed: 'Fehlgeschlagen',
          rows: 'Geschriebene Zeilen',
          p50: 'Median',
          max: 'Längster',
        },
        slowest: 'Langsamste Chunks',
        'slowest-columns': {
          chunk: 'Chunk',
          outcome: 'Ergebnis',
          duration: 'Dauer',
          attempts: 'Versuche',
          rows: 'Zeilen',
          finished: 'Fertig',
        },
        'chunk-outcomes': { IMPORTED: 'Importiert', FAILED: 'Fehlgeschlagen' },
        // Schätzung des Manifests gegen die Laufzeit des Befehls, ohne Warteschlange (LandIQ pull request 720).
        estimate: {
          label: 'Schätzung',
          planned: 'Schätzung {duration}',
          'actual-of': '{actual} von {estimate}',
          over: '+{overrun} über Schätzung ({percent} %)',
          left: 'noch ca. {left}',
        },
      },
      'no-log': 'Für diesen Lauf gibt es kein Log – er wurde von Hand außerhalb von Render gestartet.',
      actions: {
        retry: 'Erneut starten',
        start: 'Starten',
        'retry-failed-chunks': 'Fehlgeschlagene Chunks wiederholen ({count})',
        resume: 'Fortsetzen',
      },
      'retry-hint': 'Lädt den Upload vollständig neu.',
      'resume-dialog': {
        title: 'Upload fortsetzen?',
        body: 'Der Upload startet auf {environment} neu und überspringt alle Chunks, die schon hochgeladen sind.',
        cancel: 'Abbrechen',
        submit: 'Fortsetzen',
        refusals: {
          RESUME_NOT_POSSIBLE: 'Fortsetzen ist für diesen Lauf nicht mehr möglich. Die Seite wurde neu geladen.',
        },
      },
      'retry-failed-chunks-dialog': {
        title: 'Fehlgeschlagene Chunks wiederholen?',
        body: '{count, plural, one {# fehlgeschlagener Chunk geht zurück in die Warteschlange und wird erneut importiert.} other {# fehlgeschlagene Chunks gehen zurück in die Warteschlange und werden erneut importiert.}} Es wird nichts neu hochgeladen.',
        cancel: 'Abbrechen',
        submit: 'Wiederholen',
        retried: '{count, plural, one {# Chunk} other {# Chunks}} wieder in die Warteschlange gestellt.',
        refusals: {
          'no-longer-possible': 'Wiederholen ist für diesen Lauf nicht mehr möglich. Die Seite wurde neu geladen.',
          STORAGE_UNAVAILABLE:
            'LandIQ konnte S3 nicht lesen. Bereits verschobene Chunks werden importiert – versuch es gleich noch einmal.',
        },
      },
    },
    // Fleet — status and enrolment approvals (IMM-910.7, ADR 0047). `profile`,
    // `title`, field `label`/`value` and `badges` are agent-chosen and never
    // translated; only the five `state` and four `approval` tokens are.
    jobs: {
      worker: 'Worker',
      machine: 'Maschine',
      claimed: 'Übernommen',
      silent: 'Keine Rückmeldung',
      // Fortschritt und Verdacht einer laufenden Runde (fleet-agent ADR 0012):
      // eine Runde, die eine Prüfung für festgefahren hält, wird markiert, nie beendet.
      'beat-label': 'Herzschlag',
      'beat-never': 'Noch kein Herzschlag',
      'beat-stale': 'VERALTET',
      'log-label': 'Log',
      'log-silent': 'STILL',
      'progress-line': '{line} · {time}',
      'suspect-badge': 'Verdächtig',
      'suspect-title': 'Diese Runde wirkt festgefahren',
      'suspect-note': 'Markiert {time}: {reason}',
      'suspect-clear-cta': 'Alles in Ordnung',
      'suspect-release-cta': 'Freigeben',
      'release-cta': 'Job freigeben',
      'release-title': 'Diesen Job freigeben?',
      'release-body':
        'Das Arbeitspaket wird zurückgegeben und kann erneut übernommen werden. Der bisherige Halter wird ab seinem nächsten Aufruf abgewiesen — gib einen noch laufenden Halter nur frei, wenn du ihn stoppen willst.',
      'release-confirm': 'Freigeben',
      'cancel-cta': 'Abbrechen',
      'logs-heading': 'Logs',
      'timeline-heading': 'Verlauf',
      'timeline-empty': 'Diese Runde hat noch nichts gemeldet.',
      'timeline-kind-claimed': 'Übernommen',
      'timeline-kind-progress': 'Fortschritt',
      'timeline-kind-phase': 'Phase',
      'timeline-kind-done': 'Fertig',
      'timeline-kind-dead-letter': 'Braucht einen Menschen',
      'timeline-kind-note': 'Notiz',
      // Steuern einer laufenden Runde (OPS-55) — was früher eine Antwort im Buzz-Thread war.
      'messages-heading': 'Nachrichten an die Runde',
      'messages-empty': 'Noch keine Nachrichten. Die Runde liest eine Nachricht nach ihrem nächsten Tool-Aufruf.',
      'message-input': 'Nachricht an die Runde',
      'message-send-cta': 'Senden',
      'message-stop-cta': 'Runde stoppen',
      'message-stop-hint': 'Eine Nachricht, die nur „stop“ lautet, beendet die Runde am nächsten sicheren Punkt.',
      'stop-requested-note': '{name} hat um {time} das Stoppen angefordert.',
      'message-meta': '{name} · {time}',
      'message-stop-badge': 'Stopp',
      'message-delivery': {
        waiting: 'Wartet auf den Host',
        sent: 'An den Host gesendet',
        received: 'Beim Host angekommen',
        delivered: 'Zugestellt',
        'not-delivered': 'Nicht zugestellt',
      },
      'generic-error': 'Die Aktion konnte nicht ausgeführt werden. Bitte versuche es erneut.',
      // Parkte Fragen (IMM-981): eine Runde, die nicht selbst entscheiden darf,
      // parkt ihr Ticket mit einer Frage. Die Gruppe steht über den gehaltenen
      // Jobs, weil eine Frage die dringendere Blockade ist.
      'question-options-heading': 'Optionen',
      'own-answer': 'Eigene Antwort',
      'own-answer-input': 'Antwort in eigenen Worten',
      'recommended-badge': 'Empfohlen',
      'answer-cta': 'Antwort senden',
      'ask-again-cta': 'Erneut fragen',
      'ask-again-success': 'Die Frage zu {ticket} geht als Nächstes über den Messenger raus.',
      'question-read-only-note': 'Du kannst die Frage lesen. Zum Beantworten wird backoffice:fleet:command benötigt.',
    },
    'fleet-history': {
      title: 'Verlauf',
      machine: 'Maschine',
      range: 'Zeitraum',
      'range-24h': '24 Stunden',
      'range-7d': '7 Tage',
      'range-30d': '30 Tage',
      'range-custom': 'Benutzerdefiniert',
      from: 'Von',
      to: 'Bis',
      'no-readings': 'Für diesen Zeitraum liegen keine Messwerte vor.',
    },
    'fleet-board': {
      title: 'Board',
      subtitle:
        'Was jede Lane gerade tut und als Nächstes holt — wie `fleet-agent agent preview`, für alle Maschinen. Die Reihenfolge einer Schlange lässt sich per Ziehen ändern.',
      columns: 'Lanes',
      'no-lanes': 'Noch kein Board – keine Maschine hat bisher ihre Lanes gemeldet.',
      stale: 'Linear konnte nicht gelesen werden. Das Board zeigt den Stand von {time}.',
      linear: 'In Linear öffnen',
      'linear-label': '{ticket} – In Linear öffnen',
      priority: { '0': 'Keine Priorität', '1': 'Dringend', '2': 'Hoch', '3': 'Mittel', '4': 'Niedrig' },
      assistant: {
        open: 'Fleet-Assistent',
        title: 'Fleet-Assistent',
        close: 'Schließen',
        placeholder: 'Frag den Fleet-Assistenten …',
        send: 'Senden',
        empty: 'Frag nach einem Blocker, einem Ticket oder dem Stand der Fleet.',
        'channel-telegram': 'Telegram',
        working: 'Der Fleet-Assistent schreibt …',
      },
      views: { all: 'Alle Projekte' },
      free: '{n} frei',
      running: '{n} läuft',
      next: 'als Nächstes',
      workers: {
        button: 'Workers',
        'button-label': 'Worker von {lane} anzeigen',
        title: 'Worker von {lane}',
        count: '{count, plural, one {# Worker meldet diese Lane} other {# Worker melden diese Lane}}',
        none: 'Kein Worker meldet {lane}.',
        failed: 'Die Maschinen konnten nicht gelesen werden.',
        reported: 'gemeldet vor {duration}',
        'out-of-service': 'Außer Betrieb',
        offline: 'Offline',
        close: 'Schließen',
        'stop-after-round': 'Stoppt nach der laufenden Runde',
        'verb-sent': 'An {machine} gesendet — kommt mit dem nächsten Tick an.',
        'verb-failed': 'Konnte nicht gesendet werden.',
      },
      nothing: 'Nichts in der Schlange',
      'not-taken': 'Nimmt die Lane nicht',
      'pr-first': 'Erst Fix-PRs (needs-fixes), älteste zuerst — dann Tickets',
      flow: '{states} → {running}',
      'flow-with': 'mit {labels}',
      'flow-without': 'ohne {labels}',
      tags: {
        running: 'Läuft',
        silent: 'Keine Rückmeldung',
        stranded: 'Ohne Job',
        free: 'Frei',
        bound: 'Gebunden',
        parked: 'Geparkt',
        answered: 'Antwort da',
        assisted: 'Assistiert',
        capped: 'Runden aufgebraucht',
        waiting: 'Wartet auf Blocker',
      },
      since: 'seit {duration}',
      beat: 'Herzschlag vor {duration}',
      waiting: 'wartet seit {duration}',
      'history-runs': '{count, plural, one {# Runde} other {# Runden}}',
      'history-parked': '{count} geparkt',
      'history-resumed': '{count} fortgesetzt',
      stranded: 'steht in {state}, aber kein Job hält es',
      bound: 'nur für {worker}',
      capped: '{runs}/{limit} Runden — Top-up oder von Hand fertigstellen',
      assisted: 'ein Mensch baut es von Hand',
      answered: 'kommt vor allen anderen dran',
      'waiting-on': 'wartet auf {tickets}',
      'waiting-on-unknown': 'wartet auf einen Blocker',
      'running-on': 'Auf {machine}',
      'open-ticket': '{ticket} – Details öffnen',
      'drag-handle': '{ticket} verschieben',
      'drag-multi-project': 'Zum Sortieren ein Projekt wählen',
      reorder: {
        'move-up': 'Nach oben',
        'move-down': 'Nach unten',
      },
      manual: 'manuell',
      'manual-title': 'Manuell platziert',
      dnd: {
        instructions: 'Ziehen, oder mit den Pfeiltasten hoch und runter verschieben.',
        moved: '{ticket} auf Platz {position} von {total}.',
      },
      panel: {
        close: 'Details schließen',
        loading: 'Details werden geladen…',
        'load-failed': 'Die Details konnten nicht geladen werden.',
        'off-board': 'Steht gerade nicht auf dem Board.',
        lane: 'Lane',
        state: 'Status',
        priority: 'Priorität',
        order: 'Reihenfolge',
        'order-manual': 'Manuell platziert, Platz {position}',
        'order-manual-unqueued': 'Manuell platziert',
        'order-linear': 'Linear-Reihenfolge',
        tag: 'Zustand',
        'current-heading': 'Aktueller Job',
        'no-current': 'Kein Job hält dieses Ticket.',
        'blockers-heading': 'Blocker',
        'blocker-slice': 'Slice',
        'blocker-open': '{ticket} hier anzeigen',
        worker: 'Worker',
        machine: 'Maschine',
        claimed: 'Übernommen',
        'last-beat': 'Letzter Herzschlag',
        silent: 'Keine Rückmeldung',
        runs: 'Runden',
        'runs-value': '{runs} von {limit}',
        'history-heading': 'Läufe',
        'history-empty': 'Dieses Ticket wurde noch nie übernommen.',
        'run-live': 'läuft',
        'run-label': 'Lauf von {worker} am {time}',
        outcome: {
          finished: 'fertig',
          parked: 'geparkt',
          abandoned: 'abgebrochen',
          'no-run': 'nicht gestartet',
          released: 'freigegeben',
        },
        duration: 'Dauer {duration}',
        tokens: '{count} Tokens',
        'usage-unknown': 'Nutzung unbekannt',
        questions: 'Fragen und Antworten',
        unanswered: 'Noch nicht beantwortet',
        'answered-by': 'Beantwortet von {name} am {time}',
        'asked-at': 'Gefragt am {time}',
        'question-brief': 'Worum es geht: {brief}',
      },
    },
    'fleet-accounts': {
      title: 'Konten',
      subtitle:
        'Die Konten, mit denen die Worker angemeldet sind. Anmelden, neu anmelden und trennen geht nur am Worker, mit `fleet-agent accounts`.',
      'read-only-hint': 'Du kannst die Konten ansehen, aber nicht ändern.',
      empty: 'Noch hat kein Worker ein Konto gemeldet.',
      'save-error': 'Das hat nicht geklappt. Bitte versuche es erneut.',
      columns: {
        account: 'Konto',
        machine: 'Maschine',
        session: 'Sitzung',
        week: 'Woche',
        state: 'Status',
        enabled: 'Aktiv',
        actions: 'Aktionen',
      },
      'no-usage': 'Noch nicht gemessen',
      'usage-read-at': 'gemessen {time}',
      'max-weekly': 'max. {percent}',
      state: {
        ready: 'Bereit',
        paused: 'Pausiert',
        reauth: 'Neu anmelden',
        limited: 'Limit erreicht',
        'limited-session': 'Sitzungslimit',
        'limited-week': 'Wochenlimit',
      },
      'resets-in': 'Reset in {duration}',
      'resets-at': 'Reset {time}',
      'back-at': 'zurück {time}',
      'back-at-in': 'zurück {time} · in {duration}',
      duration: {
        minutes: '{minutes} Min.',
        'hours-minutes': '{hours} Std. {minutes} Min.',
        'days-hours': '{days} T. {hours} Std.',
      },
      'reauth-hint': 'Am Worker neu anmelden: `fleet-agent accounts`.',
      'enabled-label': '{label} aktiv',
      'limit-cta': {
        capped: 'Max. {percent} %',
        uncapped: 'Kein Limit',
      },
      'cancel-cta': 'Abbrechen',
      'max-weekly-dialog': {
        title: 'Wochenlimit für {label}',
        field: 'Wochenlimit in Prozent',
        hint: 'Ab diesem Anteil des Wochenkontingents nutzt der Worker das Konto nicht mehr.',
        'range-error': 'Gib eine ganze Zahl von {min} bis {max} ein.',
        'save-cta': 'Speichern',
      },
    },
    'fleet-metrics': {
      title: 'Metriken',
      subtitle: 'Token-Verbrauch der Fleet — je Maschine, Agent und Modell.',
      loading: 'Metriken werden geladen…',
      'view-machine': 'Nach Maschine',
      'view-agent': 'Nach Agent',
      'view-model': 'Nach Modell',
      'filter-period': 'Zeitraum',
      'filter-agents': 'Agenten',
      'filter-class': 'Klasse',
      'scale-day': 'Tag',
      'scale-week': 'Woche',
      'scale-month': 'Monat',
      'scale-thirteen-weeks': '13 Wochen',
      previous: 'Früherer Zeitraum',
      next: 'Späterer Zeitraum',
      'label-day': '{weekday} {date}',
      'label-week': 'KW {week}',
      'label-thirteen-weeks': 'KW {first}–{last}',
      range: '{from}–{to}',
      'agents-all': 'Alle Agenten',
      'agents-some': '{count} von {total}',
      'agents-runs': '{count, plural, one {# Run} other {# Runs}}',
      'class-all': 'Alle vier',
      'kpi-tokens-all': 'Tokens · alle vier Klassen',
      'kpi-tokens-class': 'Tokens · {class}',
      'kpi-runs': 'Runs',
      'kpi-per-run': 'Tokens je Run',
      'kpi-runtime': 'Laufzeit',
      'kpi-cost': 'Kosten',
      'kpi-cost-sub': 'gerechnet, nicht gemessen',
      'kpi-unparsed': 'davon {count} ohne lesbare Nutzung',
      'kpi-all-readable': 'Nutzung aller Runs lesbar',
      'kpi-output-per-run': 'output je Run {value}',
      'kpi-runtime-per-run': 'Ø {value} je Run',
      'no-previous': 'keine Vorperiode im Ledger',
      'versus-day': 'ggü. Vortag',
      'versus-week': 'ggü. Vorwoche',
      'versus-month': 'ggü. Vormonat',
      'versus-thirteen-weeks': 'ggü. den 13 Wochen davor',
      'delta-new': 'neu',
      'series-machine': 'Verlauf je Maschine',
      'series-agent': 'Verlauf je Agent',
      'series-model': 'Verlauf je Modell',
      'rows-agents': 'Zeilen: Agenten',
      'rows-machines': 'Zeilen: Maschinen',
      'one-scale': 'alle Zeilen auf einer Skala',
      sum: 'Summe',
      'hint-all': 'Summe aller vier Klassen — zu {share} cache read. Wähl oben eine Klasse, um sie allein zu sehen.',
      'hint-class': 'Nur {class}. Die anderen drei Klassen stehen in der Zerlegung unten.',
      'share-of-fleet': '{share} der Fleet',
      'no-run-for': 'Ohne Run in diesem Zeitraum: {list}.',
      'no-run-selected': 'In diesem Zeitraum kein Run der gewählten Agenten.',
      'heat-title': 'Wann die Maschinen arbeiten',
      'heat-hint': 'Tokens je Stunde und Wochentag',
      'heat-none': 'kein Run',
      'heat-low': 'wenig',
      'heat-high': 'viel',
      'heat-cell': '{weekday} {hour}:00 · {runs, plural, one {# Run} other {# Runs}}',
      'split-machine': 'Zerlegung je Maschine',
      'split-agent': 'Zerlegung je Agent',
      'split-model': 'Zerlegung je Modell',
      'split-hint': 'die vier Klassen nebeneinander, jede auf ihrer eigenen Größenordnung',
      'split-shape': 'Form',
      'column-machine': 'Maschine',
      'column-agent': 'Agent',
      'column-model': 'Modell',
      'no-cache-write': '{model} schreibt keinen Cache — die 0 bei cache write ist gemessen, nicht fehlend.',
      'leader-title': 'Rangliste der Agenten',
      'leader-hint': 'sortiert nach Tokens der gewählten Klasse',
      'leader-tokens': 'Tokens',
      'leader-share': 'Anteil',
      'leader-delta': 'ggü. Vorperiode',
      'leader-runs': 'Runs',
      'leader-per-run': 'je Run',
      'leader-output-per-run': 'output je Run',
      'leader-runtime': 'Ø Laufzeit',
      'leader-cost': 'Kosten',
      'mix-title': 'Modell-Mix je Agent',
      'mix-hint': 'Anteil jedes Modells an den Tokens des Agenten',
      'mix-runs': '{count}×',
      'tip-runs': '{count, plural, one {# Run} other {# Runs}}',
      'tip-unparsed': '{count} ohne lesbare Nutzung',
      'tip-unknown': 'Nutzung nicht lesbar — Tokens unbekannt, nicht 0.',
      'unparsed-note':
        '{count, plural, one {# Run} other {# Runs}} ohne lesbare Nutzung — sie zählen als Runs und in der Laufzeit, nicht in den Tokens. Jede Summe, in die sie fallen, trägt ≈.',
      'unknown-legend': 'gestrichelt: Runs gelaufen, Nutzung nicht lesbar',
      'approx-title': 'Diese Summe ist ein Mindestwert.',
      'pricing-footnote':
        'Tokens sind gemessen. Euro ist daraus gerechnet — Preistabelle Stand {asOf}, 1 USD = {rate} €, ältere Runs zu heutigen Preisen.',
      'pricing-fallback': 'Modelle ohne eigenen Preis ({models}) sind zu {fallback}-Preisen gerechnet.',
      'empty-title': 'In diesem Zeitraum ist kein Run gelaufen.',
      'empty-body': 'Der erste Run im Ledger ist vom {date}. Wähl einen späteren Zeitraum.',
      'empty-body-ledger': 'Im Ledger ist noch kein Run.',
      'empty-cta': 'Zum neuesten Zeitraum',
      'unreachable-title': 'Backend nicht erreichbar',
      'unreachable-body':
        'Die Metriken konnten nicht geladen werden. Hier steht lieber nichts als veraltete Zahlen, die wie aktuelle aussehen.',
      retry: 'Erneut versuchen',
      'tokens-thousand': '{value} Tsd',
      'tokens-million': '{value} Mio',
      'tokens-billion': '{value} Mrd',
      'duration-hours': '{hours} h {minutes} min',
      'duration-minutes': '{minutes} min',
      'duration-seconds': '{seconds} s',
      'weekday-1': 'Mo',
      'weekday-2': 'Di',
      'weekday-3': 'Mi',
      'weekday-4': 'Do',
      'weekday-5': 'Fr',
      'weekday-6': 'Sa',
      'weekday-7': 'So',
    },
    fleet: {
      title: 'Fleet',
      'history-cta': 'Verlauf',
      'back-to-index': 'Zurück zur Maschinenliste',
      'panel-close': 'Schließen',
      'approved-by': 'Freigegeben von',
      'public-key': 'Schlüssel',
      worker: 'Worker',
      'capacity-label': 'Kapazität',
      capacity: '{running} von {max} Maschinen',
      'group-pending': 'Warten auf Freigabe',
      'group-machines': 'Maschinen',
      host: 'Host',
      platform: 'Plattform',
      'agent-version': 'fleet-agent',
      'first-seen': 'Erstmals gesehen',
      'last-seen': 'Zuletzt gesehen',
      'reported-at': 'Stand',
      'clean-logs': 'Logs aufräumen',
      'no-profiles': 'Diese Maschine meldet keine Profile.',
      stale: 'Keine Rückmeldung',
      'stale-note': 'Zuletzt gesehen {time}. Die angezeigten Zustände können veraltet sein.',
      health: {
        disk: 'Festplatte',
        memory: 'Arbeitsspeicher',
        swap: 'Auslagerung',
        pressure: 'Speicherdruck',
        temperature: 'Temperatur',
        load: 'Auslastung',
        'percent-value': '{percent} %',
        'temperature-value': '{degrees} °C',
        'load-value': '{factor} × Kerne',
        gigabytes: '{value} GB',
        'used-of-total': '{used} von {total} · {percent} %',
        measured: 'Gemessen {time}',
        'no-data': 'Keine Daten',
        'disk-full': 'Festplatte fast voll',
        'memory-full': 'Arbeitsspeicher fast voll',
        throttled: 'Gedrosselt — {reason}',
        'reason-temperature': 'Temperatur {degrees} °C',
        'reason-load': 'Auslastung {factor} × Kerne',
        'reason-memory-pressure': 'Speicherdruck {percent} %',
        'reason-thermal-pressure': 'Thermischer Druck',
        'state-heading': 'Zustand',
        'last-24h': 'Letzte 24 Stunden',
        'no-readings-24h': 'Keine Messwerte in den letzten 24 Stunden.',
        'series-load-error': 'Die Messwerte konnten nicht geladen werden.',
        'history-link': 'Verlauf',
        'threshold-percent': '{percent} %',
        'threshold-load': '{factor} ×',
      },
      'read-only-note': 'Du kannst die Fleet lesen. Befehle und Freigaben brauchen backoffice:fleet:command.',
      'join-request-title': 'Anmeldung',
      'join-request-body':
        'Diese Maschine hat sich mit dem Fleet-Schlüssel angemeldet. Sie meldet ihren Zustand, führt aber keinen Befehl aus, solange sie nicht freigegeben ist.',
      'approve-cta': 'Freigeben',
      'reject-cta': 'Ablehnen',
      'revoke-cta': 'Zugang entziehen',
      'cancel-cta': 'Abbrechen',
      'approve-title': 'Maschine freigeben?',
      'approve-body':
        'Danach kann diese Maschine Befehle ausführen. Es wird kein Schlüssel angezeigt — die Maschine holt sich ihren Token selbst.',
      approved: 'Freigegeben. Die Maschine meldet sich innerhalb einer Minute.',
      'reject-title': 'Anmeldung ablehnen?',
      'reject-body':
        'Die Maschine bleibt in der Liste, damit die Ablehnung sichtbar ist. Ihre Anmeldungen werden nicht mehr angenommen.',
      'revoke-title': 'Zugang entziehen?',
      'revoke-body': 'Die Maschine wird sofort nicht mehr gesteuert. Ihr Token wird nicht mehr akzeptiert.',
      'empty-heading': 'Noch hat sich keine Maschine angemeldet.',
      'empty-body':
        'Sobald ein Host den fleet-agent startet, erscheint er hier als Anmeldung und wartet auf deine Freigabe.',
      'generic-error': 'Die Aktion konnte nicht ausgeführt werden. Bitte versuche es erneut.',
      state: {
        pending: 'Ausstehend',
        idle: 'Bereit',
        working: 'Arbeitet',
        'waiting-on-human': 'Wartet auf Rückmeldung',
        stopped: 'Gestoppt',
      },
      approval: {
        pending: 'Ausstehend',
        approved: 'Freigegeben',
        rejected: 'Abgelehnt',
        revoked: 'Entzogen',
      },
      'scope-line': '{machine} · {profile}',
      'force-update-toggle': 'Lokale Änderungen im Klon verwerfen',
      'finish-toggle': 'Erst die laufende Runde beenden lassen',
      engine: {
        title: 'Engine',
        orchestrator: 'Orchestrator {engine}',
        models: 'Planung {plan} / Umsetzung {implement}',
        source: {
          flag: 'Laufparameter',
          'host-project-lane': 'Maschine: Lane in diesem Projekt',
          'host-lane': 'Maschine: Lane',
          'host-default': 'Maschine: Standard',
          project: 'Projektkonfiguration',
          'built-in': 'Voreinstellung',
        },
        'lane-field': 'Lane',
        'lane-placeholder': 'Leer — Standard der Maschine',
        'project-field': 'Projekt',
        'project-placeholder': 'Leer — jedes Projekt',
        'scope-default-hint': 'Ohne Lane gilt die Wahl für jede Lane der Maschine, die keine eigene hat.',
        'scope-lane-hint': 'Gilt nur für diese Lane — mit Projekt nur für diese Lane in diesem Projekt.',
        unset: 'Standard der Maschine aufheben',
        'unset-lane': 'Die Wahl dieser Lane aufheben',
        'engine-field': 'Engine',
        any: 'Beliebig',
        pinned: 'Festgelegt: {engine}',
        running: 'Läuft auf {engine}',
        'plan-model-field': 'Planungsmodell',
        'plan-model-placeholder': 'Leer — Standard der Engine',
        'implement-model-field': 'Umsetzungsmodell',
        'implement-model-placeholder': 'Leer — Standard der Engine',
        'models-hint': 'Modell-IDs, wie die Engine sie erwartet (z. B. opus, sonnet).',
      },
      accounts: {
        title: 'Claude-Konten',
        'unknown-email': 'Unbekanntes Konto',
        'default-dir': '~/.claude (Standard)',
        week: 'Woche {percent}',
        session: 'Sitzung {percent}',
        'rate-limited': 'gedrosselt',
        'quota-read-at': 'Stand {time}',
        lanes: 'Lanes: {lanes}',
        auth: {
          ok: 'Angemeldet',
          'logged-out': 'Abgemeldet',
          'usage-limit': 'Nutzungslimit erreicht',
          unknown: 'Unbekannt',
        },
      },
      'weekly-limit': {
        fact: 'Wochenlimit',
        percent: '{percent} %',
        none: 'Kein Limit',
        cta: 'Wochenlimit',
        title: 'Wochenlimit für {machine}',
        field: 'Wochenlimit in Prozent',
        hint: 'Ab diesem Wochenverbrauch übernimmt die Maschine keine neuen Jobs. Laufende Jobs laufen zu Ende.',
        'save-cta': 'Speichern',
        'clear-cta': 'Limit entfernen',
        'range-error': 'Gib eine ganze Zahl von {min} bis {max} ein.',
        held: 'Wochenlimit erreicht',
        'held-note':
          '{usagePercent} % von {limitPercent} % genutzt. Die Maschine übernimmt bis {resetsAt} keine neuen Jobs, laufende Jobs laufen zu Ende.',
        'held-note-no-reset':
          '{usagePercent} % von {limitPercent} % genutzt. Die Maschine übernimmt bis zum Reset des Wochenfensters keine neuen Jobs, laufende Jobs laufen zu Ende.',
      },
      command: {
        'sync-branch-label': 'Branch aktualisieren',
        'sync-branch-title': 'Branch aktualisieren?',
        'sync-branch-body': 'Nur bei sauberem Arbeitsverzeichnis. Nichts wird überschrieben.',
        'sync-branch-cta': 'Branch aktualisieren',
        'clean-checkout-label': 'Checkout zurücksetzen',
        'clean-checkout-title': 'Checkout zurücksetzen?',
        'clean-checkout-body':
          'Setzt den Haupt-Checkout der Maschine auf einen aktuellen main zurück. Was dort liegt — nicht committet oder auf einem nie gepushten Branch — wird vorher committet, gepusht und als Draft-PR geöffnet, damit nichts verloren geht. Ignorierte Dateien wie node_modules bleiben. Laufende Runden arbeiten in eigenen Worktrees und sind nicht betroffen.',
        'clean-checkout-cta': 'Sichern und auf main',
        'restart-agent-label': 'Agent neu starten',
        'restart-agent-title': 'fleet-agent neu starten?',
        'restart-agent-body':
          'Der fleet-agent dieser Maschine wird über seinen Service neu gestartet. Laufende Runden sind nicht betroffen — der Agent beobachtet sie nur. Für kurze Zeit meldet die Maschine keinen Zustand.',
        'restart-agent-cta': 'Neu starten',
        'update-agent-label': 'Agent aktualisieren',
        'update-agent-title': 'fleet-agent aktualisieren?',
        'update-agent-body':
          'Der fleet-agent dieser Maschine holt den neuesten Code in seinen Klon, installiert neu und startet über seinen Service neu. Danach starten alle Agenten auf dem neuen Code neu — jede laufende Runde endet vorher an einer Schrittgrenze und wird danach fortgesetzt. Bei lokalen Änderungen im Klon bricht das Update ab.',
        'update-agent-cta': 'Aktualisieren',
        'force-update-agent-label': 'Update erzwingen',
        'force-update-agent-title': 'fleet-agent-Update erzwingen?',
        'force-update-agent-body':
          'Setzt den Klon des fleet-agent auf den Stand von origin zurück — lokale Änderungen und lokale Commits im Klon gehen verloren —, installiert neu, auch wenn sich nichts geändert hat, und startet den Service neu. Danach starten alle Agenten auf dem neuen Code neu; jede laufende Runde endet vorher an einer Schrittgrenze.',
        'force-update-agent-cta': 'Erzwingen',
        'start-all-label': 'Alle starten',
        'start-all-title': 'Alle Agenten starten?',
        'start-all-body':
          'Jeder gestoppte Agent dieser Maschine wird mit den Standardwerten seines Profils gestartet. Bereits laufende Agenten bleiben unverändert.',
        'start-all-cta': 'Alle starten',
        'stop-all-label': 'Alle stoppen',
        'stop-all-title': 'Alle Agenten stoppen?',
        'stop-all-body':
          'Jeder Agent dieser Maschine stoppt — eine laufende Runde endet an ihrer nächsten Schrittgrenze und wird beim nächsten Start fortgesetzt. Der Stopp hält über Neustarts und Updates, bis jemand startet.',
        'stop-all-cta': 'Alle stoppen',
        'restart-all-label': 'Alle neu starten',
        'restart-all-title': 'Alle Agenten neu starten?',
        'restart-all-body':
          'Jeder Agent dieser Maschine wird neu gestartet, auch ein gestoppter. Eine laufende Runde endet an ihrer nächsten Schrittgrenze und wird danach fortgesetzt.',
        'restart-all-cta': 'Alle neu starten',
        'start-label': 'Starten',
        'start-title': 'Lane starten?',
        'start-body': 'Hebt den Stopp dieser Lane auf dieser Maschine auf. Die Lane nimmt danach wieder Arbeit an.',
        'start-cta': 'Starten',
        'stop-label': 'Stoppen',
        'stop-title': 'Lane stoppen?',
        'stop-body':
          'Die Lane stoppt auf dieser Maschine — eine laufende Runde endet an ihrer nächsten Schrittgrenze und wird beim nächsten Start fortgesetzt. Der Stopp hält über Neustarts und Updates, bis jemand startet.',
        'stop-cta': 'Stoppen',
        'restart-label': 'Neu starten',
        'restart-title': 'Lane neu starten?',
        'restart-body':
          'Die Lane wird neu gestartet, auch wenn sie gestoppt war. Eine laufende Runde endet an ihrer nächsten Schrittgrenze und wird danach fortgesetzt.',
        'restart-cta': 'Neu starten',
        'logs-reset-label': 'Build-Log leeren',
        'logs-reset-title': 'Build-Log leeren?',
        'logs-reset-body':
          'Das Build-Log dieses Profils wird auf null gesetzt. Ein laufender Build wird nicht unterbrochen.',
        'logs-reset-cta': 'Build-Log leeren',
        'logs-label': 'Protokoll abrufen',
        'logs-download-label': 'Vollständiges Protokoll anfordern',
        'handover-label': 'Ticket übergeben',
        'e2e-unlock-label': 'E2E-Stack entsperren',
        'run-command-label': 'Befehl ausführen',
        'system-preview-label': 'Speicherbericht',
        'system-prune-label': 'Speicher aufräumen',
        'system-prune-title': 'Speicher aufräumen?',
        'system-prune-body':
          'Die Maschine löscht veraltete Worktrees aller Projekte, liegengebliebene Testcontainer, alte Docker-Images und Build-Cache und räumt den pnpm-Store auf. Laufende Runden und ihre Worktrees bleiben unberührt.',
        'system-prune-cta': 'Aufräumen',
        'agent-label': 'Agent-Befehl',
        'steer-label': 'Nachricht an Job',
        'engine-label': 'Engine wählen',
        'engine-title': 'Engine wählen?',
        'engine-body':
          'Ab ihrer nächsten Runde laufen die gewählten Lanes auf dieser Engine. Laufende Runden behalten ihre. Eine Lane, die die Maschine nicht kennt, oder eine Engine, die dort nicht installiert ist, lehnt sie ab.',
        'engine-cta': 'Engine setzen',
        'engine-pin-label': 'Engine festlegen',
        'engine-pin-title': 'Engine für eine Lane festlegen?',
        'engine-pin-body':
          'Ab ihrer nächsten Runde läuft die Lane nur noch auf Konten dieser Engine. Laufende Runden behalten ihre. Mit „Beliebig“ wählt die Flotte wieder das Konto mit dem meisten freien Limit, egal welche Engine.',
        'engine-pin-cta': 'Festlegen',
      },
      'run-command': {
        title: 'Befehl ausführen',
        body: 'Führt einen pnpm-Befehl im Checkout des gewählten Projekts auf dieser Maschine aus — ohne Shell, höchstens zehn Minuten lang.',
        'project-field': 'Projekt',
        'project-placeholder': 'Projekt wählen',
        'no-projects': 'Diese Maschine hat noch kein Projekt gemeldet.',
        'command-field': 'Befehl',
        'command-hint': 'Leerzeichen trennen die Wörter; Anführungszeichen halten ein Wort zusammen.',
        'unclosed-quote': 'Ein Anführungszeichen ({quote}) ist nicht geschlossen.',
        cta: 'Ausführen',
        'scope-line': '{machine} · {project}',
        'running-hint': 'Schließen bricht den Befehl nicht ab — sein Ergebnis steht danach im Befehlsverlauf.',
        'exit-code': 'Exit-Code {code}',
        'no-exit-code': 'Kein Exit-Code — nach zehn Minuten abgebrochen',
        output: 'Ausgabe',
        'no-output': 'Keine Ausgabe.',
        truncated: 'Frühere Ausgabe wurde abgeschnitten — hier stehen nur die letzten 16.000 Zeichen.',
        'again-cta': 'Weiterer Befehl',
        'close-cta': 'Schließen',
      },
      'e2e-unlock': {
        title: 'E2E-Stack entsperren',
        body: 'Löst die Sperre eines E2E-Stacks auf dieser Maschine über das stack unlock jedes Projekts — für einen Stack, den ein abgebrochener Durchlauf gesperrt hinterlassen hat.',
        'project-field': 'Projekt',
        'every-project': 'Alle Projekte',
        'worker-field': 'Worker',
        'worker-hint': 'Wessen Stack, z. B. mini-pc-engineer-1. Leer: der manuelle Stack des Hosts.',
        'worker-invalid':
          'Ein Worker besteht aus Buchstaben, Ziffern, Punkten, Binde- und Unterstrichen und beginnt mit einem Buchstaben oder einer Ziffer.',
        cta: 'Entsperren',
        'scope-line': '{machine} · {project} · {worker}',
        holder: 'War gesperrt von {owner} auf {host} seit {since}',
        'holder-worktree': 'Worktree {worktree}',
        'no-holder': 'Es bestand keine Sperre.',
        'again-cta': 'Weiteren entsperren',
      },
      'system-preview': {
        title: 'Speicherbericht',
        body: 'Zeigt, was ein Aufräumen auf dieser Maschine freigeben würde — veraltete Worktrees, liegen gebliebene Testcontainer, alte Docker-Images und Build-Cache, den pnpm-Store. Löscht nichts.',
        'project-field': 'Projekt',
        'every-project': 'Alle Projekte',
        cta: 'Bericht abrufen',
        'scope-line': '{machine} · {project}',
        'again-cta': 'Erneut abrufen',
      },
      outcome: {
        accepted: 'Ausgelöst — wartet auf die Maschine',
        taken: 'Gesendet — noch nicht bestätigt',
        received: 'Von der Maschine empfangen',
        running: 'Läuft',
        started: 'Gestartet',
        'already-running': 'Lief bereits',
        stopped: 'Gestoppt',
        'already-stopped': 'War bereits gestoppt',
        synced: 'Aktualisiert',
        dirty: 'Arbeitsverzeichnis nicht sauber',
        'not-fast-forwardable': 'Kein Fast-Forward möglich',
        cleaned: 'Zurückgesetzt und nachgezogen',
        restarting: 'Neustart ausgelöst',
        'unknown-profile': 'Unbekanntes Profil',
        refused: 'Abgelehnt',
        failed: 'Fehlgeschlagen',
        'timed-out': 'Nicht abgeholt — keine Maschine hat den Befehl übernommen',
        'timed-out-no-result': 'Übernommen, aber ohne Ergebnis — die Maschine hat sich nicht mehr gemeldet',
        ok: 'Ausgeführt',
        'no-pane': 'Kein tmux-Fenster',
        'log-absent': 'Kein Protokoll vorhanden',
        reset: 'Geleert',
        uploaded: 'Hochgeladen',
        'handed-over': 'Übergeben',
      },
      log: {
        'toggle-open': 'Protokolle',
        'toggle-close': 'Protokoll schließen',
        'stream-build': 'Build-Log',
        'stream-agent': 'Agent-Log',
        'stream-group': 'Protokollquelle',
        lines: 'Zeilen',
        'newest-first': 'Neueste zuerst',
        'newest-at': 'Neueste Zeile {time}',
        copy: 'Protokoll kopieren',
        copied: 'Kopiert',
        'fetch-again': 'Neu laden',
        fetching: 'Wird geladen …',
        // Nur unter `sm` sichtbar: der Block bricht nicht um, damit die Spalte
        // mit den Zeitstempeln stehen bleibt — also muss er seitlich scrollen,
        // und das sagt auf einem Telefon niemandem etwas, bis es dasteht.
        'scroll-hint': 'Seitlich scrollen für lange Zeilen',
        'empty-title': 'Noch keine Zeilen gespeichert.',
        'empty-body': 'Die Maschine hat für diesen Stream nichts gemeldet.',
        'read-only-empty': 'Protokolle lesen braucht backoffice:fleet:read.',
      },
      activity: {
        title: 'Befehlsverlauf',
        'requested-by': 'Ausgelöst von',
        'requested-by-machine': 'Ausgelöst auf',
        'e2e-unlock-manual': 'Manueller Stack des Hosts',
        'lane-project': 'nur Projekt {project}',
        finish: 'nach der laufenden Runde',
        'engine-default': 'Standard der Maschine',
        'engine-lane': 'Lane {lane}',
        'engine-lane-project': 'Lane {lane} in {project}',
        'engine-unset': 'aufgehoben',
        'engine-plan': 'Planung {model}',
        'engine-implement': 'Umsetzung {model}',
        'dirty-paths': 'Geänderte Pfade ({count})',
        steps: {
          sent: 'gesendet',
          received: 'empfangen',
          running: 'läuft',
          settled: 'fertig',
        },
      },
    },
    skills: {
      'document-analyses': {
        title: 'Dokumentanalysen',
        empty: 'Keine Skills gefunden.',
        search: 'Skill suchen…',
        status: {
          active: 'Aktiv',
          inactive: 'Inaktiv',
          unconfigured: 'Nicht konfiguriert',
        },
        'last-edited-by': 'Zuletzt bearbeitet von {name} · {date}',
        'never-edited': '—',
        columns: {
          name: 'Skill',
          status: 'Status',
          'last-edited': 'Zuletzt bearbeitet',
        },
        'category-group': DOCUMENT_CATEGORY_GROUP_LABELS,
        category: DOCUMENT_CATEGORY_LABELS,
        fallback: {
          title: 'Fallback-Skill für Dokumente',
          subtitle:
            'Wird für jedes Dokument verwendet, das keinen passenden aktiven Kategorie-Skill hat — auch für noch nicht kategorisierte Dokumente.',
          status: 'Aktiv',
          'system-attribution': 'Systemseitig eingerichtet',
          'last-edited-by': 'Zuletzt bearbeitet von {name} · {date}',
        },
        editor: {
          'prompt-label': 'Analyse-Prompt',
          'baseline-marker': 'Fallback-Skill für Dokumente (Ausgangspunkt)',
          'baseline-hint':
            'Vorbefüllt mit dem aktuell wirksamen Prompt (Fallback-Skill). Passe ihn an und aktiviere den Skill, um ihn für diesen Dokumenttyp zu verwenden.',
          save: 'Speichern',
          activate: 'Skill aktivieren',
          deactivate: 'Skill deaktivieren',
          cancel: 'Abbrechen',
          'back-cta': 'Zurück zur Liste',
          saved: 'Änderungen gespeichert.',
          'last-edited-by': 'Zuletzt bearbeitet von {name} · {date}',
          'never-edited': 'Noch nicht bearbeitet',
          status: {
            active: 'Aktiv',
            inactive: 'Inaktiv',
            unconfigured: 'Nicht konfiguriert',
          },
          errors: {
            'prompt-empty': 'Der Prompt darf nicht leer sein.',
            'prompt-too-long': 'Der Prompt ist zu lang (maximal 10.000 Zeichen).',
            generic: 'Der Skill konnte nicht gespeichert werden. Bitte versuche es erneut.',
          },
          stale: {
            title: 'Skill wurde geändert',
            body: 'Dieser Skill wurde zwischenzeitlich geändert. Bitte lade die Seite neu, bevor du speicherst.',
            reload: 'Neu laden',
          },
          'deactivate-confirm': {
            title: 'Skill deaktivieren?',
            body: 'Analysen dieses Dokumenttyps verwenden anschließend wieder den Fallback-Skill.',
            confirm: 'Deaktivieren',
            cancel: 'Abbrechen',
          },
        },
        'fallback-editor': {
          title: 'Fallback-Skill für Dokumente',
          'prompt-label': 'Prompt',
          save: 'Speichern',
          cancel: 'Abbrechen',
          'back-cta': 'Zurück zur Liste',
          saved: 'Änderungen gespeichert.',
          status: 'Aktiv',
          'last-edited-by': 'Zuletzt bearbeitet von {name} · {date}',
          'system-attribution': 'Systemseitig eingerichtet',
          errors: {
            'prompt-empty': 'Der Prompt darf nicht leer sein.',
            'prompt-too-long': 'Der Prompt ist zu lang (maximal 10.000 Zeichen).',
            generic: 'Der Skill konnte nicht gespeichert werden. Bitte versuche es erneut.',
          },
          stale: {
            title: 'Skill wurde geändert',
            body: 'Dieser Skill wurde zwischenzeitlich geändert. Bitte lade die Seite neu, bevor du speicherst.',
            reload: 'Neu laden',
          },
        },
      },
      'fact-keys': {
        title: 'Merkmalschlüssel',
        empty: 'Keine Merkmalschlüssel gefunden.',
        search: 'Merkmal suchen…',
        'read-only-hint': 'Du kannst die Taxonomie ansehen, aber nicht bearbeiten.',
        'save-error': 'Die Änderung konnte nicht gespeichert werden. Bitte versuche es erneut.',
        create: {
          button: 'Merkmal anlegen',
          title: 'Merkmal anlegen',
          'id-hint': 'Gruppenkürzel und Name, zum Beispiel FLA.WOHNFLAECHE. Die ID lässt sich später nicht ändern.',
          'immutable-hint': 'Lässt sich nach dem Anlegen nicht mehr ändern.',
          placeholder: 'Bitte wählen',
          submit: 'Anlegen',
          cancel: 'Abbrechen',
        },
        retire: {
          button: 'Stilllegen',
          title: 'Merkmal stilllegen?',
          body: 'Für {label} ({key}) können danach keine neuen Werte mehr erfasst werden. Gespeicherte Werte bleiben lesbar. Das Merkmal zählt für kein Produkt mehr als Bedarf. Das lässt sich nicht rückgängig machen, und die ID kann nicht wiederverwendet werden.',
          'warning-document-analysis':
            'Die Dokumentanalyse befüllt dieses Merkmal noch. Neue Werte daraus gehen nach dem Stilllegen verloren.',
          'warning-property-field': 'Dieses Merkmal ist dem Objektfeld {propertyField} zugeordnet.',
          confirm: 'Stilllegen',
          cancel: 'Abbrechen',
        },
        columns: {
          key: 'ID',
          label: 'Bezeichnung',
          group: 'Gruppe',
          'value-type': 'Werttyp',
          frequency: 'Häufigkeit',
          categories: 'Objektarten',
          needs: 'Bedarf',
          origin: 'Herkunft',
          status: 'Status',
          scope: 'Bereich',
          'public-safe': 'Öffentlich sichtbar',
          'property-field': 'Objekt-Feld',
          parent: 'Gehört zu',
        },
        group: {
          AUF: 'Auftrag, Stichtage und Besichtigung',
          KAT: 'Kataster und Grundstück',
          LAG: 'Lage und Umfeld',
          PLR: 'Planungsrecht und öffentlich-rechtliche Lage',
          ERS: 'Erschließung und Beiträge',
          GB: 'Grundbuch, Rechte und Baulasten',
          GEB: 'Gebäude allgemein',
          FLA: 'Flächen',
          BAU: 'Bauteile und Ausstattung',
          ENE: 'Energie und Haustechnik',
          MOD: 'Modernisierung',
          ZUS: 'Zustand und Schäden',
          NEB: 'Nebengebäude, Stellplätze und Außenanlagen',
          NUT: 'Nutzung und Vermietung',
          WEG: 'Wohnungseigentum',
          ZVG: 'Zwangsversteigerung',
          UNT: 'Unterlagen und Auskünfte',
          SON: 'Sonstiges',
          VMK: 'Vermarktung',
          GEW: 'Gewerbe',
        },
        frequency: {
          ONCE: 'einmal',
          LIST: 'Liste',
          PER_BUILDING: 'je Gebäude',
          PER_UNIT: 'je Einheit',
          PER_RIGHT: 'je Recht',
          PER_DOCUMENT: 'je Unterlage',
          PER_FINDING: 'je Befund',
          PER_PARCEL: 'je Flurstück',
          PER_MEASURE: 'je Maßnahme',
          PER_PARTIAL_AREA: 'je Teilfläche',
          PER_LAND_REGISTER_SHEET: 'je Grundbuchblatt',
          PER_INVENTORY_ENTRY: 'je lfd. Nr. Bestandsverzeichnis',
          PER_OWNER: 'je Eigentümer',
          PER_TRANSITION: 'je Übergang',
          PER_BENEFICIARY: 'je Berechtigtem',
          PER_ENCROACHMENT: 'je Überbau',
          PER_BUILDING_ENCUMBRANCE: 'je Baulast',
        },
        origin: {
          OAB_0_1: 'oab-0.1',
          IMMOOS_ADDITION: 'immoOS-Ergänzung',
        },
        status: {
          ACTIVE: 'Aktiv',
          RETIRED: 'Stillgelegt',
        },
        product: {
          ACQUISITION_BRIEFING: 'Akquisebriefing',
          VALUATION_1A: 'Bewertung 1a',
          VALUATION_1B: 'Bewertung 1b',
          EXPOSE: 'Exposé',
        },
        need: {
          REQUIRED: 'Pflicht',
          RECOMMENDED: 'Empfohlen',
          OPTIONAL: 'Optional',
          NOT_USED: 'Nicht genutzt',
        },
        needs: {
          columns: {
            category: 'Objektart',
            'marketing-type': 'Vermarktungsart',
            subtype: 'Unterart',
            situation: 'Situation',
            need: 'Bedarf',
          },
          'marketing-type': { ALL: 'Alle', SALE: 'Kauf', RENTAL: 'Miete' },
          subtype: { ALL: 'Alle' },
          situation: { ALL: 'Alle', LET: 'Vermietet', VACANT: 'Bezugsfrei', NEW_BUILD: 'Neubau' },
          'add-row': 'Eintrag hinzufügen',
          'remove-row': 'Eintrag entfernen',
          'product-control': '{control} – {product}',
          'row-control': '{control} – {product}, Eintrag {position}',
          conflict:
            'Dieser Bedarf widerspricht dem Eintrag {conditions} ({need}): Ein Objekt kann beide erfüllen, und keiner ist genauer. Lege einen Eintrag mit beiden Bedingungen an oder gleiche die Stufen an.',
        },
        'need-filter-option': '{need} für {product}',
        filters: {
          group: 'Gruppe',
          need: 'Bedarf',
          origin: 'Herkunft',
          status: 'Status',
          all: 'Alle',
        },
        'yes-no': {
          true: 'Ja',
          false: 'Nein',
        },
        'number-style': {
          PLAIN_INTEGER: 'Ganzzahl ohne Tausenderpunkt',
          GROUPED_DECIMAL: 'Dezimal mit Tausenderpunkt',
        },
        source: {
          landiq: 'immoOS IQ',
          altlasten_kampfmittel_auskunft: 'Altlasten- und Kampfmittelauskunft',
          auftrag_beweisbeschluss: 'Auftrag, Beweisbeschluss',
          bauakte_genehmigung: 'Bauakte, Genehmigung',
          baubeschreibung: 'Baubeschreibung',
          baulastenverzeichnis: 'Baulastenverzeichnis',
          bebauungsplan_planungsauskunft: 'Bebauungsplan, Planungsauskunft',
          besichtigungsprotokoll: 'Besichtigungsprotokoll',
          betriebskostenabrechnung: 'Betriebskostenabrechnung',
          bgf_berechnung: 'BGF-Berechnung',
          boris: 'BORIS',
          denkmal_auskunft: 'Denkmalauskunft',
          eigentuemer_auskunft: 'Auskunft des Eigentümers',
          energieausweis: 'Energieausweis',
          erschliessungsbeitrag_auskunft: 'Erschließungsbeitragsauskunft',
          flurkarte_kataster: 'Flurkarte, Kataster',
          fotos_aussen: 'Fotos außen',
          fotos_innen: 'Fotos innen',
          geoportal_luftbild: 'Geoportal, Luftbild',
          grundbuch: 'Grundbuch',
          grundrisse_schnitte: 'Grundrisse, Schnitte',
          heizung_schornsteinfeger: 'Heizung, Schornsteinfeger',
          mietspiegel_wohnlagenkarte: 'Mietspiegel, Wohnlagenkarte',
          mietvertrag_mieterliste: 'Mietvertrag, Mieterliste',
          modernisierungsbelege: 'Modernisierungsbelege',
          satzungen_sanierung_erhaltung: 'Satzungen (Sanierung, Erhaltung)',
          sonstiges: 'Sonstiges',
          teilungserklaerung_aufteilungsplan: 'Teilungserklärung, Aufteilungsplan',
          weg_unterlagen: 'WEG-Unterlagen',
          wohnflaechenberechnung: 'Wohnflächenberechnung',
        },
        entry: {
          'needs-heading': 'Bedarf je Produkt und Objektart',
          'details-heading': 'Aus dem Objektaufnahmebogen',
          'details-hint': 'Diese Angaben stammen aus dem Objektaufnahmebogen und lassen sich hier nicht bearbeiten.',
          description: 'Beschreibung',
          'value-list': 'Werteliste',
          subfields: 'Unterfelder',
          sources: 'Quellen',
          'photo-derivable': 'Aus Fotos ableitbar',
          'consequence-when-missing': 'Folge bei Fehlen',
          'collection-hint': 'Erhebungshinweis',
          'rule-references': 'Regelverweise',
          'number-style': 'Zahlenformat',
          unit: 'Einheit',
          parent: 'Gehört zu',
          photo: {
            YES: 'ja',
            PARTLY: 'teilweise',
            NO: 'nein',
          },
          'personal-data-hint': 'Personenbezogene Angaben können nicht veröffentlicht werden.',
          save: 'Speichern',
          cancel: 'Abbrechen',
          close: 'Schließen',
          'load-error': 'Der Eintrag konnte nicht geladen werden. Bitte versuche es erneut.',
        },
        'value-type': {
          text: 'Text',
          number: 'Zahl',
          enum: 'Auswahl',
          boolean: 'Ja/Nein',
          date: 'Datum',
          list: 'Liste',
          structure: 'Struktur',
          document_reference: 'Dokumentverweis',
        },
        scope: {
          PROPERTY_INTRINSIC: 'Objektmerkmal',
          DEAL_CONTEXT: 'Deal-Kontext',
        },
        'property-field-none': 'Keine Zuordnung',
        'public-safe-warning':
          'Öffentlich sichtbare Merkmale dürfen in einem Exposé erscheinen. Aktiviere das nur für Angaben, die Käufer sehen dürfen.',
        'public-safe-confirm': {
          title: 'Merkmal öffentlich sichtbar machen?',
          confirm: 'Öffentlich sichtbar machen',
          cancel: 'Abbrechen',
        },
      },
      'agent-skills': {
        title: 'Agent-Skills',
        empty: 'Noch keine Agent-Skills angelegt.',
        search: 'Agent-Skill suchen…',
        new: 'Neuer Agent-Skill',
        'last-edited-by': 'Zuletzt bearbeitet von {name} · {date}',
        'never-edited': '—',
        columns: {
          name: 'Name',
          key: 'Schlüssel',
          status: 'Status',
          triggers: 'Auslöser',
          'last-edited': 'Zuletzt bearbeitet',
        },
        status: {
          active: 'Aktiv',
          inactive: 'Inaktiv',
        },
        trigger: {
          EVENT: 'Ereignis',
          SCHEDULE: 'Zeitplan',
          MANUAL: 'Manuell',
        },
        editor: {
          'create-title': 'Neuer Agent-Skill',
          'key-label': 'Schlüssel',
          'key-hint': 'Nach dem Anlegen nicht mehr änderbar. Kleinbuchstaben, Ziffern, Bindestriche, Unterstriche.',
          'name-label': 'Name',
          'description-label': 'Beschreibung',
          'description-hint': 'Was dieser Skill in einfacher Sprache tut — Mitglieder sehen diesen Text.',
          'instructions-label': 'Anweisungen',
          'instructions-hint': 'Der Prompt, mit dem jeder Lauf dieses Skills startet.',
          'triggers-label': 'Unterstützte Auslöser',
          'triggers-hint': 'Wähle mindestens einen. Eine Instanz nutzt genau einen davon.',
          'allowlist-label': 'Standard-Werkzeuge',
          'allowlist-hint':
            'Die Werkzeuge, die eine neue Instanz vorschlägt. Mitglieder können sie einschränken, aber nicht erweitern.',
          'allowlist-empty': 'Keine Werkzeuge ausgewählt.',
          'approval-window-label': 'Freigabefenster (Tage)',
          'approval-window-hint':
            'Wie lange eine vorgeschlagene Aktion auf Freigabe wartet, bevor sie verfällt: {min} bis {max} Tage.',
          create: 'Anlegen',
          save: 'Speichern',
          activate: 'Aktivieren',
          deactivate: 'Deaktivieren',
          cancel: 'Abbrechen',
          created: 'Agent-Skill angelegt. Er ist noch inaktiv — aktiviere ihn, wenn er bereit ist.',
          saved: 'Gespeichert.',
          'inactive-hint': 'Dieser Agent-Skill ist inaktiv. Mitglieder können ihn nicht aktivieren.',
          errors: {
            generic: 'Die Änderung konnte nicht gespeichert werden. Bitte versuche es erneut.',
          },
          stale: {
            title: 'Zwischenzeitlich geändert',
            body: 'Jemand anderes hat diesen Agent-Skill bearbeitet, seit du die Seite geöffnet hast. Lade neu, um die aktuelle Fassung zu sehen — deine Eingaben gehen dabei verloren.',
            reload: 'Neu laden',
          },
          'deactivate-confirm': {
            title: 'Agent-Skill deaktivieren?',
            body: 'Alle Instanzen dieses Skills pausieren. Die Freigaben der Mitglieder und der Verlauf bleiben erhalten — beim Aktivieren laufen sie unverändert weiter.',
            confirm: 'Deaktivieren',
            cancel: 'Abbrechen',
          },
        },
      },
    },
  },
} as const;
